'use client';
import { RVOL_MIN_SAMPLES } from '@/lib/rvol/rvolThresholds';
// CACHE BUST: v4.1 - FORCE NEW HASH - 1767287000000 - Fixed getMarketStatus error and alert fetching
// This comment forces Next.js to generate a new content hash for this file
// Alerts: `/api/alerts/recent` with `priceMode=alert` + `barTimestamp` for marker placement (see MASSIVE_STOCKS_DATA_REFERENCE.md).

import { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'next/navigation';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import SymbolFeedLink from '@/components/SymbolFeedLink';
import ChartNewsButton from '@/components/ChartNewsButton';
// Lazy + client-only: DueDiligencePanel mounts only when the user opens the DD rail tab
// (railTab==='dd', and only when !isShot && !isEmbed), so it is never in the screenshot capture and
// never needed at first paint. Loading a heavy panel on demand keeps it out of the initial chart chunk.
const DueDiligencePanel = dynamic(() => import('@/components/DueDiligencePanel'), { ssr: false, loading: () => <div className="h-64 w-full animate-pulse rounded-xl border border-border/60 bg-card/30" /> });
import ShareMenu from '@/components/ShareMenu';
import { useSession } from 'next-auth/react';
import { useTheme } from 'next-themes';
import { ArrowLeft, Activity, Flame, Eye, Newspaper, ShoppingCart, Star, User, ShieldCheck } from 'lucide-react';
import type { IChartApi, ISeriesApi, LineStyle as LineStyleEnum } from 'lightweight-charts';
// LineStyle is a RUNTIME enum in lightweight-charts, so importing it as a value pulls the whole chart
// library into this route's initial JS chunk — defeating the `await import('lightweight-charts')` this
// file uses everywhere else (the library should load only when the chart actually mounts). Inlining the
// enum's numeric values keeps all ~28 `LineStyle.Dashed`/`.Dotted` call sites readable while dropping
// the eager import. Values verified against lightweight-charts 4.1.3 (Solid 0 · Dotted 1 · Dashed 2 ·
// LargeDashed 3 · SparseDotted 4); they are a documented, stable part of the public API.
// Each value is cast to the (type-only) LineStyle enum type so every `LineStyle.Dashed` call site
// still satisfies lightweight-charts' `lineStyle: LineStyle` params — the cast erases at runtime.
const LineStyle = {
  Solid: 0 as LineStyleEnum, Dotted: 1 as LineStyleEnum, Dashed: 2 as LineStyleEnum,
  LargeDashed: 3 as LineStyleEnum, SparseDotted: 4 as LineStyleEnum,
};
import ChartStatsReadout from '@/components/ChartStatsReadout';
import { applyHeld, heldSince } from '@/lib/minichartMerge';
import PriceAlertButton from '@/components/PriceAlertButton';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import GameBuySell from '@/components/GameBuySell';
// Lazy + client-only: these three in-flow promos/asks all render only when !isShot && !isEmbed, so
// they are provably absent from the #capture-root screenshot and not needed for first paint. Each
// fetches its own state client-side anyway; loading them on demand trims the initial chunk.
const PremiumInlineAsk = dynamic(() => import('@/components/PremiumInlineAsk'), { ssr: false });
const DailyBriefPromo = dynamic(() => import('@/components/DailyBriefPromo'), { ssr: false });
const VerifyInlineAsk = dynamic(() => import('@/components/VerifyInlineAsk'), { ssr: false });
import { patternStyle, mergeCoincidentNecklines, resolveContested, selectActivePatterns } from '@/lib/signals/levels';
import { calculateOBV, calculateADLine, calculateCMF, computeAccDist } from '@/lib/indicators/accumulation';

// Client-side debug gate. These [STATS]/[CHART] logs fired on every 2s poll (~140+ console writes/min)
// and, with DevTools open, were a real contributor to the page jank. Off unless NEXT_PUBLIC_CHART_DEBUG=1.
// Keep console.error/console.warn as-is; only the hot informational logs route through this.
const dbg: typeof console.log = process.env.NEXT_PUBLIC_CHART_DEBUG === '1' ? console.log : () => {};

// ── Timeframe aggregation ─────────────────────────────────────────────────────
type Timeframe = '1m' | '5m' | '15m' | '30m' | '1h' | '1D';
const TIMEFRAMES: { value: Timeframe; label: string; minutes: number }[] = [
  { value: '1m',  label: '1m',  minutes: 1  },
  { value: '5m',  label: '5m',  minutes: 5  },
  { value: '15m', label: '15m', minutes: 15 },
  { value: '30m', label: '30m', minutes: 30 },
  { value: '1h',  label: '1h',  minutes: 60 },
  { value: '1D',  label: '1D',  minutes: 1440 },
];

/**
 * Minimum pixels per candle, i.e. HOW FAR THE CHART CAN ZOOM OUT.
 *
 * 🚨 THIS IS A HARD CEILING ON VISIBLE HISTORY, WHICH IS NOT OBVIOUS FROM THE NAME. The widest view
 * possible is (chart width / minBarSpacing) candles - nothing else matters, not how much data is
 * loaded. It was 2, and the chart is ~1100px, so the ceiling was ~550 candles. Owner 2026-08-12:
 * "$CPOP chart shows weird and i cannot zoom out" - the header read "4531 bars" while the view
 * spanned 4:00 AM to 1:00 PM, which is 540 one-minute candles. They were already fully zoomed out
 * and the remaining ~4,000 bars were unreachable.
 *
 * At 0.1 the ceiling is ~11,000 candles, which clears the loaded history on every timeframe with
 * room to spare. Candles go sub-pixel long before that and render as a density band - the correct
 * appearance when looking at weeks at once, and what every real charting tool does at that zoom.
 *
 * ⚠ EVERY PANE MUST USE THIS SAME VALUE. The volume and RVOL panes are time-synced to the main
 * chart; if one can zoom wider than another they desync and stop lining up with the price. That is
 * why this is a constant and not five literals.
 */
const MIN_BAR_SPACING = 0.1;

/**
 * Calendar days of 1-minute history to load for a given bar size.
 *
 * Proportional to the bar size so each timeframe renders a comparable number of candles. Before
 * this the ladder flattened at 30 days, which meant 1h returned FEWER usable bars than 15m — the
 * chart got emptier as you zoomed out, which is backwards.
 */
/**
 * Calendar days of history to load per bar size, tuned to land every timeframe in the 300-1000 bar
 * range rather than to equalise them exactly.
 *
 * Strict proportionality (2 days x tfMinutes) would equalise bar counts but demands 120 days for 1h,
 * which is ~46k minute bars per request for no visible gain — a chart only renders 150-400 candles at
 * a time. 30m and 1h get more than a naive reading suggests because those were the thin ones.
 *
 * Approx bars at ~960 extended-hours minutes/day: 1m 1920 · 5m 960 · 15m 640 · 30m 640 · 1h 720 · 1D 500.
 */
const TF_LOOKBACK_DAYS: Array<[number, number]> = [
  [1, 2], [5, 5], [15, 10], [30, 20], [60, 45], [1440, 730],
];
// Not exported: an App Router page.tsx may only export default/metadata/dynamic/revalidate/viewport
// and friends, so any other export fails Next's generated route-type check. Nothing outside this file
// used it, so it is module-private now rather than moved to a lib.
function daysForTimeframe(tfMinutes: number): number {
  for (const [m, d] of TF_LOOKBACK_DAYS) if (tfMinutes <= m) return d;
  return 730;
}

/**
 * True during the hours a US equity can actually trade: 4:00 AM - 8:00 PM ET on a weekday (premarket +
 * RTH + after-hours). Used to STOP the recurring polls overnight and on weekends. The chart is painted on
 * mount regardless; it is only the recurring refresh that is pointless when nothing can move - a tab left
 * open overnight was polling /api/chart/candles every 30s (and the position endpoints every 60s) for bars
 * that do not exist, ~40k requests overnight on one real tab. app/stream/page.tsx gates on the same window.
 * See chart-api-refetches-one-symbol-39k-times-1007.
 */
function isMarketHoursET(): boolean {
  const et = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const day = et.getDay();
  if (day === 0 || day === 6) return false;
  const hour = et.getHours();
  return hour >= 4 && hour < 20;
}

function aggregateCandles(candles: Candle[], minutes: number): Candle[] {
  if (minutes <= 1 || candles.length === 0) return candles;
  const bucketMs = minutes * 60 * 1000;
  const buckets = new Map<number, Candle>();
  for (const c of candles) {
    const key = Math.floor(c.timestamp / bucketMs) * bucketMs;
    const existing = buckets.get(key);
    if (!existing) {
      buckets.set(key, { timestamp: key, open: c.open, high: c.high, low: c.low, close: c.close, volume: c.volume, baseVol: c.baseVol, baseN: c.baseN });
    } else {
      existing.high   = Math.max(existing.high, c.high);
      existing.low    = Math.min(existing.low,  c.low);
      existing.close  = c.close;
      existing.volume = (existing.volume ?? 0) + (c.volume ?? 0);
      // The expectation sums exactly like volume does, which is the whole reason it is shipped per
      // MINUTE: 5m/15m/30m all get a correct denominator with no extra work and no timezone math.
      if (c.baseVol != null) existing.baseVol = (existing.baseVol ?? 0) + c.baseVol;
      // Weakest minute in the bucket sets confidence — averaging would hide a 1-session minute.
      if (c.baseN != null) existing.baseN = existing.baseN == null ? c.baseN : Math.min(existing.baseN, c.baseN);
    }
  }
  const sorted = [...buckets.values()].sort((a, b) => a.timestamp - b.timestamp);

  // Fill MISSING intraday buckets with a flat bar (prior close, volume 0) so the higher-timeframe
  // series is contiguous — lightweight-charts leaves a visual gap for every absent bucket, and in
  // after-hours many TF windows have no trades. Skip large gaps (>~2h) so overnight stays a clean
  // session break instead of a wall of synthetic bars. (This mirrors the 1-minute synthetic fill
  // the candles API already does, which is why 1m looks continuous and 5m didn't.)
  const maxGapMs = 2 * 60 * 60 * 1000;
  const out: Candle[] = sorted.length ? [sorted[0]] : [];
  for (let i = 1; i < sorted.length; i++) {
    const prev = out[out.length - 1];
    const cur = sorted[i];
    const gap = cur.timestamp - prev.timestamp;
    if (gap > bucketMs && gap <= maxGapMs && prev.close > 0) {
      for (let t = prev.timestamp + bucketMs; t < cur.timestamp; t += bucketMs) {
        out.push({ timestamp: t, open: prev.close, high: prev.close, low: prev.close, close: prev.close, volume: 0 });
      }
    }
    out.push(cur);
  }
  return out;
}

interface TradeMeta {
  /** Emitting book: 'Watcher' (live) vs 'WatcherPaper' (dev mirror); absent on legacy alerts. */
  instance?: string;
  side?: 'buy' | 'sell';
  strategyLabel?: string;
  /**
   * The SETUP that opened the position (coil-under9, breakout-orb, topplay-first(5m), …).
   * ⚠ Distinct from `strategyLabel`, which is persona + LANE ("The Watcher · scalp") and is therefore
   * identical across every position the same AI takes on one symbol in one lane.
   * ⚠ ABSENT ON EVERY ALERT WRITTEN BEFORE 2026-08-05 — the field did not exist. Consumers must treat
   * undefined as "unknown", never as a setup named "".
   */
  setup?: string;
  reason?: string;
  shares?: number;
  entryPrice?: number;
  exitPrice?: number;
  positionSizePct?: number;
  realizedPl?: number;
  realizedPlPct?: number;
  peakProfitPct?: number;
  stopRule?: string;
  alpacaStatus?: 'filled' | 'chase_abort' | 'ema_abort' | 'exhausted' | 'window_closed' | 'blocked' | 'error';
  alpacaReason?: string;
  /** UMBRELLA TRADE (owner: one card per round trip): scale-out legs folded into the pair. */
  bankedQty?: number;        // shares sold via scale-outs before the final exit
  bankedAvg?: number;        // volume-weighted price of those scale-outs
  finalExitQty?: number;     // the runner's shares on the last fill
  finalExitPrice?: number;   // the runner's real last-fill price (exitPrice becomes the blended avg)
}

/** A lightweight-charts series marker, as assembled in the markers effect. */
type ChartMarker = {
  time: number;
  position: 'aboveBar' | 'belowBar' | 'inBar';
  color: string;
  shape: 'arrowUp' | 'arrowDown' | 'circle' | 'square';
  text: string;
  size: number;
  id: string;
};

/** Shape of /api/watcher/condition-replay — replayKeyLevel over ALL enabled Watcher
 *  conditions, honoring the ai-config toggles for the selected book (live/dev). */
interface WatcherReplayResponse {
  symbol: string;
  book?: 'live' | 'dev';
  masterOff?: boolean;
  disabled?: string[];
  notSimulated?: string[];
  perCond?: Record<string, { count: number; wins: number; losses: number; avgPlPct: number }>;
  trades: Array<{
    entryDate: string;
    entryPrice: number;
    exitDate: string | null;
    exitPrice: number | null;
    exitReason: string | null;
    plPct: number | null;
    open: boolean;
    setup?: string | null;
    entryReason?: string | null;
    stop?: number;
    target?: number;
    plannedRr?: number;
  }>;
  stats: { count: number; wins: number; winPct: number; totalPlPct: number; avgPlPct: number };
}

interface Alert {
  id: string;
  symbol: string;
  type: string;
  message: string;
  timestamp: number;
  /** Bar minute (ms) from API - align markers with candles; `timestamp` may be insert time. */
  barTimestamp?: number;
  severity: string;
  /** Thoth/Ra trade metadata — set on WS path; parse from message for DB/API replay. */
  thoth?: TradeMeta;
  data?: {
    rvol?: number;
    percentChange?: number;
    dailyPercentChange?: number;
    price?: number;
    volume1m?: number;
    emaPercentDiff?: number;
    /** Both are real fields on the API's `baseData` payload (app/api/alerts/recent/route.ts). They
     *  were missing here, so TS flagged every read of them - the code was right, the type was not. */
    delta?: number | null;
    alertLevel?: number | null;
    triggeredBy?: string;
    /** Explicit avatar for a non-AI (user / community member) trade — when set it wins over the
     *  AI avatar map, so a member's trade shows THEIR profile picture, not a default AI face. */
    avatarUrl?: string;
    /** Display name for a non-AI trader (member username), used in tooltips/labels. */
    traderName?: string;
  };
  /** News-alert metadata (from /api/alerts/recent). Headline is `message`; the rest lives here. */
  news?: {
    url?: string | null;
    sentiment?: string | null;
    publisher?: string | null;
    imageUrl?: string | null;
    publishedUtc?: string | null;
  };
}

// ── Thoth/Ra trade helpers (inline — no circular import) ──────────────────────
const _THOTH_TAG = '\n__THOTH_JSON__';
function parseTradeMeta(alert: Alert): TradeMeta | null {
  if (alert.thoth?.side) return alert.thoth;
  const msg = alert.message || '';
  const idx = msg.indexOf(_THOTH_TAG);
  if (idx < 0) return null;
  try { return JSON.parse(msg.slice(idx + _THOTH_TAG.length).trim()) as TradeMeta; } catch { return null; }
}
function stripTradeJson(msg: string): string {
  const idx = msg.indexOf(_THOTH_TAG);
  return idx >= 0 ? msg.slice(0, idx).trimEnd() : msg;
}
function alpacaPaperBadge(status: TradeMeta['alpacaStatus'], reason?: string): { label: string; tooltip: string } {
  switch (status) {
    case 'chase_abort': return { label: `Paper · chase ${reason ?? ''}`.trim(), tooltip: `Price drifted ${reason ?? 'too fast'} above signal - chase aborted` };
    case 'ema_abort':   return { label: 'Paper · EMA', tooltip: `Price left EMA buy zone${reason ? ` (${reason})` : ''}` };
    case 'exhausted':   return { label: 'Paper · no fill', tooltip: `All limit retries exhausted${reason ? ` - ${reason}` : ''}` };
    case 'window_closed': return { label: 'Paper · closed', tooltip: `Trading window closed before fill${reason ? ` (${reason})` : ''}` };
    case 'blocked':     return { label: 'Paper · blocked', tooltip: `Alpaca blocked - ${reason ?? 'pre-flight check failed'}` };
    case 'error':       return { label: 'Paper · error', tooltip: `Alpaca error - ${reason ?? 'unknown'}` };
    default:            return { label: 'Paper', tooltip: 'No Alpaca fill found - order may have been rejected or Ra was in paper-only mode' };
  }
}
// A member's paper-game trade (USER_TRADE_BUY / USER_TRADE_SELL). The Live Alerts feed may
// lowercase the type (user_trade_buy), so match case-insensitively on both the type and the
// carried triggeredBy — these render with the MEMBER'S profile picture + a trade hover, exactly
// like an AI trader, never the generic RVOL dot.
function isMemberTradeAlert(alert: Alert): boolean {
  const t = String(alert.type ?? '').toLowerCase();
  const tb = String(alert.data?.triggeredBy ?? '').toLowerCase();
  return t.startsWith('user_trade') || tb.startsWith('user_trade');
}
function isTradeAlert(alert: Alert): boolean {
  return alert.type === 'thoth' || alert.type === 'ra' || alert.type === 'osiris' || alert.type === 'solomon' || alert.type === 'watcher' || alert.type === 'rider' ||
    alert.data?.triggeredBy === 'thoth' || alert.data?.triggeredBy === 'ra' || alert.data?.triggeredBy === 'osiris' || alert.data?.triggeredBy === 'solomon' || alert.data?.triggeredBy === 'watcher' || alert.data?.triggeredBy === 'rider' ||
    // A member's paper-game buy/sell is a first-class trade marker (their picture + trade hover).
    isMemberTradeAlert(alert) ||
    // ANY alert that carries buy/sell trade metadata is a trade — render it as a profile-picture
    // trade card (never the green RVOL dot), whatever the trader source. Future-proofs user/community
    // trades so a BUY/SELL is never confused with a volume alert.
    parseTradeMeta(alert) !== null;
}

// Prefix for SYNTHETIC sell alerts fabricated from an Alpaca sell FILL when no real
// sell ALERT was emitted (wave-exit / trailing-stop / blackout exits sell on Alpaca
// but frequently emit no alert). These let a fill-closed Ra trade flow through the
// SAME render paths as an alert-closed trade (exit avatar + hover P&L line).
const RA_FILL_EXIT_PREFIX = 'ra-fillexit-';
const WATCHER_FILL_EXIT_PREFIX = 'watcher-fillexit-';
// LIVE books for the Watcher persona: 'Watcher' (legacy live) AND 'Rider' (the new paper account — the
// live story the site tells). Only 'WatcherPaper' (the dev mirror) is a DEV book. The old checks compared
// instance !== 'Watcher', which silently treated every RIDER marker as dev — so a journal-SYNTHESIZED
// Rider marker (created exactly when the real alert is missing) was hidden by the dev filter, and ADVB
// (5 trades on 07-24, all synthetic legs) rendered ZERO icons.
/**
 * AXIS + CROSSHAIR LABELS. A DAILY series must read as DATES; an intraday one as a clock.
 * There was already a useEffect flipping `timeVisible:false` for 1D, but it could never work: the
 * tickMarkFormatter below is what actually renders the label, and it was hardcoded to
 * toLocaleTimeString. So every daily candle stamped its ET close and the axis read
 * "8:00 PM · 7:00 PM · 7:00 PM …" instead of a series of dates.
 * Both formatters take `isDaily` rather than closing over state, because the chart is created once
 * and the timeframe changes underneath it — a captured value would freeze at whatever was selected
 * on mount. Callers pass timeframeRef.current, which is kept in sync.
 */
const fmtAxisTick = (time: number, isDaily: boolean) => {
  const d = new Date(time * 1000);
  return isDaily
    ? d.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' })
    : d.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', hour12: true });
};
/** Crosshair/hover label. Owner asked for an explicit 7/27/2026 on a daily bar. */
const fmtCrosshairTime = (time: number, isDaily: boolean) => {
  const d = new Date(time * 1000);
  return isDaily
    ? d.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'numeric', day: 'numeric', year: 'numeric' })
    : d.toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
};

const isLiveWatcherBook = (inst?: string | null) => !inst || inst === 'Watcher' || inst === 'Rider';

// ── Watcher condition-replay visual language (shared by icons, chips, summary) ──
const WATCHER_SETUP_COLORS: Record<string, string> = {
  double_bottom: '#a5b4fc', ema50: '#fbbf24', ema200: '#fb923c',
  breakout: '#38bdf8', chase: '#22d3ee', continuation: '#c084fc',
};
const WATCHER_SETUP_TAGS: Record<string, string> = {
  double_bottom: 'DB', ema50: 'E50', ema200: 'E200',
  breakout: 'BO', chase: 'CH', continuation: 'CT',
};
const watcherCondKey = (setup?: string | null, entryReason?: string | null) =>
  setup === 'breakout' && String(entryReason ?? '').startsWith('chase_breakout') ? 'chase' : String(setup ?? '');
const mdyToIso = (mdy: string) => { const q = mdy.split('/'); return q.length === 3 ? `${q[2]}-${q[0]}-${q[1]}` : mdy; };
function isFillExitAlert(alert: Alert): boolean {
  return alert.id.startsWith(RA_FILL_EXIT_PREFIX) || alert.id.startsWith(WATCHER_FILL_EXIT_PREFIX);
}
/** Build a synthetic SELL alert from an Alpaca sell fill, so it pairs + renders
 *  like a normal exit alert. exitPrice/shares come from the fill; barTimestamp is the
 *  fill's transaction time so the exit avatar lands on the right candle. The strategy
 *  controls the alert type so the avatar resolves to the right image (Ra vs Watcher)
 *  and the per-strategy fill lookup (alpacaFills vs watcherFills) is used on hover. */
function makeFillExitAlert(
  symbol: string,
  fill: { id: string; price: number; qty: number; transactionTime: string },
  fillMs: number,
  strategy: 'ra' | 'watcher' = 'ra',
): Alert {
  const prefix = strategy === 'watcher' ? WATCHER_FILL_EXIT_PREFIX : RA_FILL_EXIT_PREFIX;
  return {
    id: `${prefix}${fill.id}`,
    symbol,
    type: strategy,
    message: strategy === 'watcher' ? 'The Watcher exit (Alpaca fill)' : 'Ra exit (Alpaca fill)',
    timestamp: fillMs,
    barTimestamp: fillMs,
    severity: 'info',
    thoth: { side: 'sell', exitPrice: fill.price, shares: fill.qty, reason: 'alpaca_fill_exit' },
    data: { price: fill.price, triggeredBy: strategy },
  };
}

/** Ground-truth Watcher trade row from trading_journal (both books) — the source the
 *  chart pairs Watcher trades from (journal-backed rendering, 2026-07-08). */
type WatcherJournalTrade = {
  id: string; aiName: string;
  /** The BOOK discriminator, and the real pairing key. `aiName` is 'Watcher' for every engine since
   *  the 2026-07-25 consolidation, so it can no longer distinguish books; `engine` still can. */
  engine?: string;
  status: 'open' | 'closed';
  entryMs: number; avgCost: number; shares: number; entryReason: string | null;
  exitMs: number | null; exitPrice: number | null; exitReason: string | null;
  bankedQty?: number; bankedProceeds?: number;
  /** Authoritative blended round-trip result straight from trading_journal. Prefer these over any
   *  local re-derivation: the journal books every scale-out leg, the card cannot see them all. */
  realizedPl?: number | null; realizedPlPct?: number | null;
};

/** Synthetic marker alert for a journal trade leg whose real alert is missing from the
 *  loaded window (same pattern as makeFillExitAlert) — the journal decides WHAT trades
 *  exist; alerts are only where the markers draw. */
function makeJournalTradeAlert(symbol: string, jt: WatcherJournalTrade, side: 'buy' | 'sell'): Alert {
  const ms = side === 'buy' ? jt.entryMs : (jt.exitMs ?? jt.entryMs);
  const px = side === 'buy' ? jt.avgCost : (jt.exitPrice ?? 0);
  const plPct = jt.avgCost > 0 && (jt.exitPrice ?? 0) > 0 ? ((jt.exitPrice! - jt.avgCost) / jt.avgCost) * 100 : undefined;
  return {
    id: `wjournal-${side}-${jt.id}`,
    symbol,
    type: 'watcher',
    message: side === 'buy' ? 'The Watcher entry (journal)' : 'The Watcher exit (journal)',
    timestamp: ms,
    barTimestamp: ms,
    severity: 'info',
    thoth: side === 'buy'
      ? { side: 'buy', entryPrice: px, shares: jt.shares, reason: jt.entryReason ?? undefined, instance: jt.engine || jt.aiName }
      : { side: 'sell', entryPrice: jt.avgCost, exitPrice: px, shares: jt.shares,
          reason: jt.exitReason ?? 'journal_exit', realizedPlPct: plPct, instance: jt.engine || jt.aiName },
    data: { price: px, triggeredBy: 'watcher' },
  };
}

/**
 * A boolean that survives a reload. Drop-in for useState<boolean> on the indicator toggles.
 *
 * Owner 2026-07-28: every indicator reset to off on refresh, so anyone who wanted RVOL, RSI or VWAP
 * had to re-enable it through the menu on every page load and after every chart navigation.
 *
 * ⚠ THE RESTORE RUNS AFTER MOUNT, NOT DURING RENDER. This page is server-rendered, and reading
 * localStorage while rendering would produce different markup on the client than the server sent and
 * trip a hydration mismatch. The cost is that a persisted-ON indicator appears a frame late, which is
 * invisible next to the chart's own async load.
 *
 * The write is guarded on `restored` so the initial default cannot be flushed to storage before the
 * restore has run, which would erase the saved value on every mount.
 * Storage is best-effort throughout: private mode and disabled storage both throw, and an indicator
 * that merely forgets its state is not worth breaking the chart over.
 */
function usePersistentBool(key: string, initial: boolean): [boolean, React.Dispatch<React.SetStateAction<boolean>>] {
  const [value, setValue] = useState(initial);
  const restored = useRef(false);
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === '1' || raw === '0') setValue(raw === '1');
    } catch { /* storage unavailable — stay on the default */ }
    restored.current = true;
  }, [key]);
  useEffect(() => {
    if (!restored.current) return;
    try { window.localStorage.setItem(key, value ? '1' : '0'); } catch { /* ignore */ }
  }, [key, value]);
  return [value, setValue];
}

const candleTime = (c: { timestamp: number }) => c.timestamp;

interface Candle {
  timestamp: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  /** Expected volume for this bar's minutes (volume_baseline, per minute-of-day). See the RVOL pane. */
  baseVol?: number;
  /** Sessions the expectation is averaged over — drives the low-confidence styling. */
  baseN?: number;
}

/** RVOL for a bar: actual volume over expected volume. Null when there is no usable expectation,
 *  which the pane renders as a gap rather than as 0x. */
function rvolOf(c: Candle): number | null {
  if (!c.baseVol || c.baseVol <= 0) return null;
  const v = c.volume ?? 0;
  if (!Number.isFinite(v) || v <= 0) return null;
  // Zero-volume bars are excluded deliberately, for two reasons. They are almost always the SYNTHETIC
  // gap-fill bars both the API and aggregateCandles insert to keep the time axis continuous on an
  // illiquid name, so "0x" would be an artifact of our own padding rather than a fact about the tape.
  // And 0 has no position on a log axis at all. Shipping without this made the pane header read
  // "0.0x" whenever the newest bar happened to be a filler.
  return v / c.baseVol;
}
/** Below this many sampled sessions the denominator is too thin to trust; the bar is drawn muted.
 *  Measured 2026-07-28: AAPL/TSLA/MU average 63 sessions per minute with ZERO thin minutes, but a
 *  brand-new mover like EGG averaged 6 and had 314 of 391 RTH minutes under this bar. The baseline is
 *  weakest on exactly the names people most want to inspect, so the UI has to say so. */
// ⛔ WAS A HAND-COPIED `= 10`; see rvol/rvolThresholds.ts for why the boundary forced the copy.

/**
 * Top of the RVOL axis: the 99th percentile of the plotted bars, not the max.
 *
 * A single 800x print was dragging the axis to ~10000x and squashing the entire 1-10x band — where
 * essentially every bar lives — into a few pixels. Autoscaling to the max makes the one bar you
 * already knew about legible and the other 450 useless.
 *
 * The outlier is NOT hidden: it still draws, clipped at the top of the pane, which reads as
 * "off the scale", and the pane header always shows the true current multiple.
 *
 * Floored at 3x so a genuinely quiet name does not get an axis topping out at 1.05x, which would turn
 * ordinary noise into dramatic-looking bars.
 */
function rvolAxisCap(bars: Candle[]): number {
  const vals: number[] = [];
  for (const c of bars) { const r = rvolOf(c); if (r != null && Number.isFinite(r)) vals.push(r); }
  if (!vals.length) return 10;
  vals.sort((a, b) => a - b);
  const p99 = vals[Math.min(vals.length - 1, Math.floor(vals.length * 0.99))];
  return Math.max(3, p99);
}

/** Histogram points for the RVOL pane.
 *
 *  ⚠ EVERY BAR GETS A POINT, even when RVOL is unknown — unplottable bars emit a WHITESPACE point
 *  ({time} with no value) rather than being dropped. This is not cosmetic. Sub-panes are synced with
 *  setVisibleLogicalRange, which addresses bars by INDEX, not by time. Skipping bars gave the RVOL
 *  series 451 points against the main chart's 1222, so the synced index range pointed clean past the
 *  end of the shorter series and the pane rendered blank with a sliver at the left edge. Whitespace
 *  keeps the indices aligned while still drawing a GAP rather than a misleading 0x. */
function buildRvolSeries(
  bars: Candle[],
  t: (c: Candle) => unknown,
  into?: Map<number, { v: number; thin: boolean }>,
): Array<{ time: unknown; value?: number; color?: string }> {
  const out: Array<{ time: unknown; value?: number; color?: string }> = [];
  if (into) into.clear();
  for (const c of bars) {
    const time = t(c);
    const r = rvolOf(c);
    if (r == null || !Number.isFinite(r)) { out.push({ time }); continue; }
    const thin = (c.baseN ?? 0) < RVOL_MIN_SAMPLES;
    // Colour is the AT-A-GLANCE read; the hover readout carries the exact number. A thin baseline is
    // washed out at every level: the multiple may be right, but built on a handful of sessions it should
    // not look as authoritative as a 60-session denominator.
    const color = thin
      ? 'rgba(113,113,122,0.45)'
      : r >= 3 ? '#f43f5e'          // 3x+  : genuinely unusual
      : r >= 1 ? '#f59e0b'          // 1-3x : above normal
      : 'rgba(120,123,134,0.75)';   // <1x  : quieter than usual
    if (into && typeof time === 'number') into.set(time, { v: r, thin });
    out.push({ time, value: r, color });
  }
  return out;
}

// Format price with appropriate decimal places based on price level
/** Returns the profile page URL for an AI strategy, opening the game tab. */
function getAiGameUrl(strategy: string | null): string {
  if (strategy === 'ra') return '/profile/Ra?tab=game';
  if (strategy === 'osiris') return '/profile/Osiris?tab=game';
  if (strategy === 'solomon') return '/profile/Solomon?tab=game';
  if (strategy === 'watcher') return '/profile/Watcher?tab=game'; // username is 'Watcher', NOT 'The Watcher'
  if (strategy === 'ragnar') return '/profile/Ragnar?tab=game';
  if (strategy === 'cloud') return '/profile/Cloud?tab=game';
  return '/profile/Thoth?tab=game'; // default: Thoth
}

// ── Trade avatar resolution (shared by the chart overlay + the Recent Alerts card) ──
// One source of truth so every AI trader (incl. Ragnar/Cloud, which used to fall back to Thoth's
// face) and future USER trades resolve the same image + label everywhere.
type TradeStrategy = 'ra' | 'osiris' | 'solomon' | 'ragnar' | 'cloud' | 'watcher' | 'thoth' | 'rider' | 'member';
// Neutral member silhouette (self-contained data URI — no asset file, no 404). Used ONLY when a
// member has no profile picture; it must never fall back to an AI face (e.g. Thoth's).
const MEMBER_SILHOUETTE =
  "data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2064%2064'%3E%3Crect%20width='64'%20height='64'%20fill='%23334155'/%3E%3Ccircle%20cx='32'%20cy='25'%20r='12'%20fill='%2394a3b8'/%3E%3Cpath%20d='M12%2058c0-11%209-18%2020-18s20%207%2020%2018z'%20fill='%2394a3b8'/%3E%3C/svg%3E";
const AI_AVATARS: Record<TradeStrategy, string> = {
  ra: '/assets/ra/ra-avatar.png',
  osiris: '/assets/osiris/osiris-avatar.png',
  solomon: '/assets/solomon/solomon-avatar.webp',
  ragnar: '/assets/ragnar/ragnar-avatar.png',
  cloud: '/assets/cloud/cloud-avatar.png',
  watcher: '/assets/watcher/watcher-avatar.png',
  thoth: '/assets/thoth/thoth-avatar.png',
  rider: '/assets/watcher/watcher-avatar.png',   // The Rider IS a Watcher strategy → Watcher's face
  member: MEMBER_SILHOUETTE,                       // fallback only; data.avatarUrl (real picture) wins
};
const AI_LABELS: Record<TradeStrategy, string> = {
  ra: 'Ra AI', osiris: 'Osiris AI', solomon: 'Solomon AI', ragnar: 'Ragnar AI',
  cloud: 'Cloud AI', watcher: 'The Watcher AI', thoth: 'Thoth AI', rider: 'The Watcher',
  member: 'Member',
};
function resolveTradeStrategy(alert: Alert): TradeStrategy {
  const has = (s: string) => alert.type === s || alert.data?.triggeredBy === s;
  // A member's own game trade resolves to THEIR picture + name, never an AI face.
  if (isMemberTradeAlert(alert)) return 'member';
  if (has('ra')) return 'ra';
  if (has('osiris')) return 'osiris';
  if (has('solomon')) return 'solomon';
  if (has('ragnar')) return 'ragnar';
  if (has('cloud')) return 'cloud';
  if (has('watcher')) return 'watcher';
  if (has('rider')) return 'rider';
  return 'thoth';
}
/** Image for a trade avatar — a member's own picture (data.avatarUrl) wins over the AI map. */
function resolveTradeAvatar(alert: Alert, strategy: TradeStrategy): string {
  return alert.data?.avatarUrl || AI_AVATARS[strategy];
}
/** Human label — a member's name (data.traderName) wins over the AI label. */
function resolveTradeLabel(alert: Alert, strategy: TradeStrategy): string {
  return alert.data?.traderName || AI_LABELS[strategy];
}
/** Profile link for a trade avatar — a member goes to their own profile game tab; AIs to theirs. */
function resolveTradeProfileUrl(alert: Alert, strategy: TradeStrategy): string {
  if (strategy === 'member') {
    const name = alert.data?.traderName;
    return name ? `/profile/${encodeURIComponent(name)}?tab=game` : '/game';
  }
  return getAiGameUrl(strategy);
}

function formatPrice(price: number): string {
  if (price >= 5) {
    return price.toFixed(2);
  } else if (price >= 0.01) {
    // Sub-$5: show up to 4 decimals, strip trailing zeros after 2nd ($1.2977→"1.2977", $1.38→"1.38")
    return price.toFixed(4).replace(/(\.\d\d)0+$/, '$1');
  } else {
    return price.toFixed(6);
  }
}

// Map a Watcher-replay daily 'MM/DD/YYYY' bar date to a chart time coordinate (unix
// seconds). The replay is a DAILY strategy; lightweight-charts snaps a daily timestamp
// to the nearest bar on whatever TF the chart is on. Use ~16:00 UTC (mid-session) so the
// snap lands on a real intraday bar of that day. Module-level so the marker effect, the
// x-position computation, and the hover effect all share one definition.
function replayDateToChartTime(d: string): number | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(d);
  if (!m) return null;
  const [, mm, dd, yyyy] = m;
  const ms = Date.parse(`${yyyy}-${mm}-${dd}T16:00:00Z`);
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
}

// Calculate EMA values
function calculateEMA(prices: number[], period: number): number[] {
  if (prices.length === 0) return [];
  
  const ema: number[] = [];
  const multiplier = 2 / (period + 1);
  
  // First EMA is the first price
  ema[0] = prices[0];
  
  // Calculate subsequent EMAs
  for (let i = 1; i < prices.length; i++) {
    ema[i] = (prices[i] * multiplier) + (ema[i - 1] * (1 - multiplier));
  }

  return ema;
}

// ── Indicator helpers (VWAP / Anchored VWAP / RSI) ────────────────────────────
// ET calendar-date key (YYYY-MM-DD) for a bar timestamp (ms). Used to detect the
// session/day boundary so VWAP resets at the start of each new ET trading day —
// mirrors the en-US/America/New_York timezone handling the rest of the file uses.
function etDayKey(ms: number): string {
  const d = new Date(ms);
  return d.toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

// VWAP series aligned 1:1 with the supplied (timeframe-aggregated) candles.
//   typicalPrice = (high + low + close) / 3
//   vwap = Σ(typicalPrice × volume) / Σ(volume)
// anchor 'session'  → reset cumulative sums at each new ET day (standard intraday VWAP).
// anchor 'anchored' → cumulative from the first bar of the MOST RECENT ET session in
//                     view (session-open anchored VWAP); never resets within that run.
function computeVWAP(
  candles: Candle[],
  anchor: 'session' | 'anchored',
): { time: number; value: number }[] {
  if (candles.length === 0) return [];
  const out: { time: number; value: number }[] = [];
  // For the anchored variant, find the ET day of the last (most recent) bar — the anchor
  // is the first bar that shares that day, so the line spans the latest session only.
  const anchorDay = anchor === 'anchored' ? etDayKey(candles[candles.length - 1].timestamp) : null;
  let cumPV = 0;
  let cumV = 0;
  let curDay = '';
  let started = anchor === 'session'; // session mode accumulates from the very first bar
  for (const c of candles) {
    const day = etDayKey(c.timestamp);
    if (anchor === 'session') {
      // Reset at each new ET day.
      if (day !== curDay) { cumPV = 0; cumV = 0; curDay = day; }
    } else {
      // Anchored: begin accumulating only once we reach the most-recent session.
      if (!started) {
        if (day === anchorDay) started = true;
        else continue; // earlier sessions get no AVWAP point
      }
    }
    const typical = (c.high + c.low + c.close) / 3;
    const vol = c.volume ?? 0;
    cumPV += typical * vol;
    cumV += vol;
    // Volume can legitimately be 0 (synthetic fill bars) — fall back to the typical
    // price so the line stays continuous instead of dropping to NaN.
    const value = cumV > 0 ? cumPV / cumV : typical;
    out.push({ time: Math.floor(c.timestamp / 1000), value });
  }
  return out;
}

// Wilder's RSI over `period` (default 14) on a close-price array. Returns one value
// per input close (the warm-up region before the first averaged window is NaN).
function calculateRSI(closes: number[], period = 14): number[] {
  const rsi: number[] = new Array(closes.length).fill(NaN);
  if (closes.length <= period) return rsi;
  let avgGain = 0;
  let avgLoss = 0;
  // Seed: simple average of the first `period` changes.
  for (let i = 1; i <= period; i++) {
    const change = closes[i] - closes[i - 1];
    if (change >= 0) avgGain += change; else avgLoss -= change;
  }
  avgGain /= period;
  avgLoss /= period;
  rsi[period] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  // Wilder smoothing for the rest.
  for (let i = period + 1; i < closes.length; i++) {
    const change = closes[i] - closes[i - 1];
    const gain = change > 0 ? change : 0;
    const loss = change < 0 ? -change : 0;
    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
    rsi[i] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  }
  return rsi;
}

// ── Additional indicator math (all TF-aligned; computed on aggregated closes/bars) ──

// MACD (12/26/9). Returns aligned arrays (one value per close); warm-up region is NaN.
//   macd   = EMA(fast) − EMA(slow)
//   signal = EMA(macd, signalPeriod)
//   hist   = macd − signal
function calculateMACD(
  closes: number[],
  fast = 12,
  slow = 26,
  signalPeriod = 9,
): { macd: number[]; signal: number[]; hist: number[] } {
  const n = closes.length;
  const macd = new Array(n).fill(NaN);
  const signal = new Array(n).fill(NaN);
  const hist = new Array(n).fill(NaN);
  if (n === 0) return { macd, signal, hist };
  const emaFast = calculateEMA(closes, fast);
  const emaSlow = calculateEMA(closes, slow);
  // MACD line is meaningful only once the slow EMA has enough warm-up.
  const macdLine: number[] = [];
  for (let i = 0; i < n; i++) {
    const v = emaFast[i] - emaSlow[i];
    macd[i] = i >= slow - 1 ? v : NaN;
    macdLine.push(v); // keep a continuous series so the signal EMA seeds cleanly
  }
  const sig = calculateEMA(macdLine, signalPeriod);
  for (let i = 0; i < n; i++) {
    if (i >= slow - 1 + signalPeriod - 1) {
      signal[i] = sig[i];
      hist[i] = macd[i] - sig[i];
    }
  }
  return { macd, signal, hist };
}

// Bollinger Bands (period 20, mult 2). Middle = SMA(period); bands = middle ± mult·σ.
// Returns aligned arrays; warm-up region (< period) is NaN.
function calculateBollinger(
  closes: number[],
  period = 20,
  mult = 2,
): { upper: number[]; middle: number[]; lower: number[] } {
  const n = closes.length;
  const upper = new Array(n).fill(NaN);
  const middle = new Array(n).fill(NaN);
  const lower = new Array(n).fill(NaN);
  if (n < period) return { upper, middle, lower };
  let sum = 0;
  let sumSq = 0;
  for (let i = 0; i < n; i++) {
    sum += closes[i];
    sumSq += closes[i] * closes[i];
    if (i >= period) {
      sum -= closes[i - period];
      sumSq -= closes[i - period] * closes[i - period];
    }
    if (i >= period - 1) {
      const mean = sum / period;
      const variance = Math.max(0, sumSq / period - mean * mean);
      const sd = Math.sqrt(variance);
      middle[i] = mean;
      upper[i] = mean + mult * sd;
      lower[i] = mean - mult * sd;
    }
  }
  return { upper, middle, lower };
}

// Wilder ATR (period 14) on OHLC bars. Returns one value per bar (warm-up NaN).
//   TR = max(high−low, |high−prevClose|, |low−prevClose|)
//   ATR seeds with the simple mean of the first `period` TRs, then Wilder-smooths.
function calculateATR(bars: { high: number; low: number; close: number }[], period = 14): number[] {
  const n = bars.length;
  const atr = new Array(n).fill(NaN);
  if (n <= period) return atr;
  const tr: number[] = new Array(n).fill(NaN);
  for (let i = 1; i < n; i++) {
    const h = bars[i].high;
    const l = bars[i].low;
    const pc = bars[i - 1].close;
    tr[i] = Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc));
  }
  // Seed at index `period` with the average of TR[1..period].
  let seed = 0;
  for (let i = 1; i <= period; i++) seed += tr[i];
  atr[period] = seed / period;
  for (let i = period + 1; i < n; i++) {
    atr[i] = (atr[i - 1] * (period - 1) + tr[i]) / period;
  }
  return atr;
}

// Wilder ADX (period 14) on OHLC bars. Returns one value per bar (warm-up NaN).
// Standard construction: +DM/−DM → smoothed (Wilder) → +DI/−DI → DX → ADX (Wilder of DX).
function calculateADX(bars: { high: number; low: number; close: number }[], period = 14): number[] {
  const n = bars.length;
  const adx = new Array(n).fill(NaN);
  if (n <= period * 2) return adx;
  const tr = new Array(n).fill(0);
  const plusDM = new Array(n).fill(0);
  const minusDM = new Array(n).fill(0);
  for (let i = 1; i < n; i++) {
    const up = bars[i].high - bars[i - 1].high;
    const down = bars[i - 1].low - bars[i].low;
    plusDM[i] = up > down && up > 0 ? up : 0;
    minusDM[i] = down > up && down > 0 ? down : 0;
    const h = bars[i].high;
    const l = bars[i].low;
    const pc = bars[i - 1].close;
    tr[i] = Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc));
  }
  // Wilder-smoothed TR / +DM / −DM, seeded by the sum of the first `period` values.
  let trN = 0;
  let plusN = 0;
  let minusN = 0;
  for (let i = 1; i <= period; i++) { trN += tr[i]; plusN += plusDM[i]; minusN += minusDM[i]; }
  const dx: number[] = new Array(n).fill(NaN);
  for (let i = period + 1; i < n; i++) {
    trN = trN - trN / period + tr[i];
    plusN = plusN - plusN / period + plusDM[i];
    minusN = minusN - minusN / period + minusDM[i];
    const plusDI = trN === 0 ? 0 : (100 * plusN) / trN;
    const minusDI = trN === 0 ? 0 : (100 * minusN) / trN;
    const denom = plusDI + minusDI;
    dx[i] = denom === 0 ? 0 : (100 * Math.abs(plusDI - minusDI)) / denom;
  }
  // ADX = Wilder smoothing of DX. First ADX = mean of the first `period` DX values
  // (which begin at index period+1), so the first ADX lands at index period*2.
  const firstDxIdx = period + 1;
  const firstAdxIdx = firstDxIdx + period - 1;
  if (firstAdxIdx >= n) return adx;
  let dxSum = 0;
  for (let i = firstDxIdx; i <= firstAdxIdx; i++) dxSum += dx[i];
  adx[firstAdxIdx] = dxSum / period;
  for (let i = firstAdxIdx + 1; i < n; i++) {
    adx[i] = (adx[i - 1] * (period - 1) + dx[i]) / period;
  }
  return adx;
}

// Swing highs/lows: a bar whose high is the max (or low the min) of the ±`left`/`right`
// window. Returns the indices flagged as pivot highs / pivot lows.
function detectSwings(
  bars: { high: number; low: number }[],
  left = 3,
  right = 3,
): { highs: number[]; lows: number[] } {
  const highs: number[] = [];
  const lows: number[] = [];
  const n = bars.length;
  for (let i = left; i < n - right; i++) {
    let isHigh = true;
    let isLow = true;
    for (let j = i - left; j <= i + right; j++) {
      if (j === i) continue;
      if (bars[j].high >= bars[i].high) isHigh = false;
      if (bars[j].low <= bars[i].low) isLow = false;
      if (!isHigh && !isLow) break;
    }
    if (isHigh) highs.push(i);
    if (isLow) lows.push(i);
  }
  return { highs, lows };
}

// Relative volume of the LAST bar vs the mean volume of the prior `lookback` bars.
// Returns null if there isn't enough history or the baseline is zero.
function computeRVOL(volumes: number[], lookback = 20): number | null {
  const n = volumes.length;
  if (n < 2) return null;
  const last = volumes[n - 1] ?? 0;
  const start = Math.max(0, n - 1 - lookback);
  let sum = 0;
  let count = 0;
  for (let i = start; i < n - 1; i++) {
    sum += volumes[i] ?? 0;
    count++;
  }
  if (count === 0) return null;
  const avg = sum / count;
  if (avg <= 0) return null;
  return last / avg;
}

// Compact number for the OBV / A-D axis + readouts (values run into the millions/billions).
function fmtCompactNum(v: number): string {
  if (!Number.isFinite(v)) return '--';
  const a = Math.abs(v);
  if (a >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (a >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return v.toFixed(0);
}

// OBV / A-D line / CMF / Acc-Dist math moved to src/lib/indicators/accumulation.ts (2026-09-02) so
// the nightly Accumulation Score builder shares ONE implementation with this chart (imported at top).

export default function ChartPage() {
  const params = useParams();
  const rawSymbol = (params as any)?.symbol;
  const normalizedSymbol = Array.isArray(rawSymbol) ? rawSymbol[0] : rawSymbol;
  const symbol = typeof normalizedSymbol === 'string' ? normalizedSymbol.toUpperCase() : '';
  const hasValidSymbol = !!symbol;

  // lightweight-charts takes its chrome (plot background, axis text, gridlines, crosshair) via a JS
  // layout object, not CSS, so it reads the resolved theme and branches. The chart effect depends on
  // `dark`, so a theme flip tears the charts down and rebuilds them with the new chrome. The DARK value
  // at each site is kept verbatim so dark stays byte-identical; data-series colors (candles, EMAs, VWAP,
  // RSI, MACD, volume) read on either ground and stay inline.
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme !== 'light';

  const [alerts, setAlerts] = useState<Alert[]>([]);
  // RIGHT-RAIL TAB. Due diligence and Recent Alerts used to STACK, which on a busy name pushed the
  // daily chart below the fold — the rail is viewport-bounded, so two full panels cannot both fit.
  // One at a time, DD first (it answers "what is this company and why is it moving" before the tape).
  const [railTab, setRailTab] = useState<'dd' | 'alerts'>('dd');
  // Live Chat (AlertsChatDock) adds 368px of right-padding to <main> when open, which squeezes this
  // whole grid. We watch its `alerts-chat-open` class on <html> and, when it's on, collapse the chart
  // grid to a single column so the chart takes the full (reduced) width instead of being crushed to a
  // sliver beside the rail; the rail then stacks below. Self-contained: no coupling to the dock's code.
  const [chatOpen, setChatOpen] = useState(false);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const sync = () => setChatOpen(root.classList.contains('alerts-chat-open'));
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  const [candles, setCandles] = useState<Candle[]>([]);
  const [loading, setLoading] = useState(true);
  const [chartsReady, setChartsReady] = useState(false); // true only after tryCreateCharts finishes so candle setData effect runs
  const [chartError, setChartError] = useState<string | null>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  // The p-2 wrapper around the candle pane. Unlike chartContainerRef it is mounted while the chart is still loading, so
  // the height estimate can measure the real top from the first render (register chart-volume-card-shifts-after-mount-1104).
  const chartWrapRef = useRef<HTMLDivElement>(null);
  /**
   * 🚨 THE HEADER'S HEIGHT IS MEASURED, NOT ASSUMED. It is position:fixed, so it contributes no
   * flow height and the content below has to be pushed down by hand - and its height CHANGES: it
   * wraps to two rows when the column narrows (sidebar open, chat open, long ticker, extra badges).
   * The offset used to be a literal md:pt-[92px] while the header measured 113px with chat open, so
   * it covered ~21px of the timeframe row (owner 2026-08-19, screenshot).
   * ⛔ Do not "fix" a future overlap by bumping the number again - that is what produced this one.
   */

  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const volumeChartRef = useRef<IChartApi | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const volumeContainerRef = useRef<HTMLDivElement>(null);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [isMounted, setIsMounted] = useState(false);
  const isInitialLoadRef = useRef(true);
  const previousCandlesRef = useRef<Candle[]>([]);
  const isAliveRef = useRef(false);
  const candleRafIdRef = useRef<number | null>(null);
  const markerRafIdRef = useRef<number | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const volumeResizeObserverRef = useRef<ResizeObserver | null>(null);
  const volumeSyncIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const volumeSyncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const forceResizeTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const candleInitializedRef = useRef(false);
  const viewportTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);   // debounced viewport settle after a TF switch
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousMarkersRef = useRef<any[] | null>(null);
  const hoverPriceLineRef = useRef<any>(null);
  const candlesAbortRef = useRef<AbortController | null>(null);
  // PHASE-1 fast path: signature of the last candle payload — when a poll differs ONLY in the
  // forming bar, we series.update() in place instead of re-firing the whole indicator cascade.
  const prevCandlesSigRef = useRef<{ len: number; lastTs: number; firstTs: number } | null>(null);
  // The raw 1-minute window last applied, keyed by the request it answers, so the 2s poll can ask only for
  // the newest bars (?since=) and merge (register chart-candles-polls-refetch-full-window-1101).
  const candlesHeldRef = useRef<{ key: string; bars: Candle[]; fullAt: number } | null>(null);
  // Trade overlay (Thoth / Ra avatar markers)
  const [hoveredTradeId, setHoveredTradeId] = useState<string | null>(null);
  // Which clustered-marker badge (if any) is hovered — drives the cluster hover card.
  const [hoveredClusterKey, setHoveredClusterKey] = useState<string | null>(null);
  const [tradeXPos, setTradeXPos] = useState<Record<string, number | null>>({});
  const tradeXPosRafRef = useRef<number | null>(null);
  const isSyncingRef = useRef(false);
  // Follow live (prevent snapping if user pans/zooms)
  const followLiveRef = useRef(false);
  const [followLive, setFollowLive] = useState(false);
  /**
   * 🚨 WHEN THE USER LAST TOUCHED THE CHART, and the whole reason Live used to switch itself off.
   *
   * REPORTED by vokeefe91 2026-08-17: "my live view of a tickers gets toggled off and i have to keep
   * toggling it on so it sometimes shows incorrect data or older prices".
   *
   * ROOT CAUSE: `subscribeVisibleTimeRangeChange` fires for EVERY visible-range change, and it cannot
   * tell a user pan from a programmatic one. Live's own auto-scroll, `setData()` on each new tick, and
   * the volume/RSI pane syncs all move the range - so the first tick after enabling Live fired the
   * handler and turned Live off. The feature disabled itself by working.
   *
   * ⭐ THE FIX IS TO ASK THE DOM WHAT THE USER DID, rather than to infer intent from a chart event
   * that carries no such information. wheel / mousedown / touchstart on the container are unambiguous
   * user gestures; a tick arriving over the websocket is not, and never produces one.
   *
   * ⚠ THE WINDOW MUST COVER A WHOLE GESTURE, not an instant. A drag emits mousedown once and then a
   * stream of range changes as the pointer moves, so a same-tick check would only catch the first of
   * them and Live would re-arm mid-drag. 1500ms comfortably spans a pan or a momentum scroll and is
   * still far shorter than any deliberate re-enable.
   */
  const lastUserGestureAtRef = useRef(0);

  /**
   * ⭐ STAMP A REAL USER GESTURE ON THE CHART. Its own effect, mounted once, so it does not depend on
   * the chart-creation lifecycle and cannot leak a listener when the chart is rebuilt (timeframe
   * change, symbol change, resize all recreate the chart while this div persists).
   *
   * ⚠ ON THE CONTAINER, NEVER ON window. Scrolling the PAGE past the chart must not count as touching
   * the chart, or Live would switch itself off every time the reader scrolled down to the news panel -
   * which would have replaced one silent disable with another.
   *
   * passive: the handler only records a timestamp, so it must never be able to block a pan.
   * capture: the charting library owns the canvas and may stop propagation; capture sees the event
   * on the way down regardless.
   */
  useEffect(() => {
    const el = chartContainerRef.current;
    if (!el) return;
    const mark = () => { lastUserGestureAtRef.current = Date.now(); };
    const opts = { passive: true, capture: true } as AddEventListenerOptions;
    el.addEventListener('wheel', mark, opts);
    el.addEventListener('mousedown', mark, opts);
    el.addEventListener('touchstart', mark, opts);
    return () => {
      el.removeEventListener('wheel', mark, opts);
      el.removeEventListener('mousedown', mark, opts);
      el.removeEventListener('touchstart', mark, opts);
    };
  }, []);

  /**
   * Restore the sticky Live preference. Separate from the toggle so the ref and the state can never
   * disagree: followLiveRef is what the tick handler reads, followLive is what the button renders,
   * and a restore that set only one of them would show an armed button that follows nothing.
   *
   * ⚠ In an effect rather than in useState's initialiser because this component server-renders, and
   * touching localStorage during the first render is a hydration mismatch.
   */
  /**
   * Publish the fixed header's real height so the content below clears it exactly.
   *
   * ⭐ A CALLBACK REF, NOT useEffect+useRef, AND THAT DISTINCTION IS THE BUG THIS FIXES. The first
   * version observed inside a mount effect with empty deps. The header is not in the tree on the
   * first render (the page shows a loading branch first), so ref.current was null, the effect
   * returned early, and --chart-header-h was NEVER SET. Verified on prod: the var read empty and
   * the padding fell back to the old 92px literal - the overlap only looked fixed because the
   * header happened to be 73px at that width. A callback ref runs when the node actually attaches,
   * whenever that is.
   *
   * ⭐ ResizeObserver because the height CHANGES: the header re-wraps whenever its column narrows -
   * sidebar expanding, chat dock opening, longer ticker, extra badges - and each of those is a
   * silent overlap if the offset is a constant. Published as a CSS var on documentElement, the same
   * pattern as --sidebar-width and --alerts-chat-right-pad, so it costs no re-render.
   * ⚠ Rounded UP: a fractional height rounding down leaves a 1px sliver of chart under the header.
   */
  const headerRoRef = useRef<ResizeObserver | null>(null);
  const chartHeaderRef = useCallback((el: HTMLDivElement | null) => {
    headerRoRef.current?.disconnect();
    headerRoRef.current = null;
    if (!el) {
      document.documentElement.style.removeProperty('--chart-header-h');
      return;
    }
    const publish = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      if (h > 0) document.documentElement.style.setProperty('--chart-header-h', `${h}px`);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    headerRoRef.current = ro;
  }, []);
  useEffect(() => {
    try {
      if (window.localStorage.getItem('dt:chart:followLive') === '1') {
        followLiveRef.current = true;
        setFollowLive(true);
      }
    } catch { /* preference only */ }
  }, []);
  // S/R + pattern overlay — OFF by default; draws daily levels via createPriceLine when toggled on.
  // Independent S/R overlay layers (all OFF by default), TradingView-style.
  const [showMinuteLevels, setShowMinuteLevels] = usePersistentBool('tdt.chart.showMinuteLevels', false);     // current-TF intraday levels (main)
  const [showDailyLevels, setShowDailyLevels] = usePersistentBool('tdt.chart.showDailyLevels', false);       // daily levels (main + mini)
  const [showPatterns, setShowPatterns] = usePersistentBool('tdt.chart.showPatterns', false);             // DT/DB/contested necklines
  const [showVolumeProfile, setShowVolumeProfile] = usePersistentBool('tdt.chart.showVolumeProfile', false);   // POC + HVN lines
  // New indicator overlays (all OFF by default) — TF-aligned off processedCandles.
  const [showVWAP, setShowVWAP] = usePersistentBool('tdt.chart.showVWAP', false);                     // session VWAP (main, resets each ET day)
  const [showAVWAP, setShowAVWAP] = usePersistentBool('tdt.chart.showAVWAP', false);                   // anchored VWAP (main, latest session open)
  const [showRSI, setShowRSI] = usePersistentBool('tdt.chart.showRSI', false);                       // RSI(14) sub-pane below volume
  const [showRVOL, setShowRVOL] = usePersistentBool('tdt.chart.showRVOL', false);                     // Relative-volume sub-pane (per candle, any timeframe)
  const [rvolPaneValue, setRvolPaneValue] = useState<number | null>(null); // latest bar's RVOL, shown in the pane header
  const [rvolPaneThin, setRvolPaneThin] = useState(false);             // latest bar's baseline is too thinly sampled to trust
  const [showPivots, setShowPivots] = usePersistentBool('tdt.chart.showPivots', false);                 // floor-trader pivots from prior daily bar
  const [showMACD, setShowMACD] = usePersistentBool('tdt.chart.showMACD', false);                     // MACD(12/26/9) sub-pane below RSI
  const [showBollinger, setShowBollinger] = usePersistentBool('tdt.chart.showBollinger', false);           // Bollinger Bands(20,2σ) overlay on main chart
  const [showADX, setShowADX] = usePersistentBool('tdt.chart.showADX', false);                       // ADX(14) sub-pane (trend strength)
  // ── Accumulation indicators (all OFF by default) — sub-panes below ADX + a legend readout. ──
  const [showOBV, setShowOBV] = usePersistentBool('tdt.chart.showOBV', false);                       // On-Balance Volume sub-pane (single line)
  const [showADLine, setShowADLine] = usePersistentBool('tdt.chart.showADLine', false);                 // Accumulation/Distribution line sub-pane (single line)
  const [showCMF, setShowCMF] = usePersistentBool('tdt.chart.showCMF', false);                       // Chaikin Money Flow(20) oscillator sub-pane
  const [showSwings, setShowSwings] = usePersistentBool('tdt.chart.showSwings', false);                 // swing high/low markers on main chart
  // ── News markers ────────────────────────────────────────────────────────────
  // Sentiment-colored dots below the bar at each news article's time (from /api/news, Redis-cached).
  // On by default (news is sparse + genuinely useful in context); kept as a const gate like
  // showDevBook so a UI toggle can be added trivially. Merged into the markers effect below.
  const showNewsMarkers = true;
  const newsMarkersRef = useRef<ChartMarker[]>([]);
  const [newsMarkerVersion, setNewsMarkerVersion] = useState(0);
  // Hover tooltip: article(s) + alert(s) at the crosshaired bar (news 📰 + alert markers, best-in-class).
  type HoverNews = { title: string; publisher: string; url: string; sentiment: string | null };
  type HoverAlert = { label: string; color: string; price: number | null; rvol: number | null; pct: number | null };
  const newsByTimeRef = useRef<Map<number, HoverNews[]>>(new Map());
  const alertsHoverRef = useRef<Alert[]>([]);
  const [hoverInfo, setHoverInfo] = useState<null | { news: HoverNews[]; alerts: HoverAlert[] }>(null);
  // ── EMA-trend breakdown for the hover tooltip ────────────────────────────────
  // How many consecutive candles price has closed >= its 9/50/200 EMA on each timeframe. Fetched once per
  // symbol (only when that symbol actually has ema_trending alerts) and shown when an EMA-trending marker
  // is hovered, so a trader sees the full trend structure (which TF, which EMA, how many bars) at a glance.
  type EmaTfRow = { tf: string; count9: number; count50: number; count200: number; level9: number; level50: number; level200: number };
  const [emaTrendMatrix, setEmaTrendMatrix] = useState<{ symbol: string; tfs: EmaTfRow[] } | null>(null);
  const emaMatrixSymRef = useRef<string | null>(null);
  // ── Reddit post pins ────────────────────────────────────────────────────────
  // The Top-Play Reddit posts we published for this ticker (from top_play_posts.posted_at). Drawn as
  // a little Reddit-orange snoo pin at the exact minute the post went out, with a hover card showing
  // price-at-post, peak-since and current move — so members can see "we called this here" in context.
  type RedditPost = { postedAt: number; timeframes: string; bias: string; url: string };
  const [redditPosts, setRedditPosts] = useState<RedditPost[]>([]);
  const [redditXPos, setRedditXPos] = useState<Record<number, number | null>>({});
  const [hoveredRedditIdx, setHoveredRedditIdx] = useState<number | null>(null);
  /**
   * REDDIT PINS — one icon per time bucket, not one per post.
   *
   * Posted back-to-back days piled a dozen overlapping Snoo badges into an unreadable smear on the
   * DAILY chart (owner 2026-08-03). News already solved this: NewsPin buckets to the current
   * timeframe and draws ONE icon carrying a count. This applies the same shape to Reddit rather than
   * inventing a second clustering mechanism.
   *
   * ⭐ BUCKETING BY THE TIMEFRAME'S BAR SIZE is what makes the daily case work: on 1D tfSec is 86,400
   * so a whole day of posts collapses to one pin, while on 1m each post keeps its own minute. A fixed
   * bucket would have fixed the daily chart and broken the intraday one.
   *
   * The FIRST post in a bucket is the representative for the price math (it is the one whose entry the
   * "move since" is measured from); the rest ride along in `posts` for the hover card.
   */
  type RedditPin = { bucket: number; posts: RedditPost[]; count: number };
  /**
   * SNAP A POST INTO THE TRADED SESSION (owner 2026-08-06).
   *
   * The chart only has candles for the extended session, 04:00-20:00 ET. `bucket` was a pure UTC time
   * bucket, so a post published OUTSIDE that window landed on a bucket with no bar, got no x-position,
   * and silently never rendered. Posts in the two dead windows were invisible on the chart.
   *
   *   20:00-23:59 ET  ->  the 19:59 candle (last bar of that session)
   *   00:00-03:59 ET  ->  the 04:00 candle (first bar of that session)
   *
   * ⭐ ONLY THE PIN POSITION MOVES. The RedditPost objects keep their true `postedAt`, so the hover
   * card still shows the real publish time. Placement is a drawing concern; the timestamp is data.
   * ⚠ ET, not UTC, and computed via Intl so EDT/EST is handled rather than assuming a fixed offset
   * ([[project_market_session]]). ⚠ `hour: '2-digit'` with hour12:false yields '24' for midnight in
   * some runtimes, hence the %24.
   */
  const snapToSession = (postedAtMs: number): number => {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false,
      }).formatToParts(new Date(postedAtMs));
      const h = Number(parts.find((x) => x.type === 'hour')?.value ?? '0') % 24;
      const m = Number(parts.find((x) => x.type === 'minute')?.value ?? '0');
      const mins = h * 60 + m;
      if (mins >= 20 * 60) return postedAtMs - (mins - (19 * 60 + 59)) * 60_000;  // late evening -> 19:59
      if (mins < 4 * 60) return postedAtMs + (4 * 60 - mins) * 60_000;            // overnight    -> 04:00
      return postedAtMs;
    } catch { return postedAtMs; }   // never let a formatting failure drop the pin entirely
  };
  // (the redditPins memo itself lives just below the `timeframe` state — it reads it)
  // ── News pins ─────────────────────────────────────────────────────────────
  // News dropped on the chart is drawn as a crisp DOM-overlay newspaper icon (one per time bucket) with
  // its OWN hover, NOT a lightweight-charts marker. The old circle+📰 marker read as two icons (a blue dot
  // AND the emoji) and its crosshair-bucket hover missed whenever an article's minute had no candle. The
  // DOM pin knows its own articles, so hover is reliable and it's a single clean icon.
  type NewsPin = { bucket: number; articles: HoverNews[]; count: number; sentiment: string | null };
  const [newsPins, setNewsPins] = useState<NewsPin[]>([]);
  const [newsXPos, setNewsXPos] = useState<Record<number, number | null>>({});
  const [hoveredNewsIdx, setHoveredNewsIdx] = useState<number | null>(null);
  // ── The Watcher REPLAY layer ────────────────────────────────────────────────
  // Simulates the Watcher's M1 breakout strategy over this symbol's daily history
  // (server replay via /api/watcher/replay, shares watcherStrategy with the live trader).
  // Drawn as indigo W▲/▼ markers (merged into the markers effect) + faint per-trade
  // connector LineSeries, distinct from real Ra/Thoth/Osiris trades so it reads as a sim.
  const [showWatcherReplay, setShowWatcherReplay] = useState(false);
  // DEV-book (paper sandbox mirror) markers are always hidden on the chart — the chart tells the
  // LIVE story only (owner 2026-07-08: the mirror's extra trades read as clutter/contradiction,
  // and the toggle itself was confusing). Kept as a const so the visibility guards below compile.
  const showDevBook = false;
  // NOTE: visibleWatcherPos/visibleWatcherClosed are derived AFTER the position states below.
  const [watcherReplayBook, setWatcherReplayBook] = useState<'live' | 'dev'>('live');
  const [watcherReplay, setWatcherReplay] = useState<WatcherReplayResponse | null>(null);
  const [watcherReplayLoading, setWatcherReplayLoading] = useState(false);
  const watcherReplayMarkersRef = useRef<ChartMarker[]>([]);           // merged into the markers effect
  const watcherReplayLinesRef = useRef<ISeriesApi<'Line'>[]>([]);      // per-round-trip connector series
  // Hover-to-inspect for replay trades: which trade index is hovered, the screen-x of
  // each trade's entry/exit candle (for DOM avatar overlays), and the price lines drawn
  // on hover (mirrors the real-trade hoverPriceLineRef mechanism).
  const [hoveredReplayIdx, setHoveredReplayIdx] = useState<number | null>(null);
  const [replayXPos, setReplayXPos] = useState<Record<number, { entry: number | null; exit: number | null }>>({});
  // PAN/ZOOM AVATAR-TRACKING FIX: timeToCoordinate() returns a pixel x relative to the
  // CURRENT visible range, so every avatar's correct pixel moves whenever the user pans or
  // zooms. The position effects below keyed only off [candles, timeframe, …] and never re-ran
  // on pan → avatars froze at stale pixels (entry showed, exit appeared only after an
  // incidental re-render). We bump rangeVersion (throttled via rAF) inside the main chart's
  // visible-range subscription and add it to the deps of the position effects so they recompute
  // as the user scrolls. The rAF throttle coalesces the re-render storm into one per frame.
  const [rangeVersion, setRangeVersion] = useState(0);
  const rangeVersionRafRef = useRef<number | null>(null);
  const replayHoverLinesRef = useRef<ReturnType<ISeriesApi<'Candlestick'>['createPriceLine']>[]>([]);
  const clusterHoverLinesRef = useRef<ReturnType<ISeriesApi<'Candlestick'>['createPriceLine']>[]>([]);  // entry/exit lines drawn while a trade CLUSTER badge is hovered
  const [showAtrReadout, setShowAtrReadout] = usePersistentBool('tdt.chart.showAtrReadout', false);         // ATR(14) numeric readout in legend
  const [showRvolReadout, setShowRvolReadout] = usePersistentBool('tdt.chart.showRvolReadout', false);       // RVOL numeric readout in legend
  const [showAccDaysReadout, setShowAccDaysReadout] = usePersistentBool('tdt.chart.showAccDaysReadout', false); // Accumulation/distribution days count in legend
  // Legend marker toggles (owner: click a legend chip to hide/show that marker type on the chart, for a
  // cleaner view). Keys: rvol · mover · ema · halt · resume · news · trade · reddit. Empty = all shown.
  const [hiddenMarkers, setHiddenMarkers] = useState<Set<string>>(() => new Set());
  const toggleMarker = (k: string) => setHiddenMarkers((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  // Master-class indicator panel: open state + which indicator's info card is expanded.
  const [indicatorPanelOpen, setIndicatorPanelOpen] = useState(false);
  const [openInfoKey, setOpenInfoKey] = useState<string | null>(null);
  const levelLinesRef = useRef<ReturnType<ISeriesApi<'Candlestick'>['createPriceLine']>[]>([]);
  const dailyLevelLinesRef = useRef<ReturnType<ISeriesApi<'Candlestick'>['createPriceLine']>[]>([]);  // mini YTD chart
  // What's currently drawn — powers the legend detail list (lightweight-charts has no line hover).
  const [srLegend, setSrLegend] = useState<{ levels: { price: number; kind: string; touches: number; source_tf?: string; at_hvn: boolean; flipped: boolean; volume_strength: number }[]; patterns: { kind: string; neckline: number; level: number; confirmed: boolean }[]; poc: number | null } | null>(null);
  // Responsive chart height: size the candle pane to the viewport so the whole page (incl. the
  // volume pane's time axis) fits the monitor on load — no clipped labels on shorter screens.
  const [chartHeight, setChartHeight] = useState(360); // closer to the computed value → less initial-load flash
  const [volHeight, setVolHeight] = useState(130);
  // ?embed=1 (workspace iframe): the site navbar/sidebar don't exist, so the fixed header
  // pins to the true top and the panes reclaim the navbar + padding space (the geometry
  // below otherwise leaves a 64px black band up top and dead space at the bottom).
  const [isEmbed, setIsEmbed] = useState(false);
  const [isDebug, setIsDebug] = useState(false);   // ?debug=1 → show a live layout-measurement overlay
  const [debugText, setDebugText] = useState('');
  // ?shot=1 (share-image capture): drop a brand lockup into the capture wrapper + stamp data-ready when
  // the charts have drawn, so the Puppeteer screenshot (/api/shot/chart/[symbol]) is never half-rendered.
  const [isShot, setIsShot] = useState(false);
  useEffect(() => { try { const p = new URLSearchParams(window.location.search); setIsEmbed(p.get('embed') === '1'); setIsShot(p.get('shot') === '1'); setIsDebug(p.get('debug') === '1'); } catch { /* ssr */ } }, []);
  // Layout-measurement overlay (only when ?debug=1) so the owner can screenshot exact numbers instead of me guessing.
  useEffect(() => {
    if (!isDebug) return;
    const measure = () => {
      const c = chartContainerRef.current; const vol = volumeContainerRef.current;
      const cr = c?.getBoundingClientRect(); const vr = vol?.getBoundingClientRect();
      const cutBy = vr ? Math.round(vr.bottom - window.innerHeight) : null;
      setDebugText(`vh=${window.innerHeight} | chartTop=${cr ? Math.round(cr.top) : '-'} chartH=${cr ? Math.round(cr.height) : '-'} (state ${chartHeight}) | volH=${volHeight} volBottom=${vr ? Math.round(vr.bottom) : '-'} | CUT-BY=${cutBy}px`);
    };
    measure();
    const raf = requestAnimationFrame(measure); const iv = setInterval(measure, 1000);
    window.addEventListener('resize', measure);
    return () => { cancelAnimationFrame(raf); clearInterval(iv); window.removeEventListener('resize', measure); };
  }, [isDebug, chartHeight, volHeight]);
  // Watcher open-position badge (owner 2026-07-07): shows bottom-right of the chart when
  // The Watcher (live or paper mirror) currently holds this symbol; hover = details.
  type WatcherOpenPos = { aiName: string; entryPrice: number; shares: number; entryTime: number; entryReason: string | null; slices: number };
  type WatcherClosed = { aiName: string; entryPrice: number; exitPrice: number; realizedPlPct: number | null; exitTime: number; exitReason: string | null };
  const [watcherOpenPos, setWatcherOpenPos] = useState<WatcherOpenPos[]>([]);
  // RECENT closed round-trips, newest first (API caps the list). The card used to receive a single
  // `lastClosed`, which is why a symbol with real history read as one lonely trade.
  const [watcherClosed, setWatcherClosed] = useState<WatcherClosed[]>([]);
  // Chips honor the DEV opt-in exactly like markers (owner 2026-07-08: live is the default story).
  const visibleWatcherPos = useMemo(() => showDevBook ? watcherOpenPos : watcherOpenPos.filter((p) => p.aiName === 'Watcher'), [showDevBook, watcherOpenPos]);
  // While hovering the Watcher pill, draw its entry price line(s) on the chart (dashed amber, "Entry").
  const [watcherHover, setWatcherHover] = useState(false);
  useEffect(() => {
    const series = candleSeriesRef.current;
    if (!watcherHover || !series || visibleWatcherPos.length === 0) return;
    // A price line at 0 pins the whole right scale to 0 and prints a '0.000000' axis label -- which is
    // what put ~40% of empty space under the candles. A position still pending has no fill price yet, so
    // entryPrice can be null/0; those get no line rather than a line at zero.
    const lines = visibleWatcherPos
      .filter((p) => Number.isFinite(Number(p.entryPrice)) && Number(p.entryPrice) > 0)
      .map((p) => series.createPriceLine({
        price: Number(p.entryPrice), color: '#f59e0b', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true,
        title: `Entry${p.aiName === 'Watcher' ? '' : ' (dev)'}`,
      }));
    return () => { lines.forEach((l) => { try { series.removePriceLine(l); } catch { /* noop */ } }); };
  }, [watcherHover, visibleWatcherPos]);
  // Closed list honors the DEV opt-in the same way opens do, then `visibleWatcherClosed` stays as the
  // single most-recent one so the always-visible "Closed" chip keeps reading exactly as before.
  const visibleWatcherClosedList = useMemo(
    () => (showDevBook ? watcherClosed : watcherClosed.filter((c) => c.aiName === 'Watcher')),
    [showDevBook, watcherClosed],
  );
  const visibleWatcherClosed = visibleWatcherClosedList[0] ?? null;
  useEffect(() => {
    let stop = false;
    const load = async () => {
      try {
        const r = await fetch(`/api/watcher/position?symbol=${encodeURIComponent(symbol)}`, { cache: 'no-store' });
        const j = r.ok ? await r.json() : null;
        if (!stop) {
          setWatcherOpenPos(Array.isArray(j?.positions) ? j.positions : []);
          // Tolerate the pre-`closed` API shape so a stale cached response cannot blank the section.
          setWatcherClosed(Array.isArray(j?.closed) ? j.closed : j?.lastClosed ? [j.lastClosed] : []);
        }
      } catch { if (!stop) { setWatcherOpenPos([]); setWatcherClosed([]); } }
    };
    load();
    const iv = setInterval(() => { if (!document.hidden && isMarketHoursET()) load(); }, 60_000);
    return () => { stop = true; clearInterval(iv); };
  }, [symbol]);
  // The signed-in user's OWN game-book state on this symbol (open position, else most
  // recent closed round-trip) — personal chip in the positions strip; never shown to others.
  type UserPos = {
    open: { shares: number; avgCost: number; since: number } | null
    lastClosed: { exitPrice: number; realizedPl: number | null; realizedPlPct: number | null; at: number } | null
  };
  const [userPos, setUserPos] = useState<UserPos>({ open: null, lastClosed: null });
  useEffect(() => {
    let stop = false;
    const load = async () => {
      try {
        const r = await fetch(`/api/user/position?symbol=${encodeURIComponent(symbol)}`, { cache: 'no-store' });
        const j = r.ok ? await r.json() : null;
        if (!stop) setUserPos({ open: j?.open ?? null, lastClosed: j?.lastClosed ?? null });
      } catch { if (!stop) setUserPos({ open: null, lastClosed: null }); }
    };
    load();
    const iv = setInterval(() => { if (!document.hidden && isMarketHoursET()) load(); }, 60_000);
    return () => { stop = true; clearInterval(iv); };
  }, [symbol]);
  useLayoutEffect(() => {
    const embed = typeof window !== 'undefined' && window.location.search.includes('embed=1');
    // Volume is context for the candles, not a co-equal panel: it only has to show relative bar size,
    // which reads fine at ~2/3 the old height. Trimmed 100/125/145 -> 72/88/104, and every pixel saved
    // goes straight to the chart because the estimate below subtracts this from the viewport.
    const volFor = (vh: number) => (vh < 760 ? 72 : vh < 900 ? 88 : 104);
    // The right rail sits BESIDE the chart only at xl (>=1280px). Below that the layout is a single column:
    // the rail stacks UNDER the chart and the whole page scrolls, so we must NOT cram chart+volume into one
    // screen (that would shrink the chart to nothing on phones). Fit-to-viewport runs only on the wide layout;
    // mobile/tablet gets a comfortable, resolution-relative height and scrolls naturally.
    const isWide = () => (typeof window !== 'undefined' && window.innerWidth >= 1280);
    const estimate = () => {
      const vh = window.innerHeight || 900;
      const vol = volFor(vh);
      let h: number;
      if (isWide()) {
        // While loading, the candle container is not mounted yet. Measuring its always-mounted wrapper (+8px of p-2
        // padding, the container's real offset) instead of guessing a top of 300 is what keeps the loading card the
        // SAME height as the loaded one: the guess sized it 120px too tall at 1440x900, so the Volume card jumped
        // up 162px the moment the chart mounted (0.0143 CLS on prod, register chart-volume-card-shifts-after-mount-1104).
        const el = chartContainerRef.current;
        const wrap = chartWrapRef.current;
        const top = el ? el.getBoundingClientRect().top : wrap ? wrap.getBoundingClientRect().top + 8 : (embed ? 150 : 300);
        // 60 -> 28. That constant was guesswork about the chrome between the panes and it guessed HIGH,
        // so the chart started ~32px short on every load. correct() measures the ACTUAL volume bottom
        // against the viewport and trims if this overshoots, so the extra slack bought nothing.
        h = vh - top - vol - 28;                 // desktop: fit; correct() refines against the real volume bottom
      } else {
        h = Math.round(vh * 0.6);                // mobile/tablet: stacked + scrolling → ~60% of viewport
      }
      h = Math.min(embed ? 900 : 900, Math.max(240, h));
      setVolHeight(vol);
      setChartHeight(h);
    };
    // SELF-CORRECT (wide layout only): measure the ACTUAL rendered volume pane and shrink/grow the candle
    // pane so the volume bottom lands just inside the viewport — bulletproof against whatever chrome sits
    // between the chart and the volume bars, no hardcoded constants. Dead-zone (-28..+3) prevents oscillation.
    const correct = () => {
      if (!isWide()) return;                     // mobile scrolls; never shrink the chart to "fit" there
      const volEl = volumeContainerRef.current;
      if (!volEl) return;
      const overflow = Math.round(volEl.getBoundingClientRect().bottom - window.innerHeight);
      if (overflow > 3) setChartHeight((p) => Math.max(240, p - overflow - 6));
      // 840 -> 900 to match the estimator's own ceiling. They disagreed, so once correct() had trimmed
      // the chart it could never grow back to a height estimate() considered legal.
      else if (overflow < -28) setChartHeight((p) => Math.min(900, p + Math.min(-overflow - 8, 140)));
    };
    estimate();
    const raf = requestAnimationFrame(estimate);
    const t1 = setTimeout(correct, 80);
    const t2 = setTimeout(correct, 320);
    const onResize = () => { estimate(); setTimeout(correct, 120); };
    window.addEventListener('resize', onResize);
    return () => { cancelAnimationFrame(raf); clearTimeout(t1); clearTimeout(t2); window.removeEventListener('resize', onResize); };
  }, [chartsReady]);
  // Push new heights to the lightweight-charts canvases when they change (the ResizeObserver
  // covers most cases; this guarantees it on the initial responsive computation).
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        if (chartRef.current && chartContainerRef.current) {
          chartRef.current.applyOptions({ width: chartContainerRef.current.clientWidth || 800, height: chartContainerRef.current.clientHeight || chartHeight });
        }
        if (volumeChartRef.current && volumeContainerRef.current) {
          volumeChartRef.current.applyOptions({ width: volumeContainerRef.current.clientWidth || 800, height: volumeContainerRef.current.clientHeight || volHeight });
        }
      } catch {}
    }, 60);
    return () => clearTimeout(t);
  }, [chartHeight, volHeight]);
  // Stats
  const [currentPrice, setCurrentPrice] = useState<number | null>(null);
  const currentPriceRef = useRef<number | null>(null);
  currentPriceRef.current = currentPrice;
  const [dayChangePct, setDayChangePct] = useState<number | null>(null);
  const [afterHoursPct, setAfterHoursPct] = useState<number | null>(null);
  const [preMarketPct, setPreMarketPct] = useState<number | null>(null);
  const [regularClose, setRegularClose] = useState<number | null>(null);
  const [sessionPhase, setSessionPhase] = useState<'pre' | 'rth' | 'post' | 'closed'>('rth');
  const [dayVolume, setDayVolume] = useState<number | null>(null);
  const [previousClose, setPreviousClose] = useState<number | null>(null);
  const [isLastSession, setIsLastSession] = useState(false);   // today is a market holiday/weekend → stats = last session
  const [companyName, setCompanyName] = useState<string | null>(null);
  // EMA values for legend
  const [ema9Value, setEma9Value] = useState<number | null>(null);
  const [ema50Value, setEma50Value] = useState<number | null>(null);
  const [ema200Value, setEma200Value] = useState<number | null>(null);
  // Mini daily chart (YTD)
  const dailyContainerRef = useRef<HTMLDivElement | null>(null);
  const dailyChartRef = useRef<IChartApi | null>(null);
  const dailySeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const [dailyChartLoading, setDailyChartLoading] = useState(true);
  // Daily chart EMA series refs
  const dailyEma9SeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const dailyEma50SeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const dailyEma200SeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  // Daily chart EMA legend values (updated on crosshair move)
  const [dailyEma9Value, setDailyEma9Value] = useState<number | null>(null);
  const [dailyEma50Value, setDailyEma50Value] = useState<number | null>(null);
  const [dailyEma200Value, setDailyEma200Value] = useState<number | null>(null);
  // Average Daily % — trailing 30-day mean of daily open→close % change
  const [avgDailyPct, setAvgDailyPct] = useState<number | null>(null);
  // [OK] FIX #4: Refs for daily chart price line updates
  const dailyPriceLineRef = useRef<any>(null);
  const dailyPreviousCloseRef = useRef<number | null>(null);
  
  // Real-time price tracking for proper OHLC aggregation
  const realtimePriceTracker = useRef<{
    currentMinute: number;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
  } | null>(null);
  
  // EMA series refs
  const ema9SeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const ema50SeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const ema200SeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  // VWAP + Anchored VWAP overlay series (main chart). Data is always pushed alongside the
  // EMAs (so they stay TF-aligned); a dedicated effect flips their `visible` option per toggle.
  const vwapSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const avwapSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const [vwapValue, setVwapValue] = useState<number | null>(null);
  const [avwapValue, setAvwapValue] = useState<number | null>(null);
  // Pivot-point price lines (main chart) + the prior-day OHLC used to compute them.
  const pivotLinesRef = useRef<ReturnType<ISeriesApi<'Candlestick'>['createPriceLine']>[]>([]);
  const [pivotDaily, setPivotDaily] = useState<{ high: number; low: number; close: number } | null>(null);
  // RSI(14) sub-pane — a second lightweight-charts instance, time-synced to the main chart
  // (mirrors the volume-chart wiring).
  const rsiContainerRef = useRef<HTMLDivElement>(null);
  const rsiChartRef = useRef<IChartApi | null>(null);
  const rsiSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const rsiResizeObserverRef = useRef<ResizeObserver | null>(null);
  const rsiSyncIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // RVOL sub-pane — same wiring as RSI. Histogram, because relative volume reads like volume.
  const rvolContainerRef = useRef<HTMLDivElement>(null);
  const rvolChartRef = useRef<IChartApi | null>(null);
  const rvolSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  /** time (sec) -> that bar's RVOL, for the crosshair readout. Rebuilt whenever the series is set. */
  const rvolByTimeRef = useRef<Map<number, { v: number; thin: boolean }>>(new Map());
  /** What the crosshair is currently over. null = not hovering, so the header shows the latest bar. */
  const [rvolHover, setRvolHover] = useState<{ v: number; thin: boolean } | null>(null);
  /**
   * time (sec) -> the bar as DRAWN, for the OHLC readout above the chart (owner 2026-07-28: "show all
   * the price info ... so i don't have to scroll down").
   *
   * Keyed off the AGGREGATED series rather than raw minutes, so on 5m the readout describes the 5-minute
   * candle under the cursor rather than one of the five minutes inside it. Rebuilt wherever setData runs,
   * which is also what keeps it correct across timeframe switches.
   */
  const barsByTimeRef = useRef<Map<number, Candle>>(new Map());
  /**
   * Last bar time the crosshair reported, shared by BOTH hover handlers.
   *
   * ⚠ THIS GUARD IS LOAD-BEARING, NOT AN OPTIMISATION (owner 2026-07-28: chart "unresponsive").
   * This component is ~9,500 lines with ~106 useState hooks, so EVERY setState re-renders the whole
   * tree. mousemove fires 60-120x/sec and the crosshair is subscribed on two charts, so dragging the
   * cursor across the chart was triggering well over a hundred full re-renders a second — and the
   * cursor crosses many pixels per BAR, so almost all of them reported a value that had not changed.
   * Bailing when the bar is unchanged collapses that to roughly one re-render per bar crossed.
   */
  const lastHoverTimeRef = useRef<number | null>(null);
  /** Newest drawn bar, captured when the map is built. Avoids rescanning ~1,500 entries on EVERY
   *  render just to find the fallback for the not-hovering case. */
  const latestBarRef = useRef<(Candle & { t: number }) | null>(null);
  const [hoverBar, setHoverBar] = useState<(Candle & { t: number }) | null>(null);
  const rvolResizeObserverRef = useRef<ResizeObserver | null>(null);
  const rvolSyncIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /**
   * WHICH INDICATORS ARE ACTUALLY ON SCREEN (owner 2026-07-28: "unresponsive and jerky").
   *
   * Every indicator was recomputed on EVERY data tick regardless of whether its pane was visible.
   * On the DEFAULT layout, with all panes collapsed, a 1-minute MU chart still ran calculateRSI,
   * calculateMACD, calculateADX, calculateATR, calculateBollinger, VWAP, AVWAP and both RVOL passes
   * across ~1,570 bars, then setData onto series nobody could see — several times a second between the
   * 3s candle poll and the 1s live tick. That is the periodic freeze: fine for a moment, then a stall.
   *
   * A REF, NOT STATE, deliberately: these gates are read inside the data effects, and putting the
   * toggles in those dependency arrays would tear down and rebuild the whole candle pipeline every
   * time someone flicked an indicator. The ref is updated by its own effect, so the gate always sees
   * the current value while the heavy effects keep their existing dependencies.
   */
  const visRef = useRef({ rsi: false, macd: false, adx: false, rvol: false, bb: false, atr: false, vwap: false, avwap: false, obv: false, adl: false, cmf: false, accdays: false });
  useEffect(() => {
    visRef.current = {
      rsi: showRSI, macd: showMACD, adx: showADX, rvol: showRVOL,
      bb: showBollinger, atr: showAtrReadout, vwap: showVWAP, avwap: showAVWAP,
      obv: showOBV, adl: showADLine, cmf: showCMF, accdays: showAccDaysReadout,
    };
  }, [showRSI, showMACD, showADX, showRVOL, showBollinger, showAtrReadout, showVWAP, showAVWAP, showOBV, showADLine, showCMF, showAccDaysReadout]);
  /** p99 ceiling for the RVOL axis — a ref, not state, because autoscaleInfoProvider is called by the
   *  chart on every repaint and must read the CURRENT value without re-registering the callback. */
  const rvolCapRef = useRef<number | null>(null);
  const [rsiValue, setRsiValue] = useState<number | null>(null);
  // ── Bollinger Bands overlay (main chart) — upper/middle/lower line series. ──
  // Created hidden; a dedicated effect flips `visible` per toggle (mirrors VWAP).
  const bbUpperSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const bbMiddleSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const bbLowerSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  // ── MACD(12/26/9) sub-pane — its own time-synced lightweight-charts instance. ──
  const macdContainerRef = useRef<HTMLDivElement>(null);
  const macdChartRef = useRef<IChartApi | null>(null);
  const macdLineSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const macdSignalSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const macdHistSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const macdResizeObserverRef = useRef<ResizeObserver | null>(null);
  const macdSyncIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [macdValue, setMacdValue] = useState<{ macd: number; signal: number; hist: number } | null>(null);
  // ── ADX(14) sub-pane — trend-strength oscillator with a 25 reference line. ──
  const adxContainerRef = useRef<HTMLDivElement>(null);
  const adxChartRef = useRef<IChartApi | null>(null);
  const adxSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const adxResizeObserverRef = useRef<ResizeObserver | null>(null);
  const adxSyncIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [adxValue, setAdxValue] = useState<number | null>(null);
  // ── OBV sub-pane — single amber line, autoscaled (no fixed band / thresholds). ──
  const obvContainerRef = useRef<HTMLDivElement>(null);
  const obvChartRef = useRef<IChartApi | null>(null);
  const obvSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const obvResizeObserverRef = useRef<ResizeObserver | null>(null);
  const obvSyncIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const obvByTimeRef = useRef<Map<number, number>>(new Map()); // for the pane crosshair (value varies, so it is looked up)
  const [obvValue, setObvValue] = useState<number | null>(null);
  // ── A/D line sub-pane — single emerald line, autoscaled. ──
  const adlContainerRef = useRef<HTMLDivElement>(null);
  const adlChartRef = useRef<IChartApi | null>(null);
  const adlSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const adlResizeObserverRef = useRef<ResizeObserver | null>(null);
  const adlSyncIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const adlByTimeRef = useRef<Map<number, number>>(new Map());
  const [adlValue, setAdlValue] = useState<number | null>(null);
  // ── CMF(20) sub-pane — bounded oscillator around 0 with ±0.10 reference lines. ──
  const cmfContainerRef = useRef<HTMLDivElement>(null);
  const cmfChartRef = useRef<IChartApi | null>(null);
  const cmfSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const cmfResizeObserverRef = useRef<ResizeObserver | null>(null);
  const cmfSyncIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [cmfValue, setCmfValue] = useState<number | null>(null);
  // Accumulation/distribution DAY count (legend readout, not a chart line).
  const [accDist, setAccDist] = useState<{ acc: number; dist: number } | null>(null);
  // ATR(14) + RVOL numeric readouts (legend, not chart lines).
  const [atrValue, setAtrValue] = useState<number | null>(null);
  const [atrPct, setAtrPct] = useState<number | null>(null);
  const [rvolValue, setRvolValue] = useState<number | null>(null);
  // Swing high/low markers, computed in pushIndicators and merged into the markers effect.
  // A counter bump re-triggers the markers effect after swings are recomputed.
  const swingMarkersRef = useRef<Array<{ time: number; position: 'aboveBar' | 'belowBar'; color: string; shape: 'arrowUp' | 'arrowDown'; text: string; size: number; id: string }>>([]);
  const [swingTick, setSwingTick] = useState(0);
  const showSwingsRef = useRef(false);
  useEffect(() => { showSwingsRef.current = showSwings; }, [showSwings]);
  // Watchlist (for Add to Watchlist button)
  const { data: session } = useSession();
  const [watchlist, setWatchlist] = useState<Set<string>>(new Set());
  const [watchlistLoading, setWatchlistLoading] = useState(false);

  // Manual Alpaca Buy modal (admin-only)
  const [manualBuyOpen, setManualBuyOpen]                 = useState(false);
  const [manualBuyPrice, setManualBuyPrice]               = useState('');
  const [manualBuyLoading, setManualBuyLoading]           = useState(false);
  const [manualBuyError, setManualBuyError]               = useState<string | null>(null);
  const [manualBuySuccess, setManualBuySuccess]           = useState(false);
  const [manualBuyOneSharePrice, setManualBuyOneSharePrice] = useState<number | null>(null);
  // Optional manual sizing: % of account value to invest (blank = Ra's automatic slice size).
  const [manualBuyPct, setManualBuyPct]                   = useState('');

  // Chart timeframe
  const [timeframe, setTimeframe] = useState<Timeframe>('1m');
  /** Reddit posts grouped to one pin per bar. See the RedditPin note above the state. */
  const redditPins = useMemo<RedditPin[]>(() => {
    if (!redditPosts.length) return [];
    const tfSec = (TIMEFRAMES.find((t) => t.value === timeframe)?.minutes ?? 1) * 60;
    const by = new Map<number, RedditPost[]>();
    for (const p of [...redditPosts].sort((a, b) => a.postedAt - b.postedAt)) {
      const bucket = Math.floor(Math.floor(snapToSession(p.postedAt) / 1000) / tfSec) * tfSec;
      const arr = by.get(bucket) ?? [];
      arr.push(p);
      by.set(bucket, arr);
    }
    return [...by.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([bucket, posts]) => ({ bucket, posts, count: posts.length }));
  }, [redditPosts, timeframe]);
  // Drives the candle fetch window. The candle effect reads the timeframe from a REF, so without a
  // real state value in its dependency list it never refetched on a timeframe change and every
  // higher timeframe just re-aggregated the 2-day window loaded for 1m (1h showed ~36 bars).
  const [candleWindowDays, setCandleWindowDays] = useState(() => daysForTimeframe(1));
  // Ref so closures (fetchCandles, markers effect) always read the latest timeframe
  // without being re-created every time it changes.
  const timeframeRef = useRef<Timeframe>('1m');
  useEffect(() => { timeframeRef.current = timeframe; }, [timeframe]);
  // Stored so the timeframe-change effect can trigger a fresh data fetch.
  const fetchCandlesCallbackRef = useRef<(() => Promise<void>) | null>(null);

  // Ra + Thoth + Osiris entry/exit price lines and P&L comparison
  const [raGameEntry, setRaGameEntry] = useState<{ price: number; shares: number; isOpen: boolean; avgCost: number | null; timestamp?: unknown } | null>(null);
  // Authoritative Ra entry conditions from the trade journal — chart prefers these over guessing
  // the condition from the nearest alert reason (which mislabels, e.g. condA shown for a condH entry).
  const [raJournalEntries, setRaJournalEntries] = useState<Array<{ entryReason: string | null; entryPrice: number; entryTime: string; exitPrice: number | null }>>([]);
  const [thothGameEntry, setThothGameEntry] = useState<{ price: number; shares: number; isOpen: boolean; avgCost: number | null; timestamp?: unknown } | null>(null);
  const [osirisGameEntry, setOsirisGameEntry] = useState<{ price: number; shares: number; isOpen: boolean; avgCost: number | null; timestamp?: unknown } | null>(null);
  const [raExit, setRaExit] = useState<{ price: number; realizedPl: number | null; realizedPlPct: number | null; timestamp?: unknown } | null>(null);
  const [thothExit, setThothExit] = useState<{ price: number; realizedPl: number | null; realizedPlPct: number | null; timestamp?: unknown } | null>(null);
  const [osirisExit, setOsirisExit] = useState<{ price: number; realizedPl: number | null; realizedPlPct: number | null; timestamp?: unknown } | null>(null);
  const [raAlpacaFill, setRaAlpacaFill] = useState<{ price: number; qty: number } | null>(null);
  const [alpacaSellFill, setAlpacaSellFill] = useState<{ price: number; qty: number } | null>(null);
  // Full per-symbol Alpaca fill history — each trade alert is matched to its actual
  // fill (side + nearest transaction_time) so hovers show real fill prices per trade.
  const [alpacaFills, setAlpacaFills] = useState<Array<{ id: string; side: string; price: number; qty: number; transactionTime: string }>>([]);
  // The Watcher's CONFIRMED Alpaca fills (separated from Ra's via the Watcher journal on the
  // server). Watcher markers resolve their displayed entry/exit price against THESE (not the
  // mixed alpacaFills), and a Watcher exit only renders when a real sell fill exists here.
  const [watcherFills, setWatcherFills] = useState<Array<{ id: string; side: string; price: number; qty: number; transactionTime: string }>>([]);
  // Journal-authoritative signal-vs-fill pairs — lets the hover card show "signal $X → filled $Y".
  const [watcherEntries, setWatcherEntries] = useState<Array<{ side: 'buy' | 'sell'; signalPrice: number; fillPrice: number | null; signalTime: string; fillTime: string | null; reason: string | null }>>([]);
  // Ground-truth Watcher journal trades (both books). null = not loaded / query failed →
  // the alert-pairing pipeline stays authoritative. [] = journal says NO trades (trusted).
  const [journalTrades, setJournalTrades] = useState<WatcherJournalTrade[] | null>(null);
  // Has the journal REQUEST come back yet? Distinct from "the journal has trades". Before it settles,
  // journalTrades is null and the legacy alert pairing would happily pool every Watcher/Rider buy on this
  // symbol into one running stack — which is how a CLOSED position and a LATER, unrelated one rendered as a
  // single umbrella ("SXTC OPEN, 2 entries", owner 2026-07-25). Showing nothing for that brief moment is
  // strictly better than showing two separate trades merged into one.
  const [journalSettled, setJournalSettled] = useState(false);
  // How many days back the candle window must reach to include the OLDEST entry (multi-day holds).
  // REFS, not state (2026-07-07 nav-speed pass): this used to be useState in the fetch effect's
  // deps, so EVERY chart load with any trade history re-ran the whole effect — abort + a second
  // full candle download + the indicator cascade. Now entries trigger an explicit refetch ONLY
  // when the already-loaded window can't contain the oldest entry.
  const entryLookbackDaysRef = useRef(0);
  const lastFetchedDaysRef = useRef(0);
  const [alpacaPl, setAlpacaPl] = useState<number | null>(null);
  const [alpacaPlPct, setAlpacaPlPct] = useState<number | null>(null);
  // P&L card shown on hover over exit alerts
  const [plCard, setPlCard] = useState<{
    bot: 'Ra' | 'Thoth' | 'Osiris';
    gamePl: number | null; gamePlPct: number | null;
    gameEntry: number | null; gameExit: number | null;
    gameEntryTime: string | null; gameExitTime: string | null;
    alpacaEntry: number | null; alpacaExit: number | null;
    alpacaPl: number | null; alpacaPlPct: number | null;
  } | null>(null);
  const raGamePriceLineRef = useRef<any>(null);
  const thothGamePriceLineRef = useRef<any>(null);
  const raAlpacaPriceLineRef = useRef<any>(null);

  // Function to update real-time price tracker for proper OHLC aggregation
  const updateRealtimePriceTracker = (price: number, volume: number = 0) => {
    const now = new Date();
    const currentMinute = Math.floor(now.getTime() / 1000 / 60) * 60;
    
    if (!realtimePriceTracker.current || realtimePriceTracker.current.currentMinute !== currentMinute) {
      // New minute - start fresh tracking
      realtimePriceTracker.current = {
        currentMinute,
        open: price,
        high: price,
        low: price,
        close: price,
        volume: volume
      };
    } else {
      // Same minute - update OHLC
      realtimePriceTracker.current.high = Math.max(realtimePriceTracker.current.high, price);
      realtimePriceTracker.current.low = Math.min(realtimePriceTracker.current.low, price);
      realtimePriceTracker.current.close = price;
      realtimePriceTracker.current.volume += volume;
    }
  };

  // Ensure client-only rendering for volatile values to avoid hydration mismatch
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Track chart view for Most Clicked rankings (last 24h)
  useEffect(() => {
    if (!symbol) return;
    fetch('/api/stock/click', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ symbol, source: 'chart' }),
      credentials: 'include',
    }).catch(() => {});
  }, [symbol]);

  // Fetch watchlist when logged in (for Add to Watchlist button)
  useEffect(() => {
    if (!session?.user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/user/watchlist', { cache: 'no-store' });
        if (res.ok && !cancelled) {
          const data = await res.json();
          setWatchlist(new Set<string>(data.watchlist || []));
        }
      } catch {
        if (!cancelled) setWatchlist(new Set());
      }
    })();
    return () => { cancelled = true; };
  }, [session?.user]);

  const toggleWatchlist = useCallback(async () => {
    if (!symbol || !session?.user || watchlistLoading) return;
    setWatchlistLoading(true);
    try {
      const next = new Set(watchlist);
      if (next.has(symbol)) next.delete(symbol);
      else next.add(symbol);
      setWatchlist(next);
      const res = await fetch('/api/user/watchlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ watchlist: Array.from(next) }),
      });
      if (!res.ok) {
        setWatchlist(watchlist);
      } else if (typeof window !== 'undefined') {
        // Sync the left pane, WatchStars, /watchlist and /alerts in real time.
        window.dispatchEvent(new Event('watchlist-changed'));
      }
    } catch {
      setWatchlist(watchlist);
    } finally {
      setWatchlistLoading(false);
    }
  }, [symbol, session?.user, watchlist, watchlistLoading]);

  // Reflect watch changes made elsewhere on the site (WatchStars, /watchlist, /alerts) in real time.
  useEffect(() => {
    if (!session?.user) return;
    const onChange = () => {
      fetch('/api/user/watchlist', { cache: 'no-store' })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (d) setWatchlist(new Set<string>(d.watchlist || [])); })
        .catch(() => {});
    };
    window.addEventListener('watchlist-changed', onChange);
    return () => window.removeEventListener('watchlist-changed', onChange);
  }, [session?.user]);

  /** Chart x-axis + visible-range filter: bar minute, not DB insert time (backlog-safe). */
  const chartTimeMs = useCallback(
    (a: Alert) =>
      typeof a.barTimestamp === 'number' && a.barTimestamp > 0 ? a.barTimestamp : a.timestamp,
    []
  );

  /** `getVisibleRange().to` often ends at the last *completed* bar; live minute + padding sits to the right. */
  const CHART_ALERT_RIGHT_MARGIN_MS = 6 * 60 * 1000;
  const CHART_ALERT_LEFT_MARGIN_MS = 2 * 60 * 1000;
  // Signature of the last alert set we pushed to state, so a poll that returns identical alerts does not
  // call setAlerts with a fresh array and trigger a needless re-render cascade (markers + sidebar).
  const lastAlertsSigRef = useRef<string>('');

  // Fetch alerts function - based on chart's visible time range
  const fetchAlertsForSymbol = useCallback(async () => {
    try {
      // [OK] FIX: Get alerts based on chart's visible time range (not fixed trading session)
      let startTime: number;
      let endTime: number;
      
      if (chartRef.current && candleSeriesRef.current && chartContainerRef.current && document.body.contains(chartContainerRef.current)) {
        try {
          // FULL LOADED WINDOW, not the visible range (CLRO 2026-07-07 root cause): this
          // list feeds TRADE PAIRING, which needs a trade's whole lifetime. Visible-range
          // fetching silently dropped any entry alert scrolled off the left edge — the
          // exit then rendered unpaired (exit-only tooltip, no entry line) and "fixed
          // itself" on zoom-out because the wider view refetched a wider window. The
          // loaded-candle extent covers everything drawable, and pan/zoom no longer
          // changes the fetch window at all.
          const data = candleSeriesRef.current.data();
          if (data && data.length > 0) {
              const firstBar = data[0];
              const lastBar = data[data.length - 1];
              startTime = (firstBar.time as number) * 1000;
              endTime = (lastBar.time as number) * 1000;
            } else {
              // No candle data yet — query last 4 hours (matches count API window; avoids future-window bug).
              startTime = Date.now() - 4 * 60 * 60 * 1000;
              endTime = Date.now();
            }
        } catch (_) {
          // Chart range unavailable — query last 4 hours (matches count API window).
          startTime = Date.now() - 4 * 60 * 60 * 1000;
          endTime = Date.now();
        }
      } else {
        // Chart not initialized yet — query last 4 hours (matches count API window).
        startTime = Date.now() - 4 * 60 * 60 * 1000;
        endTime = Date.now();
      }

      // Widen window so alerts for the developing / right-edge minute are included (API + client filter).
      endTime += CHART_ALERT_RIGHT_MARGIN_MS;
      startTime = Math.max(0, startTime - CHART_ALERT_LEFT_MARGIN_MS);

      // Recent-alerts list must not vanish when the candle window narrows or briefly empties on an
      // overnight refresh (RDGL 2026-07-09): floor the look-back so the window always reaches the
      // last session's alerts. Extends left only — trade pairing/markers are unaffected (off-range
      // markers simply don't draw). 36h comfortably covers any overnight gap; a multi-day candle
      // span still wins via min() since its start is earlier.
      startTime = Math.min(startTime, endTime - 36 * 60 * 60 * 1000);

      const response = await fetch(
        `/api/alerts/recent?limit=500&q=${encodeURIComponent(symbol)}&from=${startTime}&to=${endTime}&priceMode=alert&includeStaleLive=1`,
        { cache: 'no-store' }
      );
      
      if (response.ok) {
        const data = await response.json();
        dbg(`[STATS] [CHART] Total alerts from API: ${data.alerts?.length || 0}`);
        
        // Filter by bar time vs visible range (API `timestamp` is often insert time - would drop backlog alerts)
        const filteredAlerts = (data.alerts || []).filter((a: Alert) => {
          const t = chartTimeMs(a);
          const isAfterStart = t >= startTime;
          const isBeforeEnd = t <= endTime;
          const isSymbolMatch = a.symbol === symbol || a.symbol.startsWith(symbol + '.');
          return isAfterStart && isBeforeEnd && isSymbolMatch;
        });

        // DEDUPLICATION pass 1: same symbol+type within the same minute
        const getMinuteTimestamp = (ts: number) => {
          const timestamp = ts > 10000000000 ? ts : ts * 1000;
          return Math.floor(timestamp / 60000) * 60000;
        };

        const recentAlerts = filteredAlerts.reduce((acc: Alert[], alert: Alert) => {
          if (!alert.symbol || !alert.type || !alert.timestamp) return acc;

          const alertMinute = getMinuteTimestamp(chartTimeMs(alert));
          // A BUY and a SELL of the same name in the same minute are DIFFERENT events (a fast trade that
          // opened + closed same-minute), so keep the trade SIDE in the dedup key — otherwise a Rider
          // entry+exit (both type='rider') collapse to one marker (NEXR entry-no-exit / ACHR exit-no-entry).
          const alertSide = parseTradeMeta(alert)?.side ?? null;
          const existingIndex = acc.findIndex(a => {
            const existingMinute = getMinuteTimestamp(chartTimeMs(a));
            return a.symbol === alert.symbol && a.type === alert.type && existingMinute === alertMinute && (parseTradeMeta(a)?.side ?? null) === alertSide;
          });

          if (existingIndex === -1) {
            acc.push(alert);
          } else {
            const existing = acc[existingIndex];
            const existingFieldCount = Object.values(existing.data || {}).filter(v => v !== undefined && v !== null && v !== 0).length;
            const newFieldCount = Object.values(alert.data || {}).filter(v => v !== undefined && v !== null && v !== 0).length;

            if (Math.random() < 0.1) {
              console.log(`[RUN] [CHART] Duplicate found: ${alert.symbol} (${alert.type})`);
            }

            if (newFieldCount > existingFieldCount) {
              acc[existingIndex] = alert;
            }
          }
          
          return acc;
        }, []);

        // DEDUPLICATION pass 2: Ra/Thoth sell de-dup per position.
        // When OOH sell cycling blocked the Ra tick, subsequent ticks re-exited
        // the same paper position creating multiple "Ra SOLD" alerts for one entry.
        // Rule: for each Ra/Thoth BUY alert, keep only the FIRST sell alert that
        // follows it. All later sell alerts for the same type+symbol in the same
        // position window are suppressed (they're retry artifacts, not real trades).
        const tradeAlertTypes = new Set(['ra', 'thoth']);
        const sortedByTime = [...recentAlerts].sort(
          (a, b) => chartTimeMs(a) - chartTimeMs(b)
        );
        // Track when the last buy fired for each bot+symbol key
        const lastBuyAt = new Map<string, number>();
        const lastSellAt = new Map<string, number>();
        const deduped = sortedByTime.filter((alert) => {
          if (!tradeAlertTypes.has(alert.type)) return true; // non-trade: keep as-is
          const meta = parseTradeMeta(alert);
          const side = meta?.side ?? 'buy';
          const key = `${alert.type}:${alert.symbol}`;
          const t = chartTimeMs(alert);
          if (side === 'buy') {
            lastBuyAt.set(key, t);
            lastSellAt.delete(key); // reset sell tracking when a new buy appears
            return true;
          }
          // Sell: only keep if no sell has been shown for this position yet
          const buyAt = lastBuyAt.get(key) ?? 0;
          const prevSellAt = lastSellAt.get(key);
          if (prevSellAt !== undefined && prevSellAt >= buyAt) {
            // Already showed a sell for this position — suppress duplicate
            return false;
          }
          lastSellAt.set(key, t);
          return true;
        });

        if (recentAlerts.length > 0 || Math.random() < 0.05) {
          dbg(`[STATS] [CHART] Filtered alerts: ${deduped.length} for ${symbol} (${recentAlerts.length} raw, sell-dedup active)`);
        }
        // Only update state when the set actually changed. Cheap signature: count + first/last id or ts.
        const first = deduped[0] as unknown as { id?: unknown; timestamp?: unknown } | undefined;
        const last = deduped[deduped.length - 1] as unknown as { id?: unknown; timestamp?: unknown } | undefined;
        const sig = `${deduped.length}:${String(first?.id ?? first?.timestamp ?? '')}|${String(last?.id ?? last?.timestamp ?? '')}`;
        if (sig !== lastAlertsSigRef.current) {
          lastAlertsSigRef.current = sig;
          setAlerts(deduped);
        }
      }
    } catch (error) {
      // Only log errors occasionally to prevent console spam
      if (Math.random() < 0.1) {
        console.error('Error fetching alerts:', error);
      }
    }
  }, [symbol, chartTimeMs]);

  // Fetch alerts: delay 800ms on first load so candle fetch gets priority,
  // then stream every 5s. (The 5s candle-refresh cycle also calls fetchAlertsForSymbol,
  // so the interval here is just the standalone poller for between-refresh freshness.)
  useEffect(() => {
    if (!symbol) {
      console.log('[STATS] [CHART] No symbol provided, skipping alert fetch');
      return;
    }
    const firstFetch = setTimeout(() => fetchAlertsForSymbol(), 800);
    const id = setInterval(() => {
      fetchAlertsForSymbol();
    }, 5000);
    return () => {
      clearTimeout(firstFetch);
      clearInterval(id);
    };
  }, [symbol, fetchAlertsForSymbol]);

  // Fetch Ra + Thoth game entries and Alpaca fill prices for this symbol
  useEffect(() => {
    if (!symbol) return;
    let cancelled = false;
    // New symbol: forget the previous symbol's lookback so its (possibly 45-day) window
    // doesn't inflate this symbol's initial candle fetch. Runs before the fetch effect below.
    entryLookbackDaysRef.current = 0;
    setJournalTrades(null); // stale journal must never pair the NEXT symbol's alerts
    setJournalSettled(false);
    fetch(`/api/chart/ra-entries?symbol=${encodeURIComponent(symbol)}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled || !data) return;
        setRaGameEntry(data.gameEntry ?? null);
        setThothGameEntry(data.thothEntry ?? null);
        setOsirisGameEntry(data.osirisEntry ?? null);
        setRaExit(data.raExit ?? null);
        setThothExit(data.thothExit ?? null);
        setOsirisExit(data.osirisExit ?? null);
        setRaAlpacaFill(data.alpacaFill ?? null);
        setAlpacaSellFill(data.alpacaSellFill ?? null);
        setAlpacaPl(data.alpacaPl ?? null);
        setAlpacaPlPct(data.alpacaPlPct ?? null);
        setAlpacaFills(Array.isArray(data.alpacaFills) ? data.alpacaFills : []);
        setWatcherFills(Array.isArray(data.watcherFills) ? data.watcherFills : []);
        setWatcherEntries(Array.isArray(data.watcherEntries) ? data.watcherEntries : []);
        setJournalTrades(Array.isArray(data.watcherJournalTrades) ? (data.watcherJournalTrades as WatcherJournalTrade[]) : null);
        setJournalSettled(true);
        setRaJournalEntries(Array.isArray(data.raJournalEntries) ? data.raJournalEntries : []);
        // ROOT-CAUSE FIX (exit with no entry on chart): a multi-day hold's entry fell outside the
        // short default candle window (e.g. OUST held Jun 29 -> exit Jul 2 shows only the exit on a
        // 1m/2-day view). Extend the window back to the oldest entry so EVERY entry renders + pairs
        // with its exit, on any timeframe. Capped so a very old trade can't blow up the candle load.
        {
          const we = (Array.isArray(data.watcherEntries) ? data.watcherEntries : []) as Array<{ fillTime?: string | null; signalTime?: string }>;
          const rje = (Array.isArray(data.raJournalEntries) ? data.raJournalEntries : []) as Array<{ entryTime?: string }>;
          const jts = (Array.isArray(data.watcherJournalTrades) ? data.watcherJournalTrades : []) as Array<{ entryMs?: number }>;
          const times = [
            ...we.map((e) => (e.fillTime ?? e.signalTime) ? Date.parse((e.fillTime ?? e.signalTime) as string) : NaN),
            ...rje.map((e) => (e.entryTime ? Date.parse(e.entryTime) : NaN)),
            ...jts.map((t) => Number(t.entryMs)), // journal trades (both books) anchor markers too
          ].filter((n) => Number.isFinite(n) && n > 0) as number[];
          const daysAgo = times.length ? Math.ceil((Date.now() - Math.min(...times)) / 86_400_000) + 1 : 0;
          entryLookbackDaysRef.current = Math.min(Math.max(daysAgo, 0), 45);
          // Refetch ONLY if the loaded window can't already contain the oldest entry.
          if (entryLookbackDaysRef.current > lastFetchedDaysRef.current) {
            fetchCandlesCallbackRef.current?.();
          }
        }
      })
      // Settle on failure too: a journal that never arrives must fall back to the legacy pairing rather
      // than suppressing Watcher/Rider cards forever.
      .catch(() => { if (!cancelled) setJournalSettled(true); });
    return () => { cancelled = true; };
  }, [symbol]);

  // Ra entry lines are shown on hover only — see the hoveredTradeId useEffect below.

  // No permanent price lines — entry/exit lines appear only on hover (see hoveredTradeId useEffect).

  // ── LIVE FORMING CANDLE state (intra-minute ticks) ─────────────────────────
  // lastBarRef mirrors the last rendered (aggregated) bar; the 1s tick loop and the 2s
  // poll both merge into it so a lagging poll never erases tick-seen highs/lows.
  const lastBarRef = useRef<{ time: number; open: number; high: number; low: number; close: number; volume: number } | null>(null);
  const lastTickAtRef = useRef(0);
  const sessionBandsRef = useRef<HTMLDivElement | null>(null);   // session shading overlay host
  const lastMinSeenRef = useRef<{ t: number; v: number } | null>(null); // snapshot-minute volume watermark (live volume ticking)

  // Fetch candles
  useEffect(() => {
    const tfMin = TIMEFRAMES.find((t) => t.value === timeframe)?.minutes ?? 1;
    setCandleWindowDays(daysForTimeframe(tfMin));
  }, [timeframe]);

  // DAILY BARS GET A DATE AXIS, NOT A CLOCK.
  // Both charts are created with timeVisible:true, which is right for intraday and wrong for 1D —
  // every daily candle stamps 8:00 PM (the bar's ET close) so the axis read as a single repeated time
  // instead of a series of dates. Applied to the volume pane too so the two axes stay aligned.
  useEffect(() => {
    const intraday = timeframe !== '1D';
    const opts = { timeScale: { timeVisible: intraday, secondsVisible: false } };
    try { chartRef.current?.applyOptions(opts); } catch { /* chart not ready yet */ }
    try { volumeChartRef.current?.applyOptions(opts); } catch { /* volume pane optional */ }
  }, [timeframe, chartsReady]);

  useEffect(() => {
    // This effect-run's own lifecycle flag. The recursive poll chain below checks it instead of the
    // SHARED isAliveRef (which only flips false on unmount): without a per-run flag, an effect RE-RUN
    // (timeframe switch, symbol revisit, the entryLookback refetch) left the previous run's in-flight
    // setTimeout callback free to call scheduleNext() again - because isAliveRef was still true - so a
    // second self-rescheduling chain kept polling alongside the new one. That intermittent 1-vs-2 chain
    // overlap is the ~40/min (vs intended ~30/min) seen in chart-api-refetches-one-symbol-39k-times-1007.
    // Setting cancelled=true in THIS run's cleanup kills exactly its own chain.
    let cancelled = false;
    const fetchCandles = async () => {
      try {
        if (candlesAbortRef.current) {
          try { candlesAbortRef.current.abort(); } catch {}
        }
        const controller = new AbortController();
        candlesAbortRef.current = controller;
        // EQUAL BAR COUNT ACROSS TIMEFRAMES. The window scales with the bar size, so every
        // timeframe shows roughly what 2 days of 1-minute bars shows:
        //   1m → 2d · 5m → 10d · 15m → 30d · 30m → 60d · 1h → 120d
        // The old ladder capped at 30 days from 15m up, so 30m and 1h were strictly worse than 15m.
        const tfMinutesNow = TIMEFRAMES.find(t => t.value === timeframeRef.current)?.minutes ?? 1;
        const baseDays = daysForTimeframe(tfMinutesNow);
        // Extend the load back to the oldest entry (capped) so multi-day-hold entries render + pair.
        // ⚡ BUT bound it by a BAR BUDGET so the 1-minute timeframe never pulls 45 days = ~43k bars =
        // 715KB, which took ~3.6s and re-ran the whole indicator cascade (the "slow to become usable"
        // blank). 45 days of 1m at ~960 extended-session bars/day blows the budget; capping it to ~8
        // days keeps the payload ~140KB and paints fast. Entries older than the cap still pair on the
        // higher timeframes (5m+ cover the same 45 days in a fraction of the bars, so they stay uncapped).
        const BAR_BUDGET = 8000;
        const barsPerDay = tfMinutesNow <= 60 ? Math.ceil(960 / tfMinutesNow) : 1; // ~16h extended session
        const maxDaysByBudget = Math.max(baseDays, Math.floor(BAR_BUDGET / Math.max(1, barsPerDay)));
        const daysNeeded = Math.min(Math.max(baseDays, entryLookbackDaysRef.current), maxDaysByBudget);
        lastFetchedDaysRef.current = daysNeeded; // what the loaded window covers (assignment: symbol/TF switches shrink it correctly)
        // ⚡ INCREMENTAL POLL. The 2s poll re-downloaded the whole window (~50KB brotli) to move ~1 bar. Once a full
        // window for THIS request is held, ask only for bars from its newest minus the overlap and merge them in;
        // a full refetch every MINICHART_FULL_EVERY_MS, and a body that does not echo since (the hour/day path, an
        // old server mid-swap) is a full replacement, never appended. Same rules as src/lib/minichartMerge.ts.
        const heldKey = `${symbol}|${daysNeeded}|${tfMinutesNow}`;
        const askedAt = Date.now();
        const since = heldSince(candlesHeldRef.current, heldKey, askedAt, candleTime);
        const response = await fetch(`/api/chart/candles?symbol=${symbol}&days=${daysNeeded}&tf=${tfMinutesNow}${since != null ? `&since=${since}` : ''}`, { cache: 'no-store', signal: controller.signal });

        if (response.ok) {
          const data = await response.json();
          const fresh = Array.isArray(data.candles) ? (data.candles as Candle[]) : null;
          const nextHeld = applyHeld(candlesHeldRef.current, heldKey, fresh, data.since, since, askedAt, candleTime);
          if (fresh && nextHeld && nextHeld.key === heldKey) {
            candlesHeldRef.current = nextHeld;
            data.candles = nextHeld.bars;
          }
          if (data.candles && data.candles.length > 0) {
            dbg(`[STATS] [CHART_DATA] Raw candles received: ${data.candles.length}`);
            dbg(`[STATS] [CHART_DATA] First candle: ${new Date(data.candles[0].timestamp).toLocaleString()}, Last candle: ${new Date(data.candles[data.candles.length - 1].timestamp).toLocaleString()}`);
            // ── PHASE-1 FAST PATH (TradingView feel) ──────────────────────────────
            // Most 2s polls only mutate the FORMING bar. setCandles() re-fires the whole
            // indicator cascade (~56 setData calls → the visible blink). When this poll differs
            // ONLY in the last raw bar, mutate just the last aggregated candle + volume bar via
            // series.update() and skip the React cascade. Any structural change (new bar,
            // backfill, TF/symbol switch) takes the full path below.
            const nc = data.candles as Candle[];
            const lastRaw = nc[nc.length - 1];
            const sig = prevCandlesSigRef.current;
            const formingOnly = !!sig && sig.len === nc.length && sig.lastTs === lastRaw.timestamp && sig.firstTs === nc[0].timestamp;
            prevCandlesSigRef.current = { len: nc.length, lastTs: lastRaw.timestamp, firstTs: nc[0].timestamp };
            let fastApplied = false;
            try {
              const tfMinNow = TIMEFRAMES.find(t => t.value === timeframeRef.current)?.minutes ?? 1;
              // Absolute-time bucketing → aggregating a 2-bucket tail yields the SAME last bucket
              // key the rendered series has; update() mutates it in place.
              const tail = tfMinNow > 1 ? aggregateCandles(nc.slice(-(tfMinNow * 2)), tfMinNow) : [lastRaw];
              const lastAgg = tail[tail.length - 1];
              // Seed/merge the live-tick forming bar: the DB/Polygon poll lags the tape by a
              // few seconds, so a stale poll must never erase tick-seen highs/lows or close.
              const t0 = Math.floor(lastAgg.timestamp / 1000);
              const prevBar = lastBarRef.current;
              const tickFresh = Date.now() - lastTickAtRef.current < 2500;
              const merged = prevBar && prevBar.time === t0
                ? { time: t0, open: lastAgg.open,
                    high: Math.max(lastAgg.high, prevBar.high), low: Math.min(lastAgg.low, prevBar.low),
                    close: tickFresh ? prevBar.close : lastAgg.close,
                    // volume is monotonic within a bucket — never let a lagging poll shrink the live bar
                    volume: Math.max(lastAgg.volume ?? 0, prevBar.volume ?? 0) }
                : { time: t0, open: lastAgg.open, high: lastAgg.high, low: lastAgg.low,
                    close: lastAgg.close, volume: lastAgg.volume ?? 0 };
              lastBarRef.current = merged;
              if (formingOnly && candleSeriesRef.current) {
                candleSeriesRef.current.update({
                  time: merged.time, open: merged.open, high: merged.high, low: merged.low, close: merged.close,
                } as never);
                try { volumeSeriesRef.current?.update({ time: merged.time, value: merged.volume } as never); } catch { /* volume pane optional */ }
                fastApplied = true;
              }
            } catch { /* fall through to the full path */ }
            if (!fastApplied) setCandles(nc);
            setLastUpdate(new Date());
            // derive header stats if provided (previousClose lets you verify %: (current - prev) / prev * 100)
            try {
              if (typeof data.currentPrice === 'number') setCurrentPrice(data.currentPrice);
              // dayChangePct can be null in pre-market (no regular session yet) — accept null too.
              setDayChangePct(typeof data.dayChangePct === 'number' ? data.dayChangePct : null);
              setAfterHoursPct(typeof data.afterHoursPct === 'number' ? data.afterHoursPct : null);
              setPreMarketPct(typeof data.preMarketPct === 'number' ? data.preMarketPct : null);
              setRegularClose(typeof data.regularClose === 'number' ? data.regularClose : null);
              if (typeof data.sessionPhase === 'string') setSessionPhase(data.sessionPhase);
              if (typeof data.isLastSession === 'boolean') setIsLastSession(data.isLastSession);
              if (typeof data.dayVolume === 'number') setDayVolume(data.dayVolume);
              if (typeof data.previousClose === 'number') setPreviousClose(data.previousClose);
            } catch {}
          }
        }
      } catch (error: any) {
        if (error?.name === 'AbortError') return;
        console.error('Error fetching candles:', error);
      } finally {
        setLoading(false);
      }
    };

    if (symbol) {
      // Register so the timeframe-change effect can trigger a fresh fetch.
      fetchCandlesCallbackRef.current = fetchCandles;
      fetchCandles();
      // Fetch company name (fire-and-forget — never blocks chart load)
      fetch(`/api/stock-info/${encodeURIComponent(symbol)}`)
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d?.name) setCompanyName(d.name) })
        .catch(() => {});

      // Determine update frequency based on market hours
      const getRefreshInterval = () => {
        const now = new Date();
        const etTime = new Date(now.toLocaleString("en-US", {timeZone: "America/New_York"}));
        const hour = etTime.getHours();
        const day = etTime.getDay();

        const isWeekend = day === 0 || day === 6;
        const isMarketHours = !isWeekend && hour >= 4 && hour < 20;
        // 2s during market hours (was 5s) for smoother live candles. The 1s version "hammered
        // the DB" before the candles route cached its DB overlay (5s TTL) + Polygon base (15s);
        // now most polls hit cache, so the DB load is bounded regardless of poll rate.
        return isMarketHours ? 2000 : 30000;
      };

      const scheduleNext = () => {
        const delay = getRefreshInterval();
        if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = setTimeout(async () => {
          // Candles only. Alerts have their OWN 5s poller (effect near the alert interval below); calling
          // fetchAlertsForSymbol here too made it run ~42x/min and re-filter 290 alerts each cycle - a
          // primary source of the chart jank. See the chart-perf redesign.
          if (cancelled) return;
          // SKIP the recurring fetch when the tab is HIDDEN or the market is CLOSED (overnight / weekend).
          // The chart is already painted on mount and nothing can move, so a poll just re-fetches bars that
          // do not exist - a tab left open overnight was hitting /api/chart/candles every 30s all night
          // (~40k requests on one real tab). Keep the chain alive so it resumes when the tab is shown and
          // the session reopens (the off-hours interval rechecks cheaply, with no network). app/stream gates
          // on the same window. See chart-api-refetches-one-symbol-39k-times-1007.
          if ((typeof document !== 'undefined' && document.visibilityState !== 'visible') || !isMarketHoursET()) {
            scheduleNext();
            return;
          }
          await fetchCandles();
          if (cancelled) return;
          scheduleNext();
        }, delay);
      };

      scheduleNext();

      return () => {
        cancelled = true;   // kill THIS run's poll chain so a re-run never leaves it polling in parallel
        if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
        if (candlesAbortRef.current) {
          try { candlesAbortRef.current.abort(); } catch {}
          candlesAbortRef.current = null;
        }
      };
    }
  }, [symbol, fetchAlertsForSymbol, candleWindowDays]);

  // ── LIVE TICK LOOP: move the forming candle WITHIN the minute (TradingView feel). ──
  // 1s poll of /api/chart/live-tick (Polygon snapshot behind a ~1s server cache) mutates
  // the last bar in place via series.update() — no React state, no indicator cascade.
  useEffect(() => {
    if (!symbol) return;
    const volColorFor = (timeSec: number, up: boolean) => {
      const et = new Date(new Date(timeSec * 1000).toLocaleString('en-US', { timeZone: 'America/New_York' }));
      const m = et.getHours() * 60 + et.getMinutes();
      if (m >= 240 && m < 570) return up ? '#3b82f6' : '#1d4ed8';   // premarket blues
      if (m >= 570 && m < 960) return up ? '#22c55e' : '#ef4444';   // RTH green/red
      if (m >= 960 && m < 1200) return up ? '#a855f7' : '#7c3aed';  // after-hours purples
      return '#26a69a';
    };
    lastMinSeenRef.current = null; // fresh baseline per symbol/effect run
    const tick = async () => {
      try {
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
        const et = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
        const d = et.getDay(); const h = et.getHours();
        if (d === 0 || d === 6 || h < 4 || h >= 20) return; // only while the tape can move
        const r = await fetch(`/api/chart/live-tick?symbol=${encodeURIComponent(symbol)}`, { cache: 'no-store' });
        if (!r.ok) return;
        const j = await r.json();
        const price = Number(j?.p); const tMs = Number(j?.t);
        if (!Number.isFinite(price) || price <= 0 || !tMs) return;
        if (Date.now() - tMs > 90_000) return; // stale tape (halt / illiquid) — don't animate a dead bar
        const tfMin = TIMEFRAMES.find(t2 => t2.value === timeframeRef.current)?.minutes ?? 1;
        const bSec = tfMin * 60;
        const bucket = Math.floor(tMs / 1000 / bSec) * bSec;
        const lb = lastBarRef.current;
        let bar: { time: number; open: number; high: number; low: number; close: number; volume: number };
        if (lb && lb.time === bucket) {
          bar = { ...lb, high: Math.max(lb.high, price), low: Math.min(lb.low, price), close: price };
        } else if (lb && bucket > lb.time) {
          // minute rolled over before the 2s poll caught up — open the new forming bar now
          bar = { time: bucket, open: price, high: price, low: price, close: price, volume: 0 };
        } else {
          return; // tick older than the rendered bar (symbol/TF just switched)
        }
        lastBarRef.current = bar;
        lastTickAtRef.current = Date.now();
        candleSeriesRef.current?.update({
          time: bar.time, open: bar.open, high: bar.high, low: bar.low, close: bar.close,
        } as never);
        // LIVE VOLUME: the forming volume bar grows with the tape on EVERY timeframe.
        // 1m: the snapshot's current-minute volume IS the bucket volume (authoritative).
        // >1m: accumulate per-tick minute-volume deltas onto the poll-seeded bucket total
        // (the 2s poll re-anchors it; the merge keeps volume monotonic within a bucket).
        const minT = Number(j?.minT); const minV = Number(j?.minV);
        if (Number.isFinite(minT) && minT > 0 && Number.isFinite(minV) && minV > 0) {
          const minSec = Math.floor(minT / 60000) * 60;
          if (minSec >= bucket && minSec < bucket + bSec) {
            const seen = lastMinSeenRef.current;
            if (tfMin === 1) {
              bar.volume = Math.max(bar.volume, minV);
            } else if (seen) {
              const delta = seen.t === minT ? Math.max(0, minV - seen.v) : minV;
              bar.volume += delta;
            } // first tick after mount: no baseline yet — skip once rather than double-count
            lastMinSeenRef.current = { t: minT, v: minV };
            try {
              volumeSeriesRef.current?.update({ time: bar.time, value: bar.volume, color: volColorFor(bar.time, bar.close >= bar.open) } as never);
            } catch { /* volume pane optional */ }
          }
        }
      } catch { /* network hiccup — next tick */ }
    };
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [symbol]);

  // ── SESSION SHADING: premarket/after-hours background bands ─────────────────
  // lightweight-charts has no native background bands, so we paint pointer-transparent
  // tint divs over the pane. Runs are LOGICAL index ranges into the rendered series
  // (logical index == array index), so logicalToCoordinate handles partial visibility
  // cleanly — no exact-bar-time lookups needed.
  useEffect(() => {
    const chart = chartRef.current;
    const host = sessionBandsRef.current;
    if (!chart || !host || loading || candles.length === 0) { if (host) host.innerHTML = ''; return; }
    const tfMin = TIMEFRAMES.find(t => t.value === timeframe)?.minutes ?? 1;
    const series = tfMin > 1 ? aggregateCandles(candles, tfMin) : candles;
    const sessOf = (tsMs: number) => {
      const et = new Date(new Date(tsMs).toLocaleString('en-US', { timeZone: 'America/New_York' }));
      const m = et.getHours() * 60 + et.getMinutes();
      if (m >= 240 && m < 570) return 'pre';
      if (m >= 960 && m < 1200) return 'ah';
      return m >= 570 && m < 960 ? 'rth' : 'off';
    };
    const runs: Array<{ sess: string; i0: number; i1: number }> = [];
    for (let i = 0; i < series.length; i++) {
      const sess = sessOf(series[i].timestamp);
      if (sess !== 'pre' && sess !== 'ah') continue;
      const last = runs[runs.length - 1];
      if (last && last.sess === sess && last.i1 === i - 1) last.i1 = i;
      else runs.push({ sess, i0: i, i1: i });
    }
    // Tints match the volume pane's session language (premarket blue / AH purple).
    const TINT: Record<string, string> = { pre: 'rgba(59,130,246,0.045)', ah: 'rgba(168,85,247,0.05)' };
    const paint = () => {
      try {
        const ts = chart.timeScale();
        const vr = ts.getVisibleLogicalRange();
        if (!vr) { host.innerHTML = ''; return; }
        let paneH = host.clientHeight - 28; // minus time axis
        try {
          const psz = (chart as unknown as { paneSize?: () => { height: number } }).paneSize?.();
          if (psz?.height) paneH = psz.height;
        } catch { /* pane API optional */ }
        let html = '';
        for (const r of runs) {
          const a = Math.max(r.i0 - 0.5, vr.from);
          const b = Math.min(r.i1 + 0.5, vr.to);
          if (a >= b) continue;
          const x1 = ts.logicalToCoordinate(a as never);
          const x2 = ts.logicalToCoordinate(b as never);
          if (x1 == null || x2 == null) continue;
          html += `<div style="position:absolute;top:0;left:${Math.round(x1)}px;width:${Math.max(1, Math.round((x2 as number) - (x1 as number)))}px;height:${paneH}px;background:${TINT[r.sess]};"></div>`;
        }
        host.innerHTML = html;
      } catch { /* chart mid-teardown */ }
    };
    paint();
    // rAF-coalesced: pan/zoom fires range events every frame — repainting bands per
    // event dragged the whole chart once other layers were active.
    let raf = 0;
    const onRange = () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; paint(); }); };
    try { chart.timeScale().subscribeVisibleLogicalRangeChange(onRange); } catch { /* chart torn down */ }
    return () => {
      if (raf) cancelAnimationFrame(raf);
      try { chart.timeScale().unsubscribeVisibleLogicalRangeChange(onRange); } catch { /* chart torn down */ }
      if (host) host.innerHTML = '';
    };
  }, [candles, timeframe, loading]);


  // ── Watcher replay: trades inside the LOADED chart window ────────────────────
  // The daily replay spans ~320 days while an intraday chart loads only a few days —
  // counting/rendering invisible trades reads as a bug (owner: "should only be
  // analyzing the visible part of the chart"). Open trades entered before the window
  // still count: that position is held NOW.
  const replayInWindow = useMemo(() => {
    const out = { idx: [] as number[], older: 0, fromIso: '', toIso: '' };
    if (!watcherReplay || watcherReplay.symbol !== symbol || candles.length === 0) return out;
    out.fromIso = mdyToIso(etDayKey(candles[0].timestamp));
    out.toIso = mdyToIso(etDayKey(candles[candles.length - 1].timestamp));
    watcherReplay.trades.forEach((tr, i) => {
      const e = mdyToIso(tr.entryDate);
      const x = tr.exitDate ? mdyToIso(tr.exitDate) : null;
      const inWin = (e >= out.fromIso && e <= out.toIso)
        || (x != null && x >= out.fromIso && x <= out.toIso)
        || (tr.open && e <= out.toIso);
      if (inWin) out.idx.push(i); else out.older++;
    });
    return out;
  }, [watcherReplay, symbol, candles]);

  // Create chart ONLY after loading is false so container is not under the loading overlay (fixes blank chart)
  useLayoutEffect(() => {
    if (!hasValidSymbol) return;
    if (loading) return;
    if (chartRef.current) return;

    // Chart chrome by theme. Each dark value is the original literal verbatim (byte-identical dark);
    // every light value is its readable counterpart on white. grid and crossLabel stay distinct so the
    // two dark shades are both preserved.
    const CHROME = dark
      ? { bg: '#0a0a0a', text: '#ffffff', grid: '#1a1a1f', cross: '#7a7a85', crossLabel: '#27272a' }
      : { bg: '#ffffff', text: '#18181b', grid: '#e4e4e7', cross: '#52525b', crossLabel: '#e4e4e7' };

    let cancelled = false;
    let rafId: number | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const FALLBACK_WIDTH = 800;
    const FALLBACK_HEIGHT = 500;
    const FALLBACK_VOL_HEIGHT = 150;

    const tryCreateCharts = async () => {
      if (cancelled) return;
      const container = chartContainerRef.current;
      const volContainer = volumeContainerRef.current;
      if (!container) {
        // Retry once after a tick (ref may not be set yet)
        timeoutId = setTimeout(() => tryCreateCharts(), 50);
        return;
      }
      if (!document.body.contains(container)) return;

      const w = container.clientWidth;
      const h = container.clientHeight || 0;
      const volW = volContainer?.clientWidth ?? 0;
      const volH = volContainer?.clientHeight ?? 0;
      const dailyEl = dailyContainerRef.current;
      const dailyW = dailyEl?.clientWidth ?? 0;

      // Use actual container width when known; fall back to FALLBACK_WIDTH only when clientWidth=0
      // Do NOT enforce Math.max with FALLBACK_WIDTH — that would force 800px on mobile
      const chartW = w > 0 ? w : FALLBACK_WIDTH;
      const chartH = h > 0 ? h : FALLBACK_HEIGHT;
      const volumeW = volW > 0 ? volW : 300;
      const volumeH = volH > 0 ? volH : FALLBACK_VOL_HEIGHT;
      const dailyWVal = dailyW > 0 ? dailyW : 300;

      // FLUID width (2026-07-13): keep the container width:100% (not a hard pixel width) so autoSize +
      // the ResizeObserver below actually track the tile when it widens — e.g. when the chat dock is
      // minimized and <main>'s padding-right animates from 368px → 0. Hard-pinning width:${chartW}px
      // froze the canvas at the old width and left a blank gap. Height stays explicit so the canvas
      // never collapses.
      (container as HTMLElement).style.cssText = `width:100%;height:${chartH}px;min-height:${chartH}px;position:relative;display:block;visibility:visible;opacity:1;`;

      let chart: IChartApi | null = null;
      const lwc = await import('lightweight-charts');
      if (cancelled) return;
      const { createChart, ColorType } = lwc;
      try {
        chart = createChart(container, {
      layout: {
        background: { type: ColorType.Solid, color: CHROME.bg },
        textColor: CHROME.text, // Pure white text
      },
      watermark: {
        visible: false, // owner 2026-07-06: watermark behind candles reads as clutter — keep OFF
        text: 'TheDesperateTrader.com',
        color: 'rgba(120, 120, 128, 0.10)', // whisper-faint brand — shows in screenshots, never in the way
        fontSize: 40,
        horzAlign: 'center',
        vertAlign: 'center',
      },
      grid: {
        vertLines: { 
          color: CHROME.grid, // Dark grey grid lines
          style: 0, // Solid lines
          visible: true
        },
        horzLines: { 
          color: CHROME.grid, // Dark grey grid lines
          style: 0, // Solid lines
          visible: true
        },
      },
      width: chartW,
      height: chartH,
      autoSize: true,
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        borderColor: CHROME.grid, // Match grid color
        rightOffset: 6, // Small padding after last candle (keeps candles packed, not stranded)
        barSpacing: 8, // Better spacing for professional look
        minBarSpacing: MIN_BAR_SPACING, // see MIN_BAR_SPACING: this is the zoom-out ceiling
        lockVisibleTimeRangeOnResize: false, // Allow resizing to show more data
        shiftVisibleRangeOnNewBar: true,
        allowBoldLabels: true,
        // Professional time scale settings
        fixLeftEdge: false,
        fixRightEdge: false,
        // Add 15 minutes of padding to the right
        rightBarStaysOnScroll: true,
        tickMarkFormatter: (time: any) => fmtAxisTick(time, timeframeRef.current === '1D')
      },
      rightPriceScale: {
        borderColor: CHROME.grid, // Match grid color
        scaleMargins: {
          top: 0.1,
          bottom: 0.1, // Consistent padding with other charts
        },
        textColor: CHROME.text, // Pure white text
      },
      leftPriceScale: {
        visible: false,
      },
      crosshair: {
        vertLine: {
          color: CHROME.cross, // Dark grey crosshair
          width: 1,
          style: 1,
          labelBackgroundColor: CHROME.crossLabel, // Match background
        },
        horzLine: {
          color: CHROME.cross, // Dark grey crosshair
          width: 1,
          style: 1,
          labelBackgroundColor: CHROME.grid, // Subtle dark background
          labelVisible: true, // Show price label on hover
        },
        mode: 1, // CrosshairMode.Normal with price labels
      },
      localization: {
        timeFormatter: (time: any) => fmtCrosshairTime(time, timeframeRef.current === '1D'),
        priceFormatter: (price: number) => {
          return formatPrice(price);
        }
      },
      });
    } catch (err) {
      console.error('Chart init failed:', err);
      setChartError('Failed to initialize chart. Please refresh.');
      return;
    }

    chartRef.current = chart;
    isAliveRef.current = true;

    let candleSeries: ISeriesApi<'Candlestick'> | null = null;
    try {
      candleSeries = chart.addCandlestickSeries({
        upColor: '#0ecb81',
        downColor: '#f6465d',
        borderUpColor: '#0ecb81',
        borderDownColor: '#f6465d',
        wickUpColor: '#3ddba4',   // wicks a shade lighter than bodies — reads better packed
        wickDownColor: '#ff7a8f',
        borderVisible: false, // solid bodies read cleaner at small candle widths
        priceLineVisible: true,  // dashed live last-price line — tracks every tick
        priceLineStyle: 2,
        priceLineWidth: 1,
        lastValueVisible: true,  // axis price tag follows the forming candle
      });
    } catch (err) {
      console.error('Series init failed:', err);
      setChartError('Failed to initialize series. Please refresh.');
      try { chart.remove(); } catch {}
      chartRef.current = null;
      return;
    }

    candleSeriesRef.current = candleSeries;

    // Add EMA series. autoscaleInfoProvider: () => null prevents EMAs from
    // expanding the Y-axis — candles alone set the visible price range.
    try {
      const ema9Series = chart.addLineSeries({
        color: '#3b82f6',
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: false,
        autoscaleInfoProvider: () => null,
      });
      ema9SeriesRef.current = ema9Series;

      const ema50Series = chart.addLineSeries({
        color: '#fbbf24',
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: false,
        autoscaleInfoProvider: () => null,
      });
      ema50SeriesRef.current = ema50Series;

      const ema200Series = chart.addLineSeries({
        color: '#10b981',
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: false,
        autoscaleInfoProvider: () => null,
      });
      ema200SeriesRef.current = ema200Series;
    } catch (err) {
      console.warn('EMA series creation failed:', err);
    }

    // VWAP + Anchored VWAP overlays. Created hidden (visible:false); the indicator-visibility
    // effect flips them on per toggle. autoscaleInfoProvider:() => null keeps them from
    // expanding the Y-axis (candles alone drive the visible range), matching the EMAs.
    try {
      const vwapSeries = chart.addLineSeries({
        color: '#f97316',          // warm orange — VWAP
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: false,
        autoscaleInfoProvider: () => null,
        visible: false,
      });
      vwapSeriesRef.current = vwapSeries;

      const avwapSeries = chart.addLineSeries({
        color: '#a78bfa',          // violet, dashed — Anchored VWAP (distinct from VWAP)
        lineWidth: 2,
        lineStyle: LineStyle.Dashed,
        priceLineVisible: false,
        lastValueVisible: false,
        autoscaleInfoProvider: () => null,
        visible: false,
      });
      avwapSeriesRef.current = avwapSeries;
    } catch (err) {
      console.warn('VWAP series creation failed:', err);
    }

    // Bollinger Bands (20, 2σ) overlay — upper/middle/lower line series. Created hidden;
    // the indicator-visibility effect flips them per toggle. autoscaleInfoProvider:() => null
    // keeps them from expanding the Y-axis (candles alone drive the range), matching the EMAs.
    try {
      const bbUpper = chart.addLineSeries({
        color: 'rgba(96,165,250,0.55)',  // semi-transparent blue — upper band
        lineWidth: 1,
        priceLineVisible: false,
        lastValueVisible: false,
        autoscaleInfoProvider: () => null,
        visible: false,
      });
      bbUpperSeriesRef.current = bbUpper;
      const bbMiddle = chart.addLineSeries({
        color: 'rgba(148,163,184,0.5)',  // grey, dashed — middle (SMA20)
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        priceLineVisible: false,
        lastValueVisible: false,
        autoscaleInfoProvider: () => null,
        visible: false,
      });
      bbMiddleSeriesRef.current = bbMiddle;
      const bbLower = chart.addLineSeries({
        color: 'rgba(96,165,250,0.55)',  // semi-transparent blue — lower band
        lineWidth: 1,
        priceLineVisible: false,
        lastValueVisible: false,
        autoscaleInfoProvider: () => null,
        visible: false,
      });
      bbLowerSeriesRef.current = bbLower;
    } catch (err) {
      console.warn('Bollinger series creation failed:', err);
    }

    // Add session background colors
    const addSessionBackgrounds = () => {
      if (!chart || !candleSeries) return;
      
      try {
        // Get the current date in ET
        const now = new Date();
        const etTime = new Date(now.toLocaleString("en-US", {timeZone: "America/New_York"}));
        const today = new Date(etTime);
        today.setHours(0, 0, 0, 0);
        
        // Premarket: 4:00 AM - 9:30 AM ET
        const premarketStart = new Date(today);
        premarketStart.setHours(4, 0, 0, 0);
        const premarketEnd = new Date(today);
        premarketEnd.setHours(9, 30, 0, 0);
        
        // Regular hours: 9:30 AM - 4:00 PM ET
        const regularStart = new Date(today);
        regularStart.setHours(9, 30, 0, 0);
        const regularEnd = new Date(today);
        regularEnd.setHours(16, 0, 0, 0);
        
        // After hours: 4:00 PM - 8:00 PM ET
        const afterhoursStart = new Date(today);
        afterhoursStart.setHours(16, 0, 0, 0);
        const afterhoursEnd = new Date(today);
        afterhoursEnd.setHours(20, 0, 0, 0);
        
        // Background colors will be added after initial 3-hour view is set
        
        // Remove background price lines to prevent duplicate labels
        // These were causing multiple price labels to appear
        
      } catch (err) {
        console.warn('Failed to add session backgrounds:', err);
      }
    };
    
    // Add session backgrounds after a short delay
    setTimeout(addSessionBackgrounds, 1000);

    // Create separate volume chart (use fallback dimensions when flex reports 0)
    if (volContainer) {
      try {
        const volumeChart = createChart(volContainer, {
          layout: {
            background: { type: ColorType.Solid, color: CHROME.bg }, // Match main chart (zinc-950)
            textColor: CHROME.text, // Match main chart
          },
          grid: {
            vertLines: { color: CHROME.grid }, // Match main chart
            horzLines: { color: CHROME.grid }, // Match main chart
          },
          width: volumeW,
          height: volumeH,
          autoSize: true, // 2026-07-13: track the w-full container (fixed height) so it stretches on chat-dock toggle
          timeScale: {
            timeVisible: true,
            secondsVisible: false,
            borderColor: CHROME.grid, // Match main chart
            rightOffset: 6, // Match main chart (small right padding)
            barSpacing: 8, // Match main chart professional spacing
            minBarSpacing: MIN_BAR_SPACING, // MUST match the main chart or the panes desync
            rightBarStaysOnScroll: true, // Match main chart padding
            lockVisibleTimeRangeOnResize: false, // [OK] REVERT: Allow resizing/syncing
            shiftVisibleRangeOnNewBar: true, // [OK] REVERT: Allow movement with new data
            fixLeftEdge: false, // Remove any built-in limits
            fixRightEdge: false, // Allow extending past last data point
            tickMarkFormatter: (time: any) => fmtAxisTick(time, timeframeRef.current === '1D')
          },
          rightPriceScale: {
            borderColor: CHROME.grid, // Match main chart
            scaleMargins: {
              top: 0.1,
              bottom: 0.1,
            },
            textColor: CHROME.text, // Match main chart
          },
          leftPriceScale: {
            visible: false,
          },
          crosshair: {
            vertLine: {
              color: CHROME.cross, // Match main chart
              width: 1,
              style: 1,
              labelBackgroundColor: CHROME.crossLabel, // Match main chart
            },
            horzLine: {
              color: CHROME.cross, // Match main chart
              width: 1,
              style: 1,
              labelBackgroundColor: CHROME.crossLabel, // Match main chart
            },
          },
          localization: {
            timeFormatter: (time: any) => fmtCrosshairTime(time, timeframeRef.current === '1D'),
            priceFormatter: (price: any) => {
              if (price >= 1000000) {
                return `${(price / 1000000).toFixed(1)}M`;
              } else if (price >= 1000) {
                return `${(price / 1000).toFixed(1)}K`;
              }
              return price.toFixed(0);
            }
          },
        });

        const volumeSeries = volumeChart.addHistogramSeries({
          color: '#787b86', // TradingView volume color
          priceFormat: {
            type: 'volume',
          },
        });

        volumeChartRef.current = volumeChart;

        // ── OHLC READOUT (owner 2026-07-28) ───────────────────────────────────────────────────────
        // Subscribed HERE, next to the main chart, rather than inside the RVOL pane block: the readout
        // must work whether or not that pane exists, and createSubPane only runs when its container is
        // mounted. Looks the bar up by timestamp from barsByTimeRef, which is rebuilt from the same
        // array the series is drawn from, so the numbers can never disagree with the candle on screen.
        // Uses param.time only — NOT param.seriesPrices, which is a removed API in this version of
        // lightweight-charts and is exactly why the daily-chart EMA legend silently never updates.
        chart.subscribeCrosshairMove((param) => {
          const time = param?.time as unknown;
          if (typeof time !== 'number') {
            if (lastHoverTimeRef.current === null) return;   // already cleared — do not re-render
            lastHoverTimeRef.current = null;
            setHoverBar(null); setRvolHover(null);
            return;
          }
          if (lastHoverTimeRef.current === time) return;      // same bar — nothing changed, no re-render
          lastHoverTimeRef.current = time;
          const bar = barsByTimeRef.current.get(time);
          // Both readouts are driven from ONE handler and one guard. They were previously two separate
          // subscriptions setting two states, so a single mouse move re-rendered the tree twice.
          setHoverBar(bar ? { ...bar, t: time } : null);
          setRvolHover(rvolByTimeRef.current.get(time) ?? null);
        });
        volumeSeriesRef.current = volumeSeries;
        // Debug: Volume chart created successfully

        // ONE-WAY SYNC: Main chart controls volume chart (prevents feedback loops)
        // Add delay to ensure charts are fully initialized
        setTimeout(() => {
          // [OK] ROBUST SYNC: Use LOGICAL RANGE to preserve rightOffset padding!
          const syncVolumeChart = () => {
            if (!chartContainerRef.current || !document.body.contains(chartContainerRef.current)) return;
            if (!volumeChartRef.current || !chartRef.current) return;
            try {
              // [OK] CRITICAL: Use getVisibleLogicalRange() instead of getVisibleRange()
              const mainLogicalRange = chartRef.current.timeScale().getVisibleLogicalRange();
              const volumeLogicalRange = volumeChartRef.current.timeScale().getVisibleLogicalRange();
              if (!mainLogicalRange || mainLogicalRange.from == null || mainLogicalRange.to == null) return;
              if (volumeLogicalRange && Math.abs(mainLogicalRange.from - volumeLogicalRange.from) < 0.5 && Math.abs(mainLogicalRange.to - volumeLogicalRange.to) < 0.5) return;
              requestAnimationFrame(() => {
                if (!volumeChartRef.current || !volumeContainerRef.current || !document.body.contains(volumeContainerRef.current)) return;
                try {
                  volumeChartRef.current.timeScale().setVisibleLogicalRange({
                    from: mainLogicalRange.from,
                    to: mainLogicalRange.to,
                  });
                } catch (_) { /* Value is null */ }
              });
            } catch (_) { /* Value is null */ }
          };

          // Subscribe to visible time range changes - ALWAYS sync on any change
          chart.timeScale().subscribeVisibleTimeRangeChange((timeRange) => {
            /**
             * ⛔ ONLY A REAL GESTURE TURNS LIVE OFF. This used to disable Live on ANY range change,
             * which included the ones Live itself causes: setData() on every incoming tick shifts the
             * range, and the volume/RSI syncs below set it explicitly. The result was that Live died
             * on the first tick after being switched on, so the user re-enabled it, got one tick, and
             * lost it again - and while it was off the viewport stayed put as new candles printed
             * off-screen, which is the "older prices" half of the report.
             *
             * lastUserGestureAtRef is stamped by wheel/mousedown/touchstart on the container, so a
             * websocket tick can never satisfy this and a pan always does.
             */
            const byUser = Date.now() - lastUserGestureAtRef.current < 1500;
            if (byUser && timeRange && timeRange.from && timeRange.to) {
              followLiveRef.current = false;
              setFollowLive(false);
            }
            
            // [OK] CRITICAL: ALWAYS sync volume chart, regardless of conditions
            // Works even when scrolling into future/empty areas or when timeRange is undefined
            syncVolumeChart();

            // AVATAR PAN/ZOOM TRACKING: bump rangeVersion (throttled to one rAF frame) so the
            // trade-avatar position effects (computeTradeXPos → tradeXPos + replayXPos) recompute
            // against the NEW visible range. Without this the avatars stay at stale pixel x's on
            // pan (entry visible, exit only after an incidental re-render). rAF coalescing avoids
            // an infinite re-render loop from the high-frequency range-change firing.
            if (rangeVersionRafRef.current == null) {
              rangeVersionRafRef.current = requestAnimationFrame(() => {
                rangeVersionRafRef.current = null;
                setRangeVersion(v => v + 1);
              });
            }

            // [OK] FIX: Refresh alerts when chart visible range changes
            if (symbol && timeRange && timeRange.from && timeRange.to) {
              fetchAlertsForSymbol();
            }
          });
          
          // [OK] NEW: CROSSHAIR SYNC - When hovering on price chart, show crosshair on volume chart at same time position
          chart.subscribeCrosshairMove((param) => {
            if (!volumeChartRef.current || !volumeContainerRef.current || !document.body.contains(volumeContainerRef.current)) return;
            try {
              if (param && param.time && volumeSeriesRef.current) {
                volumeChartRef.current.setCrosshairPosition(0, param.time, volumeSeriesRef.current);
              } else if (!param) {
                volumeChartRef.current.clearCrosshairPosition();
              }
            } catch (_) { /* Value is null */ }
          });

          // [OK] ADDITIONAL SYNC: Also sync on chart resize to ensure alignment
          if (volumeResizeObserverRef.current) {
            volumeResizeObserverRef.current.disconnect();
          }
          volumeResizeObserverRef.current = new ResizeObserver(() => {
            syncVolumeChart();
          });
          if (chartContainerRef.current) {
            volumeResizeObserverRef.current.observe(chartContainerRef.current);
          }
          
          // [OK] MOUSE INTERACTION SYNC: Sync immediately when user interacts with chart
          // This catches manual panning/scrolling that might not trigger other events
          const chartElement = chartContainerRef.current;
          if (chartElement) {
            // rAF-coalesced: raw mousemove fires per-pixel — syncing the volume pane on
            // EVERY event was constant drag while hovering/panning the chart.
            let volSyncRaf = 0;
            const requestVolSync = () => {
              if (!volSyncRaf) volSyncRaf = requestAnimationFrame(() => { volSyncRaf = 0; syncVolumeChart(); });
            };
            const handleMouseMove = () => requestVolSync();
            const handleWheel = () => requestVolSync();
            const handleTouchMove = () => requestVolSync();
            
            chartElement.addEventListener('mousemove', handleMouseMove, { passive: true });
            chartElement.addEventListener('wheel', handleWheel, { passive: true });
            chartElement.addEventListener('touchmove', handleTouchMove, { passive: true });
            
            // Store cleanup functions
            if (!(window as any)._chartSyncCleanup) {
              (window as any)._chartSyncCleanup = [];
            }
            (window as any)._chartSyncCleanup.push(() => {
              chartElement.removeEventListener('mousemove', handleMouseMove);
              chartElement.removeEventListener('wheel', handleWheel);
              chartElement.removeEventListener('touchmove', handleTouchMove);
            });
          }

          // [OK] AGGRESSIVE PERIODIC SYNC: Ultra-fast fallback to ensure perfect alignment during manual panning
          // This is especially important when manually scrolling or panning into empty areas
          if (volumeSyncIntervalRef.current) {
            clearInterval(volumeSyncIntervalRef.current);
          }
          volumeSyncIntervalRef.current = setInterval(() => {
            if (document.visibilityState !== 'visible') return; // no sync work in background tabs
            if (!chartContainerRef.current || !document.body.contains(chartContainerRef.current)) return;
            if (!volumeChartRef.current || !chartRef.current) return;
            try {
              const mainRange = chartRef.current.timeScale().getVisibleRange();
              const volumeRange = volumeChartRef.current.timeScale().getVisibleRange();
              if (mainRange && volumeRange) {
                const fromDiff = Math.abs((mainRange.from as number) - (volumeRange.from as number));
                const toDiff = Math.abs((mainRange.to as number) - (volumeRange.to as number));
                if (fromDiff > 0.1 || toDiff > 0.1) syncVolumeChart();
              }
            } catch (_) { /* Value is null */ }
          }, 250); // was 50ms — 20Hz range-polling per pane was a constant CPU drain
        }, 500); // Reduced delay for faster sync
      } catch (err) {
        console.error('Volume chart init failed:', err);
      }
    }

    // ── DEFERRED PANES ──────────────────────────────────────────────────────────
    // RSI/MACD/ADX are hidden at load (toggles default OFF) and the daily pane sits
    // below the fold — building 4 extra chart instances on the critical path delayed
    // time-to-first-candle. Main + volume go live NOW (chartsReady fires immediately);
    // the rest build ~250ms later, then one cascade re-fire populates their data.
    setChartsReady(true);
    const createDeferredPanes = () => {

    // ── RSI(14) sub-pane ────────────────────────────────────────────────────────
    // Separate lightweight-charts instance below the volume pane, time-synced to the main
    // chart exactly like the volume chart. Fixed 0-100 scale with 30/50/70 reference lines.
    const rsiContainer = rsiContainerRef.current;
    if (rsiContainer) {
      try {
        const rsiW = (rsiContainer.clientWidth || 0) > 0 ? rsiContainer.clientWidth : 300;
        const rsiH = (rsiContainer.clientHeight || 0) > 0 ? rsiContainer.clientHeight : 120;
        const rsiChart = createChart(rsiContainer, {
          layout: {
            background: { type: ColorType.Solid, color: CHROME.bg }, // Match main chart (zinc-950)
            textColor: CHROME.text,
          },
          grid: {
            vertLines: { color: CHROME.grid },
            horzLines: { color: CHROME.grid },
          },
          width: rsiW,
          height: rsiH,
          autoSize: false,
          timeScale: {
            timeVisible: true,
            secondsVisible: false,
            borderColor: CHROME.grid,
            rightOffset: 6,
            barSpacing: 8,
            minBarSpacing: MIN_BAR_SPACING,
            rightBarStaysOnScroll: true,
            lockVisibleTimeRangeOnResize: false,
            shiftVisibleRangeOnNewBar: true,
            fixLeftEdge: false,
            fixRightEdge: false,
            tickMarkFormatter: (time: any) => fmtAxisTick(time, timeframeRef.current === '1D'),
          },
          rightPriceScale: {
            borderColor: CHROME.grid,
            // Lock the visible band to 0-100 so the oscillator reads like TradingView.
            scaleMargins: { top: 0.1, bottom: 0.1 },
            textColor: CHROME.text,
          },
          leftPriceScale: { visible: false },
          crosshair: {
            vertLine: { color: CHROME.cross, width: 1, style: 1, labelBackgroundColor: CHROME.crossLabel },
            horzLine: { color: CHROME.cross, width: 1, style: 1, labelBackgroundColor: CHROME.crossLabel },
          },
          localization: {
            timeFormatter: (time: any) => fmtCrosshairTime(time, timeframeRef.current === '1D'),
            priceFormatter: (price: any) => (typeof price === 'number' ? price.toFixed(1) : `${price}`),
          },
        });

        const rsiSeries = rsiChart.addLineSeries({
          color: '#e879f9',          // fuchsia — RSI line
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: true,
          // Pin the pane to 0-100 regardless of the data's local min/max.
          autoscaleInfoProvider: () => ({ priceRange: { minValue: 0, maxValue: 100 } }),
        });
        // 30 / 50 / 70 reference lines (overbought/oversold + midline).
        try {
          rsiSeries.createPriceLine({ price: 70, color: 'rgba(248,113,113,0.5)', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: '70' });
          rsiSeries.createPriceLine({ price: 50, color: 'rgba(113,113,122,0.4)', lineWidth: 1, lineStyle: LineStyle.Dotted, axisLabelVisible: false, title: '' });
          rsiSeries.createPriceLine({ price: 30, color: 'rgba(52,211,153,0.5)', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: '30' });
        } catch (_) { /* createPriceLine may fail before data — harmless */ }

        rsiChartRef.current = rsiChart;
        rsiSeriesRef.current = rsiSeries;

        // ONE-WAY SYNC: main chart drives the RSI pane's visible range (mirrors the volume sync).
        setTimeout(() => {
          const syncRsiChart = () => {
            if (!chartContainerRef.current || !document.body.contains(chartContainerRef.current)) return;
            if (!rsiChartRef.current || !chartRef.current) return;
            try {
              const mainLogicalRange = chartRef.current.timeScale().getVisibleLogicalRange();
              const rsiLogicalRange = rsiChartRef.current.timeScale().getVisibleLogicalRange();
              if (!mainLogicalRange || mainLogicalRange.from == null || mainLogicalRange.to == null) return;
              if (rsiLogicalRange && Math.abs(mainLogicalRange.from - rsiLogicalRange.from) < 0.5 && Math.abs(mainLogicalRange.to - rsiLogicalRange.to) < 0.5) return;
              requestAnimationFrame(() => {
                if (!rsiChartRef.current || !rsiContainerRef.current || !document.body.contains(rsiContainerRef.current)) return;
                try {
                  rsiChartRef.current.timeScale().setVisibleLogicalRange({ from: mainLogicalRange.from, to: mainLogicalRange.to });
                } catch (_) { /* Value is null */ }
              });
            } catch (_) { /* Value is null */ }
          };

          // Re-sync on every visible-range change of the main chart.
          chart.timeScale().subscribeVisibleTimeRangeChange(() => { syncRsiChart(); });

          // Mirror the main chart's crosshair onto the RSI pane.
          chart.subscribeCrosshairMove((param) => {
            if (!rsiChartRef.current || !rsiContainerRef.current || !document.body.contains(rsiContainerRef.current)) return;
            try {
              if (param && param.time && rsiSeriesRef.current) {
                rsiChartRef.current.setCrosshairPosition(50, param.time, rsiSeriesRef.current);
              } else if (!param) {
                rsiChartRef.current.clearCrosshairPosition();
              }
            } catch (_) { /* Value is null */ }
          });

          // Keep aligned on resize.
          if (rsiResizeObserverRef.current) rsiResizeObserverRef.current.disconnect();
          rsiResizeObserverRef.current = new ResizeObserver(() => { syncRsiChart(); });
          if (chartContainerRef.current) rsiResizeObserverRef.current.observe(chartContainerRef.current);

          // Mouse-interaction sync (panning/scrolling) — reuse the shared cleanup bucket.
          const chartElement = chartContainerRef.current;
          if (chartElement) {
            // ONE SYNC PER FRAME, NOT PER EVENT. mousemove fires 60-120x/sec while the cursor moves,
            // and FIVE panes each attach their own handler to this same container, so a single sweep
            // across the chart could trigger hundreds of getVisibleLogicalRange calls a second. rAF
            // coalescing collapses a burst to at most one sync per painted frame; the sync itself is
            // unchanged, so panning and zooming still track exactly as before.
            let queued = false;
            const coalesced = () => {
              if (queued) return;
              queued = true;
              requestAnimationFrame(() => { queued = false; syncRsiChart(); });
            };
            const onMove = coalesced;
            const onWheel = coalesced;
            const onTouch = coalesced;
            chartElement.addEventListener('mousemove', onMove, { passive: true });
            chartElement.addEventListener('wheel', onWheel, { passive: true });
            chartElement.addEventListener('touchmove', onTouch, { passive: true });
            if (!(window as any)._chartSyncCleanup) (window as any)._chartSyncCleanup = [];
            (window as any)._chartSyncCleanup.push(() => {
              chartElement.removeEventListener('mousemove', onMove);
              chartElement.removeEventListener('wheel', onWheel);
              chartElement.removeEventListener('touchmove', onTouch);
            });
          }

          // Periodic fallback sync (matches the volume pane's aggressive alignment).
          if (rsiSyncIntervalRef.current) clearInterval(rsiSyncIntervalRef.current);
          rsiSyncIntervalRef.current = setInterval(() => {
            if (document.visibilityState !== 'visible') return; // no sync work in background tabs
            if (rsiContainerRef.current && rsiContainerRef.current.offsetParent === null) return; // pane hidden
            if (!chartContainerRef.current || !document.body.contains(chartContainerRef.current)) return;
            if (!rsiChartRef.current || !chartRef.current) return;
            try {
              const mainRange = chartRef.current.timeScale().getVisibleRange();
              const rsiRange = rsiChartRef.current.timeScale().getVisibleRange();
              if (mainRange && rsiRange) {
                const fromDiff = Math.abs((mainRange.from as number) - (rsiRange.from as number));
                const toDiff = Math.abs((mainRange.to as number) - (rsiRange.to as number));
                if (fromDiff > 0.1 || toDiff > 0.1) syncRsiChart();
              }
            } catch (_) { /* Value is null */ }
          }, 250); // was 50ms — 20Hz range-polling per pane was a constant CPU drain
        }, 500);
      } catch (err) {
        console.error('RSI chart init failed:', err);
      }
    }

    // ── Shared sub-pane factory (MACD / ADX) ────────────────────────────────────
    // Builds a lightweight-charts instance time-synced to the main chart, exactly like the
    // RSI/volume panes (one-way sync: main chart drives the sub-pane's visible range). Returns
    // the created chart, and wires resize/crosshair/periodic sync via the shared cleanup bucket.
    // Sub-panes share the main chart's x-axis, so they must agree with it on 1D or the crosshair
    // reads a date on price and a clock on MACD.
    const subPaneTickFmt = (time: any) => fmtCrosshairTime(time, timeframeRef.current === '1D');
    const createSubPane = (
      container: HTMLDivElement,
      paneChartRef: React.MutableRefObject<IChartApi | null>,
      paneResizeRef: React.MutableRefObject<ResizeObserver | null>,
      paneIntervalRef: React.MutableRefObject<ReturnType<typeof setInterval> | null>,
      paneContainerRef: React.RefObject<HTMLDivElement>,
      priceFmt: (price: any) => string,
    ): IChartApi | null => {
      try {
        const w = (container.clientWidth || 0) > 0 ? container.clientWidth : 300;
        const h = (container.clientHeight || 0) > 0 ? container.clientHeight : 120;
        const sub = createChart(container, {
          layout: { background: { type: ColorType.Solid, color: CHROME.bg }, textColor: CHROME.text },
          grid: { vertLines: { color: CHROME.grid }, horzLines: { color: CHROME.grid } },
          width: w,
          height: h,
          autoSize: false,
          timeScale: {
            timeVisible: true, secondsVisible: false, borderColor: CHROME.grid, rightOffset: 6,
            barSpacing: 8, minBarSpacing: MIN_BAR_SPACING, rightBarStaysOnScroll: true,
            lockVisibleTimeRangeOnResize: false, shiftVisibleRangeOnNewBar: true,
            fixLeftEdge: false, fixRightEdge: false, tickMarkFormatter: subPaneTickFmt,
          },
          rightPriceScale: { borderColor: CHROME.grid, scaleMargins: { top: 0.1, bottom: 0.1 }, textColor: CHROME.text },
          leftPriceScale: { visible: false },
          crosshair: {
            vertLine: { color: CHROME.cross, width: 1, style: 1, labelBackgroundColor: CHROME.crossLabel },
            horzLine: { color: CHROME.cross, width: 1, style: 1, labelBackgroundColor: CHROME.crossLabel },
          },
          localization: { timeFormatter: subPaneTickFmt, priceFormatter: priceFmt },
        });
        paneChartRef.current = sub;

        // One-way sync wiring (mirrors the RSI pane). Deferred so charts are fully initialized.
        setTimeout(() => {
          const syncPane = () => {
            if (!chartContainerRef.current || !document.body.contains(chartContainerRef.current)) return;
            if (!paneChartRef.current || !chartRef.current) return;
            // A COLLAPSED PANE COSTS NOTHING (owner 2026-07-28: chart "jerky").
            // Sub-panes are kept mounted and hidden, and this ran on every mousemove regardless. On the
            // DEFAULT layout that meant MACD, ADX and RVOL were all being range-synced continuously
            // while invisible. The 250ms interval already had this guard; the pointer path did not.
            if (paneContainerRef.current && paneContainerRef.current.offsetParent === null) return;
            try {
              const mainLogical = chartRef.current.timeScale().getVisibleLogicalRange();
              const paneLogical = paneChartRef.current.timeScale().getVisibleLogicalRange();
              if (!mainLogical || mainLogical.from == null || mainLogical.to == null) return;
              if (paneLogical && Math.abs(mainLogical.from - paneLogical.from) < 0.5 && Math.abs(mainLogical.to - paneLogical.to) < 0.5) return;
              requestAnimationFrame(() => {
                if (!paneChartRef.current || !paneContainerRef.current || !document.body.contains(paneContainerRef.current)) return;
                try { paneChartRef.current.timeScale().setVisibleLogicalRange({ from: mainLogical.from, to: mainLogical.to }); } catch (_) { /* Value is null */ }
              });
            } catch (_) { /* Value is null */ }
          };
          chart.timeScale().subscribeVisibleTimeRangeChange(() => { syncPane(); });
          chart.subscribeCrosshairMove((param) => {
            if (!paneChartRef.current || !paneContainerRef.current || !document.body.contains(paneContainerRef.current)) return;
            try { paneChartRef.current.clearCrosshairPosition(); } catch (_) { /* harmless */ }
          });
          if (paneResizeRef.current) paneResizeRef.current.disconnect();
          paneResizeRef.current = new ResizeObserver(() => { syncPane(); });
          if (chartContainerRef.current) paneResizeRef.current.observe(chartContainerRef.current);
          const el = chartContainerRef.current;
          if (el) {
            // ONE SYNC PER FRAME, NOT PER EVENT. mousemove fires 60-120x/sec while the cursor moves,
            // and FIVE panes each attach their own handler to this same container, so a single sweep
            // across the chart could trigger hundreds of getVisibleLogicalRange calls a second. rAF
            // coalescing collapses a burst to at most one sync per painted frame; the sync itself is
            // unchanged, so panning and zooming still track exactly as before.
            let queued = false;
            const coalesced = () => {
              if (queued) return;
              queued = true;
              requestAnimationFrame(() => { queued = false; syncPane(); });
            };
            const onMove = coalesced;
            const onWheel = coalesced;
            const onTouch = coalesced;
            el.addEventListener('mousemove', onMove, { passive: true });
            el.addEventListener('wheel', onWheel, { passive: true });
            el.addEventListener('touchmove', onTouch, { passive: true });
            if (!(window as any)._chartSyncCleanup) (window as any)._chartSyncCleanup = [];
            (window as any)._chartSyncCleanup.push(() => {
              el.removeEventListener('mousemove', onMove);
              el.removeEventListener('wheel', onWheel);
              el.removeEventListener('touchmove', onTouch);
            });
          }
          if (paneIntervalRef.current) clearInterval(paneIntervalRef.current);
          paneIntervalRef.current = setInterval(() => {
            if (document.visibilityState !== 'visible') return; // no sync work in background tabs
            if (container.offsetParent === null) return; // pane hidden (display:none) — skip
            if (!chartContainerRef.current || !document.body.contains(chartContainerRef.current)) return;
            if (!paneChartRef.current || !chartRef.current) return;
            try {
              const mainRange = chartRef.current.timeScale().getVisibleRange();
              const paneRange = paneChartRef.current.timeScale().getVisibleRange();
              if (mainRange && paneRange) {
                const fromDiff = Math.abs((mainRange.from as number) - (paneRange.from as number));
                const toDiff = Math.abs((mainRange.to as number) - (paneRange.to as number));
                if (fromDiff > 0.1 || toDiff > 0.1) syncPane();
              }
            } catch (_) { /* Value is null */ }
          }, 250); // was 50ms — 20Hz range-polling per pane was a constant CPU drain
        }, 500);
        return sub;
      } catch (err) {
        console.error('Sub-pane init failed:', err);
        return null;
      }
    };

    // ── MACD(12/26/9) sub-pane ──────────────────────────────────────────────────
    const macdContainer = macdContainerRef.current;
    if (macdContainer) {
      const macdChart = createSubPane(
        macdContainer, macdChartRef, macdResizeObserverRef, macdSyncIntervalRef, macdContainerRef,
        (price: any) => (typeof price === 'number' ? price.toFixed(3) : `${price}`),
      );
      if (macdChart) {
        try {
          // Histogram first (drawn behind the lines). Per-point color set at push time.
          const macdHist = macdChart.addHistogramSeries({ priceLineVisible: false, lastValueVisible: false, color: '#26a69a' });
          macdHistSeriesRef.current = macdHist;
          const macdLine = macdChart.addLineSeries({ color: '#3b82f6', lineWidth: 2, priceLineVisible: false, lastValueVisible: true });
          macdLineSeriesRef.current = macdLine;
          const macdSignal = macdChart.addLineSeries({ color: '#f97316', lineWidth: 2, priceLineVisible: false, lastValueVisible: false });
          macdSignalSeriesRef.current = macdSignal;
          try { macdLine.createPriceLine({ price: 0, color: 'rgba(113,113,122,0.5)', lineWidth: 1, lineStyle: LineStyle.Dotted, axisLabelVisible: false, title: '' }); } catch (_) { /* harmless */ }
        } catch (err) { console.warn('MACD series creation failed:', err); }
      }
    }

    // ── RVOL sub-pane (owner 2026-07-28, customer request) ──────────────────────
    // Relative volume per candle, at whatever timeframe is selected, so RVOL can be read as a TREND
    // the way price is. The denominator comes from volume_baseline (average volume per minute-of-day),
    // which is what makes this honest across the session: a flat "average N-minute volume" would make
    // every open candle read ~5x and every lunch candle ~0.3x, i.e. it would just redraw the clock.
    const rvolContainer = rvolContainerRef.current;
    if (rvolContainer) {
      const rvolChart = createSubPane(
        rvolContainer, rvolChartRef, rvolResizeObserverRef, rvolSyncIntervalRef, rvolContainerRef,
        (price: any) => (typeof price === 'number' ? `${price.toFixed(price >= 10 ? 0 : 1)}x` : `${price}`),
      );
      if (rvolChart) {
        try {
          // ── BARS, WITH THE HOVER READOUT KEPT (owner 2026-07-28) ──────────────────────────────────
          // Tried a line; the owner preferred the bars. They were right, and the two were never actually
          // in tension: the line was proposed only because a line cannot carry per-point colour, so the
          // hover readout was going to replace that information. Hover works on a histogram too, so this
          // keeps BOTH — colour encodes the level at a glance (grey under 1x, amber 1-3x, red 3x+, muted
          // when the baseline is thin) and the crosshair gives the exact multiple.
          const rvolHist = rvolChart.addHistogramSeries({
            priceLineVisible: false, lastValueVisible: true, color: '#f59e0b',
            priceFormat: { type: 'custom', formatter: (p: number) => `${p.toFixed(p >= 10 ? 0 : 1)}x` },
            // Keep the chart's own autoscale for the VISIBLE bars, then cap the top at the series p99
            // (see rvolAxisCap). Wrapping the default rather than replacing it means zooming into a
            // quiet stretch still rescales sensibly; it just cannot be hijacked by one spike.
            autoscaleInfoProvider: (orig: () => any) => {
              const res = orig();
              const cap = rvolCapRef.current;
              if (!res?.priceRange || !cap || !Number.isFinite(cap)) return res;
              return {
                ...res,
                priceRange: {
                  // Log scale cannot render <= 0, so the floor stays strictly positive.
                  minValue: Math.max(0.05, Math.min(res.priceRange.minValue, 0.5)),
                  maxValue: Math.max(Math.min(res.priceRange.maxValue, cap), 3),
                },
              };
            },
          });
          rvolSeriesRef.current = rvolHist;
          // ⚠ LOG SCALE IS NOT COSMETIC — ON LINEAR THIS PANE IS UNREADABLE.
          // RVOL spans three orders of magnitude on one symbol in one session: EGG ran 0.2x to 800x on
          // 2026-07-28. On a linear axis that single spike takes the whole pane and every ordinary bar
          // collapses to a 1px smear on the floor, which is exactly how it shipped and exactly why it
          // looked like it was doing nothing. On a log axis 0.5x and 2x sit the same distance either
          // side of 1x, so "twice normal" and "half normal" are equally legible and the trend is
          // visible at every level. Changing the chart TYPE would not have fixed this; the range would.
          // Apply to the SERIES' own price scale — chart.priceScale('right') did not take on this
          // version (the axis kept rendering a linear 0.0x..200x ladder, which is impossible on log).
          try { rvolHist.priceScale().applyOptions({ mode: 1, scaleMargins: { top: 0.1, bottom: 0.05 } }); } catch (_) { /* PriceScaleMode.Logarithmic */ }
          // 1x is "exactly normal for this minute of the day" — the only reference line that means
          // anything here, and the whole point of the indicator.
          try { rvolHist.createPriceLine({ price: 1, color: 'rgba(113,113,122,0.6)', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: '1x' }); } catch (_) { /* harmless */ }
          // HOVER READOUT. Wired to BOTH charts on purpose: the natural gesture is to hover the PRICE
          // candle and ask "what was volume doing there", and the panes share one x-axis, so a crosshair
          // over either should answer it. Looking the value up by timestamp also means it stays correct
          // across timeframe changes without touching the series.
          // Only the RVOL PANE's own crosshair is wired here. Hovering the main chart is already handled
          // by the single guarded handler next to the main chart, which sets both readouts at once —
          // subscribing again here would re-render the tree a second time per mouse move.
          const reportHover = (param: any) => {
            const time = param?.time;
            if (typeof time !== 'number') {
              if (lastHoverTimeRef.current === null) return;
              lastHoverTimeRef.current = null;
              setRvolHover(null); setHoverBar(null);
              return;
            }
            if (lastHoverTimeRef.current === time) return;
            lastHoverTimeRef.current = time;
            setRvolHover(rvolByTimeRef.current.get(time) ?? null);
            const bar = barsByTimeRef.current.get(time);
            setHoverBar(bar ? { ...bar, t: time } : null);
          };
          try { rvolChart.subscribeCrosshairMove(reportHover); } catch (_) { /* harmless */ }

          // Mirror the main chart's crosshair onto the RVOL pane, exactly as volume and RSI do above.
          // RVOL subscribed to its OWN crosshair (reportHover, to drive the readout) but never received
          // the price chart's, so hovering the candles lit up every pane except this one.
          //
          // ⚠ THE PRICE ARGUMENT MATTERS HERE, unlike on the volume pane. setCrosshairPosition is
          // IGNORED when the price falls outside the pane's visible range, and RVOL's
          // autoscaleInfoProvider above floors minValue at 0.05 -- it never includes 0. Copying
          // volume's `0` therefore placed the crosshair nowhere and failed silently, which is exactly
          // how this looked "fixed" while still doing nothing.
          // Use the bar's ACTUAL RVOL (already indexed for the readout), falling back to 1.0, the
          // baseline multiple that is always inside the range by construction.
          // ⚠ DIAGNOSTIC (add ?xhair=1 to the URL). Two real defects have already been fixed here -- the
          // subscription was missing, then the price was outside the pane's range -- and the crosshair
          // still does not appear. Every remaining hypothesis is unfalsifiable from the server, and the
          // page is auth-gated so it cannot be driven headlessly. This logs the four things that can
          // still be wrong, ONCE, so one hover settles it instead of another round of guessing.
          const XDBG = typeof window !== 'undefined' && window.location.search.includes('xhair=1');
          let xdbgDone = false;

          // ── DRAW THE LINE OURSELVES ─────────────────────────────────────────────────────────────
          // setCrosshairPosition is a dead end here. Instrumented, it fires with perfect arguments --
          // {time: 1785484560, rvolAtTime: 0.4, priceUsed: 0.4, hasSeries: true, mapSize: 3120}, no
          // throw -- and lightweight-charts still renders nothing on this pane, while the identically
          // configured volume pane works. Three fixes chasing that difference all failed.
          //
          // A 1px absolutely-positioned div is deterministic: it cannot be silently declined. The pane
          // is time-synced and width-matched to the main chart, so the x coordinate is well defined.
          // timeToCoordinate() is the correct source for it; param.point.x is the fallback for when the
          // time is not on this pane's scale.
          //
          // The canvas is inset by the container's p-2 padding, so the offset is measured rather than
          // assumed -- hardcoding 8px would drift the moment that class changes.
          const rvolHost = rvolContainerRef.current;
          let rvolVLine: HTMLDivElement | null = null;
          if (rvolHost) {
            rvolHost.style.position = 'relative';
            rvolVLine = document.createElement('div');
            rvolVLine.setAttribute('data-xhair', 'rvol');
            rvolVLine.style.cssText = `position:absolute;top:0;bottom:0;width:1px;background:${CHROME.cross};pointer-events:none;display:none;z-index:5`;
            rvolHost.appendChild(rvolVLine);
          }
          const placeRvolLine = (t: unknown, fallbackX: number | null) => {
            if (!rvolVLine || !rvolHost || !rvolChartRef.current) return;
            let x: number | null = null;
            try { x = rvolChartRef.current.timeScale().timeToCoordinate(t as never) as number | null; } catch { x = null; }
            if (x == null) x = fallbackX;
            if (x == null) { rvolVLine.style.display = 'none'; return; }
            const cv = rvolHost.querySelector('canvas');
            const inset = cv ? cv.getBoundingClientRect().left - rvolHost.getBoundingClientRect().left : 0;
            rvolVLine.style.left = `${Math.round(x + inset)}px`;
            rvolVLine.style.display = 'block';
          };

          chart.subscribeCrosshairMove((param) => {
            // ⚠ NO document.body.contains() GUARD. That was the actual bug: the diagnostic returned
            // {chart:true, container:true, inDom:FALSE} while the pane was plainly visible on screen.
            // body.contains() is false for a node in a portal, in a detached-then-reattached subtree,
            // or mid-remount -- none of which mean the chart is gone. Volume happens to survive it;
            // RVOL does not, and chasing that difference is chasing a symptom.
            // The chart handle is what we write to, and the call is already inside a try/catch, so a
            // genuinely removed chart throws and is swallowed. `isConnected` is the correct DOM test
            // if one is ever wanted again -- it is true inside portals and shadow roots.
            if (!rvolChartRef.current || !rvolSeriesRef.current) {
              if (XDBG && !xdbgDone) { xdbgDone = true; console.log('[XHAIR] no chart/series yet', { chart: !!rvolChartRef.current, series: !!rvolSeriesRef.current }); }
              return;
            }
            try {
              if (param && param.time) {
                const v = rvolByTimeRef.current.get(param.time as number);
                const price = typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : 1;
                // Kept as well as the drawn line: when it works it also gives the price-axis label,
                // and it is harmless when it does not. The DIV is what actually guarantees the line.
                try { rvolChartRef.current.setCrosshairPosition(price, param.time, rvolSeriesRef.current); } catch { /* ignore */ }
                placeRvolLine(param.time, param.point?.x ?? null);
                if (XDBG && !xdbgDone) {
                  xdbgDone = true;
                  let tc: number | null = null;
                  try { tc = rvolChartRef.current.timeScale().timeToCoordinate(param.time as never) as number | null; } catch { tc = null; }
                  console.log('[XHAIR] drawn', { time: param.time, priceUsed: price, timeToCoordinate: tc, pointX: param.point?.x ?? null, lineLeft: rvolVLine?.style.left });
                }
              } else {
                try { rvolChartRef.current.clearCrosshairPosition(); } catch { /* ignore */ }
                if (rvolVLine) rvolVLine.style.display = 'none';
              }
            } catch (e) {
              if (XDBG && !xdbgDone) { xdbgDone = true; console.log('[XHAIR] THREW', e); }
            }
          });
        } catch (err) { console.warn('RVOL series creation failed:', err); }
      }
    }

    // ── ADX(14) sub-pane ────────────────────────────────────────────────────────
    const adxContainer = adxContainerRef.current;
    if (adxContainer) {
      const adxChart = createSubPane(
        adxContainer, adxChartRef, adxResizeObserverRef, adxSyncIntervalRef, adxContainerRef,
        (price: any) => (typeof price === 'number' ? price.toFixed(1) : `${price}`),
      );
      if (adxChart) {
        try {
          const adxLine = adxChart.addLineSeries({
            color: '#eab308',           // amber — ADX line
            lineWidth: 2,
            priceLineVisible: false,
            lastValueVisible: true,
            // Pin to a sensible 0-60 band (ADX rarely exceeds 60) for a stable read.
            autoscaleInfoProvider: () => ({ priceRange: { minValue: 0, maxValue: 60 } }),
          });
          adxSeriesRef.current = adxLine;
          try {
            adxLine.createPriceLine({ price: 25, color: 'rgba(248,113,113,0.5)', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: '25' });
            adxLine.createPriceLine({ price: 20, color: 'rgba(113,113,122,0.4)', lineWidth: 1, lineStyle: LineStyle.Dotted, axisLabelVisible: false, title: '' });
          } catch (_) { /* harmless */ }

          // Same crosshair mirror as volume / RSI / RVOL. ADX had the identical omission, so with all
          // four panes open the cursor previously appeared on two of them and not the other two.
          // Value 25 is the ADX trend threshold already drawn as a price line, so the synced crosshair
          // lands on a meaningful level rather than the floor of the pane.
          // Same guard fix as RVOL above: body.contains() reports false for a pane that is visibly on
          // screen (portal / remount), so it silently suppressed the sync. Guard on the chart handle.
          chart.subscribeCrosshairMove((param) => {
            if (!adxChartRef.current || !adxSeriesRef.current) return;
            try {
              if (param && param.time) {
                adxChartRef.current.setCrosshairPosition(25, param.time, adxSeriesRef.current);
              } else if (!param) {
                adxChartRef.current.clearCrosshairPosition();
              }
            } catch (_) { /* Value is null */ }
          });
        } catch (err) { console.warn('ADX series creation failed:', err); }
      }
    }

    // ── OBV sub-pane ─────────────────────────────────────────────────────────────
    // On-Balance Volume — a single autoscaled line (no fixed band, no threshold lines). Clones the
    // ADX pane wiring; the crosshair value is looked up per-bar (obvByTimeRef) since OBV has no fixed
    // reference level like ADX's 25.
    const obvContainer = obvContainerRef.current;
    if (obvContainer) {
      const obvChart = createSubPane(
        obvContainer, obvChartRef, obvResizeObserverRef, obvSyncIntervalRef, obvContainerRef,
        (price: any) => (typeof price === 'number' ? fmtCompactNum(price) : `${price}`),
      );
      if (obvChart) {
        try {
          const obvLine = obvChart.addLineSeries({
            color: '#f5b642',           // amber — OBV line
            lineWidth: 2,
            priceLineVisible: false,
            lastValueVisible: true,
          });
          obvSeriesRef.current = obvLine;
          // Same crosshair mirror as ADX; the reference value is the bar's own OBV (autoscaled pane).
          chart.subscribeCrosshairMove((param) => {
            if (!obvChartRef.current || !obvSeriesRef.current) return;
            try {
              if (param && param.time) {
                const v = obvByTimeRef.current.get(param.time as number);
                if (typeof v === 'number' && Number.isFinite(v)) obvChartRef.current.setCrosshairPosition(v, param.time, obvSeriesRef.current);
              } else if (!param) {
                obvChartRef.current.clearCrosshairPosition();
              }
            } catch (_) { /* Value is null */ }
          });
        } catch (err) { console.warn('OBV series creation failed:', err); }
      }
    }

    // ── A/D line sub-pane ────────────────────────────────────────────────────────
    // Williams Accumulation/Distribution — a single autoscaled emerald line. Same wiring as OBV.
    const adlContainer = adlContainerRef.current;
    if (adlContainer) {
      const adlChart = createSubPane(
        adlContainer, adlChartRef, adlResizeObserverRef, adlSyncIntervalRef, adlContainerRef,
        (price: any) => (typeof price === 'number' ? fmtCompactNum(price) : `${price}`),
      );
      if (adlChart) {
        try {
          const adlLine = adlChart.addLineSeries({
            color: '#37d399',           // emerald — A/D line
            lineWidth: 2,
            priceLineVisible: false,
            lastValueVisible: true,
          });
          adlSeriesRef.current = adlLine;
          chart.subscribeCrosshairMove((param) => {
            if (!adlChartRef.current || !adlSeriesRef.current) return;
            try {
              if (param && param.time) {
                const v = adlByTimeRef.current.get(param.time as number);
                if (typeof v === 'number' && Number.isFinite(v)) adlChartRef.current.setCrosshairPosition(v, param.time, adlSeriesRef.current);
              } else if (!param) {
                adlChartRef.current.clearCrosshairPosition();
              }
            } catch (_) { /* Value is null */ }
          });
        } catch (err) { console.warn('A/D line series creation failed:', err); }
      }
    }

    // ── CMF(20) sub-pane ─────────────────────────────────────────────────────────
    // Chaikin Money Flow — a bounded oscillator around 0 (accumulation above, distribution below),
    // clones the RSI pane: fixed band + reference price lines at 0 / ±0.10. Crosshair mirrors at 0.
    const cmfContainer = cmfContainerRef.current;
    if (cmfContainer) {
      const cmfChart = createSubPane(
        cmfContainer, cmfChartRef, cmfResizeObserverRef, cmfSyncIntervalRef, cmfContainerRef,
        (price: any) => (typeof price === 'number' ? price.toFixed(2) : `${price}`),
      );
      if (cmfChart) {
        try {
          const cmfLine = cmfChart.addLineSeries({
            color: '#a78bfa',           // violet — CMF line
            lineWidth: 2,
            priceLineVisible: false,
            lastValueVisible: true,
            // Pin to a stable ±0.5 band so the ±0.10 reference lines stay meaningful (like RSI's 0-100).
            autoscaleInfoProvider: () => ({ priceRange: { minValue: -0.5, maxValue: 0.5 } }),
          });
          cmfSeriesRef.current = cmfLine;
          try {
            cmfLine.createPriceLine({ price: 0.10, color: 'rgba(52,211,153,0.5)', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: '+.10' });
            cmfLine.createPriceLine({ price: 0, color: 'rgba(113,113,122,0.4)', lineWidth: 1, lineStyle: LineStyle.Dotted, axisLabelVisible: false, title: '' });
            cmfLine.createPriceLine({ price: -0.10, color: 'rgba(248,113,113,0.5)', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: '-.10' });
          } catch (_) { /* createPriceLine may fail before data — harmless */ }
          // Crosshair mirror: 0 is the midline and always inside the pinned band.
          chart.subscribeCrosshairMove((param) => {
            if (!cmfChartRef.current || !cmfSeriesRef.current) return;
            try {
              if (param && param.time) {
                cmfChartRef.current.setCrosshairPosition(0, param.time, cmfSeriesRef.current);
              } else if (!param) {
                cmfChartRef.current.clearCrosshairPosition();
              }
            } catch (_) { /* Value is null */ }
          });
        } catch (err) { console.warn('CMF series creation failed:', err); }
      }
    }

    // Create YTD daily chart (use fallback width when flex reports 0)
    if (dailyEl) {
      try {
        const dailyChart = createChart(dailyEl, {
          layout: {
            background: { type: ColorType.Solid, color: CHROME.bg }, // Match main chart (zinc-950)
            textColor: CHROME.text, // Match main chart
          },
          grid: {
            vertLines: { color: CHROME.grid }, // Match main chart
            horzLines: { color: CHROME.grid }, // Match main chart
          },
          width: dailyWVal,
          height: 250, // Taller for better visibility
          autoSize: true, // 2026-07-13: track the w-full container so it stretches on chat-dock toggle (was false → left a blank gap)
          timeScale: {
            timeVisible: true,
            secondsVisible: false,
            borderColor: CHROME.grid, // Match main chart
            rightOffset: 20, // Professional padding after last candle (doubled, consistent across all charts)
            barSpacing: 3, // Better spacing for daily chart
            minBarSpacing: MIN_BAR_SPACING,
            tickMarkFormatter: (time: any, tickMarkType: any, locale: string) => {
              const date = new Date(time * 1000);
              return date.toLocaleDateString('en-US', {
                timeZone: 'America/New_York',
                month: 'short',
                day: 'numeric'
              });
            }
          },
          rightPriceScale: {
            borderColor: CHROME.grid,
            scaleMargins: {
              top: 0.1,
              bottom: 0.1,
            },
            textColor: CHROME.text,
            entireTextOnly: false,
            mode: 0, // PriceScaleMode.Normal
            autoScale: true, // Re-fit Y-axis to visible candles when zooming in
          },
          leftPriceScale: {
            visible: false,
          },
          crosshair: {
            vertLine: {
              color: CHROME.cross,
              width: 1,
              labelBackgroundColor: CHROME.crossLabel,
            },
            horzLine: {
              color: CHROME.cross,
              width: 1,
              labelBackgroundColor: CHROME.grid, // Subtle dark background
              labelVisible: true, // Show price label on hover
            },
            mode: 1, // CrosshairMode.Normal with price labels
          },
          localization: {
            timeFormatter: (time: any) => {
              const date = new Date(time * 1000);
              return date.toLocaleDateString('en-US', {
                timeZone: 'America/New_York',
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              });
            },
            priceFormatter: (price: any) => {
              return `$${price.toFixed(2)}`;
            }
          }
        });

        const dailySeries = dailyChart.addCandlestickSeries({
          upColor: '#0ecb81',
          downColor: '#f6465d',
          borderUpColor: '#0ecb81',
          borderDownColor: '#f6465d',
          wickUpColor: '#3ddba4',   // wicks a shade lighter than bodies — reads better packed
          wickDownColor: '#ff7a8f',
          borderVisible: false, // solid bodies read cleaner at small candle widths
          priceLineVisible: false, // Hide default price line to prevent duplicate labels
          lastValueVisible: false, // Hide default last value label to prevent duplicate labels
        });

        dailyChartRef.current = dailyChart;
        dailySeriesRef.current = dailySeries;

        // Add EMA overlays to daily chart (same colours as the minute chart).
        // autoscaleInfoProvider: () => null keeps EMAs from expanding the Y-axis —
        // the candlestick series alone drives the visible price range.
        try {
          const dEma9 = dailyChart.addLineSeries({
            color: '#3b82f6',   // blue  — 9 EMA
            lineWidth: 1,
            priceLineVisible: false,
            lastValueVisible: false,
            autoscaleInfoProvider: () => null,
          });
          dailyEma9SeriesRef.current = dEma9;

          const dEma50 = dailyChart.addLineSeries({
            color: '#fbbf24',   // yellow — 50 EMA
            lineWidth: 1,
            priceLineVisible: false,
            lastValueVisible: false,
            autoscaleInfoProvider: () => null,
          });
          dailyEma50SeriesRef.current = dEma50;

          const dEma200 = dailyChart.addLineSeries({
            color: '#10b981',   // green  — 200 EMA
            lineWidth: 1,
            priceLineVisible: false,
            lastValueVisible: false,
            autoscaleInfoProvider: () => null,
          });
          dailyEma200SeriesRef.current = dEma200;
        } catch (err) {
          console.warn('Daily EMA series creation failed:', err);
        }

        // Add reference lines for after-hours and premarket
        // Track all price lines to ensure proper cleanup
        let currentPriceLine: any = null; // Track the current price line
        let allPriceLines: any[] = []; // Track all price lines for cleanup
        const addReferenceLines = (previousClose: number, currentPrice: number) => {
          try {
            // CRITICAL: Check if daily chart and series exist before proceeding
            if (!dailyChartRef.current || !dailySeriesRef.current) {
              console.log(`[STATS] [REFERENCE_LINES] Daily chart disabled - skipping reference lines`);
              return;
            }
            
            const now = new Date();
            const etTime = new Date(now.toLocaleString("en-US", {timeZone: "America/New_York"}));
            const hour = etTime.getHours();
            const minute = etTime.getMinutes();
            const timeInMinutes = hour * 60 + minute;
            
            console.log(`[STATS] [REFERENCE_LINES] addReferenceLines called. Current ET time: ${etTime.toLocaleString()}, timeInMinutes: ${timeInMinutes}`);
            console.log(`[STATS] [REFERENCE_LINES] previousClose: $${previousClose}, currentPrice (from state): $${currentPrice}`);
            
            // [OK] FIX: Remove price line via ref (handled by useEffect) to prevent duplicates
            // The useEffect manages the price line lifecycle, so we don't need to manage it here
            try {
              const dailySeries = dailySeriesRef.current; // Get the series reference
              
              // Remove the tracked current price line via ref (if useEffect created it)
              if (dailyPriceLineRef.current) {
                try {
                  dailySeries.removePriceLine(dailyPriceLineRef.current);
                  dailyPriceLineRef.current = null;
                } catch (e) {
                  // Ignore errors
                }
              }
              
              // Remove all tracked price lines (for other reference lines if any)
              allPriceLines.forEach((line: any) => {
                try {
                  dailySeries.removePriceLine(line);
                } catch (e) {
                  // Ignore errors for individual line removal
                }
              });
              allPriceLines = []; // Clear the array
              
              // Note: Price line creation is now handled by useEffect to prevent duplicates
            } catch (err) {
              console.warn('Failed to remove existing price lines:', err);
            }
          } catch (error) {
            console.warn('Failed to add reference lines:', error);
          }
        };

        // Fetch YTD daily data
        const fetchYTDData = async () => {
          try {
            setDailyChartLoading(true);
            const response = await fetch(`/api/chart/daily?symbol=${symbol}&period=ytd`);
            if (response.ok) {
              const data = await response.json();
              if (data.candles && data.candles.length > 0) {
                const dailyCandles = data.candles.map((candle: any) => ({
                  time: Math.floor(new Date(candle.timestamp).getTime() / 1000),
                  open: candle.open,
                  high: candle.high,
                  low: candle.low,
                  close: candle.close,
                }));
                dailySeries.setData(dailyCandles);
                console.log(`[STATS] [YTD_CHART] Loaded ${dailyCandles.length} daily candles for ${symbol}`);

                // Calculate 9 / 50 / 200 EMAs and push to series
                const closePrices = dailyCandles.map((c: any) => c.close);
                const dEma9Values  = calculateEMA(closePrices, 9);
                const dEma50Values = calculateEMA(closePrices, 50);
                const dEma200Values = calculateEMA(closePrices, 200);
                if (dailyEma9SeriesRef.current) {
                  dailyEma9SeriesRef.current.setData(
                    dailyCandles.map((c: any, i: number) => ({ time: c.time, value: dEma9Values[i] }))
                  );
                }
                if (dailyEma50SeriesRef.current) {
                  dailyEma50SeriesRef.current.setData(
                    dailyCandles.map((c: any, i: number) => ({ time: c.time, value: dEma50Values[i] }))
                  );
                }
                if (dailyEma200SeriesRef.current) {
                  dailyEma200SeriesRef.current.setData(
                    dailyCandles.map((c: any, i: number) => ({ time: c.time, value: dEma200Values[i] }))
                  );
                }
                // Seed legend with the rightmost (latest) values
                setDailyEma9Value(dEma9Values[dEma9Values.length - 1] ?? null);
                setDailyEma50Value(dEma50Values[dEma50Values.length - 1] ?? null);
                setDailyEma200Value(dEma200Values[dEma200Values.length - 1] ?? null);

                // Floor-trader pivots use the PRIOR completed daily bar. During RTH the last
                // daily bar can be today's forming session, so pick the most recent bar whose
                // ET date is strictly before today (else fall back to the last bar).
                try {
                  const todayKey = etDayKey(Date.now());
                  let prior: any = null;
                  for (let i = data.candles.length - 1; i >= 0; i--) {
                    const c = data.candles[i];
                    if (etDayKey(new Date(c.timestamp).getTime()) < todayKey) { prior = c; break; }
                  }
                  if (!prior && data.candles.length > 0) prior = data.candles[data.candles.length - 1];
                  if (prior && [prior.high, prior.low, prior.close].every((v: any) => typeof v === 'number' && !Number.isNaN(v))) {
                    setPivotDaily({ high: prior.high, low: prior.low, close: prior.close });
                  }
                } catch (_) { /* leave pivots unset on malformed data */ }

                // Average Daily % — trailing 30 days, open→close % per day
                // Measures consistent trend direction and typical daily volatility.
                const last30 = data.candles.slice(-30);
                if (last30.length >= 2) {
                  const dailyPcts = last30
                    .filter((c: any) => c.open > 0)
                    .map((c: any) => ((c.close - c.open) / c.open) * 100);
                  const avg = dailyPcts.reduce((s: number, v: number) => s + v, 0) / dailyPcts.length;
                  setAvgDailyPct(Number.isFinite(avg) ? avg : null);
                }
                
                // Store previous close for price line updates
                if (data.candles.length > 0) {
                  const lastCandle = data.candles[data.candles.length - 1];
                  dailyPreviousCloseRef.current = lastCandle.close;
                  
                  // Add reference lines after data is loaded (only if daily chart is enabled)
                  if (dailyChartRef.current && dailySeriesRef.current) {
                    const previousClose = lastCandle.close;
                    const currentPriceValue = currentPriceRef.current ?? previousClose; // Use live price if available, else last close
                    addReferenceLines(previousClose, currentPriceValue);
                    // Note: Price line creation is handled by useEffect to prevent duplicates
                    // The useEffect will create/update the price line when currentPrice changes
                  } else if (data.candles.length > 0) {
                    console.log(`[STATS] [REFERENCE_LINES] Skipping reference lines - daily chart disabled`);
                  }
                }
                
                // Don't use fitContent() - it overrides rightOffset padding
                // The chart will auto-scale with the configured rightOffset: 20
              } else {
                console.warn(`[STATS] [YTD_CHART] No candles returned for ${symbol} (period: ytd)`);
              }
            } else {
              console.error(`[STATS] [YTD_CHART] API error: ${response.status} ${response.statusText}`);
            }
          } catch (err) {
            console.error(`[STATS] [YTD_CHART] Failed to fetch YTD data for ${symbol}:`, err);
          } finally {
            setDailyChartLoading(false);
          }
        };

        fetchYTDData();

        // Update daily EMA legend values as the crosshair moves
        // ⚠ `param.seriesPrices` WAS REMOVED from lightweight-charts; this handler read it and silently
        // did nothing, so the daily EMA legend never once updated on hover. It also never threw — the
        // guard `if (!param.seriesPrices) return` swallowed it — which is why it went unnoticed. It is
        // one of the file's standing type errors, and the type checker was right.
        // The replacement is `param.seriesData`, which returns the DATA POINT for a series; for a line
        // series that is `{ time, value }`, so the value has to be unwrapped rather than used directly.
        dailyChart.subscribeCrosshairMove((param) => {
          const sd = (param as unknown as { seriesData?: Map<unknown, unknown> })?.seriesData;
          if (!param?.time || !sd) return;
          const valOf = (s: unknown) => {
            if (!s) return undefined;
            const d = sd.get(s) as { value?: number; close?: number } | undefined;
            // line series -> {value}; guard `close` too so this keeps working if the series type changes.
            return typeof d?.value === 'number' ? d.value : typeof d?.close === 'number' ? d.close : undefined;
          };
          const e9   = valOf(dailyEma9SeriesRef.current);
          const e50  = valOf(dailyEma50SeriesRef.current);
          const e200 = valOf(dailyEma200SeriesRef.current);
          if (e9   != null) setDailyEma9Value(e9);
          if (e50  != null) setDailyEma50Value(e50);
          if (e200 != null) setDailyEma200Value(e200);
        });
      } catch (err) {
        console.error('YTD chart init failed:', err);
      }
    }

      // Re-fire the candles cascade once so the late-built panes get their data
      // (their setData calls bailed on null refs during the initial cascade).
      setCandles((prev) => (prev.length ? [...prev] : prev));
    }; // end createDeferredPanes
    setTimeout(() => {
      if (cancelled) return;
      try { createDeferredPanes(); } catch (e) { console.warn('[CHART] deferred pane creation failed:', e); }
    }, 250);

    // Force chart to repaint with correct size - multiple delays so chart paints when container becomes visible
    const forceResize = () => {
      if (cancelled || !chartRef.current || !chartContainerRef.current) return;
      if (!document.body.contains(chartContainerRef.current)) return;
      try {
        const cw = chartContainerRef.current.clientWidth || 300;
        const ch = chartContainerRef.current.clientHeight || FALLBACK_HEIGHT;
        if (cw > 0 && ch > 0) {
          chartRef.current.applyOptions({ width: cw, height: ch });
        }
      } catch (_) { /* Value is null / chart destroyed */ }
      if (volumeChartRef.current && volumeContainerRef.current && document.body.contains(volumeContainerRef.current)) {
        try {
          const vw = volumeContainerRef.current.clientWidth || 300;
          const vh = volumeContainerRef.current.clientHeight || FALLBACK_VOL_HEIGHT;
          if (vw > 0 && vh > 0) {
            volumeChartRef.current.applyOptions({ width: vw, height: vh });
          }
        } catch (_) { /* Value is null / chart destroyed */ }
      }
    };
    forceResizeTimeoutsRef.current.push(
      setTimeout(forceResize, 100),
      setTimeout(forceResize, 300),
      setTimeout(forceResize, 600),
      setTimeout(forceResize, 1200)
    );

    // Handle resize with ResizeObserver for smoother updates
    if (chartContainerRef.current) {
      const observer = new ResizeObserver(() => {
        if (!isAliveRef.current || !chartRef.current || !chartContainerRef.current) return;
        if (!document.body.contains(chartContainerRef.current)) return;
        try {
          const cw = chartContainerRef.current.clientWidth;
          const ch = chartContainerRef.current.clientHeight || 500;
          if (cw > 0 && ch > 0) {
            chartRef.current.applyOptions({
              width: cw,
              height: ch,
            });
          }
          if (volumeChartRef.current && volumeContainerRef.current && document.body.contains(volumeContainerRef.current)) {
            const vw = volumeContainerRef.current.clientWidth;
            if (vw > 0) {
              volumeChartRef.current.applyOptions({ width: vw });
            }
          }
        } catch (_) { /* Value is null / chart destroyed */ }
      });
      observer.observe(chartContainerRef.current);
      resizeObserverRef.current = observer;
    }

    // Handle volume chart resize
    if (volumeContainerRef.current) {
      const volumeObserver = new ResizeObserver(() => {
        if (!isAliveRef.current || !volumeChartRef.current || !volumeContainerRef.current) return;
        if (!document.body.contains(volumeContainerRef.current)) return;
        try {
          volumeChartRef.current.applyOptions({
            width: volumeContainerRef.current.clientWidth,
          });
        } catch (_) { /* Value is null / chart destroyed */ }
      });
      volumeObserver.observe(volumeContainerRef.current);
      volumeResizeObserverRef.current = volumeObserver;
    }

    }; // end tryCreateCharts
    const run = () => {
      if (cancelled) return;
      // Delay so container is definitely laid out (fixes blank chart when created too early)
      timeoutId = setTimeout(() => {
        if (cancelled) return;
        tryCreateCharts();
      }, 100);
    };
    rafId = requestAnimationFrame(() => {
      if (cancelled) return;
      rafId = requestAnimationFrame(run);
    });

    return () => {
      cancelled = true;
      setChartsReady(false);
      if (timeoutId != null) clearTimeout(timeoutId);
      if (rafId != null) cancelAnimationFrame(rafId);
      forceResizeTimeoutsRef.current.forEach((t) => { try { clearTimeout(t); } catch {} });
      forceResizeTimeoutsRef.current = [];
      isAliveRef.current = false;
      if (resizeObserverRef.current) {
        try { resizeObserverRef.current.disconnect(); } catch {}
        resizeObserverRef.current = null;
      }
      if (volumeResizeObserverRef.current) {
        try { volumeResizeObserverRef.current.disconnect(); } catch {}
        volumeResizeObserverRef.current = null;
      }
      // [OK] Cleanup volume sync interval and timeout
      if (volumeSyncIntervalRef.current) {
        try { clearInterval(volumeSyncIntervalRef.current); } catch {}
        volumeSyncIntervalRef.current = null;
      }
      if (volumeSyncTimeoutRef.current) {
        try { clearTimeout(volumeSyncTimeoutRef.current); } catch {}
        volumeSyncTimeoutRef.current = null;
      }
      // RSI sub-pane sync cleanup (mirrors the volume pane).
      if (rsiResizeObserverRef.current) {
        try { rsiResizeObserverRef.current.disconnect(); } catch {}
        rsiResizeObserverRef.current = null;
      }
      if (rsiSyncIntervalRef.current) {
        try { clearInterval(rsiSyncIntervalRef.current); } catch {}
        rsiSyncIntervalRef.current = null;
      }
      // MACD / ADX sub-pane sync cleanup (mirror the RSI pane).
      if (macdResizeObserverRef.current) { try { macdResizeObserverRef.current.disconnect(); } catch {} macdResizeObserverRef.current = null; }
      if (macdSyncIntervalRef.current) { try { clearInterval(macdSyncIntervalRef.current); } catch {} macdSyncIntervalRef.current = null; }
      if (adxResizeObserverRef.current) { try { adxResizeObserverRef.current.disconnect(); } catch {} adxResizeObserverRef.current = null; }
      if (adxSyncIntervalRef.current) { try { clearInterval(adxSyncIntervalRef.current); } catch {} adxSyncIntervalRef.current = null; }
      // OBV / A-D / CMF sub-pane sync cleanup (mirror the ADX pane).
      if (obvResizeObserverRef.current) { try { obvResizeObserverRef.current.disconnect(); } catch {} obvResizeObserverRef.current = null; }
      if (obvSyncIntervalRef.current) { try { clearInterval(obvSyncIntervalRef.current); } catch {} obvSyncIntervalRef.current = null; }
      if (adlResizeObserverRef.current) { try { adlResizeObserverRef.current.disconnect(); } catch {} adlResizeObserverRef.current = null; }
      if (adlSyncIntervalRef.current) { try { clearInterval(adlSyncIntervalRef.current); } catch {} adlSyncIntervalRef.current = null; }
      if (cmfResizeObserverRef.current) { try { cmfResizeObserverRef.current.disconnect(); } catch {} cmfResizeObserverRef.current = null; }
      if (cmfSyncIntervalRef.current) { try { clearInterval(cmfSyncIntervalRef.current); } catch {} cmfSyncIntervalRef.current = null; }

      // [OK] Cleanup mouse event listeners for sync
      if ((window as any)._chartSyncCleanup) {
        (window as any)._chartSyncCleanup.forEach((cleanup: () => void) => {
          try { cleanup(); } catch {}
        });
        (window as any)._chartSyncCleanup = [];
      }

      // Cancel any pending avatar-range rAF so it can't fire after unmount.
      if (rangeVersionRafRef.current != null) {
        cancelAnimationFrame(rangeVersionRafRef.current);
        rangeVersionRafRef.current = null;
      }

      try {
        if (chartRef.current) chartRef.current.remove();
        if (volumeChartRef.current) volumeChartRef.current.remove();
        if (dailyChartRef.current) dailyChartRef.current.remove();
        if (rsiChartRef.current) rsiChartRef.current.remove();
        if (macdChartRef.current) macdChartRef.current.remove();
        if (adxChartRef.current) adxChartRef.current.remove();
        if (obvChartRef.current) obvChartRef.current.remove();
        if (adlChartRef.current) adlChartRef.current.remove();
        if (cmfChartRef.current) cmfChartRef.current.remove();
        if (rvolChartRef.current) rvolChartRef.current.remove();
      } catch {}
      candleSeriesRef.current = null;
      chartRef.current = null;
      volumeChartRef.current = null;
      volumeSeriesRef.current = null;
      dailyChartRef.current = null;
      dailySeriesRef.current = null;
      ema9SeriesRef.current = null;
      ema50SeriesRef.current = null;
      ema200SeriesRef.current = null;
      // New indicator series/refs.
      vwapSeriesRef.current = null;
      avwapSeriesRef.current = null;
      rsiChartRef.current = null;
      rsiSeriesRef.current = null;
      pivotLinesRef.current = [];
      bbUpperSeriesRef.current = null;
      bbMiddleSeriesRef.current = null;
      bbLowerSeriesRef.current = null;
      macdChartRef.current = null;
      macdLineSeriesRef.current = null;
      macdSignalSeriesRef.current = null;
      macdHistSeriesRef.current = null;
      adxChartRef.current = null;
      adxSeriesRef.current = null;
      obvChartRef.current = null;
      obvSeriesRef.current = null;
      adlChartRef.current = null;
      adlSeriesRef.current = null;
      cmfChartRef.current = null;
      cmfSeriesRef.current = null;
      rvolChartRef.current = null;
      rvolSeriesRef.current = null;
      swingMarkersRef.current = [];
    };
  }, [hasValidSymbol, loading, symbol, fetchAlertsForSymbol, dark]);

  // When loading becomes false, force chart to repaint with current container size (overlay is gone)
  useEffect(() => {
    if (loading) return;
    if (!chartRef.current || !chartContainerRef.current || !document.body.contains(chartContainerRef.current)) return;
    const id = requestAnimationFrame(() => {
      try {
        const w = chartContainerRef.current?.clientWidth ?? 800;
        const h = chartContainerRef.current?.clientHeight ?? 500;
        if (w > 0 && h > 0) chartRef.current?.applyOptions({ width: w, height: h });
      } catch (_) {}
    });
    return () => cancelAnimationFrame(id);
  }, [loading]);

  // Update candles smoothly when data changes (incremental)
  useEffect(() => {
    // Debug: Candle update effect triggered - removed verbose logging
    
    if (!candleSeriesRef.current) {
      // Debug: Candle update skipped: NO SERIES
      return;
    }
    if (candles.length === 0) {
      // Debug: Candle update skipped: NO CANDLES
      return;
    }
    if (!isAliveRef.current) {
      // Debug: Candle update skipped: NOT ALIVE
      return;
    }

    // Create a "current minute" candle for real-time appearance (professional logic)
    // Aggregate 1-min bars into the selected timeframe before any further processing.
    const tfMinutes = TIMEFRAMES.find(t => t.value === timeframe)?.minutes ?? 1;
    let processedCandles = tfMinutes > 1 ? aggregateCandles([...candles], tfMinutes) : [...candles];
    if (currentPrice && candles.length > 0) {
      const now = new Date();
      updateRealtimePriceTracker(currentPrice);

      // Forming candle aligned to the CURRENT timeframe bucket (not always 1m) so the live
      // bar matches the aggregated series — no stray 1-minute bar left on a 5m/15m chart.
      const bucketSec = Math.max(1, tfMinutes) * 60;
      const currentBucket = Math.floor(now.getTime() / 1000 / bucketSec) * bucketSec; // bucket START (sec)
      const lastAgg = processedCandles[processedCandles.length - 1];
      const lastAggTime = lastAgg ? Math.floor(lastAgg.timestamp / 1000) : 0;

      const etNow = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
      const currentHourET = etNow.getHours();
      const dayOfWeek = etNow.getDay(); // 0 = Sunday, 6 = Saturday
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const isTradingHours = !isWeekend && (
        (currentHourET >= 9 && currentHourET < 16) || // Regular hours
        (currentHourET >= 4 && currentHourET < 9) ||  // Premarket
        (currentHourET >= 16 && currentHourET < 20)   // After hours
      );

      // Suppress on a holiday/last-session view (isLastSession) — otherwise we'd fabricate a
      // forming bar far past the last real candle.
      if (isTradingHours && !isLastSession && realtimePriceTracker.current) {
        const rt = realtimePriceTracker.current;
        if (currentBucket > lastAggTime) {
          // A new bucket has opened — append one forming candle at the bucket start.
          processedCandles.push({ timestamp: currentBucket * 1000, open: rt.open, high: rt.high, low: rt.low, close: rt.close, volume: rt.volume });
        } else if (lastAgg && currentBucket === lastAggTime) {
          // Still inside the last bucket — fold the live tick into it (keep aggregated volume).
          lastAgg.high = Math.max(lastAgg.high, rt.high);
          lastAgg.low = Math.min(lastAgg.low, rt.low);
          lastAgg.close = rt.close;
        }
      }
    }

    const lastPrev = previousCandlesRef.current;
    const prevLen = lastPrev.length;
    const nextLen = processedCandles.length;

    // ── VWAP / Anchored VWAP / RSI push ─────────────────────────────────────────
    // Computed off `pc` (the SAME timeframe-aggregated bars the candles/EMAs use) so they
    // stay perfectly aligned — never recomputed from raw 1m. Called from every setData
    // branch below (init / same-bar / new-bar) so the indicators move in lock-step.
    const pushIndicators = (pc: Candle[]) => {
      try {
        const vwapData = computeVWAP(pc, 'session').filter(d => Number.isFinite(d.value) && d.value > 0);
        if (vwapSeriesRef.current && vwapData.length > 0) {
          try { vwapSeriesRef.current.setData(vwapData as any); } catch (_) { /* Value is null */ }
          setVwapValue(vwapData[vwapData.length - 1].value);
        }
        const avwapData = computeVWAP(pc, 'anchored').filter(d => Number.isFinite(d.value) && d.value > 0);
        if (avwapSeriesRef.current && avwapData.length > 0) {
          try { avwapSeriesRef.current.setData(avwapData as any); } catch (_) { /* Value is null */ }
          setAvwapValue(avwapData[avwapData.length - 1].value);
        }
      } catch (_) { /* indicator push is best-effort */ }
      try {
        const rsiVals = visRef.current.rsi ? calculateRSI(pc.map(c => c.close), 14) : [];
        const rsiData = pc
          .map((c, i) => ({ time: Math.floor(c.timestamp / 1000) as any, value: rsiVals[i] }))
          .filter(d => d != null && Number.isFinite(d.value));
        if (rsiSeriesRef.current && rsiData.length > 0) {
          try { rsiSeriesRef.current.setData(rsiData as any); } catch (_) { /* Value is null */ }
          setRsiValue(rsiData[rsiData.length - 1].value as number);
        }
      } catch (_) { /* RSI push is best-effort */ }
      pushExtraIndicators(pc);
    };

    // ── MACD / Bollinger / ADX / ATR / RVOL / swings push ───────────────────────
    // Same `pc` (timeframe-aggregated bars) the candles/EMAs/VWAP use, so everything stays
    // perfectly aligned. Sub-pane series + overlay series are always fed; toggles control
    // visibility (overlays) or the pane's mount (sub-panes). Numeric readouts update state.
    const pushExtraIndicators = (pc: Candle[]) => {
      const t = (c: Candle) => Math.floor(c.timestamp / 1000) as any;
      // Bollinger Bands (20, 2σ) overlay.
      try {
        const { upper, middle, lower } = visRef.current.bb ? calculateBollinger(pc.map(c => c.close), 20, 2) : { upper: [], middle: [], lower: [] };
        const toData = (arr: number[]) => pc.map((c, i) => ({ time: t(c), value: arr[i] })).filter(d => Number.isFinite(d.value));
        const up = toData(upper); const mid = toData(middle); const lo = toData(lower);
        if (bbUpperSeriesRef.current && up.length > 0) { try { bbUpperSeriesRef.current.setData(up as any); } catch (_) { /* null */ } }
        if (bbMiddleSeriesRef.current && mid.length > 0) { try { bbMiddleSeriesRef.current.setData(mid as any); } catch (_) { /* null */ } }
        if (bbLowerSeriesRef.current && lo.length > 0) { try { bbLowerSeriesRef.current.setData(lo as any); } catch (_) { /* null */ } }
      } catch (_) { /* best-effort */ }
      // MACD (12/26/9) sub-pane.
      try {
        const { macd, signal, hist } = visRef.current.macd ? calculateMACD(pc.map(c => c.close), 12, 26, 9) : { macd: [], signal: [], hist: [] };
        const macdData = pc.map((c, i) => ({ time: t(c), value: macd[i] })).filter(d => Number.isFinite(d.value));
        const signalData = pc.map((c, i) => ({ time: t(c), value: signal[i] })).filter(d => Number.isFinite(d.value));
        const histData = pc.map((c, i) => ({ time: t(c), value: hist[i], color: (hist[i] >= 0 ? 'rgba(38,166,154,0.6)' : 'rgba(239,83,80,0.6)') })).filter(d => Number.isFinite(d.value));
        if (macdHistSeriesRef.current && histData.length > 0) { try { macdHistSeriesRef.current.setData(histData as any); } catch (_) { /* null */ } }
        if (macdLineSeriesRef.current && macdData.length > 0) { try { macdLineSeriesRef.current.setData(macdData as any); } catch (_) { /* null */ } }
        if (macdSignalSeriesRef.current && signalData.length > 0) { try { macdSignalSeriesRef.current.setData(signalData as any); } catch (_) { /* null */ } }
        const li = macd.length - 1;
        if (li >= 0 && Number.isFinite(macd[li]) && Number.isFinite(signal[li])) {
          setMacdValue({ macd: macd[li], signal: signal[li], hist: hist[li] });
        }
      } catch (_) { /* best-effort */ }
      // ADX (14) sub-pane.
      try {
        const adxVals = visRef.current.adx ? calculateADX(pc, 14) : [];
        const adxData = pc.map((c, i) => ({ time: t(c), value: adxVals[i] })).filter(d => Number.isFinite(d.value));
        if (adxSeriesRef.current && adxData.length > 0) { try { adxSeriesRef.current.setData(adxData as any); } catch (_) { /* null */ } }
        const last = [...adxVals].reverse().find(v => Number.isFinite(v));
        if (last != null) setAdxValue(last);
      } catch (_) { /* best-effort */ }
      // OBV sub-pane (single line).
      try {
        const obvVals = visRef.current.obv ? calculateOBV(pc) : [];
        const obvData = pc.map((c, i) => ({ time: t(c), value: obvVals[i] })).filter(d => Number.isFinite(d.value));
        obvByTimeRef.current = new Map(obvData.map((d) => [d.time as number, d.value as number]));
        if (obvSeriesRef.current && obvData.length > 0) { try { obvSeriesRef.current.setData(obvData as any); } catch (_) { /* null */ } }
        if (obvData.length > 0) setObvValue(obvData[obvData.length - 1].value as number);
      } catch (_) { /* best-effort */ }
      // A/D line sub-pane (single line).
      try {
        const adlVals = visRef.current.adl ? calculateADLine(pc) : [];
        const adlData = pc.map((c, i) => ({ time: t(c), value: adlVals[i] })).filter(d => Number.isFinite(d.value));
        adlByTimeRef.current = new Map(adlData.map((d) => [d.time as number, d.value as number]));
        if (adlSeriesRef.current && adlData.length > 0) { try { adlSeriesRef.current.setData(adlData as any); } catch (_) { /* null */ } }
        if (adlData.length > 0) setAdlValue(adlData[adlData.length - 1].value as number);
      } catch (_) { /* best-effort */ }
      // CMF(20) oscillator sub-pane. Warm-up (i<19) stays NaN and is filtered out, like RSI.
      try {
        const cmfVals = visRef.current.cmf ? calculateCMF(pc, 20) : [];
        const cmfData = pc.map((c, i) => ({ time: t(c), value: cmfVals[i] })).filter(d => Number.isFinite(d.value));
        if (cmfSeriesRef.current && cmfData.length > 0) { try { cmfSeriesRef.current.setData(cmfData as any); } catch (_) { /* null */ } }
        if (cmfData.length > 0) setCmfValue(cmfData[cmfData.length - 1].value as number);
      } catch (_) { /* best-effort */ }
      // Accumulation/distribution day count (legend readout).
      try {
        const ad = visRef.current.accdays ? computeAccDist(pc, 25) : null;
        setAccDist(ad);
      } catch (_) { /* best-effort */ }
      // RVOL sub-pane. Denominator already rides on each bar as baseVol (summed through
      // aggregateCandles), so this works identically on 1m/5m/15m/30m/1h/1D.
      try {
        barsByTimeRef.current = new Map(pc.map((c) => [Math.floor(c.timestamp / 1000), c]));
        latestBarRef.current = pc.length ? { ...pc[pc.length - 1], t: Math.floor(pc[pc.length - 1].timestamp / 1000) } : null;
        rvolCapRef.current = visRef.current.rvol ? rvolAxisCap(pc) : null;
        const rvolData = visRef.current.rvol ? buildRvolSeries(pc, t, rvolByTimeRef.current) : [];
        if (rvolSeriesRef.current && rvolData.length > 0) { try { rvolSeriesRef.current.setData(rvolData as any); } catch (_) { /* null */ } }
        const lastBar = [...pc].reverse().find((c) => rvolOf(c) != null);
        setRvolPaneValue(lastBar ? rvolOf(lastBar) : null);
        setRvolPaneThin(lastBar ? (lastBar.baseN ?? 0) < RVOL_MIN_SAMPLES : false);
      } catch (_) { /* best-effort */ }
      // ATR (14) numeric readout.
      try {
        const atrVals = visRef.current.atr ? calculateATR(pc, 14) : [];
        const lastAtr = [...atrVals].reverse().find(v => Number.isFinite(v));
        const lastClose = pc.length > 0 ? pc[pc.length - 1].close : null;
        if (lastAtr != null) {
          setAtrValue(lastAtr);
          setAtrPct(lastClose && lastClose > 0 ? (lastAtr / lastClose) * 100 : null);
        }
      } catch (_) { /* best-effort */ }
      // RVOL numeric readout (last bar vs prior-20 mean).
      try {
        const rvol = computeRVOL(pc.map(c => c.volume ?? 0), 20);
        if (rvol != null) setRvolValue(rvol);
      } catch (_) { /* best-effort */ }
      // Swing highs/lows markers (merged into the markers effect via a ref + tick bump).
      try {
        const { highs, lows } = detectSwings(pc, 3, 3);
        const markers: typeof swingMarkersRef.current = [];
        for (const i of highs) {
          markers.push({ time: t(pc[i]) as number, position: 'aboveBar', color: '#fbbf24', shape: 'arrowDown', text: '', size: 0.7, id: `swing-h-${Math.floor(pc[i].timestamp / 1000)}` });
        }
        for (const i of lows) {
          markers.push({ time: t(pc[i]) as number, position: 'belowBar', color: '#60a5fa', shape: 'arrowUp', text: '', size: 0.7, id: `swing-l-${Math.floor(pc[i].timestamp / 1000)}` });
        }
        const prev = swingMarkersRef.current;
        const changed = prev.length !== markers.length || prev.some((p, i) => !markers[i] || p.id !== markers[i].id);
        swingMarkersRef.current = markers;
        // Only re-trigger the markers effect when swings are ON and the set actually changed.
        if (changed && showSwingsRef.current) setSwingTick(v => v + 1);
      } catch (_) { /* best-effort */ }
    };

    if (candleRafIdRef.current) cancelAnimationFrame(candleRafIdRef.current);
    candleRafIdRef.current = requestAnimationFrame(() => {
      if (!isAliveRef.current || !candleSeriesRef.current) return;
      if (!chartContainerRef.current || !document.body.contains(chartContainerRef.current)) return;

      // First initialization: set full data once
      if (!candleInitializedRef.current) {
        // Debug: First candle data initialization - removed verbose logging
        
        // Debug: Afterhours candles in data - removed verbose logging
        
        // Candlestick data must be only { time, open, high, low, close } - no extra fields (avoids "Value is null")
        // Sort ascending and dedupe by time (last wins) - duplicate timestamps cause lightweight-charts to throw
        const rawInitial = processedCandles
          .map(c => ({
            time: Math.floor(c.timestamp / 1000) as any,
            open: c.open,
            high: c.high,
            low: c.low,
            close: c.close,
          }))
          .filter(d => [d.time, d.open, d.high, d.low, d.close].every(v => v != null && !Number.isNaN(v)));
        const byTime = new Map<number, typeof rawInitial[0]>();
        rawInitial.forEach(d => { byTime.set(d.time as number, d); });
        const initial = Array.from(byTime.values()).sort((a, b) => (a.time as number) - (b.time as number));

        console.log(`[STATS] [CHART_INIT] Formatted candles count: ${initial.length}`);
        if (initial.length > 0) {
          console.log(`[STATS] [CHART_INIT] First formatted candle: ${new Date(initial[0].time * 1000).toLocaleString()}, Last formatted candle: ${new Date(initial[initial.length - 1].time * 1000).toLocaleString()}`);
        }

        // Set volume data in separate chart
        if (volumeSeriesRef.current && volumeChartRef.current) {
          // Use all volume data (no filtering) to match main chart
          const volumeData = processedCandles.map(c => {
            const candleTime = new Date(c.timestamp);
            const etTime = new Date(candleTime.toLocaleString("en-US", {timeZone: "America/New_York"}));
            const hour = etTime.getHours();
            const minute = etTime.getMinutes();
            const timeInMinutes = hour * 60 + minute;
            
            // Determine volume color based on session
            let volumeColor = '#26a69a'; // Default
            if (timeInMinutes >= 240 && timeInMinutes < 570) { // Premarket
              volumeColor = c.close >= c.open ? '#3b82f6' : '#1d4ed8'; // Blue tones
            } else if (timeInMinutes >= 570 && timeInMinutes < 960) { // Regular hours
              volumeColor = c.close >= c.open ? '#22c55e' : '#ef4444'; // Green/Red
            } else if (timeInMinutes >= 960 && timeInMinutes < 1200) { // After hours
              volumeColor = c.close >= c.open ? '#a855f7' : '#7c3aed'; // Purple tones
            }
            
            return {
              time: Math.floor(c.timestamp / 1000) as any,
              value: c.volume,
              color: volumeColor,
            };
          });
          try {
            console.log(`[STATS] [VOLUME_INIT] Starting volume data setup...`);
            console.log(`[STATS] [VOLUME_INIT] Raw volume data length: ${volumeData.length}`);
            
            // [OK] CRITICAL FIX: Add invisible "anchor" points to volume chart for future padding
            // This allows setVisibleRange() to work correctly even when scrolling into empty areas
            const volumeDataWithPadding = [...volumeData];
            
            if (volumeData.length > 0) {
              const lastVolumeBar = volumeData[volumeData.length - 1];
              const lastTime = lastVolumeBar.time as number;
              
              console.log(`[STATS] [VOLUME_INIT] Last volume bar time: ${new Date(lastTime * 1000).toLocaleString()}`);
              
              // Invisible anchor points so scrolling into empty space works. Spaced at the
              // CURRENT timeframe's bucket interval (NOT a hardcoded 1 minute) — otherwise on
              // a 5m chart lightweight-charts detects a 1m base interval for the volume pane
              // and it stops lining up with the 5m candles.
              const padStepSec = Math.max(1, tfMinutes) * 60;
              const paddingBars = 40;
              for (let i = 1; i <= paddingBars; i++) {
                volumeDataWithPadding.push({
                  time: (lastTime + (i * padStepSec)) as any, // one TF bucket per step
                  value: 0.001, // tiny non-zero value (invisible but counts as "data")
                  color: '#787b86'
                });
              }
              
              console.log(`[STATS] [VOLUME_INIT] [OK] Added ${paddingBars} anchor points for future padding`);
              console.log(`[STATS] [VOLUME_INIT] Total volume data: ${volumeData.length} real bars + ${paddingBars} padding bars = ${volumeDataWithPadding.length} total`);
            } else {
              console.warn(`[WARN] [VOLUME_INIT] No volume data to add padding to!`);
            }
            
            console.log(`[STATS] [VOLUME_INIT] Calling volumeSeriesRef.current.setData with ${volumeDataWithPadding.length} bars...`);
            if (isAliveRef.current && volumeSeriesRef.current && volumeContainerRef.current && document.body.contains(volumeContainerRef.current)) {
              const safe = volumeDataWithPadding.filter(d => d != null && (d.value == null ? false : !Number.isNaN(d.value)));
              const sorted = [...safe].sort((a, b) => (a.time as number) - (b.time as number));
              try { volumeSeriesRef.current.setData(sorted); } catch (e) { console.warn('Volume setData(init) failed:', e); }
            }
            console.log(`[STATS] [VOLUME_INIT] [OK] setData completed successfully`);
            
            // [OK] CRITICAL: DON'T sync immediately here - let the initial setup (line 1389) handle it
            // Calling setVisibleRange() here would STRIP the rightOffset padding!
            // The volume chart will be synced via setVisibleLogicalRange() in the initial setup (line 1389)
            // Debug: Volume data set successfully
          } catch (err) {
            console.warn('[WARN] Volume data failed:', err);
          }
        }

        // Calculate and set EMA data
        try {
          const closePrices = processedCandles.map(c => c.close);
          const ema9Values = calculateEMA(closePrices, 9);
          const ema50Values = calculateEMA(closePrices, 50);
          const ema200Values = calculateEMA(closePrices, 200);

          // Create EMA data arrays
          const ema9Data = processedCandles.map((c, index) => ({
            time: Math.floor(c.timestamp / 1000) as any,
            value: ema9Values[index],
          }));

          const ema50Data = processedCandles.map((c, index) => ({
            time: Math.floor(c.timestamp / 1000) as any,
            value: ema50Values[index],
          }));

          const ema200Data = processedCandles.map((c, index) => ({
            time: Math.floor(c.timestamp / 1000) as any,
            value: ema200Values[index],
          }));

          // Set EMA data (filter NaN to avoid "Value is null")
          // > 0, not just !NaN: Number.isFinite(0) is true, and a single zero on a PRICE series drags the
          // right scale down to 0 — which is what puts ~40% of empty space under the candles.
          const ema9Valid = ema9Data.filter(d => d != null && Number.isFinite(d.value) && d.value > 0);
          const ema50Valid = ema50Data.filter(d => d != null && Number.isFinite(d.value) && d.value > 0);
          const ema200Valid = ema200Data.filter(d => d != null && Number.isFinite(d.value) && d.value > 0);
          if (ema9SeriesRef.current && ema9Valid.length > 0) {
            try { ema9SeriesRef.current.setData(ema9Valid); } catch (e) { console.warn('EMA9 setData failed:', e); }
            setEma9Value(ema9Valid[ema9Valid.length - 1].value);
          }
          if (ema50SeriesRef.current && ema50Valid.length > 0) {
            try { ema50SeriesRef.current.setData(ema50Valid); } catch (e) { console.warn('EMA50 setData failed:', e); }
            setEma50Value(ema50Valid[ema50Valid.length - 1].value);
          }
          if (ema200SeriesRef.current && ema200Valid.length > 0) {
            try { ema200SeriesRef.current.setData(ema200Valid); } catch (e) { console.warn('EMA200 setData failed:', e); }
            setEma200Value(ema200Valid[ema200Valid.length - 1].value);
          }

          // Debug: EMA data set successfully
        } catch (err) {
          console.warn('[WARN] EMA calculation failed:', err);
        }

        // VWAP / AVWAP / RSI — pushed alongside the EMAs so they share the aggregated grid.
        pushIndicators(processedCandles);

        // Debug: Mapped initial data - removed verbose logging
        
        try {
          if (!isAliveRef.current || !candleSeriesRef.current || !chartContainerRef.current || !document.body.contains(chartContainerRef.current)) return;
          // Load ALL data into the chart so user can zoom out to see full previous day
          try { candleSeriesRef.current.setData(initial); } catch (e) { console.warn('Candle setData(init) failed:', e); return; }
            
          // Debug: setData(init) successful
          try { 
            // [OK] FIX: Set visible range to show LAST 4 HOURS of actual trading data
            // On weekends, this shows 4:00 PM - 8:00 PM ET (after-hours session)
            const timeScale = chartRef.current?.timeScale();
            if (timeScale && initial.length > 0 && isAliveRef.current) {
              // Get the last candle's timestamp
              const lastCandle = initial[initial.length - 1];
              const lastCandleTime = lastCandle.time as number;
              const lastCandleDate = new Date(lastCandleTime * 1000);
              
              // Show last 4 hours of data (covers full after-hours session: 4PM-8PM ET)
              const fourHoursAgo = lastCandleTime - (4 * 60 * 60); // 4 hours in seconds
              
              // Add padding based on rightOffset setting (20 bars = ~20 minutes for 1-min chart)
              // This gives visual breathing room without extending hours into the future
              const paddingMinutes = 20; // Match rightOffset: 20 setting
              const paddingTime = lastCandleTime + (paddingMinutes * 60); // padding in seconds
              
              console.log(`[STATS] [CHART_INIT] Last candle time: ${lastCandleDate.toLocaleString("en-US", {timeZone: "America/New_York"})} ET`);
              console.log(`[STATS] [CHART_INIT] Ideal range: ${new Date(fourHoursAgo * 1000).toLocaleString("en-US", {timeZone: "America/New_York"})} to ${new Date(paddingTime * 1000).toLocaleString("en-US", {timeZone: "America/New_York"})} ET`);
              console.log(`[STATS] [CHART_INIT] Total candles available: ${initial.length}`);
              
              // Fill the chart width with packed candles (~7px/bar) so they sit side-by-side
              // (TradingView-style) on every timeframe — not a few bars stretched across.
              const initW = chartContainerRef.current?.clientWidth || 1100;
              const barsToShow = Math.max(120, Math.floor(initW / 7));
              const lastBarIndex = initial.length - 1;
              const firstBarIndex = Math.max(0, lastBarIndex - barsToShow + 1);

              console.log(`[STATS] [CHART_INIT] Setting logical range: bars ${firstBarIndex} to ${lastBarIndex} (${lastBarIndex - firstBarIndex + 1} bars to fill width)`);

              try {
                timeScale.setVisibleLogicalRange({
                  from: firstBarIndex as any,
                  to: (lastBarIndex + 6) as any, // small right padding
                });
              } catch (e) { console.warn('setVisibleLogicalRange(init) failed:', e); }
              console.log(`[STATS] [CHART_INIT] [OK] Logical range set with rightOffset padding included`);
              
              // [OK] Sync volume chart with exact same logical range after a delay
              setTimeout(() => {
                if (!isAliveRef.current || !volumeChartRef.current || !volumeContainerRef.current || !document.body.contains(volumeContainerRef.current)) return;
                try {
                  volumeChartRef.current.timeScale().setVisibleLogicalRange({
                    from: firstBarIndex as any,
                    to: (lastBarIndex + 6) as any, // small right padding (match main chart)
                  });
                  console.log(`[STATS] [CHART_INIT] [OK] Volume chart synced with EXACT same logical range (includes padding)`);
                } catch (e) { console.warn('Volume logical range sync failed:', e); }
              }, 300); // Longer delay to ensure both charts fully rendered
              
              console.log(`Chart initialized: ${initial.length} candles, showing bars ${firstBarIndex}-${lastBarIndex} + ${paddingMinutes} bar padding`);
              // Force resize after first data so canvas repaints (fixes blank chart when created before visible)
              if (chartRef.current && chartContainerRef.current && document.body.contains(chartContainerRef.current)) {
                try {
                  const cw = chartContainerRef.current.clientWidth || 800;
                  const ch = chartContainerRef.current.clientHeight || 500;
                  if (cw > 0 && ch > 0) {
                    chartRef.current.applyOptions({ width: cw, height: ch });
                  }
                } catch (_) { /* Value is null */ }
              }
            }
            
            // Debug: Chart time scale info - removed verbose logging
          } catch (err) {
            console.warn('[WARN] setVisibleRange() failed:', err);
          }
        } catch (err) {
          console.error('[FAIL] setData(init) failed:', err);
          return;
        }
        candleInitializedRef.current = true;
        previousCandlesRef.current = processedCandles;
        return;
      }

      const prevLast = lastPrev[prevLen - 1];
        const nextLast = processedCandles[nextLen - 1];
      if (!prevLast || !nextLast) {
        previousCandlesRef.current = processedCandles;
        return;
      }

      const prevTs = prevLast.timestamp;
      const nextTs = nextLast.timestamp;

      // Same bar updated -> use setData() for reliability (prevents flat line bug)
      if (nextTs === prevTs) {
        try {
          if (!candleSeriesRef.current) return;
          const chartData = processedCandles
            .map(c => ({ time: Math.floor(c.timestamp / 1000) as any, open: c.open, high: c.high, low: c.low, close: c.close }))
            .filter(d => [d.time, d.open, d.high, d.low, d.close].every(v => v != null && !Number.isNaN(v)))
            .sort((a, b) => (a.time as number) - (b.time as number));
          try { candleSeriesRef.current.setData(chartData); } catch (e) { console.warn('Candle setData(same-bar) failed:', e); }
          // Only scroll to real time if user is near the end of the chart
          try { 
            const timeScale = chartRef.current?.timeScale();
            if (timeScale) {
              const visibleRange = timeScale.getVisibleRange();
              const lastCandleTime = Math.floor(nextLast.timestamp / 1000);
              // Only scroll if following live and within 10s of last bar
              if (followLiveRef.current && visibleRange && visibleRange.to && (lastCandleTime - (visibleRange.to as number)) < 10) {
                // Use requestAnimationFrame for smoother scrolling with 15-minute padding
                requestAnimationFrame(() => {
                  try {
                    // Set visible range with 15 minutes of padding
                    const now = new Date();
                    // Don't use fitContent() to preserve rightOffset padding
                    // timeScale.fitContent();
                    
                    // [OK] Also update volume chart to stay in sync using LOGICAL RANGE
                    if (volumeChartRef.current) {
                      const mainLogicalRange = timeScale.getVisibleLogicalRange();
                      if (mainLogicalRange && mainLogicalRange.from !== null && mainLogicalRange.to !== null) {
                        volumeChartRef.current.timeScale().setVisibleLogicalRange({
                          from: mainLogicalRange.from,
                          to: mainLogicalRange.to,
                        });
                      }
                    }
                  } catch (err) {
                    console.warn('scrollToRealTime with padding failed:', err);
                  }
                });
              }
            }
          } catch {}
        } catch (err) { console.warn('update(same-bar) skipped:', err); }
        
        // Update EMA for same bar - use setData() for reliability
        try {
          // CRITICAL: EMAs MUST be computed on the AGGREGATED series (processedCandles), not the
          // raw 1-minute `candles`. lightweight-charts shares one time scale across all series and
          // snaps it to the FINEST resolution present — a 1-minute EMA line forces the whole grid
          // to 1 minute, so the correct 5-minute candles render spaced out with gaps. (This ran on
          // every tick/re-fetch, overriding the aggregated EMAs set on the timeframe switch.)
          const closePrices = processedCandles.map(c => c.close);
          const ema9Values = calculateEMA(closePrices, 9);
          const ema50Values = calculateEMA(closePrices, 50);
          const ema200Values = calculateEMA(closePrices, 200);
          const ema9Data = processedCandles.map((c, i) => ({ time: Math.floor(c.timestamp / 1000) as any, value: ema9Values[i] })).filter(d => d != null && !Number.isNaN(d.value));
          const ema50Data = processedCandles.map((c, i) => ({ time: Math.floor(c.timestamp / 1000) as any, value: ema50Values[i] })).filter(d => d != null && !Number.isNaN(d.value));
          const ema200Data = processedCandles.map((c, i) => ({ time: Math.floor(c.timestamp / 1000) as any, value: ema200Values[i] })).filter(d => d != null && !Number.isNaN(d.value));
          if (ema9SeriesRef.current && ema9Data.length > 0) { try { ema9SeriesRef.current.setData(ema9Data); } catch (e) {} }
          if (ema50SeriesRef.current && ema50Data.length > 0) { try { ema50SeriesRef.current.setData(ema50Data); } catch (e) {} }
          if (ema200SeriesRef.current && ema200Data.length > 0) { try { ema200SeriesRef.current.setData(ema200Data); } catch (e) {} }
        } catch (err) {
          console.warn('EMA setData(same-bar) failed:', err);
        }

        // VWAP / AVWAP / RSI — same-bar tick refresh (keeps them aligned with the live bar).
        pushIndicators(processedCandles);

        // [OK] FIX #1: Update volume data when same bar is updated
        try {
          if (volumeSeriesRef.current && isAliveRef.current && volumeContainerRef.current && document.body.contains(volumeContainerRef.current)) {
            const volumeData = processedCandles.map(c => {
              const candleTime = new Date(c.timestamp);
              const etTime = new Date(candleTime.toLocaleString("en-US", {timeZone: "America/New_York"}));
              const hour = etTime.getHours();
              const minute = etTime.getMinutes();
              const timeInMinutes = hour * 60 + minute;
              let volumeColor = '#26a69a';
              if (timeInMinutes >= 240 && timeInMinutes < 570) volumeColor = c.close >= c.open ? '#3b82f6' : '#1d4ed8';
              else if (timeInMinutes >= 570 && timeInMinutes < 960) volumeColor = c.close >= c.open ? '#22c55e' : '#ef4444';
              else if (timeInMinutes >= 960 && timeInMinutes < 1200) volumeColor = c.close >= c.open ? '#a855f7' : '#7c3aed';
              return { time: Math.floor(c.timestamp / 1000) as any, value: c.volume, color: volumeColor };
            });
            const volSorted = volumeData.filter(d => d != null && !Number.isNaN(d.value)).sort((a, b) => (a.time as number) - (b.time as number));
            try { volumeSeriesRef.current.setData(volSorted); } catch (e) { console.warn('Volume setData(same-bar) failed:', e); }
          }
        } catch (err) {
          console.warn('Volume setData(same-bar) failed:', err);
        }
        
        previousCandlesRef.current = processedCandles;
        return;
      }

      // New bar appended -> use setData() for reliability (prevents flat line bug)
      if (nextTs > prevTs) {
        try {
          if (!candleSeriesRef.current) return;
          const chartData = processedCandles
            .map(c => ({ time: Math.floor(c.timestamp / 1000) as any, open: c.open, high: c.high, low: c.low, close: c.close }))
            .filter(d => [d.time, d.open, d.high, d.low, d.close].every(v => v != null && !Number.isNaN(v)))
            .sort((a, b) => (a.time as number) - (b.time as number));
          try { candleSeriesRef.current.setData(chartData); } catch (e) { console.warn('Candle setData(new-bar) failed:', e); }
          // Only scroll to real time if user is near the end of the chart
          try { 
            const timeScale = chartRef.current?.timeScale();
            if (timeScale) {
              const visibleRange = timeScale.getVisibleRange();
              const lastCandleTime = Math.floor(nextLast.timestamp / 1000);
              // Only scroll if following live and within 10s of last bar
              if (followLiveRef.current && visibleRange && visibleRange.to && (lastCandleTime - (visibleRange.to as number)) < 10) {
                // Use requestAnimationFrame for smoother scrolling with 15-minute padding
                requestAnimationFrame(() => {
                  try {
                    // Set visible range with 15 minutes of padding
                    const now = new Date();
                    // Don't use fitContent() to preserve rightOffset padding
                    // timeScale.fitContent();
                    
                    // [OK] Also update volume chart to stay in sync using LOGICAL RANGE
                    if (volumeChartRef.current && volumeContainerRef.current && document.body.contains(volumeContainerRef.current)) {
                      try {
                        const mainLogicalRange = timeScale.getVisibleLogicalRange();
                        if (mainLogicalRange && mainLogicalRange.from !== null && mainLogicalRange.to !== null) {
                          volumeChartRef.current.timeScale().setVisibleLogicalRange({
                            from: mainLogicalRange.from,
                            to: mainLogicalRange.to,
                          });
                        }
                      } catch (_) { /* Value is null */ }
                    }
                  } catch (_) { /* Value is null */ }
                });
              }
            }
          } catch {}
        } catch (err) { console.warn('update(new-bar) skipped:', err); }
        
        // Update EMA for new bar - use setData() for reliability
        try {
          // CRITICAL: EMAs MUST be computed on the AGGREGATED series (processedCandles), not the
          // raw 1-minute `candles`. lightweight-charts shares one time scale across all series and
          // snaps it to the FINEST resolution present — a 1-minute EMA line forces the whole grid
          // to 1 minute, so the correct 5-minute candles render spaced out with gaps. (This ran on
          // every tick/re-fetch, overriding the aggregated EMAs set on the timeframe switch.)
          const closePrices = processedCandles.map(c => c.close);
          const ema9Values = calculateEMA(closePrices, 9);
          const ema50Values = calculateEMA(closePrices, 50);
          const ema200Values = calculateEMA(closePrices, 200);
          const ema9Data = processedCandles.map((c, i) => ({ time: Math.floor(c.timestamp / 1000) as any, value: ema9Values[i] })).filter(d => d != null && !Number.isNaN(d.value));
          const ema50Data = processedCandles.map((c, i) => ({ time: Math.floor(c.timestamp / 1000) as any, value: ema50Values[i] })).filter(d => d != null && !Number.isNaN(d.value));
          const ema200Data = processedCandles.map((c, i) => ({ time: Math.floor(c.timestamp / 1000) as any, value: ema200Values[i] })).filter(d => d != null && !Number.isNaN(d.value));
          if (ema9SeriesRef.current && ema9Data.length > 0) { try { ema9SeriesRef.current.setData(ema9Data); } catch (e) {} }
          if (ema50SeriesRef.current && ema50Data.length > 0) { try { ema50SeriesRef.current.setData(ema50Data); } catch (e) {} }
          if (ema200SeriesRef.current && ema200Data.length > 0) { try { ema200SeriesRef.current.setData(ema200Data); } catch (e) {} }
        } catch (err) {
          console.warn('EMA setData(new-bar) failed:', err);
        }

        // VWAP / AVWAP / RSI — new-bar refresh (the freshly-opened bucket starts the next point).
        pushIndicators(processedCandles);

        // [OK] FIX #1: Update volume data when new bar is appended
        try {
          if (volumeSeriesRef.current && isAliveRef.current && volumeContainerRef.current && document.body.contains(volumeContainerRef.current)) {
            const volumeData = processedCandles.map(c => {
              const candleTime = new Date(c.timestamp);
              const etTime = new Date(candleTime.toLocaleString("en-US", {timeZone: "America/New_York"}));
              const hour = etTime.getHours();
              const minute = etTime.getMinutes();
              const timeInMinutes = hour * 60 + minute;
              
              // Determine volume color based on session
              let volumeColor = '#26a69a'; // Default
              if (timeInMinutes >= 240 && timeInMinutes < 570) { // Premarket
                volumeColor = c.close >= c.open ? '#3b82f6' : '#1d4ed8'; // Blue tones
              } else if (timeInMinutes >= 570 && timeInMinutes < 960) { // Regular hours
                volumeColor = c.close >= c.open ? '#22c55e' : '#ef4444'; // Green/Red
              } else if (timeInMinutes >= 960 && timeInMinutes < 1200) { // After hours
                volumeColor = c.close >= c.open ? '#a855f7' : '#7c3aed'; // Purple tones
              }
              
              return {
                time: Math.floor(c.timestamp / 1000) as any,
                value: c.volume,
                color: volumeColor,
              };
            });
            const volSorted = [...volumeData].filter(d => d != null && !Number.isNaN(d.value)).sort((a, b) => (a.time as number) - (b.time as number));
            try { volumeSeriesRef.current.setData(volSorted); } catch (e) { console.warn('Volume setData(new-bar) failed:', e); }
          }
        } catch (err) {
          console.warn('Volume setData(new-bar) failed:', err);
        }
        
        previousCandlesRef.current = processedCandles;
        return;
      }

      // Out-of-order or reset: fall back to setData once
      const reset = processedCandles
        .map(c => ({ time: Math.floor(c.timestamp / 1000) as any, open: c.open, high: c.high, low: c.low, close: c.close }))
        .filter(d => [d.time, d.open, d.high, d.low, d.close].every(v => v != null && !Number.isNaN(v)))
        .sort((a, b) => (a.time as number) - (b.time as number));
      if (candleSeriesRef.current) {
        try { candleSeriesRef.current.setData(reset); } catch (err) { console.warn('setData(reset) skipped:', err); }
      }
      // Keep VWAP/AVWAP/RSI in sync on the reset path too.
      pushIndicators(processedCandles);
      previousCandlesRef.current = processedCandles; // was wrongly set to raw `candles` — caused incremental path to compare wrong baseline
    });

    return () => {
      if (candleRafIdRef.current) cancelAnimationFrame(candleRafIdRef.current);
      candleRafIdRef.current = null;
    };
    // PERF (owner 2026-07-14: switching timeframes is choppy / locks up): `currentPrice` was in this
    // dep list, so the ENTIRE aggregate → indicator → ~56-setData cascade re-ran on every 2s price
    // tick — the exact "visible blink" the PHASE-1 fast path (series.update() above) was built to
    // avoid. Every forming-only tick already goes through that fast path, and any structural change
    // calls setCandles() → this effect. So this effect only needs to run on structural changes; the
    // per-tick cascade (which got heavy after switching to a higher TF's larger dataset) is gone.
    // currentPrice is still read for the forming bar when the effect DOES run (fresh at that render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candles, chartsReady, timeframe, isLastSession]);

  // ── S/R + double-top/bottom overlay (off by default) ────────────────────────
  // Four independent layers: intraday levels, daily levels (main + mini), DT/DB/contested
  // necklines, and POC/HVN. Levels are dotted with opacity+width scaled by volume; patterns
  // are dashed, recency+proximity filtered, with coincident DT/DB collapsed to a neutral
  // "contested" marker. The mini YTD chart draws daily levels as bare lines (no labels).
  useEffect(() => {
    const main = candleSeriesRef.current;
    const mini = dailySeriesRef.current;
    if (main) { for (const ln of levelLinesRef.current) { try { main.removePriceLine(ln); } catch {} } }
    if (mini) { for (const ln of dailyLevelLinesRef.current) { try { mini.removePriceLine(ln); } catch {} } }
    levelLinesRef.current = []; dailyLevelLinesRef.current = [];
    const anyOn = showMinuteLevels || showDailyLevels || showPatterns || showVolumeProfile;
    if (!anyOn || !hasValidSymbol || !main) { setSrLegend(null); return; }

    const SR_MAX_MAIN = 6, SR_MAX_MINI = 4, PAT_PER_SIDE = 2, PAT_RECENT_FRAC = 0.5;
    type Lv = { price: number; kind: string; strength: number; touches: number; flipped: boolean; volume_strength: number; at_hvn: boolean; source_tf?: string };
    type Pt = { kind: 'double_top' | 'double_bottom'; confirmed: boolean; neckline: number; level: number; ts?: number };
    const hexA = (hex: string, a: number) => { const n = parseInt(hex.slice(1), 16); return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`; };

    // Pick + draw the strongest levels (dedup across TF, cap to `max`), volume-scaled.
    const drawLevels = (s: ISeriesApi<'Candlestick'>, levels: Lv[], lo: number, hi: number, max: number, bare: boolean) => {
      const lines: ReturnType<ISeriesApi<'Candlestick'>['createPriceLine']>[] = [];
      const inRange = levels.filter((l) => l.price >= lo && l.price <= hi).sort((a, b) => b.strength - a.strength);
      const picked: Lv[] = [];
      for (const lv of inRange) { if (picked.some((p) => Math.abs(p.price - lv.price) / lv.price < 0.0015)) continue; picked.push(lv); if (picked.length >= max) break; }
      for (const lv of picked) {
        const isSup = lv.kind === 'support';
        const base = isSup ? '#34d399' : '#fb7185';                        // ALWAYS by current role (flip is just a ↺ glyph)
        const vs = Math.max(0, Math.min(1, lv.volume_strength ?? 0));
        lines.push(s.createPriceLine({
          price: lv.price,
          color: hexA(base, 0.4 + 0.55 * vs),                              // faint→opaque by volume
          lineWidth: (vs > 0.66 ? 3 : vs > 0.33 ? 2 : 1) as 1 | 2 | 3,     // thin→bold by volume
          lineStyle: 1,                                                    // dotted = static S/R
          axisLabelVisible: !bare,
          title: bare ? '' : `${isSup ? 'S' : 'R'}${lv.source_tf === 'daily' ? 'ᴅ' : ''}${lv.at_hvn ? '★' : ''}${lv.flipped ? '↺' : ''}`,  // compact tag; detail → legend
        }));
      }
      return { lines, picked };
    };

    // Draw the recency+proximity-filtered, contested-resolved pattern necklines.
    const drawPatterns = (s: ISeriesApi<'Candlestick'>, patterns: Pt[], lo: number, hi: number, currentPrice: number) => {
      const lines: ReturnType<ISeriesApi<'Candlestick'>['createPriceLine']>[] = [];
      // Draw each pattern at its actual LEVEL (double-bottom = the low, double-top = the high) so the
      // overlay matches reality. (Previously drew the neckline, which inverted it: a double-bottom's
      // neckline is the PEAK above its lows → bottoms rendered high, tops low. That misled entry reads.)
      const inRange = patterns.filter((p) => p.level >= lo && p.level <= hi);
      const tol = Math.max(currentPrice * 0.0015, 1e-6);
      const merged = mergeCoincidentNecklines(inRange, tol);
      const tsList = merged.map((p) => p.ts ?? 0).filter((t) => t > 0);
      const nowTs = tsList.length ? Math.max(...tsList) : 0, minTs = tsList.length ? Math.min(...tsList) : 0;
      const active = selectActivePatterns(merged, { nowTs, recentMs: (nowTs - minTs) * PAT_RECENT_FRAC, currentPrice, perSide: PAT_PER_SIDE });
      const resolved = resolveContested(active, tol);
      for (const p of resolved) {
        const st = patternStyle(p);
        lines.push(s.createPriceLine({
          price: p.level,
          color: hexA(st.color, st.dim ? 0.45 : 0.95),
          lineWidth: (st.dim ? 1 : 2) as 1 | 2,
          lineStyle: st.dim ? 2 : 3,                                       // forming dashed / confirmed large-dashed (≠ dotted S/R)
          axisLabelVisible: true,
          title: st.label,
        }));
      }
      return { lines, resolved };
    };

    const drawVP = (s: ISeriesApi<'Candlestick'>, vap: { poc: number; hvns: number[] } | null, lo: number, hi: number) => {
      const lines: ReturnType<ISeriesApi<'Candlestick'>['createPriceLine']>[] = [];
      if (vap && vap.poc >= lo && vap.poc <= hi) {
        lines.push(s.createPriceLine({ price: vap.poc, color: '#f5c518', lineWidth: 3, lineStyle: 0, axisLabelVisible: true, title: 'POC' }));
        for (const h of (vap.hvns ?? []).filter((h) => h >= lo && h <= hi && Math.abs(h - vap.poc) > 1e-6).slice(0, 6)) {
          lines.push(s.createPriceLine({ price: h, color: hexA('#f5c518', 0.45), lineWidth: 1, lineStyle: 0, axisLabelVisible: true, title: 'HVN' }));
        }
      }
      return lines;
    };

    let cancelled = false;
    (async () => {
      try {
        const tfs = Array.from(new Set([timeframe, 'daily']));
        const results = await Promise.all(tfs.map((tf) =>
          fetch(`/api/levels?symbol=${encodeURIComponent(symbol)}&tf=${tf}`)
            .then((r) => (r.ok ? r.json() : (console.warn('[S/R] /api/levels', tf, r.status), null)))
            .catch((e) => (console.warn('[S/R] fetch error', tf, e), null))
        ));
        const s = candleSeriesRef.current;
        if (cancelled || !s) return;
        const chartData = results[0], dailyData = results[1] ?? results[0];
        const intradayLevels: Lv[] = (chartData?.levels ?? []).filter((l: Lv) => l.source_tf === timeframe);
        const dailyLevels: Lv[] = (dailyData?.levels ?? []).filter((l: Lv) => l.source_tf === 'daily');
        const allPatterns: Pt[] = [...(chartData?.patterns ?? []), ...(dailyData?.patterns ?? [])];

        // Main chart: clamp to the loaded candles' price range so nothing paints off-axis.
        let lo = 0, hi = Number.POSITIVE_INFINITY;
        if (candles.length) { let mn = Infinity, mx = 0; for (const c of candles) { if (c.low < mn) mn = c.low; if (c.high > mx) mx = c.high; } if (mn < mx) { lo = mn; hi = mx; } }
        const currentPrice = candles.length ? candles[candles.length - 1]!.close : (lo + hi) / 2;

        const mainLines: ReturnType<ISeriesApi<'Candlestick'>['createPriceLine']>[] = [];
        const wantLevels: Lv[] = [];
        if (showMinuteLevels) wantLevels.push(...intradayLevels);
        if (showDailyLevels) wantLevels.push(...dailyLevels);
        const lvRes = (showMinuteLevels || showDailyLevels) ? drawLevels(s, wantLevels, lo, hi, SR_MAX_MAIN, false) : { lines: [], picked: [] as Lv[] };
        mainLines.push(...lvRes.lines);
        const ptRes = showPatterns ? drawPatterns(s, allPatterns, lo, hi, currentPrice) : { lines: [], resolved: [] as { kind: string; neckline: number; level: number; confirmed: boolean }[] };
        mainLines.push(...ptRes.lines);
        if (showVolumeProfile) mainLines.push(...drawVP(s, chartData?.vap ?? null, lo, hi));
        levelLinesRef.current = mainLines;

        // YTD mini-chart: daily levels only, as bare lines (no labels on a thumbnail).
        const miniSeries = dailySeriesRef.current;
        if (miniSeries && showDailyLevels) {
          dailyLevelLinesRef.current = drawLevels(miniSeries, dailyLevels, 0, Number.POSITIVE_INFINITY, SR_MAX_MINI, true).lines;
        }

        setSrLegend({
          levels: lvRes.picked.map((l) => ({ price: l.price, kind: l.kind, touches: l.touches, source_tf: l.source_tf, at_hvn: l.at_hvn, flipped: l.flipped, volume_strength: l.volume_strength })),
          patterns: ptRes.resolved,
          poc: showVolumeProfile ? (chartData?.vap?.poc ?? null) : null,
        });
        console.log(`[S/R] ${mainLines.length} main + ${dailyLevelLinesRef.current.length} mini lines · ${lvRes.picked.length} levels, ${ptRes.resolved.length} patterns (from ${allPatterns.length}) · range $${lo.toFixed(2)}–$${hi.toFixed(2)}`);
      } catch (e) { console.warn('[S/R] overlay error', e); }
    })();
    return () => { cancelled = true; };
  }, [showMinuteLevels, showDailyLevels, showPatterns, showVolumeProfile, symbol, hasValidSymbol, chartsReady, timeframe]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── VWAP / Anchored VWAP visibility ─────────────────────────────────────────
  // Data is pushed continuously (alongside the EMAs); this just flips the series' `visible`
  // flag per toggle. Depends on `timeframe` so it re-applies after the TF-change series
  // recreation (which re-adds the series hidden). Same pattern keeps the EMAs always-on.
  useEffect(() => {
    try { vwapSeriesRef.current?.applyOptions({ visible: showVWAP }); } catch (_) { /* series may be mid-recreate */ }
    try { avwapSeriesRef.current?.applyOptions({ visible: showAVWAP }); } catch (_) { /* series may be mid-recreate */ }
  }, [showVWAP, showAVWAP, chartsReady, timeframe]);

  // ── Bollinger Bands visibility ──────────────────────────────────────────────
  // Data is pushed continuously; this just flips the three band series' `visible` flag.
  // Depends on `timeframe` so it re-applies after the TF-change series recreation.
  useEffect(() => {
    try { bbUpperSeriesRef.current?.applyOptions({ visible: showBollinger }); } catch (_) { /* mid-recreate */ }
    try { bbMiddleSeriesRef.current?.applyOptions({ visible: showBollinger }); } catch (_) { /* mid-recreate */ }
    try { bbLowerSeriesRef.current?.applyOptions({ visible: showBollinger }); } catch (_) { /* mid-recreate */ }
  }, [showBollinger, chartsReady, timeframe]);

  // ── MACD pane: re-apply real container size + re-sync when toggled on ────────
  // (The pane is mounted collapsed/display:none when off, so its canvas starts at fallback width.)
  useEffect(() => {
    if (!showMACD) return;
    const id = setTimeout(() => {
      try {
        if (macdChartRef.current && macdContainerRef.current && document.body.contains(macdContainerRef.current)) {
          const w = macdContainerRef.current.clientWidth || 800;
          const h = macdContainerRef.current.clientHeight || 120;
          if (w > 0 && h > 0) macdChartRef.current.applyOptions({ width: w, height: h });
          const main = chartRef.current?.timeScale().getVisibleLogicalRange();
          if (main && main.from != null && main.to != null) {
            try { macdChartRef.current.timeScale().setVisibleLogicalRange({ from: main.from, to: main.to }); } catch (_) { /* null */ }
          }
        }
      } catch (_) { /* mid-recreate */ }
    }, 60);
    return () => clearTimeout(id);
  }, [showMACD, chartsReady]);

  // ── ADX pane: re-apply real container size + re-sync when toggled on ─────────
  useEffect(() => {
    if (!showADX) return;
    const id = setTimeout(() => {
      try {
        if (adxChartRef.current && adxContainerRef.current && document.body.contains(adxContainerRef.current)) {
          const w = adxContainerRef.current.clientWidth || 800;
          const h = adxContainerRef.current.clientHeight || 120;
          if (w > 0 && h > 0) adxChartRef.current.applyOptions({ width: w, height: h });
          const main = chartRef.current?.timeScale().getVisibleLogicalRange();
          if (main && main.from != null && main.to != null) {
            try { adxChartRef.current.timeScale().setVisibleLogicalRange({ from: main.from, to: main.to }); } catch (_) { /* null */ }
          }
        }
      } catch (_) { /* mid-recreate */ }
    }, 60);
    return () => clearTimeout(id);
  }, [showADX, chartsReady]);

  // ── OBV pane: re-apply real container size + re-sync when toggled on (mirrors ADX). ──
  useEffect(() => {
    if (!showOBV) return;
    const id = setTimeout(() => {
      try {
        if (obvChartRef.current && obvContainerRef.current && document.body.contains(obvContainerRef.current)) {
          const w = obvContainerRef.current.clientWidth || 800;
          const h = obvContainerRef.current.clientHeight || 120;
          if (w > 0 && h > 0) obvChartRef.current.applyOptions({ width: w, height: h });
          const main = chartRef.current?.timeScale().getVisibleLogicalRange();
          if (main && main.from != null && main.to != null) {
            try { obvChartRef.current.timeScale().setVisibleLogicalRange({ from: main.from, to: main.to }); } catch (_) { /* null */ }
          }
        }
      } catch (_) { /* mid-recreate */ }
    }, 60);
    return () => clearTimeout(id);
  }, [showOBV, chartsReady]);

  // ── A/D line pane: re-apply real container size + re-sync when toggled on. ──
  useEffect(() => {
    if (!showADLine) return;
    const id = setTimeout(() => {
      try {
        if (adlChartRef.current && adlContainerRef.current && document.body.contains(adlContainerRef.current)) {
          const w = adlContainerRef.current.clientWidth || 800;
          const h = adlContainerRef.current.clientHeight || 120;
          if (w > 0 && h > 0) adlChartRef.current.applyOptions({ width: w, height: h });
          const main = chartRef.current?.timeScale().getVisibleLogicalRange();
          if (main && main.from != null && main.to != null) {
            try { adlChartRef.current.timeScale().setVisibleLogicalRange({ from: main.from, to: main.to }); } catch (_) { /* null */ }
          }
        }
      } catch (_) { /* mid-recreate */ }
    }, 60);
    return () => clearTimeout(id);
  }, [showADLine, chartsReady]);

  // ── CMF pane: re-apply real container size + re-sync when toggled on. ──
  useEffect(() => {
    if (!showCMF) return;
    const id = setTimeout(() => {
      try {
        if (cmfChartRef.current && cmfContainerRef.current && document.body.contains(cmfContainerRef.current)) {
          const w = cmfContainerRef.current.clientWidth || 800;
          const h = cmfContainerRef.current.clientHeight || 120;
          if (w > 0 && h > 0) cmfChartRef.current.applyOptions({ width: w, height: h });
          const main = chartRef.current?.timeScale().getVisibleLogicalRange();
          if (main && main.from != null && main.to != null) {
            try { cmfChartRef.current.timeScale().setVisibleLogicalRange({ from: main.from, to: main.to }); } catch (_) { /* null */ }
          }
        }
      } catch (_) { /* mid-recreate */ }
    }, 60);
    return () => clearTimeout(id);
  }, [showCMF, chartsReady]);

  // ── Pivot points (floor-trader) ─────────────────────────────────────────────
  // Horizontal price lines on the main candle series from the PRIOR completed daily bar:
  //   PP = (H+L+C)/3,  R1 = 2·PP − L,  S1 = 2·PP − H,  R2 = PP + (H−L),  S2 = PP − (H−L).
  // Drawn with createPriceLine, mirroring the S/R level pattern (R reddish, S greenish, PP gold).
  // Re-runs on toggle / TF change (the candle series is recreated on a TF switch) / new daily data.
  useEffect(() => {
    const s = candleSeriesRef.current;
    if (s) { for (const ln of pivotLinesRef.current) { try { s.removePriceLine(ln); } catch {} } }
    pivotLinesRef.current = [];
    if (!showPivots || !s || !pivotDaily) return;
    const { high, low, close } = pivotDaily;
    if (![high, low, close].every((v) => typeof v === 'number' && Number.isFinite(v)) || high <= low) return;
    const pp = (high + low + close) / 3;
    const range = high - low;
    const levels: { price: number; color: string; title: string }[] = [
      { price: pp,                color: '#f5c518', title: 'PP' },   // gold / neutral
      { price: 2 * pp - low,      color: '#fb7185', title: 'R1' },   // reddish resistance
      { price: pp + range,        color: '#fb7185', title: 'R2' },
      { price: 2 * pp - high,     color: '#34d399', title: 'S1' },   // greenish support
      { price: pp - range,        color: '#34d399', title: 'S2' },
    ];
    const lines: ReturnType<ISeriesApi<'Candlestick'>['createPriceLine']>[] = [];
    for (const lv of levels) {
      if (!Number.isFinite(lv.price)) continue;
      try {
        lines.push(s.createPriceLine({
          price: lv.price,
          color: lv.color,
          lineWidth: lv.title === 'PP' ? 2 : 1,
          lineStyle: LineStyle.Dashed,
          axisLabelVisible: true,
          title: lv.title,
        }));
      } catch (_) { /* createPriceLine can throw before data — skip */ }
    }
    pivotLinesRef.current = lines;
  }, [showPivots, pivotDaily, chartsReady, timeframe]);

  // RSI pane is created on a collapsed (display:none) container, so its canvas starts at the
  // fallback width. When the user toggles it on, re-apply the real container size and re-sync the
  // visible range to the main chart so it lines up immediately.
  useEffect(() => {
    if (!showRSI) return;
    const id = setTimeout(() => {
      try {
        if (rsiChartRef.current && rsiContainerRef.current && document.body.contains(rsiContainerRef.current)) {
          const w = rsiContainerRef.current.clientWidth || 800;
          const h = rsiContainerRef.current.clientHeight || 120;
          if (w > 0 && h > 0) rsiChartRef.current.applyOptions({ width: w, height: h });
          // Re-sync to the main chart's logical range.
          const main = chartRef.current?.timeScale().getVisibleLogicalRange();
          if (main && main.from != null && main.to != null) {
            try { rsiChartRef.current.timeScale().setVisibleLogicalRange({ from: main.from, to: main.to }); } catch (_) { /* Value is null */ }
          }
        }
      } catch (_) { /* chart may be mid-recreate */ }
    }, 60);
    return () => clearTimeout(id);
  }, [showRSI, chartsReady]);

  // Same show-time resize for the RVOL pane. Sub-panes are created while COLLAPSED (display:none), so
  // clientWidth is 0 at creation and createSubPane falls back to its 300px default. Without this the
  // pane renders as a ~300px stub squashed against the left edge while volume spans the full width —
  // which is exactly how it first shipped.
  useEffect(() => {
    if (!showRVOL) return;
    const id = setTimeout(() => {
      try {
        if (rvolChartRef.current && rvolContainerRef.current && document.body.contains(rvolContainerRef.current)) {
          const w = rvolContainerRef.current.clientWidth || 800;
          const h = rvolContainerRef.current.clientHeight || 120;
          if (w > 0 && h > 0) rvolChartRef.current.applyOptions({ width: w, height: h });
          const main = chartRef.current?.timeScale().getVisibleLogicalRange();
          if (main && main.from != null && main.to != null) {
            try { rvolChartRef.current.timeScale().setVisibleLogicalRange({ from: main.from, to: main.to }); } catch (_) { /* Value is null */ }
          }
        }
      } catch (_) { /* chart may be mid-recreate */ }
    }, 60);
    return () => clearTimeout(id);
  }, [showRVOL, chartsReady]);

  // When timeframe changes, do a full synchronous reset of candles + volume + EMAs.
  // The main candle effect handles incremental updates, but its "reset" branch only
  // updates the candlestick series and stores raw 1m candles in previousCandlesRef —
  // leaving the volume chart and EMA overlays stuck on the old timeframe.
  useEffect(() => {
    if (!candleInitializedRef.current) return; // not yet initialized — main effect handles first load
    if (!candleSeriesRef.current || !isAliveRef.current || candles.length === 0) return;

    const tfMinutes = TIMEFRAMES.find(t => t.value === timeframe)?.minutes ?? 1;
    const aggregated = tfMinutes > 1 ? aggregateCandles([...candles], tfMinutes) : [...candles];

    // ── ROOT-CAUSE FIX: reset the shared time-scale bar interval ────────────────
    // lightweight-charts derives the bar interval from the FIRST data load (1-minute) and does
    // NOT re-derive it on setData. So after switching to 5m the time scale keeps the 1-minute
    // grid and positions each 5-minute candle 5 slots apart with 4 EMPTY slots between them —
    // the "gaps" (and why the crosshair reads 1-minute times like 3:23). REMOVING and RE-ADDING
    // the series forces the time scale to re-derive the interval from the new timeframe's data,
    // so candles sit adjacent again. Done for the main chart (candle + EMAs) and the volume pane.
    try {
      const chart = chartRef.current;
      if (chart && candleSeriesRef.current) {
        try { chart.removeSeries(candleSeriesRef.current); } catch {}
        if (ema9SeriesRef.current)   { try { chart.removeSeries(ema9SeriesRef.current); } catch {} }
        if (ema50SeriesRef.current)  { try { chart.removeSeries(ema50SeriesRef.current); } catch {} }
        if (ema200SeriesRef.current) { try { chart.removeSeries(ema200SeriesRef.current); } catch {} }
        // VWAP/AVWAP overlays also live on this chart — drop + re-add so they survive the
        // time-scale re-derivation (else they orphan on the removed series). Pivot price lines
        // lived on the old candle series, so drop their refs too (effect redraws them).
        if (vwapSeriesRef.current)  { try { chart.removeSeries(vwapSeriesRef.current); } catch {} }
        if (avwapSeriesRef.current) { try { chart.removeSeries(avwapSeriesRef.current); } catch {} }
        levelLinesRef.current = [];          // price lines lived on the old series
        pivotLinesRef.current = [];          // pivot price lines lived on the old series
        previousMarkersRef.current = null;   // force the markers effect to redraw on the new series
        candleSeriesRef.current = chart.addCandlestickSeries({ upColor: '#089981', downColor: '#f23645', borderUpColor: '#089981', borderDownColor: '#f23645', wickUpColor: '#089981', wickDownColor: '#f23645', borderVisible: true, priceLineVisible: false, lastValueVisible: false });
        ema9SeriesRef.current   = chart.addLineSeries({ color: '#3b82f6', lineWidth: 2, priceLineVisible: false, lastValueVisible: false, autoscaleInfoProvider: () => null });
        ema50SeriesRef.current  = chart.addLineSeries({ color: '#fbbf24', lineWidth: 2, priceLineVisible: false, lastValueVisible: false, autoscaleInfoProvider: () => null });
        ema200SeriesRef.current = chart.addLineSeries({ color: '#10b981', lineWidth: 2, priceLineVisible: false, lastValueVisible: false, autoscaleInfoProvider: () => null });
        // Re-add hidden; the indicator-visibility effect (deps include timeframe) re-applies the toggle.
        vwapSeriesRef.current  = chart.addLineSeries({ color: '#f97316', lineWidth: 2, priceLineVisible: false, lastValueVisible: false, autoscaleInfoProvider: () => null, visible: false });
        avwapSeriesRef.current = chart.addLineSeries({ color: '#a78bfa', lineWidth: 2, lineStyle: LineStyle.Dashed, priceLineVisible: false, lastValueVisible: false, autoscaleInfoProvider: () => null, visible: false });
        // Bollinger overlays lived on the old candle chart's time scale — drop + re-add hidden.
        if (bbUpperSeriesRef.current)  { try { chart.removeSeries(bbUpperSeriesRef.current); } catch {} }
        if (bbMiddleSeriesRef.current) { try { chart.removeSeries(bbMiddleSeriesRef.current); } catch {} }
        if (bbLowerSeriesRef.current)  { try { chart.removeSeries(bbLowerSeriesRef.current); } catch {} }
        bbUpperSeriesRef.current  = chart.addLineSeries({ color: 'rgba(96,165,250,0.55)', lineWidth: 1, priceLineVisible: false, lastValueVisible: false, autoscaleInfoProvider: () => null, visible: false });
        bbMiddleSeriesRef.current = chart.addLineSeries({ color: 'rgba(148,163,184,0.5)', lineWidth: 1, lineStyle: LineStyle.Dashed, priceLineVisible: false, lastValueVisible: false, autoscaleInfoProvider: () => null, visible: false });
        bbLowerSeriesRef.current  = chart.addLineSeries({ color: 'rgba(96,165,250,0.55)', lineWidth: 1, priceLineVisible: false, lastValueVisible: false, autoscaleInfoProvider: () => null, visible: false });
      }
      if (volumeChartRef.current && volumeSeriesRef.current) {
        try { volumeChartRef.current.removeSeries(volumeSeriesRef.current); } catch {}
        volumeSeriesRef.current = volumeChartRef.current.addHistogramSeries({ color: '#787b86', priceFormat: { type: 'volume' } });
      }
      // RSI pane: recreate its series so its independent time scale re-derives the new TF interval
      // (matches the volume-pane treatment). Re-add the 30/50/70 reference lines.
      if (rsiChartRef.current && rsiSeriesRef.current) {
        try { rsiChartRef.current.removeSeries(rsiSeriesRef.current); } catch {}
        const rs = rsiChartRef.current.addLineSeries({
          color: '#e879f9', lineWidth: 2, priceLineVisible: false, lastValueVisible: true,
          autoscaleInfoProvider: () => ({ priceRange: { minValue: 0, maxValue: 100 } }),
        });
        try {
          rs.createPriceLine({ price: 70, color: 'rgba(248,113,113,0.5)', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: '70' });
          rs.createPriceLine({ price: 50, color: 'rgba(113,113,122,0.4)', lineWidth: 1, lineStyle: LineStyle.Dotted, axisLabelVisible: false, title: '' });
          rs.createPriceLine({ price: 30, color: 'rgba(52,211,153,0.5)', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: '30' });
        } catch (_) { /* harmless */ }
        rsiSeriesRef.current = rs;
      }
      // MACD pane: recreate series so its time scale re-derives the new TF interval.
      if (macdChartRef.current) {
        if (macdHistSeriesRef.current)   { try { macdChartRef.current.removeSeries(macdHistSeriesRef.current); } catch {} }
        if (macdLineSeriesRef.current)   { try { macdChartRef.current.removeSeries(macdLineSeriesRef.current); } catch {} }
        if (macdSignalSeriesRef.current) { try { macdChartRef.current.removeSeries(macdSignalSeriesRef.current); } catch {} }
        macdHistSeriesRef.current = macdChartRef.current.addHistogramSeries({ priceLineVisible: false, lastValueVisible: false, color: '#26a69a' });
        const ml = macdChartRef.current.addLineSeries({ color: '#3b82f6', lineWidth: 2, priceLineVisible: false, lastValueVisible: true });
        try { ml.createPriceLine({ price: 0, color: 'rgba(113,113,122,0.5)', lineWidth: 1, lineStyle: LineStyle.Dotted, axisLabelVisible: false, title: '' }); } catch (_) { /* harmless */ }
        macdLineSeriesRef.current = ml;
        macdSignalSeriesRef.current = macdChartRef.current.addLineSeries({ color: '#f97316', lineWidth: 2, priceLineVisible: false, lastValueVisible: false });
      }
      // ADX pane: recreate series + 25/20 reference lines.
      if (adxChartRef.current && adxSeriesRef.current) {
        try { adxChartRef.current.removeSeries(adxSeriesRef.current); } catch {}
        const ax = adxChartRef.current.addLineSeries({
          color: '#eab308', lineWidth: 2, priceLineVisible: false, lastValueVisible: true,
          autoscaleInfoProvider: () => ({ priceRange: { minValue: 0, maxValue: 60 } }),
        });
        try {
          ax.createPriceLine({ price: 25, color: 'rgba(248,113,113,0.5)', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: '25' });
          ax.createPriceLine({ price: 20, color: 'rgba(113,113,122,0.4)', lineWidth: 1, lineStyle: LineStyle.Dotted, axisLabelVisible: false, title: '' });
        } catch (_) { /* harmless */ }
        adxSeriesRef.current = ax;
      }
      // OBV pane: recreate series so its time scale re-derives the new TF interval (autoscaled line).
      if (obvChartRef.current && obvSeriesRef.current) {
        try { obvChartRef.current.removeSeries(obvSeriesRef.current); } catch {}
        obvSeriesRef.current = obvChartRef.current.addLineSeries({ color: '#f5b642', lineWidth: 2, priceLineVisible: false, lastValueVisible: true });
      }
      // A/D line pane: recreate series (autoscaled line).
      if (adlChartRef.current && adlSeriesRef.current) {
        try { adlChartRef.current.removeSeries(adlSeriesRef.current); } catch {}
        adlSeriesRef.current = adlChartRef.current.addLineSeries({ color: '#37d399', lineWidth: 2, priceLineVisible: false, lastValueVisible: true });
      }
      // CMF pane: recreate series + 0 / ±0.10 reference lines.
      if (cmfChartRef.current && cmfSeriesRef.current) {
        try { cmfChartRef.current.removeSeries(cmfSeriesRef.current); } catch {}
        const cx = cmfChartRef.current.addLineSeries({
          color: '#a78bfa', lineWidth: 2, priceLineVisible: false, lastValueVisible: true,
          autoscaleInfoProvider: () => ({ priceRange: { minValue: -0.5, maxValue: 0.5 } }),
        });
        try {
          cx.createPriceLine({ price: 0.10, color: 'rgba(52,211,153,0.5)', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: '+.10' });
          cx.createPriceLine({ price: 0, color: 'rgba(113,113,122,0.4)', lineWidth: 1, lineStyle: LineStyle.Dotted, axisLabelVisible: false, title: '' });
          cx.createPriceLine({ price: -0.10, color: 'rgba(248,113,113,0.5)', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: '-.10' });
        } catch (_) { /* harmless */ }
        cmfSeriesRef.current = cx;
      }
    } catch (e) { console.warn('TF change: series recreate failed', e); }

    // Main candles
    const chartData = aggregated
      .map(c => ({ time: Math.floor(c.timestamp / 1000) as any, open: c.open, high: c.high, low: c.low, close: c.close }))
      .filter(d => [d.time, d.open, d.high, d.low, d.close].every(v => v != null && !Number.isNaN(v)))
      .sort((a, b) => (a.time as number) - (b.time as number));
    try { candleSeriesRef.current.setData(chartData); } catch (e) { console.warn('TF change: candle setData failed', e); }

    // EMAs (recalculated on the aggregated closes)
    try {
      const closes = aggregated.map(c => c.close);
      const e9 = calculateEMA(closes, 9);
      const e50 = calculateEMA(closes, 50);
      const e200 = calculateEMA(closes, 200);
      const toSeries = (vals: number[]) =>
        aggregated.map((c, i) => ({ time: Math.floor(c.timestamp / 1000) as any, value: vals[i] }))
          .filter(d => d != null && !Number.isNaN(d.value));
      if (ema9SeriesRef.current)   try { ema9SeriesRef.current.setData(toSeries(e9));     } catch {}
      if (ema50SeriesRef.current)  try { ema50SeriesRef.current.setData(toSeries(e50));   } catch {}
      if (ema200SeriesRef.current) try { ema200SeriesRef.current.setData(toSeries(e200)); } catch {}
      setEma9Value(e9[e9.length - 1] ?? null);
      setEma50Value(e50[e50.length - 1] ?? null);
      setEma200Value(e200[e200.length - 1] ?? null);
    } catch (e) { console.warn('TF change: EMA reset failed', e); }

    // VWAP / AVWAP / RSI — recomputed on the aggregated bars for the new timeframe.
    try {
      const vwapData = computeVWAP(aggregated, 'session').filter(d => Number.isFinite(d.value) && d.value > 0);
      if (vwapSeriesRef.current && vwapData.length > 0) { try { vwapSeriesRef.current.setData(vwapData as any); } catch {} setVwapValue(vwapData[vwapData.length - 1].value); }
      const avwapData = computeVWAP(aggregated, 'anchored').filter(d => Number.isFinite(d.value));
      if (avwapSeriesRef.current && avwapData.length > 0) { try { avwapSeriesRef.current.setData(avwapData as any); } catch {} setAvwapValue(avwapData[avwapData.length - 1].value); }
      const rsiVals = visRef.current.rsi ? calculateRSI(aggregated.map(c => c.close), 14) : [];
      const rsiData = aggregated.map((c, i) => ({ time: Math.floor(c.timestamp / 1000) as any, value: rsiVals[i] })).filter(d => d != null && Number.isFinite(d.value));
      if (rsiSeriesRef.current && rsiData.length > 0) { try { rsiSeriesRef.current.setData(rsiData as any); } catch {} setRsiValue(rsiData[rsiData.length - 1].value as number); }
    } catch (e) { console.warn('TF change: indicator reset failed', e); }

    // MACD / Bollinger / ADX / ATR / RVOL / swings — recomputed on the aggregated bars.
    try {
      const t = (c: Candle) => Math.floor(c.timestamp / 1000) as any;
      const { upper, middle, lower } = visRef.current.bb ? calculateBollinger(aggregated.map(c => c.close), 20, 2) : { upper: [], middle: [], lower: [] };
      const toData = (arr: number[]) => aggregated.map((c, i) => ({ time: t(c), value: arr[i] })).filter(d => Number.isFinite(d.value));
      if (bbUpperSeriesRef.current)  { const d = toData(upper);  if (d.length) try { bbUpperSeriesRef.current.setData(d as any); } catch {} }
      if (bbMiddleSeriesRef.current) { const d = toData(middle); if (d.length) try { bbMiddleSeriesRef.current.setData(d as any); } catch {} }
      if (bbLowerSeriesRef.current)  { const d = toData(lower);  if (d.length) try { bbLowerSeriesRef.current.setData(d as any); } catch {} }
      const { macd, signal, hist } = visRef.current.macd ? calculateMACD(aggregated.map(c => c.close), 12, 26, 9) : { macd: [], signal: [], hist: [] };
      const macdData = aggregated.map((c, i) => ({ time: t(c), value: macd[i] })).filter(d => Number.isFinite(d.value));
      const signalData = aggregated.map((c, i) => ({ time: t(c), value: signal[i] })).filter(d => Number.isFinite(d.value));
      const histData = aggregated.map((c, i) => ({ time: t(c), value: hist[i], color: (hist[i] >= 0 ? 'rgba(38,166,154,0.6)' : 'rgba(239,83,80,0.6)') })).filter(d => Number.isFinite(d.value));
      if (macdHistSeriesRef.current && histData.length) { try { macdHistSeriesRef.current.setData(histData as any); } catch {} }
      if (macdLineSeriesRef.current && macdData.length) { try { macdLineSeriesRef.current.setData(macdData as any); } catch {} }
      if (macdSignalSeriesRef.current && signalData.length) { try { macdSignalSeriesRef.current.setData(signalData as any); } catch {} }
      const mi = macd.length - 1;
      if (mi >= 0 && Number.isFinite(macd[mi]) && Number.isFinite(signal[mi])) setMacdValue({ macd: macd[mi], signal: signal[mi], hist: hist[mi] });
      const adxVals = visRef.current.adx ? calculateADX(aggregated, 14) : [];
      const adxData = aggregated.map((c, i) => ({ time: t(c), value: adxVals[i] })).filter(d => Number.isFinite(d.value));
      if (adxSeriesRef.current && adxData.length) { try { adxSeriesRef.current.setData(adxData as any); } catch {} }
      const lastAdx = [...adxVals].reverse().find(v => Number.isFinite(v));
      if (lastAdx != null) setAdxValue(lastAdx);
      // OBV / A-D / CMF / acc-dist — recomputed on the aggregated bars for the new timeframe.
      const obvVals = visRef.current.obv ? calculateOBV(aggregated) : [];
      const obvData = aggregated.map((c, i) => ({ time: t(c), value: obvVals[i] })).filter(d => Number.isFinite(d.value));
      obvByTimeRef.current = new Map(obvData.map((d) => [d.time as number, d.value as number]));
      if (obvSeriesRef.current && obvData.length) { try { obvSeriesRef.current.setData(obvData as any); } catch {} }
      if (obvData.length) setObvValue(obvData[obvData.length - 1].value as number);
      const adlVals = visRef.current.adl ? calculateADLine(aggregated) : [];
      const adlData = aggregated.map((c, i) => ({ time: t(c), value: adlVals[i] })).filter(d => Number.isFinite(d.value));
      adlByTimeRef.current = new Map(adlData.map((d) => [d.time as number, d.value as number]));
      if (adlSeriesRef.current && adlData.length) { try { adlSeriesRef.current.setData(adlData as any); } catch {} }
      if (adlData.length) setAdlValue(adlData[adlData.length - 1].value as number);
      const cmfVals = visRef.current.cmf ? calculateCMF(aggregated, 20) : [];
      const cmfData = aggregated.map((c, i) => ({ time: t(c), value: cmfVals[i] })).filter(d => Number.isFinite(d.value));
      if (cmfSeriesRef.current && cmfData.length) { try { cmfSeriesRef.current.setData(cmfData as any); } catch {} }
      if (cmfData.length) setCmfValue(cmfData[cmfData.length - 1].value as number);
      setAccDist(visRef.current.accdays ? computeAccDist(aggregated, 25) : null);
      barsByTimeRef.current = new Map(aggregated.map((c) => [Math.floor(c.timestamp / 1000), c]));
      latestBarRef.current = aggregated.length ? { ...aggregated[aggregated.length - 1], t: Math.floor(aggregated[aggregated.length - 1].timestamp / 1000) } : null;
      rvolCapRef.current = visRef.current.rvol ? rvolAxisCap(aggregated) : null;
      const rvolData = visRef.current.rvol ? buildRvolSeries(aggregated, t, rvolByTimeRef.current) : [];
      if (rvolSeriesRef.current && rvolData.length) { try { rvolSeriesRef.current.setData(rvolData as any); } catch {} }
      const lastRvolBar = [...aggregated].reverse().find((c) => rvolOf(c) != null);
      setRvolPaneValue(lastRvolBar ? rvolOf(lastRvolBar) : null);
      setRvolPaneThin(lastRvolBar ? (lastRvolBar.baseN ?? 0) < RVOL_MIN_SAMPLES : false);
      const atrVals = visRef.current.atr ? calculateATR(aggregated, 14) : [];
      const lastAtr = [...atrVals].reverse().find(v => Number.isFinite(v));
      const lastClose = aggregated.length > 0 ? aggregated[aggregated.length - 1].close : null;
      if (lastAtr != null) { setAtrValue(lastAtr); setAtrPct(lastClose && lastClose > 0 ? (lastAtr / lastClose) * 100 : null); }
      const rvol = computeRVOL(aggregated.map(c => c.volume ?? 0), 20);
      if (rvol != null) setRvolValue(rvol);
      const { highs, lows } = detectSwings(aggregated, 3, 3);
      const sm: typeof swingMarkersRef.current = [];
      for (const i of highs) sm.push({ time: t(aggregated[i]) as number, position: 'aboveBar', color: '#fbbf24', shape: 'arrowDown', text: '', size: 0.7, id: `swing-h-${Math.floor(aggregated[i].timestamp / 1000)}` });
      for (const i of lows) sm.push({ time: t(aggregated[i]) as number, position: 'belowBar', color: '#60a5fa', shape: 'arrowUp', text: '', size: 0.7, id: `swing-l-${Math.floor(aggregated[i].timestamp / 1000)}` });
      swingMarkersRef.current = sm;
      if (showSwingsRef.current) setSwingTick(v => v + 1);
    } catch (e) { console.warn('TF change: extra indicator reset failed', e); }

    // Volume chart
    if (volumeSeriesRef.current && volumeContainerRef.current && document.body.contains(volumeContainerRef.current)) {
      try {
        const volData = aggregated.map(c => {
          const et = new Date(new Date(c.timestamp).toLocaleString('en-US', { timeZone: 'America/New_York' }));
          const t = et.getHours() * 60 + et.getMinutes();
          const color = t >= 240 && t < 570
            ? (c.close >= c.open ? '#3b82f6' : '#1d4ed8')
            : t >= 570 && t < 960
              ? (c.close >= c.open ? '#22c55e' : '#ef4444')
              : t >= 960 && t < 1200
                ? (c.close >= c.open ? '#a855f7' : '#7c3aed')
                : '#26a69a';
          return { time: Math.floor(c.timestamp / 1000) as any, value: c.volume, color };
        }).filter(d => !Number.isNaN(d.value)).sort((a, b) => (a.time as number) - (b.time as number));
        try { volumeSeriesRef.current.setData(volData); } catch (e) { console.warn('TF change: volume setData failed', e); }
      } catch (e) { console.warn('TF change: volume reset failed', e); }
    }

    // Fix the ref so the main incremental effect has the right baseline
    previousCandlesRef.current = aggregated;

    // ── Reset viewport to show the last ~200 bars of the NEW timeframe ────────
    //
    // TradingView behavior: switching from 1m → 5m scrolls to show ~200 5m bars
    // (≈16 hrs of trading history), not just 4 hours of 1m data squeezed into
    // 5m candles. Without this, the user sees the same narrow time window with
    // wider but fewer candles — the chart appears to "barely change."
    // Anchor a comfortable, width-filling window to the MOST RECENT bars. Apply once now and
    // once more after the async re-fetch lands (~750ms) — a single settle, NOT a per-tick
    // re-anchor, so the chart lands cleanly instead of jumping while it loads. We read the
    // live series data() so the delayed pass uses the final (larger) dataset, not the stale one.
    const applyViewport = () => {
      try {
        const ts = chartRef.current?.timeScale();
        const data = candleSeriesRef.current?.data();
        if (!ts || !data || data.length < 2) return;
        const w = chartContainerRef.current?.clientWidth || 1100;
        const barsToShow = Math.max(90, Math.floor(w / 9));    // ~9px/bar → comfortable, packed candles
        const last = data.length - 1;
        const from = Math.max(0, last - barsToShow + 1);
        ts.setVisibleLogicalRange({ from: from as any, to: (last + 4) as any });
        if (volumeChartRef.current) { try { volumeChartRef.current.timeScale().setVisibleLogicalRange({ from: from as any, to: (last + 4) as any }); } catch (_) { /* Value is null */ } }
      } catch (_) { /* Value is null */ }
    };
    applyViewport();
    if (viewportTimerRef.current) clearTimeout(viewportTimerRef.current);
    viewportTimerRef.current = setTimeout(applyViewport, 750);

    // ── Re-fetch candles when switching to a longer timeframe ─────────────────
    //
    // The API returns 1.5 days by default. For 5m/15m/30m/1h we need more
    // historical bars (5-30 days). Trigger a fresh fetch so the data catches up.
    // timeframeRef.current is already updated at this point (via its own useEffect).
    const newTfMinutes = TIMEFRAMES.find(t => t.value === timeframe)?.minutes ?? 1;
    if (newTfMinutes > 1 && fetchCandlesCallbackRef.current) {
      void fetchCandlesCallbackRef.current();
    }

    // Re-align alert markers for the new timeframe bucket size.
    // (The markers useEffect fires automatically because it depends on [alerts, chartTimeMs, timeframe])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // ⚠ showRVOL IS IN HERE ON PURPOSE (owner 2026-08-07: "the RVOL indicator under volume is gone, and
  // the cursor no longer reports RVOL per candle"). 46524fb3 stopped computing hidden indicators —
  // correct, and it is why this pane is cheap — but it gated RVOL on `visRef.current.rvol`, a REF.
  // A ref mutation triggers no render and no effect, and this rebuild only fired on [timeframe], so
  // turning RVOL on updated the flag and NOTHING recomputed: buildRvolSeries never ran, the histogram
  // stayed empty, and because the same call also fills `rvolByTimeRef` (the crosshair lookup), the
  // per-candle readout died with it. ONE skipped build, BOTH symptoms.
  // Adding the flag to the deps rebuilds ONCE PER TOGGLE — not per tick — so the performance intent of
  // 46524fb3 is kept and the correctness bug is closed.
  }, [timeframe, showRVOL]); // timeframe change, and RVOL toggling on (see above)

  // Update markers when alerts change (diff to avoid redundant work)
  useEffect(() => {
    if (!candleSeriesRef.current) return;
    if (!isAliveRef.current) return;

    if (markerRafIdRef.current) cancelAnimationFrame(markerRafIdRef.current);

    markerRafIdRef.current = requestAnimationFrame(() => {
      if (!candleSeriesRef.current || !isAliveRef.current) return;
      if (!chartContainerRef.current || !document.body.contains(chartContainerRef.current)) return;

      // Get chart time range to filter alerts - use visible range for better performance
      let visibleRange = null;
      try {
        const timeScale = chartRef.current?.timeScale();
        visibleRange = timeScale?.getVisibleRange();
      } catch (_) { /* Value is null */ }
      
      // Use visible range with a small buffer (30 minutes) for better performance
      const bufferMinutes = 30 * 60; // 30 minutes in seconds
      const liveRightExtraSec = Math.ceil(CHART_ALERT_RIGHT_MARGIN_MS / 1000);
      const chartStartTime = visibleRange?.from ? Math.floor(visibleRange.from as number) - bufferMinutes : 0;
      const chartEndTime = visibleRange?.to
        ? Math.floor(visibleRange.to as number) + bufferMinutes + liveRightExtraSec
        : Number.MAX_SAFE_INTEGER;

      // Debug: Chart time range for alerts - removed verbose logging

      // Group alerts by minute only (not by type) for better stacking
      const groupedByMinute = new Map<number, Alert[]>();
      let filteredOutCount = 0;
      
      alerts.forEach(alert => {
        // Bar minute for candle alignment (insert-time `timestamp` misplaces markers under backlog)
        const alertTimeSeconds = Math.floor(chartTimeMs(alert) / 1000);
        
        // Only show alerts that fall within the generous time range
        if (alertTimeSeconds < chartStartTime || alertTimeSeconds > chartEndTime) {
          filteredOutCount++;
          return;
        }
        
        // Align alert to the bar bucket for the current timeframe.
        //
        // On a 1m chart each bar is exactly 1 minute wide, so aligning to the
        // minute boundary works. On a 5m chart every bar covers 5 minutes and its
        // timestamp is the OPENING of that 5-minute window (e.g. 9:30, 9:35…).
        // An alert that fired at 9:32 must be placed on the 9:30 bar — otherwise
        // lightweight-charts looks for a 9:32 bar that doesn't exist and silently
        // drops the marker.
        const tfSec = (TIMEFRAMES.find(t => t.value === timeframeRef.current)?.minutes ?? 1) * 60;
        const now = new Date();
        const currentBucket = Math.floor(now.getTime() / 1000 / tfSec) * tfSec;
        const alertBucket   = Math.floor(alertTimeSeconds / tfSec) * tfSec;

        // Future alerts → snap to current bucket
        const bucketTimestamp = alertBucket <= currentBucket ? alertBucket : currentBucket;

        if (!groupedByMinute.has(bucketTimestamp)) {
          groupedByMinute.set(bucketTimestamp, []);
        }
        groupedByMinute.get(bucketTimestamp)!.push(alert);
      });

      // Debug: Alert grouping results - removed verbose logging

      // Create stacked markers for multiple alerts on same minute
      const markers: any[] = [];
      
      Array.from(groupedByMinute.entries()).forEach(([minuteTimestamp, minuteAlerts]) => {
        // Sort alerts by type priority: RVOL first, then Mover, then EMA
        const sortedAlerts = minuteAlerts.sort((a, b) => {
          const typeOrder = { 'rvol': 0, 'mover': 1, 'ema': 2 };
          return (typeOrder[a.type as keyof typeof typeOrder] || 3) - (typeOrder[b.type as keyof typeof typeOrder] || 3);
        });
        
        // Create markers with improved stacking for marketing-quality visualization
        // Limit to 4 alerts per minute to prevent excessive stacking
        const maxAlertsPerMinute = 4;
        sortedAlerts.slice(0, maxAlertsPerMinute).forEach((alert, stackIndex) => {
        // Thoth/Ra trades render in the custom HTML overlay — skip here
        if (isTradeAlert(alert)) return;
        // News alerts get the dedicated 📰 markers (from /api/news) — don't also draw them here, where
        // an unhandled 'news' type would fall through to the default green circle and read like RVOL.
        if (alert.type === 'news') return;
        // Legend toggle: skip this marker if the viewer clicked its legend chip off (rvol/mover/ema/halt/resume).
        const _legendKey = alert.type === 'rvol' ? 'rvol' : alert.type === 'mover' ? 'mover'
          : alert.type === 'halt' ? 'halt' : alert.type === 'resume' ? 'resume'
          : alert.type === 'ema_cross_below' ? 'emaBreak'
          : String(alert.type).startsWith('ema') ? 'ema' : null;
        if (_legendKey && hiddenMarkers.has(_legendKey)) return;
        let color = '#10b981';
        let shape: 'circle' | 'arrowUp' | 'arrowDown' | 'square' = 'circle';
          let size = 1.0; // Base size
        
          if (alert.type === 'mover') {
            // Yellow arrows for movers - point in direction of movement (use numeric sign; don't treat 0 as falsy)
          color = '#eab308';
            const pc = alert.data?.percentChange;
            shape =
              typeof pc === 'number' && pc > 0
                ? 'arrowUp'
                : typeof pc === 'number' && pc < 0
                  ? 'arrowDown'
                  : 'circle';
            size = 1.2; // Slightly larger for movers (most important)
          } else if (alert.type === 'ema' || alert.type === 'ema_near') {
            color = '#a855f7'; // Purple arrowUp for EMA near — price approaching/at EMA (bullish setup)
            shape = 'arrowUp'; // Distinct from RVOL (green circle) so icons are never ambiguous
            size = 0.9;
          } else if (alert.type === 'ema_cross_above') {
            color = '#22d3ee'; // Cyan arrow up — price crossed above 200 EMA (bullish)
            shape = 'arrowUp';
            size = 1.0;
          } else if (alert.type === 'ema_cross_below') {
            color = '#f97316'; // Orange arrow down — price crossed below 200 EMA (bearish)
            shape = 'arrowDown';
            size = 1.0;
          } else if (alert.type === 'rvol') {
            color = '#10b981'; // Green circle for RVOL alerts
            shape = 'circle';
            size = 1.1; // Slightly larger for RVOL (very important)
          } else if (alert.type === 'ema_trending') {
            color = '#818cf8'; // Indigo square — EMA trend continuation (distinct from RVOL green circles)
            shape = 'square';
            size = 0.75; // Smaller to reduce clutter (many fire per session)
          } else if (alert.type === 'halt') {
            color = '#ef4444'; // Red square for halt alerts
            shape = 'square';
            size = 1.3; // Larger for visibility
          } else if (alert.type === 'resume') {
            color = '#22c55e'; // Green square for resume alerts
            shape = 'square';
            size = 1.3; // Larger for visibility
          }
          
          // [OK] FIX: Marker `time` must match a bar's UTCTimestamp (integer seconds). Fractional offsets
          // (e.g. +0.1s) are not valid bar times — lightweight-charts drops markers on the latest candle.
          // Stack multiple alerts on the same minute using the same `time`; the library stacks markers.

        const marker = {
            time: minuteTimestamp as any,
          position: 'belowBar' as const,
          color,
          shape,
            text: '', // No text to prevent overlap
            size, // Dynamic size based on alert type
            // Add subtle visual enhancement for marketing appeal
            id: `${alert.type}-${minuteTimestamp}-${stackIndex}-${alert.id}`,
          };

          markers.push(marker);
        });
      });

      // COLLAPSE PER RENDERED BAR.
      //
      // maxAlertsPerMinute caps stacking at 4 per MINUTE, which is correct on the 1m chart and wrong
      // everywhere else: on 1h up to 60 separate minutes snap onto the same candle, so the cap lets
      // through 4 x 60 markers and they fan into a vertical column. Owner saw ~10 stacked orange
      // arrows on SKYQ and reasonably read it as a bug.
      //
      // One marker per (bar, alert type, side) is all a chart can convey anyway — the Recent Alerts
      // panel is where the individual events are legible.
      const _tfMin = TIMEFRAMES.find((t) => t.value === timeframe)?.minutes ?? 1;
      if (_tfMin > 1 && markers.length) {
        const bucket = _tfMin * 60;
        const seen = new Set<string>();
        const collapsed = markers.filter((m: any) => {
          const type = String(m.id ?? '').split('-')[0];
          const k = `${Math.floor(Number(m.time) / bucket)}:${type}:${m.position}`;
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });
        markers.length = 0;
        markers.push(...collapsed);
      }

      // Orphan Alpaca sell fills: sells in alpaca_fill_cache that have no corresponding
      // Ra sell alert within 5 minutes. This catches positions closed by stop-loss orders
      // during Ra's RTH blackout — Alpaca fill exists but emitRaStrategyAlert never fired.
      // Shown as red arrowDown markers so the chart accurately reflects real Alpaca activity.
      const raSellAlertTimeSecs = alerts
        .filter(a => { const m = parseTradeMeta(a); return m?.side === 'sell' && a.type === 'ra'; })
        .map(a => chartTimeMs(a) / 1000);
      // ONE ARROW PER BAR, not per FILL (owner: DFNS "why is there 6 red down arrows stacked on top of
      // each other"). A single exit is filled in slices by the escalating marketable-limit ladder — DFNS
      // sold 116 shares in SIX fills inside one minute, and this loop drew a marker for each, which
      // lightweight-charts then stacked vertically because they share a bar time. The chart looked like
      // six exits when there was one. Collapse fills into their bar bucket first, then draw a single
      // arrow carrying the combined size, so the picture matches the trade rather than the plumbing.
      void raSellAlertTimeSecs;
      const tfSec2 = (TIMEFRAMES.find(t => t.value === timeframeRef.current)?.minutes ?? 1) * 60;
      const sellByBar = new Map<number, { qty: number; fills: number; firstId: string }>();
      for (const fill of alpacaFills) {
        if (fill.side !== 'sell') continue;
        const fillMs = new Date(fill.transactionTime).getTime();
        if (!Number.isFinite(fillMs)) continue;
        const bucketSec = Math.floor(fillMs / 1000 / tfSec2) * tfSec2;
        const cur = sellByBar.get(bucketSec);
        if (cur) { cur.qty += Number(fill.qty) || 0; cur.fills += 1; }
        else sellByBar.set(bucketSec, { qty: Number(fill.qty) || 0, fills: 1, firstId: String(fill.id) });
      }
      // NO window gate (THE bug): this marker effect does NOT re-run on scroll, so window-gating here
      // meant the exit marker was never added when you scroll to an OLD trade — the entry shows via its
      // scroll-aware avatar, but the exit fill marker did not. lightweight-charts only renders markers
      // currently in view, so adding all sell-fill markers is safe. (YMAT: 8 sells present, 0 drawn.)
      // LEGEND-GATED (owner 2026-07-27: "so many down red arrows that i cannot remove from chart").
      // These orphan sell-fill arrows were the ONE marker path with no hiddenMarkers check — every other
      // path (series markers, news pins, reddit pins, the trade overlay) honours the legend, so clicking
      // "Trade" off removed the numbered trade glyphs and left these bare red arrows behind with no chip
      // that could clear them. They ARE exits, so they belong to the 'trade' key.
      for (const [bucketSec, agg] of (hiddenMarkers.has('trade') ? [] : sellByBar)) {
        markers.push({
          time: bucketSec as any,
          position: 'aboveBar' as const,
          color: '#f43f5e',
          shape: 'arrowDown' as const,
          text: '',
          size: 1.4,
          id: `alpaca-sell-bar-${bucketSec}-${agg.firstId}`,
        });
      }

      // Swing high/low structure markers (computed in pushIndicators, TF-aligned). Merged here
      // because lightweight-charts' setMarkers REPLACES all markers on the series — they must
      // co-exist with the alert/exit markers in one array. Only added when the layer is on.
      if (showSwings) {
        for (const sm of swingMarkersRef.current) markers.push({ ...sm });
      }

      // The Watcher REPLAY markers (indigo W▲ entries / W▼ exits). Same rationale as
      // swings: setMarkers replaces all markers, so the replay markers must be merged
      // into this one array. Times are precomputed in the replay effect (TF-aware).
      if (showWatcherReplay) {
        for (const wm of watcherReplayMarkersRef.current) markers.push({ ...wm });
      }

      // News markers (sentiment-colored dots at each article's time). Same merge rationale as
      // swings/replay — setMarkers replaces all, so they co-exist in this one array.
      // BUGFIX (owner 2026-07-17): lightweight-charts CLAMPS a marker whose time is before the
      // first loaded bar onto that first bar — so a warrant/parent's OLD articles (published days
      // before the intraday window) all piled up as a stack of 📰 dots on the leftmost candle,
      // reading as "news dropped here" when nothing did. Only draw news markers whose bucket time
      // actually falls inside the loaded candle range (±1 bucket of slack).
      // `!hiddenMarkers.has('news')` added 2026-07-27 alongside the sell-fill fix: the DOM news PINS
      // already honoured the legend chip but these SERIES markers did not, so turning "News" off cleared
      // the pins and left the 📰 dots on the candles. Same defect, same cause — a marker path that
      // predates the legend and was never wired to it.
      if (showNewsMarkers && !hiddenMarkers.has('news') && newsMarkersRef.current.length) {
        if (candles.length) {
          const tfSlack = (TIMEFRAMES.find((tt) => tt.value === timeframe)?.minutes ?? 1) * 60;
          const firstSec = Math.floor(candles[0].timestamp / 1000) - tfSlack;
          const lastSec = Math.floor(candles[candles.length - 1].timestamp / 1000) + tfSlack;
          for (const nm of newsMarkersRef.current) {
            const tsec = nm.time as number;
            if (tsec >= firstSec && tsec <= lastSec) markers.push({ ...nm });
          }
        } else {
          for (const nm of newsMarkersRef.current) markers.push({ ...nm });
        }
      }

      // Markers must be sorted by time for correct rendering
      markers.sort((a, b) => (a.time as number) - (b.time as number));

      // Shallow diff to avoid redundant setMarkers
      const prev = previousMarkersRef.current;
      const same = Array.isArray(prev)
        && prev.length === markers.length
        && prev.every((p, i) => {
          const m = markers[i];
          return p.time === m.time && p.position === m.position && p.color === m.color && p.shape === m.shape && p.text === m.text;
        });
      if (!same && candleSeriesRef.current && isAliveRef.current) {
        const validMarkers = markers.filter(m => m != null && typeof m.time === 'number' && !Number.isNaN(m.time));
        try { candleSeriesRef.current.setMarkers(validMarkers); } catch (err) { console.warn('setMarkers skipped:', err); }
        previousMarkersRef.current = validMarkers;
      }
    });

    return () => {
      if (markerRafIdRef.current) cancelAnimationFrame(markerRafIdRef.current);
      markerRafIdRef.current = null;
    };
  // timeframe in deps: bucket size changes → all marker times must be recalculated
  // alpacaFills in deps: orphan sell markers need to re-render when fill history loads
  // showSwings/swingTick: re-merge swing structure markers when toggled or recomputed
  // showWatcherReplay/watcherReplay: re-merge replay markers when toggled or data arrives
  }, [alerts, chartTimeMs, timeframe, alpacaFills, showSwings, swingTick, showWatcherReplay, watcherReplay, newsMarkerVersion, hiddenMarkers]);

  // ── News markers: fetch /api/news (Redis-cached 30 min) and bucket each article's publish time
  // to the current timeframe, then bump newsMarkerVersion so the markers effect re-merges. Refetch
  // on timeframe change is cheap (cache hit); bucketing is what actually changes per TF. ──────────
  useEffect(() => {
    if (!symbol) { newsMarkersRef.current = []; return; }
    let alive = true;
    fetch(`/api/news?symbol=${encodeURIComponent(symbol)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!alive) return;
        const tfSec = (TIMEFRAMES.find((t) => t.value === timeframe)?.minutes ?? 1) * 60;
        const byBucket = new Map<number, { sentiment: string | null; count: number }>();
        const articlesByBucket = new Map<number, HoverNews[]>();
        for (const a of (j?.articles ?? []) as { publishedUtc?: string; sentiment?: string | null; title?: string; url?: string; publisher?: { name?: string } }[]) {
          const ms = a.publishedUtc ? new Date(a.publishedUtc).getTime() : NaN;
          if (!Number.isFinite(ms)) continue;
          const bucket = Math.floor(Math.floor(ms / 1000) / tfSec) * tfSec;
          const ex = byBucket.get(bucket);
          if (ex) ex.count += 1;
          else byBucket.set(bucket, { sentiment: a.sentiment ?? null, count: 1 });
          const arr = articlesByBucket.get(bucket) ?? [];
          if (arr.length < 4) arr.push({ title: a.title ?? '', publisher: a.publisher?.name ?? '', url: a.url ?? '', sentiment: a.sentiment ?? null });
          articlesByBucket.set(bucket, arr);
        }
        newsByTimeRef.current = articlesByBucket;
        // News is rendered as crisp DOM-overlay newspaper icons (see newsPins render) with their own hover —
        // NOT lightweight-charts markers. Keep the LWC news markers empty so there's exactly one clean icon
        // per bucket (no blue dot + emoji double-up) and the hover can't miss on a candle-less minute.
        newsMarkersRef.current = [];
        setNewsPins([...byBucket.entries()].map(([bucket, info]) => ({ bucket, articles: articlesByBucket.get(bucket) ?? [], count: info.count, sentiment: info.sentiment })));
        setNewsMarkerVersion((v) => v + 1);
      })
      .catch(() => { /* leave markers as-is on error */ });
    return () => { alive = false; };
  }, [symbol, timeframe]);

  // ── Reddit post pins: fetch the Top-Play posts we published for this ticker. Timeframe-independent
  // (the pin snaps to whatever bars are loaded), so this only refetches on symbol change. ───────────
  useEffect(() => {
    if (!symbol) { setRedditPosts([]); return; }
    let alive = true;
    fetch(`/api/stocks/reddit-posts?symbol=${encodeURIComponent(symbol)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!alive) return;
        setRedditPosts(Array.isArray(j?.posts) ? (j.posts as RedditPost[]) : []);
      })
      .catch(() => { if (alive) setRedditPosts([]); });
    return () => { alive = false; };
  }, [symbol]);

  // Keep the latest alerts in a ref so the crosshair handler (subscribed once) reads current data.
  useEffect(() => { alertsHoverRef.current = alerts; }, [alerts]);

  // Fetch the per-symbol EMA-trend matrix once, and only when this symbol actually has ema_trending alerts
  // (so names that never trended never hit the endpoint). Powers the hover-tooltip trend breakdown.
  useEffect(() => {
    if (!symbol) { emaMatrixSymRef.current = null; setEmaTrendMatrix(null); return; }
    if (emaMatrixSymRef.current === symbol) return; // already fetched for this symbol
    const hasEmaTrend = alerts.some((a) => String(a.type || '').toLowerCase().startsWith('ema_trend'));
    if (!hasEmaTrend) return;
    emaMatrixSymRef.current = symbol;
    let alive = true;
    fetch(`/api/ema-trending/symbol?symbol=${encodeURIComponent(symbol)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!alive) return;
        if (j && Array.isArray(j.tfs)) setEmaTrendMatrix({ symbol: String(j.symbol || symbol), tfs: j.tfs as EmaTfRow[] });
      })
      .catch(() => { emaMatrixSymRef.current = null; /* allow a retry on next alert update */ });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, alerts]);

  // ── Hover tooltip: article(s) + scanner alert(s) at the crosshaired bar ──────────────────────────
  // Hover the 📰 news icon (or any alert marker) and read what happened on that candle — best-in-class.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const alertMeta = (t: string): { label: string; color: string } => {
      if (t === 'rvol') return { label: 'RVOL', color: '#10b981' };
      if (t === 'mover') return { label: 'Mover', color: '#eab308' };
      if (t === 'ema' || t === 'ema_near') return { label: 'Near 200 EMA', color: '#a855f7' };
      if (t === 'ema_cross_above') return { label: 'EMA cross up', color: '#22d3ee' };
      if (t === 'ema_cross_below') return { label: 'EMA cross down', color: '#f97316' };
      if (t === 'ema_trending' || t.startsWith('ema_trend')) return { label: 'EMA trending', color: '#818cf8' };
      if (t === 'halt') return { label: 'Trading halt', color: '#ef4444' };
      if (t === 'resume') return { label: 'Trading resumed', color: '#22c55e' };
      return { label: t.replace(/_/g, ' ') || 'Alert', color: '#a1a1aa' };
    };
    const handler = (param: { time?: unknown; point?: unknown }) => {
      const t = param?.time as number | undefined;
      if (t == null || !param.point) { setHoverInfo(null); return; }
      const tfSec = (TIMEFRAMES.find((x) => x.value === timeframeRef.current)?.minutes ?? 1) * 60;
      const bucket = Math.floor(Number(t) / tfSec) * tfSec;
      const news = newsByTimeRef.current.get(bucket) ?? [];
      const alertsAt: HoverAlert[] = [];
      for (const a of alertsHoverRef.current) {
        if (isTradeAlert(a)) continue;
        const type = String(a.type || '').toLowerCase();
        if (type === 'news') continue;
        const abucket = Math.floor(Math.floor(chartTimeMs(a) / 1000) / tfSec) * tfSec;
        if (abucket !== bucket) continue;
        const m = alertMeta(type);
        alertsAt.push({
          label: m.label, color: m.color,
          price: typeof a.data?.price === 'number' ? a.data.price : null,
          rvol: typeof a.data?.rvol === 'number' ? a.data.rvol : null,
          pct: typeof a.data?.percentChange === 'number' ? a.data.percentChange : null,
        });
        if (alertsAt.length >= 6) break;
      }
      if (news.length === 0 && alertsAt.length === 0) { setHoverInfo(null); return; }
      setHoverInfo({ news, alerts: alertsAt });
    };
    chart.subscribeCrosshairMove(handler);
    return () => { try { chart.unsubscribeCrosshairMove(handler); } catch { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartsReady, symbol]);

  // ── The Watcher REPLAY: fetch on enable ──────────────────────────────────────
  // Hits /api/watcher/replay (server runs replayWatcher — the SAME strategy the live
  // trader uses). Fetched once per symbol when the layer is first enabled.
  useEffect(() => {
    if (!showWatcherReplay) return;
    if (watcherReplay && watcherReplay.symbol === symbol && (watcherReplay.book ?? 'live') === watcherReplayBook) return; // cached for this symbol+book
    let cancelled = false;
    setWatcherReplayLoading(true);
    fetch(`/api/watcher/condition-replay?symbol=${encodeURIComponent(symbol)}&book=${watcherReplayBook}`)
      .then((r) => r.json())
      .then((data: WatcherReplayResponse) => {
        if (cancelled) return;
        setWatcherReplay(data);
      })
      .catch((e) => { if (!cancelled) console.warn('[WATCHER_REPLAY] fetch failed:', e); })
      .finally(() => { if (!cancelled) setWatcherReplayLoading(false); });
    return () => { cancelled = true; };
  }, [showWatcherReplay, symbol, watcherReplay, watcherReplayBook]);

  // ── The Watcher REPLAY: build markers + connector lines (or tear them down) ───
  // Markers are stored in a ref (merged into the markers effect, which we bump via
  // watcherReplay being in its dep array). Connector LineSeries are owned here and
  // removed cleanly on disable / symbol change / unmount.
  useEffect(() => {
    const removeLines = () => {
      for (const s of watcherReplayLinesRef.current) {
        try { chartRef.current?.removeSeries(s); } catch { /* series already gone */ }
      }
      watcherReplayLinesRef.current = [];
    };

    // OFF (or no data yet): clear markers + lines.
    if (!showWatcherReplay || !watcherReplay || watcherReplay.symbol !== symbol) {
      watcherReplayMarkersRef.current = [];
      removeLines();
      return;
    }

    const dateToChartTime = replayDateToChartTime;

    // Canvas arrow markers were replaced by the DOM icon badges (hover-capable, less
    // clutter). This effect now owns ONLY the entry→exit connector lines — and only for
    // trades inside the loaded chart window (the daily replay spans ~320 days).
    const idxSet = new Set(replayInWindow.idx);
    removeLines(); // rebuild lines from scratch

    watcherReplay.trades.forEach((tr, i) => {
      if (!idxSet.has(i)) return;
      const entryT = dateToChartTime(tr.entryDate);
      if (entryT == null) return;
      // Exit marker + connector line (only for closed trades).
      if (!tr.open && tr.exitDate && tr.exitPrice != null && tr.plPct != null) {
        const exitT = dateToChartTime(tr.exitDate);
        const win = tr.plPct > 0;
        if (exitT != null) {
          // Connector line entry→exit (green if profit, red if loss). Solid + brighter
          // than before so the round-trip path is visible against candles.
          if (chartRef.current && entryT < exitT) {
            try {
              const line = chartRef.current.addLineSeries({
                color: win ? 'rgba(52,211,153,0.7)' : 'rgba(248,113,113,0.7)',
                lineWidth: 2, lineStyle: LineStyle.Dashed,
                priceLineVisible: false, lastValueVisible: false,
                crosshairMarkerVisible: false,
                autoscaleInfoProvider: () => null, // never let the sim line rescale the chart
              });
              line.setData([
                { time: entryT as never, value: tr.entryPrice },
                { time: exitT as never, value: tr.exitPrice },
              ]);
              watcherReplayLinesRef.current.push(line);
            } catch { /* addLineSeries can throw if chart is mid-teardown */ }
          }
        }
      }
    });

    watcherReplayMarkersRef.current = []; // canvas markers retired — DOM badges are the icons
    // Nudge the markers effect to re-merge (watcherReplay is in its deps, so setting
    // the ref here + the state dep already covers it; this assignment is the payload).

    return () => { removeLines(); };
  }, [showWatcherReplay, watcherReplay, symbol, replayInWindow]);

  // Pair buy + sell alerts for completed-trade visualization
  // ── Trade pair type: supports pyramid (multiple buys before one sell) ────────
  type TradePair = {
    /** All BUY alerts in chronological order (could be > 1 for pyramid entries). */
    buyAlerts: Alert[];
    /** Convenience: first buy (for backward compat / timestamp display). */
    buyAlert: Alert;
    sellAlert: Alert;
    sellMeta: TradeMeta | null;
    /** Scale-out (partial) SELL alerts inside the trade's lifetime — journal-backed pairs
     *  register these so hovering ANY partial shows the full trade card (LUCY 2026-07-08:
     *  the 9:04 scaleout_resistance marker was an orphan with no card). */
    partialSellAlerts?: Alert[];
    /** Weighted-average entry price across all buy alerts' entryPrice/shares. */
    avgEntryPrice: number | null;
    /** Sum of all buy shares (for P&L denominator). */
    totalBuyShares: number | null;
    /** 'ra' | 'thoth' | 'osiris' | 'watcher' */
    strategy: 'ra' | 'thoth' | 'osiris' | 'watcher';
  };

  const alertStrategy = (a: Alert): 'ra' | 'thoth' | 'osiris' | 'watcher' =>
    a.type === 'ra' || a.data?.triggeredBy === 'ra' ? 'ra'
    : a.type === 'osiris' || a.data?.triggeredBy === 'osiris' ? 'osiris'
    // The Rider IS the Watcher persona (new paper account) — its alerts must join the Watcher pairing pool, or
    // every Rider round-trip orphans (exit shows the bare marker hover, no entry link).
    : a.type === 'watcher' || a.type === 'rider' || a.data?.triggeredBy === 'watcher' || a.data?.triggeredBy === 'rider' ? 'watcher'
    : 'thoth';

  type OpenTradePair = {
    buyAlerts: Alert[];
    buyAlert: Alert;
    /** Scale-outs already taken while the position is still open (see TradePair). */
    partialSellAlerts?: Alert[];
    avgEntryPrice: number | null;
    totalBuyShares: number | null;
    strategy: 'ra' | 'thoth' | 'osiris' | 'watcher';
  };

  // ── JOURNAL-BACKED Watcher trade pairing (2026-07-08) ─────────────────────────
  // trading_journal is ground truth for WHAT Watcher trades exist; the alert stream only
  // supplies marker anchors. Each journal trade claims its book's buy alerts inside the
  // trade's lifetime (±15 min tolerance) and the sell alert nearest its exit; a missing
  // leg gets a synthesized marker (same pattern as fill-exits). This replaces the alert
  // archaeology whose five failure flavors we fixed one-by-one on 2026-07-07. null →
  // journal unavailable → the legacy alert pipeline below stays fully authoritative.
  const watcherJournalView = useMemo(() => {
    if (!journalTrades) return null;
    const TOL = 15 * 60 * 1000;
    const watcherAlerts = alerts
      .filter(a => isTradeAlert(a) && alertStrategy(a) === 'watcher')
      .sort((a, b) => (a.barTimestamp ?? a.timestamp) - (b.barTimestamp ?? b.timestamp));
    // Book of an alert = its meta.instance ('Rider' | 'Watcher' | 'WatcherPaper'), matched against the journal
    // trade's ai_name. Rider alerts carry instance='Rider' and must pair with ai_name='Rider' journal rows;
    // legacy/absent instance falls back to 'Watcher'.
    const bookOf = (a: Alert) => parseTradeMeta(a)?.instance || 'Watcher';
    // Pair on ENGINE, not ai_name. Since the consolidation every journal row is ai_name='Watcher'
    // (99 of 99 recent rows are ai_name='Watcher' / engine='Rider'), so the old
    // `bookOf(a) !== jt.aiName` test could never be true for a Rider alert. The result was NOT a
    // visible error: every trade fell through to a SYNTHETIC marker while the real buy/sell alerts
    // stayed unclaimed and rendered as separate one-leg "positions" — the same fill shown twice, once
    // as "The Watcher" and once as "The Watcher AI", one CLOSED and one OPEN. Falls back to aiName so
    // pre-consolidation rows (engine absent) still pair exactly as before.
    const journalBookOf = (jt: WatcherJournalTrade) => jt.engine || jt.aiName;
    const used = new Set<string>();
    const pairs: TradePair[] = [];
    const openPairs: OpenTradePair[] = [];
    const synthetic: Alert[] = [];
    const sorted = [...journalTrades].sort((a, b) => a.entryMs - b.entryMs);
    for (const jt of sorted) {
      const endMs = jt.exitMs ?? Infinity;
      const buys = watcherAlerts.filter(a => {
        if (used.has(a.id)) return false;
        const m = parseTradeMeta(a);
        if ((m?.side ?? 'buy') !== 'buy') return false;
        if (bookOf(a) !== journalBookOf(jt)) return false;
        const ms = a.barTimestamp ?? a.timestamp;
        return ms >= jt.entryMs - TOL && ms <= endMs + 60_000;
      });
      buys.forEach(b => used.add(b.id));
      let buyAlerts = buys;
      if (buyAlerts.length === 0) {
        const synth = makeJournalTradeAlert(symbol, jt, 'buy');
        synthetic.push(synth);
        buyAlerts = [synth];
      }
      // ALL of this book's sells inside the trade's lifetime belong to this trade —
      // the one nearest the journal exit is the canonical EXIT, the rest are SCALE-OUTS
      // (the Watcher's standardized exit takes partials at resistance; v1 left them as
      // orphan markers with no hover card — LUCY 9:04 scaleout_resistance, 2026-07-08).
      const lifetimeSells = watcherAlerts.filter(a => {
        if (used.has(a.id)) return false;
        const m = parseTradeMeta(a);
        if (m?.side !== 'sell') return false;
        if (bookOf(a) !== journalBookOf(jt)) return false;
        const ms = a.barTimestamp ?? a.timestamp;
        return ms >= jt.entryMs - 60_000 && ms <= (jt.exitMs ?? Infinity) + TOL;
      });
      if (jt.status === 'closed' && jt.exitMs != null && (jt.exitPrice ?? 0) > 0) {
        let sellAlert: Alert | null = null;
        let bestD = Infinity;
        for (const c of lifetimeSells) {
          const d = Math.abs((c.barTimestamp ?? c.timestamp) - jt.exitMs);
          if (d < bestD) { bestD = d; sellAlert = c; }
        }
        if (sellAlert && bestD > TOL) sellAlert = null; // nothing near the real exit — synthesize
        const partialSellAlerts = lifetimeSells.filter(c => c !== sellAlert);
        partialSellAlerts.forEach(c => used.add(c.id));
        if (sellAlert) { used.add(sellAlert.id); }
        else { sellAlert = makeJournalTradeAlert(symbol, jt, 'sell'); synthetic.push(sellAlert); }
        // Journal numbers are authoritative in the hover card even when a real alert anchors it.
        // UMBRELLA TRADE (owner): the pair's exit is the volume-weighted BLENDED average across the banked
        // scale-out legs + the runner's final fill — one trade, avg in -> avg out, performance from the blend.
        // 🚨 jt.shares IS THE TOTAL POSITION, NOT THE RUNNER. This read `bq + jt.shares`, which
        // double-counts the banked leg: ZBAO 2026-07-31 held 67 shares (43 banked + 24 runner) and the
        // card showed 110, turning a real +$8.14 into a displayed +$12.64 — ~55% overstated, on every
        // scaled trade. `banked_qty` comes from watcher_positions where `shares` is the REMAINDER;
        // `trading_journal.shares` is the whole round trip. Mixing the two tables' meanings was the bug.
        const bq = jt.bankedQty ?? 0, bp = jt.bankedProceeds ?? 0;
        const totalQty = jt.shares;                       // the journal already counts every leg
        const runnerQty = Math.max(0, totalQty - bq);      // what was left for the final fill
        const blendedExit = bq > 0 && totalQty > 0 ? (bp + jt.exitPrice! * runnerQty) / totalQty : jt.exitPrice!;
        // PREFER THE STORED RESULT. The journal books the round trip; re-deriving it here is how the
        // two numbers drifted apart in the first place. Local maths stays only as the fallback for a
        // row the journal has not settled yet.
        const plPct = jt.realizedPlPct ?? (jt.avgCost > 0 ? ((blendedExit - jt.avgCost) / jt.avgCost) * 100 : null);
        const sellMeta: TradeMeta = {
          ...(parseTradeMeta(sellAlert) ?? {}),
          side: 'sell',
          instance: jt.engine || jt.aiName,
          entryPrice: jt.avgCost,
          exitPrice: blendedExit,
          shares: totalQty,
          realizedPl: jt.realizedPl ?? (jt.avgCost > 0 && totalQty > 0 ? (blendedExit - jt.avgCost) * totalQty : parseTradeMeta(sellAlert)?.realizedPl),
          realizedPlPct: plPct ?? undefined,
          reason: jt.exitReason ?? parseTradeMeta(sellAlert)?.reason,
          bankedQty: bq > 0 ? bq : undefined,
          bankedAvg: bq > 0 ? bp / bq : undefined,
          // The RUNNER's shares, not the whole position. Showing jt.shares here printed "Runner x67"
          // on a trade whose runner was 24, which is what made the card's arithmetic look plausible.
          finalExitQty: bq > 0 ? runnerQty : undefined,
          finalExitPrice: bq > 0 ? jt.exitPrice! : undefined,
        };
        pairs.push({
          buyAlerts, buyAlert: buyAlerts[0], sellAlert, sellMeta, partialSellAlerts,
          avgEntryPrice: jt.avgCost > 0 ? jt.avgCost : null,
          totalBuyShares: jt.shares > 0 ? jt.shares : null,
          strategy: 'watcher',
        });
      } else {
        // OPEN trade: scale-outs already taken still belong to it (hover -> open card).
        lifetimeSells.forEach(c => used.add(c.id));
        openPairs.push({
          buyAlerts, buyAlert: buyAlerts[0], partialSellAlerts: lifetimeSells,
          avgEntryPrice: jt.avgCost > 0 ? jt.avgCost : null,
          totalBuyShares: jt.shares > 0 ? jt.shares : null,
          strategy: 'watcher',
        });
      }
    }
    return { pairs, openPairs, synthetic };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journalTrades, alerts, symbol]);

  // ── Fill-closed Ra exits (no sell ALERT) ─────────────────────────────────────
  // Ra's wave-exit / trailing-stop / blackout exits SELL on Alpaca but frequently
  // emit NO sell alert, so those buys never pair → no exit avatar, no hover P&L line.
  // Here we pair each leftover Ra buy-group (no matching sell alert) with the earliest
  // Alpaca SELL fill that occurs AFTER the latest buy. We synthesize a sell ALERT from
  // that fill so the trade flows through the SAME completed-pair render paths.
  // Returns Map<`${symbol}::ra`, { sellAlert, buyAlerts }> for the closed groups.
  const raFillExitsByKey = useMemo(() => {
    const out = new Map<string, { sellAlert: Alert; buyAlerts: Alert[] }>();
    if (alpacaFills.length === 0 && watcherFills.length === 0) return out;
    const tradeAlerts = alerts
      .filter(isTradeAlert)
      .sort((a, b) => (a.barTimestamp ?? a.timestamp) - (b.barTimestamp ?? b.timestamp));
    // Same accumulation as completedTradePairs: track the still-open buy-group per key.
    // A SELL alert closes the group ONLY when it's a real exit. A phantom Watcher sell
    // (sell alert with no matching Watcher fill) must NOT close the group — otherwise the
    // group looks closed here, no fill-exit is synthesized, and completedTradePairs (which
    // also keeps it open) ends up drawing no exit at all. So mirror the phantom check.
    const openBuys = new Map<string, Alert[]>();
    for (const alert of tradeAlerts) {
      const meta = parseTradeMeta(alert);
      const rawSide = meta?.side ?? 'buy';
      const strat = alertStrategy(alert);
      const side = strat === 'osiris' ? (rawSide === 'sell' ? 'buy' : 'sell') : rawSide;
      // Book-aware key: the live Watcher and the dev mirror share strat 'watcher' — a DEV
      // sell must never close a LIVE buy group (CMPS 2026-07-07: chart said closed, journal
      // correctly said open). Untagged legacy alerts default to the live book.
      const bookTag = strat === 'watcher' ? (isLiveWatcherBook(meta?.instance) ? '::live' : '::dev') : '';
      const key = `${alert.symbol}::${strat}${bookTag}`;
      if (side === 'buy') {
        if (!openBuys.has(key)) openBuys.set(key, []);
        openBuys.get(key)!.push(alert);
      } else if (side === 'sell') {
        if (strat === 'watcher' && watcherFills.length > 0) {
          const ms = alert.barTimestamp ?? alert.timestamp;
          const hasSellFill = watcherFills.some(f => {
            if (f.side !== 'sell') return false;
            const t = new Date(f.transactionTime).getTime();
            return Number.isFinite(t) && Math.abs(t - ms) <= 5 * 60 * 1000;
          });
          if (!hasSellFill) continue; // phantom — leave the buy group open
        }
        // legacy-untagged fallback (mirrors completedTradePairs — see CRNX note there)
        let delKey = key;
        if (strat === 'watcher' && bookTag === '::dev' && (openBuys.get(key)?.length ?? 0) === 0) {
          const legacyKey = `${alert.symbol}::watcher::live`;
          const lg = openBuys.get(legacyKey) ?? [];
          if (lg.length > 0 && lg.every(b => !parseTradeMeta(b)?.instance)) delKey = legacyKey;
        }
        openBuys.delete(delKey);
      }
    }
    // Per-strategy sell fills (Ra → alpacaFills, Watcher → watcherFills). Each fill is
    // claimed at most once. Synthesize a sell alert of the matching strategy so the exit
    // avatar (indigo Watcher image) + hover P&L line render identically to an alert-closed one.
    const sellFillsFor = (
      fills: Array<{ id: string; side: string; price: number; qty: number; transactionTime: string }>,
    ) => fills
      .filter(f => f.side === 'sell')
      .map(f => ({ ...f, ms: new Date(f.transactionTime).getTime() }))
      .filter(f => Number.isFinite(f.ms))
      .sort((a, b) => a.ms - b.ms);
    const raSellFills = sellFillsFor(alpacaFills);
    const watcherSellFills = sellFillsFor(watcherFills);
    const usedFillIds = new Set<string>();
    for (const [key, buyAlerts] of openBuys) {
      if (buyAlerts.length === 0) continue;
      // Ra → Alpaca fills; Watcher → Watcher CONFIRMED fills. Thoth/Osiris/Solomon aren't
      // Alpaca-live here, so they get no fill-based exit.
      // NOTE: watcher keys now carry a ::live/::dev book suffix (2026-07-07) — endsWith
      // silently stopped matching, killing fill-based Watcher exits. includes() is correct.
      const strat: 'ra' | 'watcher' | null = key.endsWith('::ra') ? 'ra'
        : key.includes('::watcher') ? 'watcher' : null;
      if (!strat) continue;
      // Journal-backed rendering owns ALL watcher pairs (incl. fill-closed ones) when loaded.
      if (strat === 'watcher' && (watcherJournalView || !journalSettled)) continue; // journal owns watcher/rider — never pair them from a half-loaded page
      const sellFills = strat === 'watcher' ? watcherSellFills : raSellFills;
      const lastBuyMs = Math.max(...buyAlerts.map(b => b.barTimestamp ?? b.timestamp));
      // Earliest unclaimed sell fill after the latest buy (60 s grace for clock skew).
      const fill = sellFills.find(f => !usedFillIds.has(f.id) && f.ms >= lastBuyMs - 60_000);
      if (!fill) continue;
      usedFillIds.add(fill.id);
      out.set(key, {
        sellAlert: makeFillExitAlert(buyAlerts[0].symbol, fill, fill.ms, strat),
        buyAlerts,
      });
    }
    return out;
  }, [alerts, alpacaFills, watcherFills, watcherJournalView, journalSettled]);

  // Synthetic sell alerts for fill-closed Ra exits — threaded into the avatar map,
  // hover-line lookup, and x-position computation so the exit renders like a real one.
  const fillExitSellAlerts = useMemo(
    () => Array.from(raFillExitsByKey.values()).map(v => v.sellAlert),
    [raFillExitsByKey],
  );

  // Real alerts + synthetic fill-exit sells. Use this (NOT `alerts`) for the marker
  // overlay map, hover-line lookup, and trade-X-position computation so a fill-closed
  // exit gets an avatar + x-coordinate exactly like an alert-closed one. Everything
  // else (alert lists, counts, WS handling) keeps using the raw `alerts` state.
  const tradeAlertsForRender = useMemo<Alert[]>(() => {
    // Synthetic journal markers outside the loaded candle window have no bar to anchor to —
    // rendering them produced edge-clamped drifting ghosts. The pair still exists (hover on
    // the in-window leg shows the card with the off-screen connector tag); only the marker
    // itself is suppressed.
    const firstMs = candles.length ? candles[0].timestamp : null;
    const lastMs = candles.length ? candles[candles.length - 1].timestamp + 6 * 60_000 : null;
    const synth = (watcherJournalView?.synthetic ?? []).filter(sa => {
      if (firstMs == null) return false;
      const ms = sa.barTimestamp ?? sa.timestamp;
      return ms >= firstMs && (lastMs == null || ms <= lastMs);
    });
    const extra = [...fillExitSellAlerts, ...synth];
    return extra.length ? [...alerts, ...extra] : alerts;
  }, [alerts, fillExitSellAlerts, watcherJournalView, candles]);

  const completedTradePairs = useMemo<TradePair[]>(() => {
    const tradeAlerts = alerts
      .filter(isTradeAlert)
      .sort((a, b) => (a.barTimestamp ?? a.timestamp) - (b.barTimestamp ?? b.timestamp));
    const pairs: TradePair[] = [];
    // Accumulate ALL buys per (symbol::strategy) — pyramid entries each push into the array
    const openBuys = new Map<string, Alert[]>();
    for (const alert of tradeAlerts) {
      const meta = parseTradeMeta(alert);
      const rawSide = meta?.side ?? 'buy';
      const strat = alertStrategy(alert);
      // Member game trades are NEVER round-trip-paired here: alertStrategy() has no member bucket
      // (it would fall to 'thoth'), so pairing would cross-contaminate a member's buy/sell with
      // Thoth's — and across DIFFERENT members (the key has no user id). Each member leg renders as
      // its own standalone marker; a member SELL still shows realized P&L from its own trade meta.
      if (isMemberTradeAlert(alert)) continue;
      // Journal-backed rendering (2026-07-08): when the journal has loaded, it is the ONLY
      // source of watcher pairs — watcher alerts are just marker anchors, never paired here.
      if (strat === 'watcher' && (watcherJournalView || !journalSettled)) continue; // journal owns watcher/rider — never pair them from a half-loaded page
      // Osiris is a short strategy: SHORT (sell) = entry, COVER (buy) = exit — flip so pairing works like Ra/Thoth
      const side = strat === 'osiris' ? (rawSide === 'sell' ? 'buy' : 'sell') : rawSide;
      // Book-aware key: the live Watcher and the dev mirror share strat 'watcher' — a DEV
      // sell must never close a LIVE buy group (CMPS 2026-07-07: chart said closed, journal
      // correctly said open). Untagged legacy alerts default to the live book.
      const bookTag = strat === 'watcher' ? (isLiveWatcherBook(meta?.instance) ? '::live' : '::dev') : '';
      const key = `${alert.symbol}::${strat}${bookTag}`;
      if (side === 'buy') {
        if (!openBuys.has(key)) openBuys.set(key, []);
        openBuys.get(key)!.push(alert);
      } else if (side === 'sell') {
        // Phantom Watcher SELL (premature-exit bug): a Watcher sell ALERT with no matching
        // Alpaca sell FILL must NOT close the position — Alpaca still holds it. Skip pairing so
        // the buy group stays open and no fake exit/round-trip is drawn. (Only suppress once
        // watcherFills has loaded, so first paint doesn't hide a genuinely-closed trade.)
        if (strat === 'watcher' && watcherFills.length > 0) {
          const ms = alert.barTimestamp ?? alert.timestamp;
          const hasSellFill = watcherFills.some(f => {
            if (f.side !== 'sell') return false;
            const t = new Date(f.transactionTime).getTime();
            return Number.isFinite(t) && Math.abs(t - ms) <= 5 * 60 * 1000;
          });
          if (!hasSellFill) continue; // phantom — leave buys open
        }
        // LEGACY FALLBACK (CRNX 2026-07-07): pre-2026-07-03 alerts carry no instance tag
        // and default to the live book, so a DEV exit found no dev-tagged buys and rendered
        // as an orphan (no pair = no full hover card). Pair the dev sell against an
        // ALL-UNTAGGED live-keyed group; tagged live buys are never consumed by a dev sell.
        let buyAlerts = openBuys.get(key) ?? [];
        let usedKey = key;
        if (buyAlerts.length === 0 && strat === 'watcher' && bookTag === '::dev') {
          const legacyKey = `${alert.symbol}::watcher::live`;
          const legacyGroup = openBuys.get(legacyKey) ?? [];
          if (legacyGroup.length > 0 && legacyGroup.every(b => !parseTradeMeta(b)?.instance)) {
            buyAlerts = legacyGroup;
            usedKey = legacyKey;
          }
        }
        if (buyAlerts.length > 0) {
          // Weighted-average entry price across all pyramid buys
          let totalCost = 0, totalSh = 0, unknownLeg = false;
          for (const b of buyAlerts) {
            const bm = parseTradeMeta(b);
            const bSh = bm?.shares ?? 0;
            const bPx = bm?.entryPrice ?? b.data?.price ?? 0;
            if (bSh > 0 && bPx > 0) { totalCost += bPx * bSh; totalSh += bSh; } else unknownLeg = true;
          }
          pairs.push({
            buyAlerts,
            buyAlert: buyAlerts[0],
            sellAlert: alert,
            sellMeta: meta,
            avgEntryPrice: totalSh > 0 && !unknownLeg ? totalCost / totalSh : null,
            totalBuyShares: totalSh > 0 ? totalSh : null,
            strategy: strat,
          });
          openBuys.delete(usedKey);
        }
      }
    }
    // Fill-closed exits: leftover Ra/Watcher buy-groups paired with a broker sell FILL
    // that emitted no sell alert (Ra wave-exit/trail/blackout, or a Watcher exit whose
    // sell alert is missing/dropped). Build the SAME TradePair shape (synthetic sell alert)
    // so the exit avatar + hover P&L line render identically to an alert-closed trade.
    // strategy comes from the synthetic alert so a Watcher exit pairs as a Watcher trade.
    for (const [, { sellAlert, buyAlerts }] of raFillExitsByKey) {
      // NEVER average over a SUBSET of the legs (owner: SXTC showed "Avg $0.0890 · 2x entries" when the
      // share-weighted answer was $0.0836). The old guard skipped any leg whose alert meta carried no
      // `shares`, dropping it from BOTH the numerator and the denominator — so the average silently became
      // "the legs we happened to know about" while the Init/Add rows below still rendered the missing leg
      // from its FILL. Two different sources, one of them labelled "Avg". Now a leg we cannot weigh marks
      // the whole average unreliable and we return null, so the card falls back to the fill-resolved
      // average (which always has qty) or shows nothing — never a confident wrong number.
      let totalCost = 0, totalSh = 0, unknownLeg = false;
      for (const b of buyAlerts) {
        const bm = parseTradeMeta(b);
        const bSh = bm?.shares ?? 0;
        const bPx = bm?.entryPrice ?? b.data?.price ?? 0;
        if (bSh > 0 && bPx > 0) { totalCost += bPx * bSh; totalSh += bSh; } else unknownLeg = true;
      }
      pairs.push({
        buyAlerts,
        buyAlert: buyAlerts[0],
        sellAlert,
        sellMeta: parseTradeMeta(sellAlert),
        avgEntryPrice: totalSh > 0 && !unknownLeg ? totalCost / totalSh : null,
        totalBuyShares: totalSh > 0 ? totalSh : null,
        strategy: alertStrategy(sellAlert),
      });
    }
    // Ground-truth watcher pairs (journal-backed) — appended after the legacy pipeline so
    // trade numbering stays stable for non-watcher strategies.
    if (watcherJournalView) pairs.push(...watcherJournalView.pairs);
    return pairs;
  }, [alerts, raFillExitsByKey, watcherFills, watcherJournalView, journalSettled]);

  // Fast lookup: alertId → its completed pair (for tooltip + partner highlighting)
  const completedPairByAlertId = useMemo(() => {
    const map = new Map<string, { pair: TradePair; tradeNum: number }>();
    completedTradePairs.forEach((pair, i) => {
      // Register ALL buy alerts so hovering any pyramid entry highlights the whole group
      for (const b of pair.buyAlerts) map.set(b.id, { pair, tradeNum: i + 1 });
      map.set(pair.sellAlert.id, { pair, tradeNum: i + 1 });
      // Scale-outs: hovering a partial exit shows the SAME trade card.
      for (const ps of pair.partialSellAlerts ?? []) map.set(ps.id, { pair, tradeNum: i + 1 });
    });
    return map;
  }, [completedTradePairs]);

  // Open trade pairs — buy alerts with no sell yet (current live positions).
  // Same accumulation logic as completedTradePairs but returns the leftovers.
  // (OpenTradePair type hoisted above the journal view memo.)
  const openTradePairs = useMemo<OpenTradePair[]>(() => {
    const tradeAlerts = alerts
      .filter(isTradeAlert)
      .sort((a, b) => (a.barTimestamp ?? a.timestamp) - (b.barTimestamp ?? b.timestamp));
    const openBuysMap = new Map<string, Alert[]>();
    for (const alert of tradeAlerts) {
      const meta = parseTradeMeta(alert);
      const rawSide = meta?.side ?? 'buy';
      const strat = alertStrategy(alert);
      // Member game trades never open a paired position here (see completedTradePairs) — each
      // member leg is a standalone marker, so it must not pool into a thoth-keyed open group.
      if (isMemberTradeAlert(alert)) continue;
      const side = strat === 'osiris' ? (rawSide === 'sell' ? 'buy' : 'sell') : rawSide;
      // Book-aware key: the live Watcher and the dev mirror share strat 'watcher' — a DEV
      // sell must never close a LIVE buy group (CMPS 2026-07-07: chart said closed, journal
      // correctly said open). Untagged legacy alerts default to the live book.
      const bookTag = strat === 'watcher' ? (isLiveWatcherBook(meta?.instance) ? '::live' : '::dev') : '';
      const key = `${alert.symbol}::${strat}${bookTag}`;
      if (strat === 'watcher' && (watcherJournalView || !journalSettled)) continue; // journal owns watcher/rider — never pair them from a half-loaded page
      if (side === 'buy') {
        if (!openBuysMap.has(key)) openBuysMap.set(key, []);
        openBuysMap.get(key)!.push(alert);
      } else if (side === 'sell') {
        // Phantom Watcher SELL: don't close an open position the broker still holds.
        if (strat === 'watcher' && watcherFills.length > 0) {
          const ms = alert.barTimestamp ?? alert.timestamp;
          const hasSellFill = watcherFills.some(f => {
            if (f.side !== 'sell') return false;
            const t = new Date(f.transactionTime).getTime();
            return Number.isFinite(t) && Math.abs(t - ms) <= 5 * 60 * 1000;
          });
          if (!hasSellFill) continue; // phantom — keep position open
        }
        openBuysMap.delete(key);
      }
    }
    // Drop any group that was closed by an Alpaca sell FILL (no sell alert) — it now
    // renders as a COMPLETED pair, so it must not also show as an open position.
    for (const key of raFillExitsByKey.keys()) openBuysMap.delete(key);
    const legacy = Array.from(openBuysMap.values()).map(buyAlerts => {
      let totalCost = 0, totalSh = 0, unknownLeg = false;
      for (const b of buyAlerts) {
        const bm = parseTradeMeta(b);
        const bSh = bm?.shares ?? 0;
        const bPx = bm?.entryPrice ?? b.data?.price ?? 0;
        if (bSh > 0 && bPx > 0) { totalCost += bPx * bSh; totalSh += bSh; } else unknownLeg = true;
      }
      return {
        buyAlerts,
        buyAlert: buyAlerts[0],
        avgEntryPrice: totalSh > 0 && !unknownLeg ? totalCost / totalSh : null,
        totalBuyShares: totalSh > 0 ? totalSh : null,
        strategy: alertStrategy(buyAlerts[0]),
      };
    });
    return watcherJournalView ? [...legacy, ...watcherJournalView.openPairs] : legacy;
  }, [alerts, raFillExitsByKey, watcherFills, watcherJournalView, journalSettled]);

  const openPairByAlertId = useMemo(() => {
    const map = new Map<string, OpenTradePair>();
    for (const op of openTradePairs) {
      for (const b of op.buyAlerts) map.set(b.id, op);
      for (const ps of op.partialSellAlerts ?? []) map.set(ps.id, op);
    }
    return map;
  }, [openTradePairs]);

  // TRADE NUMBERS (owner: "I can't tell how many trades happened. It's really confusing.").
  // Every round trip gets a chronological number that is printed ON its marker, and the ENTRY and the
  // EXIT of the same trade share it. That single change makes the chart countable at a glance — three
  // numbers means three trades — and makes a RE-ENTRY obvious, which is exactly where DFNS went wrong
  // three times in a row. Completed and open trades are numbered in one sequence so the numbers read
  // left-to-right across the chart regardless of which pipeline produced them.
  const tradeNumberByAlertId = useMemo(() => {
    const map = new Map<string, number>();
    const groups: Array<{ entryMs: number; ids: string[] }> = [];
    const msOf = (a: Alert) => a.barTimestamp ?? a.timestamp;
    for (const pr of completedTradePairs) {
      groups.push({
        entryMs: msOf(pr.buyAlert),
        ids: [...pr.buyAlerts.map(b => b.id), pr.sellAlert.id, ...(pr.partialSellAlerts ?? []).map(x => x.id)],
      });
    }
    for (const op of openTradePairs) {
      groups.push({
        entryMs: msOf(op.buyAlert),
        ids: [...op.buyAlerts.map(b => b.id), ...(op.partialSellAlerts ?? []).map(x => x.id)],
      });
    }
    groups.sort((a, b) => a.entryMs - b.entryMs);
    groups.forEach((g, i) => { for (const id of g.ids) map.set(id, i + 1); });
    return map;
  }, [completedTradePairs, openTradePairs]);

  // ── Alpaca fill resolution ──────────────────────────────────────────────────
  // Ra alerts store the SIGNAL price in TradeMeta (entryPrice = limit at submit,
  // exitPrice = trigger price), but the actual Alpaca fill differs slightly
  // (limit chase / slippage). Match each Ra trade alert to its real fill:
  // same side, nearest transaction_time within 30 min.
  // Core nearest-fill matcher over an arbitrary fills array (Ra's mixed cache, or the
  // Watcher's confirmed-fills subset). Factored out so Watcher markers can resolve against
  // watcherFills with identical matching semantics.
  const findFillIn = useCallback((
    fills: Array<{ id: string; side: string; price: number; qty: number; transactionTime: string }>,
    side: 'buy' | 'sell', alertMs: number, exclude?: Set<string>, beforeMs?: number,
  ) => {
    let best: { id: string; price: number; qty: number } | null = null;
    let bestDelta = Infinity;
    for (const f of fills) {
      if (f.side !== side) continue;
      if (exclude?.has(f.id)) continue;
      const t = new Date(f.transactionTime).getTime();
      if (!Number.isFinite(t)) continue;
      // beforeMs: only consider fills at/before this time (entry lookup from a sell
      // alert must not match a later re-entry buy). 60 s grace for clock skew.
      if (beforeMs != null && t > beforeMs + 60_000) continue;
      const d = Math.abs(t - alertMs);
      if (d < bestDelta) { bestDelta = d; best = { id: f.id, price: f.price, qty: f.qty }; }
    }
    // 5-min window: Alpaca fills happen within seconds of the alert (limit chase is fast).
    // 30 min was too wide — paper trades with no Alpaca execution matched wrong fills,
    // showing phantom P&L (e.g. the +19.5% fill appearing on an unrelated paper trade).
    return best && bestDelta <= 5 * 60 * 1000 ? best : null;
  }, []);

  const findAlpacaFill = useCallback((side: 'buy' | 'sell', alertMs: number, exclude?: Set<string>, beforeMs?: number) =>
    findFillIn(alpacaFills, side, alertMs, exclude, beforeMs), [findFillIn, alpacaFills]);

  // Watcher equivalent — resolves against the Watcher's CONFIRMED fills only. Returns null
  // (no marker price / no exit line) when no real fill backs the leg, which is exactly how a
  // phantom Watcher sell gets excluded.
  const findWatcherFill = useCallback((side: 'buy' | 'sell', alertMs: number, exclude?: Set<string>, beforeMs?: number) =>
    findFillIn(watcherFills, side, alertMs, exclude, beforeMs), [findFillIn, watcherFills]);

  // Strategy-aware dispatcher used by the marker/hover render gates (extends the old
  // `strategy === 'ra'` fill resolution to also cover Watcher).
  const findFillForStrategy = useCallback((strategy: string, side: 'buy' | 'sell', alertMs: number, exclude?: Set<string>, beforeMs?: number) =>
    strategy === 'ra' ? findAlpacaFill(side, alertMs, exclude, beforeMs)
    : strategy === 'watcher' ? findWatcherFill(side, alertMs, exclude, beforeMs)
    : null, [findAlpacaFill, findWatcherFill]);

  // ── Phantom Watcher SELL exclusion ───────────────────────────────────────────
  // The old premature-exit bug emitted Watcher SELL *alerts* with NO matching Alpaca sell
  // FILL (the position is actually still held — e.g. GHI/HSCS). Those must NOT render as
  // exits or pair against an entry, so the chart matches Alpaca. A Watcher sell alert is a
  // phantom iff no Watcher sell fill exists near it (and watcherFills is loaded — before the
  // fetch resolves we don't suppress anything, to avoid hiding real exits on first paint).
  const phantomWatcherSellIds = useMemo(() => {
    const ids = new Set<string>();
    if (watcherFills.length === 0) return ids; // nothing to compare against yet → suppress none
    for (const a of alerts) {
      const isWatcher = a.type === 'watcher' || a.data?.triggeredBy === 'watcher';
      if (!isWatcher) continue;
      const m = parseTradeMeta(a);
      if ((m?.side ?? 'buy') !== 'sell') continue;
      const ms = a.barTimestamp ?? a.timestamp;
      if (!findWatcherFill('sell', ms)) ids.add(a.id);
    }
    return ids;
  }, [alerts, watcherFills, findWatcherFill]);

  // Fill-resolved entry/exit for a completed Ra pair. Entry is the qty-weighted
  // average across pyramid buys (each buy alert claims its nearest unused fill).
  const alpacaPricesForPair = useCallback((pair: TradePair | null) => {
    if (!pair || (pair.strategy !== 'ra' && pair.strategy !== 'watcher')) return { entry: null as number | null, exit: null as number | null, qty: null as number | null };
    const used = new Set<string>();
    let cost = 0, sh = 0;
    for (const b of pair.buyAlerts) {
      const f = findFillForStrategy(pair.strategy, 'buy', b.barTimestamp ?? b.timestamp, used);
      if (f) { used.add(f.id); cost += f.price * f.qty; sh += f.qty; }
    }
    const sellFill = findFillForStrategy(pair.strategy, 'sell', pair.sellAlert.barTimestamp ?? pair.sellAlert.timestamp);
    return {
      entry: sh > 0 ? cost / sh : null,
      exit: sellFill?.price ?? null,
      qty: sh > 0 ? sh : (sellFill?.qty ?? null),
    };
  }, [findFillForStrategy]);

  // SNAP INDEX for marker x-anchoring: ET-day -> sorted bar-seconds of the ACTIVE-timeframe series. Built ONCE
  // per data/timeframe change. This used to be rebuilt INSIDE the scroll RAF (aggregate + a per-bar Intl date
  // call x ~4k bars x every frame) — that was the choppy, laggy panning (owner). Now the RAF only does the
  // cheap nearest-bar lookup.
  const barSnapIndex = useMemo(() => {
    const tfMin = TIMEFRAMES.find((t) => t.value === timeframe)?.minutes ?? 1;
    const series = tfMin > 1 ? aggregateCandles(candles, tfMin) : candles;
    const byDay = new Map<string, number[]>();
    for (const c of series) {
      const sec = Math.floor(c.timestamp / 1000);
      const key = etDayKey(c.timestamp);
      const arr = byDay.get(key);
      if (arr) arr.push(sec); else byDay.set(key, [sec]);
    }
    return byDay;
  }, [candles, timeframe]);

  // ── Trade overlay: compute x-positions for Thoth/Ra avatar markers ───────────
  // Called on alert change AND on chart scroll/zoom via subscribeVisibleTimeRangeChange.
  const computeTradeXPos = useCallback(() => {
    if (tradeXPosRafRef.current) cancelAnimationFrame(tradeXPosRafRef.current);
    tradeXPosRafRef.current = requestAnimationFrame(() => {
      const chart = chartRef.current;
      if (!chart) return;
      const ts = chart.timeScale();
      const pos: Record<string, number | null> = {};
      // Bucket each trade alert's bar time to the ACTIVE timeframe (not just the
      // minute): lightweight-charts 4.1.3 timeToCoordinate only resolves a time
      // that EXACTLY matches a loaded bar (findNearest=false). On a 5m/15m/etc
      // chart the series bars are TF-bucketed, so a minute-bucketed time never
      // matched → BOTH the entry AND exit avatars vanished on aggregated TFs.
      // Snapping to the TF bucket fixes entry+exit avatars symmetrically.
      const tfMinutesLive = TIMEFRAMES.find(t => t.value === timeframe)?.minutes ?? 1;
      const tfBucketMs = Math.max(1, tfMinutesLive) * 60 * 1000;
      // Visible plot width — timeToCoordinate returns a FINITE OFF-SCREEN x (negative, or past the width) for a bar
      // panned beyond the chart edge. Accepting any finite value let the avatar render OUTSIDE the chart, over the
      // sidebar, with its z-index on top (owner: while SCROLLING the Watcher icon + its ×N chip escape the frame and
      // stay on top of everything). Bounding x to the plot keeps off-screen markers HIDDEN, not spilled.
      const paneW = (() => { try { const w = ts.width(); return typeof w === 'number' && w > 0 ? w : (chartContainerRef.current?.clientWidth ?? 0); } catch { return chartContainerRef.current?.clientWidth ?? 0; } })();
      const EDGE = 24;   // allow a marker within ~a half-avatar of the edge to show; drop anything further off-screen
      // SNAP-TO-LOADED-BAR (owner: ADVB traded 5x on 07-24 and rendered ZERO icons). timeToCoordinate returns
      // null unless the time EXACTLY matches a loaded bar — and flooring to the TF bucket still misses whenever
      // that exact bucket has NO bar: a halt (ADVB halted repeatedly on its +36% day), a zero-print minute on a
      // thin name, a session boundary. Snap to the nearest loaded bar on the same ET day instead. The day index
      // is the memoized barSnapIndex (building it here, inside the scroll RAF, made panning choppy).
      const snapT = (secsIn: number): number | null => {
        const bars = barSnapIndex.get(etDayKey(secsIn * 1000));
        if (!bars || !bars.length) return null;
        let best = bars[0], bd = Math.abs(bars[0] - secsIn);
        for (const s of bars) { const d = Math.abs(s - secsIn); if (d < bd) { bd = d; best = s; } }
        return best;
      };
      for (const a of tradeAlertsForRender) {
        if (!isTradeAlert(a)) continue;
        const ms = a.barTimestamp ?? a.timestamp;
        const floored = Math.floor(ms / tfBucketMs) * tfBucketMs / 1000;
        const secs = snapT(floored) ?? floored;
        try {
          const x = ts.timeToCoordinate(secs as any);
          const onScreen = typeof x === 'number' && Number.isFinite(x) && x >= -EDGE && (paneW <= 0 || x <= paneW + EDGE);
          pos[a.id] = onScreen ? (x as number) : null;
        } catch { pos[a.id] = null; }
      }
      setTradeXPos(pos);

      // The Watcher replay: screen-x for each trade's entry + exit candle, so DOM
      // avatar overlays can sit on the right candle (same timeToCoordinate mechanism
      // as the real-trade markers above). Recomputed on every scroll/zoom too.
      if (showWatcherReplay && watcherReplay && watcherReplay.symbol === symbol) {
        const rpos: Record<number, { entry: number | null; exit: number | null }> = {};

        // ── EXIT-AVATAR FIX ──────────────────────────────────────────────────
        // replayDateToChartTime maps a daily MM/DD/YYYY to NOON-ET (16:00:00Z).
        // The MAIN chart is INTRADAY (1m–1h), so its time-scale points are
        // minute-bucketed candle timestamps — and lightweight-charts 4.1.3's
        // timeToCoordinate(time) returns null UNLESS `time` EXACTLY matches a
        // loaded bar's timestamp (TimeScale._timeToIndex with findNearest=false
        // returns null for any non-exact / out-of-range time). The entry noon
        // happened to coincide with a real RTH bar so it rendered; the exit noon
        // often did NOT (exit day past the right edge, or no 12:00 bar loaded) →
        // xp.exit was null. Fix: snap each replay date to the NEAREST loaded
        // candle on the SAME ET calendar day and feed THAT exact bar time to
        // timeToCoordinate — deterministic for both entry and exit.
        // Day -> bar-seconds map is the memoized barSnapIndex (was rebuilt per RAF frame — choppy panning).
        // Snap a replay noon-ET time (sec) to the closest loaded bar on the same
        // ET day. Returns null when that day isn't in the intraday window at all
        // (legitimately off-chart) — never invents a coordinate.
        const snapToLoadedBar = (secs: number | null): number | null => {
          if (secs == null) return null;
          const key = etDayKey(secs * 1000);
          const bars = barSnapIndex.get(key);
          if (!bars || bars.length === 0) return null;
          let best = bars[0];
          let bestDist = Math.abs(best - secs);
          for (let k = 1; k < bars.length; k++) {
            const d = Math.abs(bars[k] - secs);
            if (d < bestDist) { bestDist = d; best = bars[k]; }
          }
          return best;
        };
        const coord = (secs: number | null): number | null => {
          const snapped = snapToLoadedBar(secs);
          if (snapped == null) return null;
          try { const x = ts.timeToCoordinate(snapped as any); return typeof x === 'number' && Number.isFinite(x) ? x : null; }
          catch { return null; }
        };
        const inWinSet = new Set(replayInWindow.idx);
        watcherReplay.trades.forEach((tr, i) => {
          if (!inWinSet.has(i)) return; // outside the loaded window — nothing to place
          rpos[i] = {
            entry: coord(replayDateToChartTime(tr.entryDate)),
            exit: tr.exitDate ? coord(replayDateToChartTime(tr.exitDate)) : null,
          };
        });
        setReplayXPos(rpos);
      } else {
        setReplayXPos({});
      }

      // Reddit post pins: snap each post's minute to the NEAREST loaded bar (within a few buckets)
      // and resolve its pixel x. Posts whose minute isn't in the loaded window get null (no pin) —
      // never clamped to the first bar (the same clamp bug that hit the news markers).
      if (redditPins.length) {
        const tfMin = TIMEFRAMES.find((t) => t.value === timeframe)?.minutes ?? 1;
        const series = tfMin > 1 ? aggregateCandles(candles, tfMin) : candles;
        const maxDist = tfMin * 60 * 3; // within 3 buckets of a real bar, else off-chart
        const rp: Record<number, number | null> = {};
        if (series.length) {
          const secs = series.map((c) => Math.floor(c.timestamp / 1000));
          // Keyed by PIN index, matching the render below. One x per bucket, not per post.
          redditPins.forEach((pin, i) => {
            const psec = Math.floor(pin.posts[0].postedAt / 1000);
            let best = secs[0]; let bd = Math.abs(best - psec);
            for (let k = 1; k < secs.length; k++) { const d = Math.abs(secs[k] - psec); if (d < bd) { bd = d; best = secs[k]; } }
            if (bd > maxDist) { rp[i] = null; return; }
            try { const x = ts.timeToCoordinate(best as any); rp[i] = typeof x === 'number' && Number.isFinite(x) ? x : null; }
            catch { rp[i] = null; }
          });
        }
        setRedditXPos(rp);
      } else {
        setRedditXPos({});
      }

      // News pins: same nearest-loaded-bar snap as the reddit pins, so a newspaper icon sits over the
      // candle whose minute the article dropped on (null → off-chart, never clamped to the first bar).
      if (newsPins.length) {
        const tfMin = TIMEFRAMES.find((t) => t.value === timeframe)?.minutes ?? 1;
        const series = tfMin > 1 ? aggregateCandles(candles, tfMin) : candles;
        // News isn't tick-precise and often drops in a no-candle gap (overnight / pre-market). The hover
        // now works regardless of exact placement, so allow snapping to the nearest session-edge candle
        // within ~2h — a 2:30am headline lands on the 4:00am open bar instead of vanishing. Weekend-gap
        // news (>2h from any bar) still drops rather than piling on a boundary candle.
        const maxDist = Math.max(tfMin * 60 * 3, 2 * 3600);
        const np: Record<number, number | null> = {};
        if (series.length) {
          const secs = series.map((c) => Math.floor(c.timestamp / 1000));
          newsPins.forEach((pin, i) => {
            let best = secs[0]; let bd = Math.abs(best - pin.bucket);
            for (let k = 1; k < secs.length; k++) { const d = Math.abs(secs[k] - pin.bucket); if (d < bd) { bd = d; best = secs[k]; } }
            if (bd > maxDist) { np[i] = null; return; }
            try { const x = ts.timeToCoordinate(best as any); np[i] = typeof x === 'number' && Number.isFinite(x) ? x : null; }
            catch { np[i] = null; }
          });
        }
        setNewsXPos(np);
      } else {
        setNewsXPos({});
      }
    });
  // redditPins (not redditPosts): the x-position loop keys off PIN index now, and pins rebucket on a
  // timeframe change, so watching the raw posts would leave stale pixels after switching 1m -> 1D.
  }, [tradeAlertsForRender, showWatcherReplay, watcherReplay, symbol, candles, timeframe, replayInWindow, redditPins, newsPins, barSnapIndex]);

  // Update trade positions when alerts change OR the visible range changes (pan/zoom).
  // rangeVersion is bumped by the main chart's visible-range subscription so BOTH the live
  // trade avatars (tradeXPos) and the Watcher replay avatars (replayXPos) — both computed by
  // computeTradeXPos — re-resolve their pixel x against the new range and track the candles.
  useEffect(() => {
    computeTradeXPos();
    return () => { if (tradeXPosRafRef.current) cancelAnimationFrame(tradeXPosRafRef.current); };
  }, [computeTradeXPos, rangeVersion]);

  // Snap crosshair to hovered trade candle + show single Alpaca price line.
  // Only ONE line is drawn on the chart — the Alpaca fill (buy) or exit price (sell).
  // Paper/game entry prices are described in the tooltip text, not charted.
  useEffect(() => {
    // Clear all price lines on every hover change
    for (const ref of [hoverPriceLineRef, raGamePriceLineRef, thothGamePriceLineRef, raAlpacaPriceLineRef]) {
      if (ref.current && candleSeriesRef.current) {
        try { candleSeriesRef.current.removePriceLine(ref.current); } catch {}
        ref.current = null;
      }
    }
    if (!hoveredTradeId || !chartRef.current || !candleSeriesRef.current) return;
    const alert = tradeAlertsForRender.find(a => a.id === hoveredTradeId);
    if (!alert) return;
    const meta = parseTradeMeta(alert);
    const rawHoverSide = meta?.side ?? 'buy';
    const isOsirisHover = alert.type === 'osiris' || alert.data?.triggeredBy === 'osiris';
    // Osiris: SHORT (sell) is the entry; COVER (buy) is the exit — flip so hover logic matches Ra/Thoth
    const isBuy = isOsirisHover ? rawHoverSide === 'sell' : rawHoverSide === 'buy';
    const isRaHover     = alert.type === 'ra'     || alert.data?.triggeredBy === 'ra';
    const isThothHover  = alert.type === 'thoth'  || alert.data?.triggeredBy === 'thoth';
    const isWatcherHover = alert.type === 'watcher' || alert.data?.triggeredBy === 'watcher';
    // Strategy whose markers resolve to REAL Alpaca fills (Ra + Watcher). For these, the hover
    // line sits at the fill price and the labels read "Alpaca Entry/Exit".
    const isFillHover = isRaHover || isWatcherHover;
    const hoverStrategy = isRaHover ? 'ra' : isWatcherHover ? 'watcher' : '';
    // isOsirisHover already declared above (needed before isBuy)

    const ms = alert.barTimestamp ?? alert.timestamp;

    // For Ra/Watcher: meta.entryPrice/exitPrice are SIGNAL prices recorded at submit time —
    // the actual Alpaca fill differs slightly (limit chase / slippage). Resolve the real fill
    // for THIS trade (Ra → alpacaFills; Watcher → watcherFills); fall back to meta if no match.
    const rawHoverFill = isFillHover ? findFillForStrategy(hoverStrategy, rawHoverSide === 'buy' ? 'buy' : 'sell', ms) : null;
    const signalPx = isBuy ? (meta?.entryPrice ?? alert.data?.price) : (meta?.exitPrice ?? alert.data?.price);
    // The hover CARD shows the PAIR's authoritative prices — avg entry / journal exit (sellMeta.exitPrice). The
    // price LINE must equal the card, so anchor to those, NOT the hovered alert's own meta (which can be a SIGNAL
    // price) or a loose time-matched fill. The matcher isn't book-aware (Rider paper + live Watcher both map to
    // 'watcher'), so it grabbed a stray $9.19 fill and drew it as an NVVE exit the journal recorded at $7.55 —
    // flipping a −12.6% loss into a fake win (owner: line ≠ hover). Trust the fill ONLY when it's within tolerance
    // of the pair's authoritative price; otherwise use that authoritative price (= the Alpaca paper fill).
    const hvPair = completedPairByAlertId.get(alert.id)?.pair ?? null;
    const authPx = hvPair ? (isBuy ? (hvPair.avgEntryPrice ?? null) : (hvPair.sellMeta?.exitPrice ?? null)) : null;
    const refPx = (typeof authPx === 'number' && authPx > 0) ? authPx : signalPx;
    const hoverFill = (rawHoverFill && typeof refPx === 'number' && refPx > 0
      && Math.abs(rawHoverFill.price - refPx) / refPx <= 0.12) ? rawHoverFill : null;
    const tradePrice = hoverFill?.price ?? refPx;

    const alpacaLinePrice = tradePrice;

    // Snap crosshair to the bar at trade price
    const secs = Math.floor(ms / 1000 / 60) * 60;
    try {
      if (typeof alpacaLinePrice === 'number' && alpacaLinePrice > 0) {
        chartRef.current.setCrosshairPosition(alpacaLinePrice, secs as any, candleSeriesRef.current);
      }
    } catch {}

    // Draw price lines on hover:
    //   Ra buy  → cyan  "Alpaca Entry $X.XX" (meta.entryPrice = Alpaca fill for that trade)
    //   Ra sell → orange "Alpaca Exit $X.XX" + cyan "Alpaca Entry $X.XX"
    //   Thoth/Osiris buy  → amber "Entry" at signal price
    //   Thoth/Osiris sell → rose  "Exit"  + amber "Entry"
    // For Watcher, only treat as a real Alpaca leg when a fill actually backed it (hoverFill).
    // A phantom Watcher sell has no fill → falls through to the signal price / generic label.
    const hasAlpacaEntry = (isRaHover || (isWatcherHover && hoverFill != null)) && typeof tradePrice === 'number' && tradePrice > 0;
    // tradePrice is already meta.exitPrice for sell alerts — use it directly rather than
    // chart-level alpacaSellFill which only tracks the current/latest open position.
    const hasAlpacaExit  = (isRaHover || (isWatcherHover && hoverFill != null)) && !isBuy && typeof tradePrice === 'number' && tradePrice > 0;

    // Primary line: Alpaca fill for Ra; signal price for others.
    // alpacaLinePrice = tradePrice = meta.entryPrice (buy) or meta.exitPrice (sell) — per-trade correct.
    if (typeof alpacaLinePrice === 'number' && alpacaLinePrice > 0) {
      const isRaExitLine = !isBuy && (isRaHover || (isWatcherHover && hoverFill != null));
      try {
        hoverPriceLineRef.current = candleSeriesRef.current.createPriceLine({
          price: alpacaLinePrice,
          color: isBuy
            ? (hasAlpacaEntry ? '#22d3ee' : '#f59e0b')   // Ra: cyan; others: amber
            : (isRaExitLine   ? '#fb923c' : '#fb7185'),   // Ra: orange; others: rose
          lineWidth: isBuy ? 1 : 2,
          lineStyle: LineStyle.Dashed,
          axisLabelVisible: true,
          title: isBuy
            ? (hasAlpacaEntry ? `Alpaca Entry $${alpacaLinePrice.toFixed(2)}` : 'Entry')
            : (isRaExitLine   ? `Alpaca Exit $${alpacaLinePrice.toFixed(2)}` : 'Exit'),
        });
      } catch {}
    }

    // Secondary line on sell hover: entry reference (always visible if entry bar is off-screen).
    // Ra: use avgEntryPrice from the matched pair, or meta.entryPrice from the sell alert itself
    // (Ra stores both entryPrice and exitPrice in the sell alert's TradeMeta).
    if (!isBuy) {
      const pairEntry = completedPairByAlertId.get(alert.id);
      // Ra/Watcher: prefer the real Alpaca entry fill (weighted across pyramid buys)
      const alpacaPairPx = isFillHover ? alpacaPricesForPair(pairEntry?.pair ?? null) : { entry: null, exit: null, qty: null };
      const paperEntryForLine = alpacaPairPx.entry
        ?? pairEntry?.pair.avgEntryPrice
        ?? (isThothHover ? (thothGameEntry?.avgCost ?? thothGameEntry?.price) : null)
        ?? (isOsirisHover ? (osirisGameEntry?.avgCost ?? osirisGameEntry?.price) : null)
        ?? (parseTradeMeta(pairEntry?.pair.buyAlert ?? alert)?.entryPrice);
      const entryForLine = paperEntryForLine ?? null;
      // Label the line "Alpaca Entry" only when it's a real resolved fill price.
      const entryIsAlpaca = alpacaPairPx.entry != null;
      // exitRef is the primary line price (already per-trade via alpacaLinePrice = tradePrice)
      const exitRef: number | undefined = typeof alpacaLinePrice === 'number' ? alpacaLinePrice : undefined;
      // Draw BOTH legs' lines unless they're essentially identical (within 0.02%) — the user wants
      // entry AND exit visible on either hover even on near-break-even trades (e.g. $27.45 → $27.43).
      if (typeof entryForLine === 'number' && entryForLine > 0 && exitRef != null && Math.abs(entryForLine - exitRef) / exitRef > 0.0002) {
        try {
          raGamePriceLineRef.current = candleSeriesRef.current.createPriceLine({
            price: entryForLine,
            color: entryIsAlpaca ? '#22d3ee' : '#f59e0b',
            lineWidth: 1,
            lineStyle: LineStyle.Dashed,
            axisLabelVisible: true,
            title: entryIsAlpaca
              ? `Alpaca Entry $${entryForLine.toFixed(2)}`
              : (pairEntry?.pair.buyAlerts && pairEntry.pair.buyAlerts.length > 1
                  ? `Avg Entry (${pairEntry.pair.buyAlerts.length}×)`
                  : 'Entry'),
          });
        } catch {}
      }
    }

    // PAIR-HIGHLIGHT FIX: hovering the ENTRY leg of a CLOSED trade should also show the paired
    // EXIT line (the exit-leg hover already draws both legs above; this makes entry-hover
    // symmetric so BOTH price lines appear no matter which leg the user points at). Only for a
    // completed pair where this buy alert is one of the entry legs.
    if (isBuy) {
      const pairEntry = completedPairByAlertId.get(alert.id);
      const pair = pairEntry?.pair ?? null;
      if (pair) {
        const alpacaPairPx = isFillHover ? alpacaPricesForPair(pair) : { entry: null, exit: null, qty: null };
        const exitForLine = alpacaPairPx.exit
          ?? pair.sellMeta?.exitPrice
          ?? parseTradeMeta(pair.sellAlert)?.exitPrice
          ?? pair.sellAlert.data?.price
          ?? null;
        const exitIsAlpaca = alpacaPairPx.exit != null;
        // entryRef = the primary line just drawn for this buy (alpacaLinePrice). Skip the paired
        // exit line if it'd sit on top of the entry line (avoid a duplicate overlapping label).
        const entryRef: number | undefined = typeof alpacaLinePrice === 'number' ? alpacaLinePrice : undefined;
        if (typeof exitForLine === 'number' && exitForLine > 0 &&
            (entryRef == null || Math.abs(exitForLine - entryRef) / entryRef > 0.0002)) {
          const plPct = pair.sellMeta?.realizedPlPct;
          const exitColor = isFillHover && exitIsAlpaca ? '#fb923c'
            : (typeof plPct === 'number' ? (plPct >= 0 ? '#34d399' : '#fb7185') : '#fb7185');
          try {
            raAlpacaPriceLineRef.current = candleSeriesRef.current.createPriceLine({
              price: exitForLine,
              color: exitColor,
              lineWidth: 2,
              lineStyle: LineStyle.Dashed,
              axisLabelVisible: true,
              title: exitIsAlpaca ? `Alpaca Exit $${exitForLine.toFixed(2)}` : `Exit $${exitForLine.toFixed(2)}`,
            });
          } catch {}
        }
      }
    }

    // P&L comparison card on sell hover
    if (!isBuy && (isRaHover || isThothHover || isOsirisHover)) {
      const bot = isRaHover ? 'Ra' : isOsirisHover ? 'Osiris' : 'Thoth';
      const gameExitData  = isRaHover ? raExit : isOsirisHover ? osirisExit : thothExit;
      const gameEntryData = isRaHover ? raGameEntry : isOsirisHover ? osirisGameEntry : thothGameEntry;
      // Per-trade Alpaca fills for THIS hovered trade (not the symbol's latest fill)
      const cardPair = completedPairByAlertId.get(alert.id)?.pair ?? null;
      const cardPairPx = isRaHover ? alpacaPricesForPair(cardPair) : { entry: null, exit: null, qty: null };
      const cardEntry = cardPairPx.entry ?? (isRaHover ? raAlpacaFill?.price ?? null : null);
      const cardExit  = cardPairPx.exit  ?? (isRaHover ? (hoverFill?.price ?? alpacaSellFill?.price ?? null) : null);
      const cardPl    = cardPairPx.entry != null && cardPairPx.exit != null && cardPairPx.qty != null
        ? (cardPairPx.exit - cardPairPx.entry) * cardPairPx.qty : alpacaPl;
      const cardPlPct = cardPairPx.entry != null && cardPairPx.exit != null && cardPairPx.entry > 0
        ? ((cardPairPx.exit - cardPairPx.entry) / cardPairPx.entry) * 100 : alpacaPlPct;
      const fmtTime = (ts: unknown) => {
        if (ts == null) return null;
        try {
          return new Date(ts as string | number).toLocaleTimeString('en-US', {
            timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', hour12: true,
          });
        } catch { return null; }
      };
      setPlCard({
        bot,
        gamePl:     gameExitData?.realizedPl    ?? null,
        gamePlPct:  gameExitData?.realizedPlPct ?? null,
        gameEntry:  gameEntryData?.avgCost ?? gameEntryData?.price ?? null,
        gameExit:   gameExitData?.price    ?? null,
        gameEntryTime: fmtTime(gameEntryData?.timestamp),
        gameExitTime:  fmtTime(gameExitData?.timestamp),
        alpacaEntry:  cardEntry,
        alpacaExit:   cardExit,
        alpacaPl:     cardPl,
        alpacaPlPct:  cardPlPct,
      });
    } else {
      setPlCard(null);
    }

    return () => {
      for (const ref of [hoverPriceLineRef, raGamePriceLineRef, thothGamePriceLineRef, raAlpacaPriceLineRef]) {
        if (ref.current && candleSeriesRef.current) {
          try { candleSeriesRef.current.removePriceLine(ref.current); } catch {}
          ref.current = null;
        }
      }
      setPlCard(null);
    };
  }, [hoveredTradeId, alerts, tradeAlertsForRender, raGameEntry, thothGameEntry, osirisGameEntry, raExit, thothExit, osirisExit, raAlpacaFill, alpacaSellFill, alpacaPl, alpacaPlPct, findAlpacaFill, findFillForStrategy, alpacaPricesForPair, completedPairByAlertId]);

  // ── The Watcher REPLAY hover: entry + exit price lines on the candle series ───
  // Mirrors the real-trade hover above (createPriceLine on candleSeriesRef). Hovering a
  // replay trade's avatar draws a dashed Entry line + Exit line so the round-trip is
  // unmistakable; the DOM tooltip (rendered below) carries the prices / reason / P&L.
  useEffect(() => {
    const clear = () => {
      for (const pl of replayHoverLinesRef.current) {
        try { candleSeriesRef.current?.removePriceLine(pl); } catch {}
      }
      replayHoverLinesRef.current = [];
    };
    clear();
    if (hoveredReplayIdx == null || !candleSeriesRef.current) return;
    if (!showWatcherReplay || !watcherReplay || watcherReplay.symbol !== symbol) return;
    const tr = watcherReplay.trades[hoveredReplayIdx];
    if (!tr) return;
    try {
      // Entry line — indigo, always present.
      replayHoverLinesRef.current.push(candleSeriesRef.current.createPriceLine({
        price: tr.entryPrice,
        color: '#a5b4fc',
        lineWidth: 2,
        lineStyle: LineStyle.Dashed,
        axisLabelVisible: true,
        title: `W Entry $${formatPrice(tr.entryPrice)}`,
      }));
      // Exit line — green/red, only for closed trades.
      if (!tr.open && tr.exitPrice != null && tr.plPct != null) {
        replayHoverLinesRef.current.push(candleSeriesRef.current.createPriceLine({
          price: tr.exitPrice,
          color: tr.plPct >= 0 ? '#34d399' : '#f87171',
          lineWidth: 2,
          lineStyle: LineStyle.Dashed,
          axisLabelVisible: true,
          title: `W Exit $${formatPrice(tr.exitPrice)}`,
        }));
      }
    } catch {}
    return () => { clear(); };
  }, [hoveredReplayIdx, showWatcherReplay, watcherReplay, symbol]);

  // Subscribe to chart scroll/zoom so markers follow the time axis
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const ts = chart.timeScale();
    ts.subscribeVisibleTimeRangeChange(computeTradeXPos);
    return () => { try { ts.unsubscribeVisibleTimeRangeChange(computeTradeXPos); } catch {} };
  // Re-subscribe when computeTradeXPos changes (i.e. alerts changed) or chart initialises
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computeTradeXPos, candleInitializedRef.current]);

  // [OK] FIX #4: Update daily chart price line when currentPrice changes or daily chart loads
  useEffect(() => {
    // Wait for daily chart to be ready
    if (!dailyChartRef.current || !dailySeriesRef.current || dailyChartLoading) return;
    
    // Use currentPrice if available, otherwise use previous close as fallback
    const priceToShow = currentPrice || dailyPreviousCloseRef.current;
    if (!priceToShow) return; // No price available yet
    
    // Remove existing price line to prevent duplicates
    if (dailyPriceLineRef.current) {
      try {
        dailySeriesRef.current.removePriceLine(dailyPriceLineRef.current);
      } catch (e) {
        // Ignore errors
      }
      dailyPriceLineRef.current = null;
    }
    
    // Create new price line with current price
    try {
      const now = new Date();
      const etTime = new Date(now.toLocaleString("en-US", {timeZone: "America/New_York"}));
      const hour = etTime.getHours();
      const minute = etTime.getMinutes();
      const timeInMinutes = hour * 60 + minute;
      
      // Only show price line during trading hours (4 AM - 8 PM ET)
      if (timeInMinutes >= 240 || timeInMinutes <= 1200) {
        dailyPriceLineRef.current = dailySeriesRef.current.createPriceLine({
          price: priceToShow,
          color: '#10b981', // Emerald green for current price
          lineWidth: 1,
          lineStyle: LineStyle.Dotted,
        });
      }
    } catch (err) {
      console.warn('Failed to update daily chart price line:', err);
    }
  }, [currentPrice, dailyChartLoading]); // Also trigger when daily chart finishes loading
  
  // Note: Daily chart price line is updated in useEffect above when currentPrice changes

  // [OK] CRITICAL: Prevent SSR hydration mismatch - only render on client
  if (!isMounted) {
    return (
      <div className="min-h-screen text-foreground flex items-center justify-center">
        <div className="text-muted-foreground">Loading chart...</div>
      </div>
    );
  }

  // Invalid symbol UI (rendered after hooks to preserve hooks order)
  if (!hasValidSymbol) {
  return (
      <div className="min-h-screen text-foreground flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-4">Invalid Symbol</h1>
          <p className="text-muted-foreground">Please provide a valid stock symbol in the URL</p>
          <Link prefetch={false} href="/alerts" className="mt-4 inline-block px-4 py-2 bg-amber-500 text-black rounded-lg hover:bg-amber-400 transition-colors">
            Back to Alerts
          </Link>
        </div>
      </div>
    );
  }

  // Error boundary for chart errors
  if (chartError) {
    return (
      <div className="min-h-screen text-foreground flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-4">Chart Error</h1>
          <p className="text-muted-foreground mb-4">{chartError}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-amber-500 text-black rounded-lg hover:bg-amber-400 transition-colors mr-4"
          >
            Reload Page
          </button>
          <Link prefetch={false} href="/alerts" className="inline-block px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-accent dark:hover:bg-zinc-600 transition-colors">
            Back to Alerts
          </Link>
        </div>
      </div>
    );
  }

  // Inline JSX: do NOT use an inner component (e.g. ChartMain) - React would see a new
  // function reference every render and unmount/remount the tree, destroying the chart canvas.
  return (
    <ErrorBoundary>
      <div className="min-h-screen text-foreground">
      {/* THE HEADER IS position:fixed, SO IT ESCAPES <main> AND MUST RESERVE BOTH EDGES ITSELF.
          <main> carries padding-right: var(--alerts-chat-right-pad) for the chat dock, which is how
          every in-flow page makes room for it. A fixed element is laid out against the VIEWPORT, so
          that padding cannot reach this bar: it stayed full-bleed and the dock covered its right end
          (measured 2026-08-17 at 1920px, chat open: Live button at x1783-1841, dock edge at x1552 -
          231px of controls underneath it, including the Live toggle the owner was reaching for).
          left was already wired to --sidebar-width; right was a hardcoded right-0, and that asymmetry
          WAS the bug. Both vars are published on documentElement, so both inherit here.
          ⚠ Keep these two together. Same pairing in BandCentered.tsx:51, SubBannerSlot.tsx:43 and
          alerts/page.tsx:4509 - this file was the only fixed bar reserving one edge and not the other.
          Embed (workspace iframe) has neither sidebar nor dock, so both collapse to 0 there. */}
      {/* Header */}
      <div ref={chartHeaderRef} className={`border-b border-border/50 bg-gradient-to-r dark:from-zinc-900/95 dark:to-zinc-900/90 backdrop-blur-md fixed ${isShot ? 'hidden ' : ''}${isEmbed ? 'top-0' : 'top-16'} z-20 shadow-lg transition-[left,right] duration-200`} style={{ left: isEmbed ? 0 : 'var(--sidebar-width, 0px)', right: isEmbed ? 0 : 'var(--alerts-chat-right-pad, 0px)' }}>
        <div className="mx-auto max-w-[1920px] px-4 py-2.5 md:px-6">
          {/* md:flex-wrap — the header width is driven by --sidebar-width, so at a normal viewport the
              chart pane can still be narrow (left nav open). Wrapping lets the action cluster drop to
              its own row instead of overlapping the ticker/price (owner 2026-07-17). */}
          <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-x-4 md:gap-y-2">
            <div className="flex min-w-0 items-center gap-4">
              {/* In embed (workspace iframe) this link would load /alerts INSIDE the frame — hide it */}
              {!isEmbed && (
                <>
                  <Link prefetch={false}
                    href="/alerts"
                    title="Back to alerts"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-muted/40 text-muted-foreground transition-all hover:border-border dark:hover:border-zinc-600 hover:text-amber-600 dark:hover:text-amber-300"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Link>
                </>
              )}
              <div className="flex min-w-0 items-center gap-3">
                <div className="w-1 h-9 bg-gradient-to-b from-amber-500 to-amber-600 rounded-full shrink-0"></div>
                <div className="flex min-w-0 flex-col gap-1">
                  {/* Line 1 — essentials only (ticker · price · daily change · after-hours). One clean line. */}
                  <div className="flex items-baseline gap-x-3 leading-none">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={`/api/logo?symbol=${encodeURIComponent(symbol)}`} alt="" width={28} height={28}
                      className="h-7 w-7 self-center shrink-0 rounded-md bg-muted object-contain ring-1 ring-border dark:ring-white/10"
                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden' }} />
                    <span className="text-2xl font-extrabold tracking-tight text-amber-600 dark:text-amber-400">${symbol}</span>
                    <SymbolFeedLink symbol={symbol} iconClass="h-4 w-4" className="self-center -ml-1.5 shrink-0 opacity-60 transition-opacity hover:opacity-100" />
                    {currentPrice != null && (
                      <span className="text-2xl font-bold tabular-nums text-foreground">${formatPrice(currentPrice)}</span>
                    )}
                    {dayChangePct != null && (
                      <span
                        className={`shrink-0 text-sm font-bold tabular-nums ${dayChangePct >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}
                        title={sessionPhase === 'post' || sessionPhase === 'closed'
                          ? 'Regular-session change - frozen at the 4:00 PM ET close, vs previous close'
                          : '% change vs previous session close (last price). Same basis as major finance sites.'}
                      >
                        {dayChangePct >= 0 ? '+' : ''}{dayChangePct.toFixed(2)}%
                      </span>
                    )}
                    {(() => {
                      // In pre-market before the symbol has printed, preMarketPct is null. Rather than
                      // drop the chip entirely, fall back to the PRIOR session's after-hours move --
                      // but label it "PREV AH", never a bare "AH". A bare AH at 4:21 AM reads as
                      // "after-hours is happening now", which is what the owner reported on IQV.
                      const isPre = sessionPhase === 'pre';
                      const livePre = isPre && preMarketPct != null;
                      const ext = livePre ? preMarketPct : afterHoursPct;
                      if (ext == null || Math.abs(ext) < 0.05) return null;
                      const stale = !livePre && (isPre || isLastSession);
                      const up = ext >= 0;
                      return (
                        <span className={`self-center inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md px-1.5 py-[3px] text-xs font-semibold leading-none ${livePre ? 'bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-300' : 'bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-300'}`}
                          title={livePre ? 'Pre-market move vs the previous close (4:00-9:30 AM ET)' : (stale ? "The previous session's after-hours move, vs that day's 4:00 PM close. This symbol has not printed yet today." : 'After-hours move vs today\u2019s 4:00 PM ET close (4:00-8:00 PM ET)')}>
                          <span className="text-[9px] font-bold uppercase tracking-wider opacity-60">{livePre ? 'Pre' : stale ? 'Prev AH' : 'AH'}</span>
                          <span className={`tabular-nums ${up ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>{up ? '\u25b2' : '\u25bc'}{Math.abs(ext).toFixed(2)}%</span>
                        </span>
                      );
                    })()}
                  </div>
                  {/* Line 2 — ONE guaranteed line: session \u00b7 company \u00b7 stats. The company is the only
                      flexible element and truncates so the stats always fit (no wrap to a 3rd line). */}
                  <div className="flex min-w-0 items-center gap-x-3 overflow-hidden whitespace-nowrap text-xs">
                    {(() => {
                      // The LIVE phase wins over isLastSession. A symbol that has not printed yet in
                      // pre-market still sits in a live session -- the tape is running, this name just
                      // has not traded -- so a grey "Market closed" dot at 4:21 AM was wrong twice over.
                      const S = sessionPhase === 'pre' ? { d: 'bg-sky-400', pulse: true, tt: isLastSession ? 'Live \u2014 pre-market (4:00\u20139:30 AM ET). No prints yet today, so the chart still shows the last session.' : 'Live \u2014 pre-market (4:00\u20139:30 AM ET)' }
                        : (isLastSession || sessionPhase === 'closed') ? { d: 'bg-muted', pulse: false, tt: isLastSession ? 'Market closed \u2014 showing the last trading session' : 'Market closed \u2014 outside 4:00 AM\u20138:00 PM ET' }
                        : sessionPhase === 'post' ? { d: 'bg-violet-400', pulse: true, tt: 'Live \u2014 after-hours (4:00\u20138:00 PM ET)' }
                        : { d: 'bg-emerald-400', pulse: true, tt: 'Live \u2014 regular session (9:30 AM\u20134:00 PM ET)' };
                      return (
                        <span className="inline-flex shrink-0 items-center" title={S.tt}>
                          <span className={`h-2 w-2 rounded-full ${S.d} ${S.pulse ? 'animate-pulse' : ''}`}></span>
                        </span>
                      );
                    })()}
                    {companyName && (
                      <span className="min-w-0 shrink truncate font-medium text-muted-foreground">{companyName}</span>
                    )}
                    {previousClose != null && (
                      <span className="inline-flex shrink-0 items-baseline gap-1"><span className="text-[10px] font-semibold uppercase tracking-wide text-zinc-600">Prev</span><span className="tabular-nums text-foreground/80">${formatPrice(previousClose)}</span></span>
                    )}
                    {dayVolume != null && dayVolume > 0 && (
                      <span className="inline-flex shrink-0 items-baseline gap-1"><span className="text-[10px] font-semibold uppercase tracking-wide text-zinc-600">Vol</span><span className="tabular-nums text-foreground/80">{Intl.NumberFormat('en-US', { notation: 'compact' }).format(dayVolume)}</span></span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* On mobile the action pills scroll horizontally in their own row (no room to sit
                beside the ticker) so they never overlap the price. Desktop stays a single row. */}
            <div className="-mx-4 flex w-[calc(100%+2rem)] items-center gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:w-auto md:shrink-0 md:flex-wrap md:justify-end md:gap-y-2 md:overflow-visible md:px-0 md:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {/* Share: page-faithful screenshot of the whole chart (chart + daily + recent alerts) with
                  the brand lockup, download + one-click to X/Reddit/StockTwits. Hidden in embed/shot so
                  it never appears inside the captured image. */}
              {hasValidSymbol && !isEmbed && !isShot && (
                <ShareMenu
                  url={`https://thedesperatetrader.com/chart/${encodeURIComponent(symbol)}`}
                  imgPath={`/api/shot/chart/${encodeURIComponent(symbol)}.png`}
                  title={`$${symbol} chart`}
                  caption={`$${symbol} live chart, RVOL alerts and key levels on TheDesperateTrader.\n\n$${symbol}`}
                />
              )}
              {/* Watch badge (owner 2026-07-07): the old lone Eye icon told users nothing —
                  now a labeled star badge with a clear Watching state. */}
              {session?.user && hasValidSymbol && (
                <button
                  type="button"
                  onClick={toggleWatchlist}
                  disabled={watchlistLoading}
                  className={[
                    'inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border px-2.5 text-xs font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50',
                    watchlist.has(symbol)
                      ? 'border-amber-300 dark:border-amber-500/60 bg-amber-50 dark:bg-amber-500/15 text-amber-600 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-500/25'
                      : 'border-border bg-muted/50 text-foreground/80 hover:border-amber-300 dark:hover:border-amber-600/50 hover:text-amber-600 dark:hover:text-amber-300',
                  ].join(' ')}
                  aria-pressed={watchlist.has(symbol)}
                  title={watchlist.has(symbol) ? 'On your watchlist - click to remove' : 'Add to your watchlist'}
                >
                  <Star className={`h-3.5 w-3.5 ${watchlist.has(symbol) ? 'fill-amber-400 text-amber-600 dark:text-amber-400' : ''}`} />
                  {watchlist.has(symbol) ? 'Watching' : 'Watch'}
                </button>
              )}
              {/* 🚨 PRICE ALERT, added 2026-08-15 — deliberately RIGHT HERE, beside Watch.
                  user_price_alerts held 5 rows from 3 users in the platform's whole history, not
                  because the feature is broken or unwanted but because its control lived only on
                  pages nobody opens (/alerts/price: 3 users ever, /watchlist: 6). This page has 512
                  logged-in users and had no way to set one. The Watch badge next to it is the proof
                  the placement is what matters: same users, same flow, 158 adopters. */}
              {session?.user && hasValidSymbol && !isEmbed && !isShot && (
                <PriceAlertButton symbol={symbol} price={currentPrice} compact />
              )}
              {/* Feed badge — the ticker's hangout page (posts + live alerts) */}
              {hasValidSymbol && (
                <Link
                  href={`/stocks/${encodeURIComponent(symbol)}`}
                  target={isEmbed ? '_top' : undefined}
                  className="inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border px-2.5 text-xs font-semibold transition-all border-border bg-muted/50 text-foreground/80 hover:border-sky-300 dark:hover:border-sky-600/50 hover:text-sky-700 dark:hover:text-sky-300"
                  title={`$${symbol} feed - trader talk & live alerts`}
                >
                  <Newspaper className="h-3.5 w-3.5" />
                  Feed
                </Link>
              )}
              {session?.user?.isAdmin && hasValidSymbol && (
                <button
                  type="button"
                  onClick={() => {
                    // Prefill with the chart price immediately, then refresh to the LIVE ask (the
                    // price you'd actually BUY at). The chart's currentPrice is a polled minute-bar
                    // value that LAGS in after-hours — that's what made the manual limit show stale/off
                    // prices. /api/game/quote = real-time Alpaca snapshot (bid/ask/last).
                    setManualBuyPrice(currentPrice != null ? String(currentPrice) : '');
                    fetch(`/api/game/quote?symbol=${encodeURIComponent(symbol)}`)
                      .then(r => (r.ok ? r.json() : null))
                      .then(q => {
                        const live = (typeof q?.ask === 'number' && q.ask > 0) ? q.ask
                          : (typeof q?.last === 'number' && q.last > 0) ? q.last : null;
                        if (live != null) setManualBuyPrice(String(Number(live.toFixed(4))));
                      })
                      .catch(() => {});
                    setManualBuyError(null);
                    setManualBuySuccess(false);
                    setManualBuyOpen(true);
                  }}
                  className="inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border px-2.5 text-xs font-semibold transition-all border-emerald-300 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 hover:border-emerald-300 dark:hover:border-emerald-400/60 hover:bg-emerald-50 dark:hover:bg-emerald-500/25"
                  title="Manual Alpaca Buy - admin only"
                >
                  <ShoppingCart className="h-3.5 w-3.5" />
                  Alpaca
                </button>
              )}
              {hasValidSymbol && (
                <GameBuySell
                  symbol={symbol}
                  defaultPrice={currentPrice ?? undefined}
                  compact={false}
                />
              )}
              <div className="mx-0.5 h-5 w-px shrink-0 bg-muted"></div>
              <div className="inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-border/50 bg-muted/50 px-2.5" title={`${alerts.length} alerts this session`}>
                <Flame className="h-3.5 w-3.5 text-amber-500" />
                <span className="text-xs font-semibold text-foreground tabular-nums">{alerts.length}</span>
              </div>
              {/* Follow-live pill (was a lost checkbox): auto-follows the newest candle so
                  watchers never lose the leading edge; off = your pan/zoom is respected. */}
              <button
                type="button"
                onClick={() => {
                  /**
                   * ⭐ THE CHOICE PERSISTS. vokeefe91 also asked "is there a way to always keep it as
                   * live for the ones on my watchlist" - and the answer used to be no, because this
                   * was useState(false) and every navigation between symbols reset it. Sticky here
                   * means one click covers the whole session and every ticker, which is what the
                   * question was actually about.
                   *
                   * ⚠ Wrapped: localStorage throws in private mode and in an embedded iframe, and a
                   * preference is never worth taking the chart down for.
                   */
                  const v = !followLive;
                  followLiveRef.current = v;
                  setFollowLive(v);
                  try { window.localStorage.setItem('dt:chart:followLive', v ? '1' : '0'); } catch { /* preference only */ }
                }}
                aria-pressed={followLive}
                title={(followLive ? 'Following the newest candle - click to stop' : 'Auto-follow the newest candle as it prints') + (isMounted ? ` · last update ${lastUpdate.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })} ET` : '')}
                className={[
                  'inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border px-2.5 text-xs font-semibold transition-all',
                  followLive
                    ? 'border-amber-300 dark:border-amber-500/60 bg-amber-50 dark:bg-amber-500/15 text-amber-600 dark:text-amber-300'
                    : 'border-border bg-muted/40 text-muted-foreground hover:border-border dark:hover:border-zinc-600 hover:text-foreground/80',
                ].join(' ')}
              >
                <span className={`inline-block h-1.5 w-1.5 rounded-full ${followLive ? 'animate-pulse bg-amber-400' : 'bg-muted'}`} />
                Live
              </button>
              <span className="hidden shrink-0 text-[10px] tabular-nums text-zinc-600 2xl:inline" suppressHydrationWarning title="Last chart update">
                {isMounted ? lastUpdate.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', hour12: true }) : '--:--'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content — embed pins the header at top-0, so the content offset shrinks too. In shot mode
          this wrapper is #capture-root (the screenshot target); data-ready flips to 1 only once the
          candles + daily chart have drawn so the share image is never half-rendered. */}
      <div
        id="capture-root"
        data-ready={(isMounted && !loading && !dailyChartLoading && candles.length > 0) ? '1' : '0'}
        className={`mx-auto max-w-[1920px] ${isShot ? 'bg-background px-6 pt-6 pb-6' : isEmbed ? 'px-4 pb-4' : 'px-4 pb-6 md:px-6'}`}
        // ⚠ THE FALLBACK IS THE OLD LITERAL ON PURPOSE. --chart-header-h is only set once the
        // ResizeObserver has run, so the very first paint (and SSR) must still clear a typical
        // header. Getting it slightly wrong for one frame is fine; having no offset at all would
        // drop the whole chart under the header.
        style={isShot ? undefined : {
          // ⚠ max(), NOT the bare var. The measured value can only ever be TOO SMALL in practice -
          // it is published synchronously when the header attaches, before late content (price,
          // badges, the wrap) grows it, and the ResizeObserver that corrects it does not run while
          // the tab is backgrounded. A stale-low value would put the header back over the content,
          // which is the whole bug. Clamping to the historical literal means the offset can only
          // ever be too generous, never too tight, and it still tracks a genuinely taller header.
          paddingTop: `calc(max(var(--chart-header-h, 0px), ${isEmbed ? '84px' : '92px'}) + 12px)`,
        }}
      >
        {/* Subscribe ask. <PremiumBanner/> is barred from /chart/* (PremiumBanner.tsx:43) because a
            full-width bar would cover the fixed header above - correctly. This sits inside the
            already-cleared content column (pt-[124px]), so nothing is covered.
            ⚠ NEVER IN A SHARE IMAGE OR AN EMBED. This wrapper is #capture-root, so an ungated ask
            would be baked into every social screenshot we publish and into workspace iframes. */}
        {/* Verify ask, same placement and the same share-image/embed gate. Mutually exclusive with
            the subscribe ask above by construction, not by coordination here. */}
        {!isShot && !isEmbed && <VerifyInlineAsk surface="chart" />}
        {!isShot && !isEmbed && <PremiumInlineAsk surface="chart" />}
        {/* Same gating as the ask above: this wrapper is #capture-root, so an ungated card would be
            baked into every social share image and into the workspace iframe. The promo also stands
            down on its own whenever the money ask above has something to say. */}
        {!isShot && !isEmbed && <DailyBriefPromo />}
        {/* Brand lockup for the share image only. The real header is position:fixed (not captured), so
            this banner carries the logo + ticker + URL at the top-left, above the chart, no overlap. */}
        {isShot && (
          <div className="mb-4 flex items-center justify-between border-b border-border pb-4">
            <div className="flex items-center gap-3">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/seedling-logo.png" alt="TheDesperateTrader" width={46} height={46} className="shrink-0 object-contain" />
              <div>
                <div className="text-2xl font-black tracking-tight text-foreground">${symbol}</div>
                <div className="brand-ink text-base font-bold text-amber-600 dark:text-amber-400">TheDesperateTrader.com</div>
              </div>
            </div>
            <div className="text-right text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              Live chart · RVOL alerts · key levels
            </div>
          </div>
        )}
        {isDebug && (
          <div className="fixed top-[70px] left-1/2 -translate-x-1/2 z-[999999] rounded border border-emerald-300 dark:border-emerald-500/60 bg-background/95 px-3 py-1.5 font-mono text-[12px] leading-tight text-emerald-700 dark:text-emerald-300 shadow-2xl pointer-events-none max-w-[95vw] break-words">
            {debugText || 'measuring…'}
          </div>
        )}
        <div className={`grid grid-cols-1 gap-6 ${chatOpen ? '' : 'xl:grid-cols-4'}`}>
          {/* Chart Section */}
          <div className={`space-y-3 flex flex-col ${chatOpen ? '' : 'xl:col-span-3'}`}>
            {/* Removed Trading Session Legend for cleaner layout */}

            {/* ── Control bar: timeframe + S/R overlay layers (one tidy strip) ── */}
            <div className="flex items-center gap-3 flex-wrap rounded-lg border border-border/70 bg-card/40 px-2.5 py-1.5">
              {/* Timeframe — single-select segment */}
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wide">TF</span>
                <div className="flex items-center gap-0.5 rounded-md bg-card/70 p-0.5">
                  {TIMEFRAMES.map(tf => (
                    <button
                      key={tf.value}
                      onClick={() => setTimeframe(tf.value)}
                      className={[
                        'rounded px-2.5 py-1 text-xs font-bold transition-all',
                        timeframe === tf.value ? 'bg-amber-500 text-black shadow-sm' : 'text-muted-foreground hover:text-foreground/80 hover:bg-accent dark:hover:bg-zinc-800/60',
                      ].join(' ')}
                    >
                      {tf.label}
                    </button>
                  ))}
                </div>
                <span className="text-[11px] text-zinc-600 tabular-nums">
                  {candles.length > 0 ? `${aggregateCandles(candles, TIMEFRAMES.find(t => t.value === timeframe)?.minutes ?? 1).length} bars` : ''}
                </span>
              </div>

              <div className="hidden h-5 w-px bg-muted/60 md:block" />

              {/* Master-class indicator panel trigger + categorized drawer */}
              {symbol && <ChartNewsButton symbol={symbol} />}
              {/* WATCHER TRADES (owner 2026-08-06): every trade we took on this name, chart + list,
                  click a row to pin it. Its own route on purpose — this page is ~9,900 lines and is
                  what people trade from, so the review view shares its DATA and none of its code. */}
              {symbol && (
                <Link
                  href={`/chart/${encodeURIComponent(symbol)}/trades`}
                  title="Every Watcher trade on this symbol: chart, list, and click-to-inspect"
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border bg-muted/60 px-2 py-1 text-xs font-semibold text-foreground/80 transition-colors hover:border-amber-300 dark:hover:border-amber-500/60 hover:text-amber-600 dark:hover:text-amber-300"
                >
                  <Activity className="h-3.5 w-3.5" /> Trades
                </Link>
              )}
              {(() => {
                // Count active layers for the badge.
                const activeCount = [
                  showMinuteLevels, showDailyLevels, showPatterns, showSwings, showPivots,
                  showVolumeProfile, showVWAP, showAVWAP, showRvolReadout,
                  showMACD, showADX, showBollinger, showRSI, showAtrReadout,
                  showOBV, showADLine, showCMF, showAccDaysReadout,
                ].filter(Boolean).length;
                return (
                  <div className="relative">
                    <button
                      onClick={() => setIndicatorPanelOpen((v) => !v)}
                      className={[
                        'rounded-md px-3 py-1.5 text-xs font-semibold transition-all inline-flex items-center gap-2 border',
                        indicatorPanelOpen
                          ? 'bg-amber-500 text-black border-amber-300 dark:border-amber-400 shadow-sm'
                          : 'bg-card/70 text-foreground/80 border-border/60 hover:text-foreground hover:border-border dark:hover:border-zinc-600',
                      ].join(' ')}
                    >
                      <Activity className="w-3.5 h-3.5" />
                      Indicators
                      {activeCount > 0 && (
                        <span className={[
                          'inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold tabular-nums',
                          indicatorPanelOpen ? 'bg-background/25 text-black' : 'bg-amber-50 dark:bg-amber-500/20 text-amber-600 dark:text-amber-300',
                        ].join(' ')}>{activeCount}</span>
                      )}
                      <svg className={['w-3 h-3 transition-transform', indicatorPanelOpen ? 'rotate-180' : ''].join(' ')} viewBox="0 0 12 12" fill="none"><path d="M3 5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </button>

                    {indicatorPanelOpen && (() => {
                      // ── Indicator catalog (verbatim user copy) grouped by category ──
                      type Row = {
                        key: string; label: string; on: boolean; set: (fn: (v: boolean) => boolean) => void;
                        swatch: string; def: string; how: string; best: string;
                      };
                      const categories: { name: string; accent: string; rows: Row[] }[] = [
                        { name: 'STRUCTURE', accent: '#34d399', rows: [
                          { key: 'swings', label: 'Price Structure (swing highs/lows)', on: showSwings, set: setShowSwings, swatch: '#60a5fa',
                            def: "The market's own footprint - levels where price keeps turning, plus swing highs/lows.",
                            how: 'Zero lag, zero smoothing - the purest read of where buyers/sellers commit. Long: buy support/swing lows. Short: sell resistance/swing highs.',
                            best: "If you keep one thing, it's this - everything else is derivative." },
                          { key: 'srMin', label: 'Intraday S/R', on: showMinuteLevels, set: setShowMinuteLevels, swatch: '#fb7185',
                            def: 'Current-timeframe support/resistance levels drawn from where intraday price keeps turning.',
                            how: 'Pairs with swing structure - buy the support floor, sell the resistance ceiling on this timeframe.',
                            best: 'Levels where price has actually transacted beat any drawn line.' },
                          { key: 'srDaily', label: 'Daily S/R', on: showDailyLevels, set: setShowDailyLevels, swatch: '#f87171',
                            def: 'Daily support/resistance - the bigger structural levels (also drawn on the 12-month mini-chart).',
                            how: 'Higher-timeframe levels carry more weight; the lines intraday price respects.',
                            best: 'Daily levels trump intraday ones when they conflict.' },
                          { key: 'patterns', label: 'Patterns (DT/DB)', on: showPatterns, set: setShowPatterns, swatch: '#2dd4bf',
                            def: 'Double-top (bearish) / double-bottom (bullish) / contested necklines.',
                            how: 'A neckline break confirms the reversal - buy a DB break up, sell a DT break down.',
                            best: 'Wait for the neckline break; the pattern alone is just a shape.' },
                          { key: 'pivots', label: 'Pivot Points', on: showPivots, set: setShowPivots, swatch: '#f5c518',
                            def: 'Floor-trader auto S/R (PP/R1/R2/S1/S2) from the prior day.',
                            how: 'Clean intraday levels. Long: bounce S1/S2, break R1. Short: reject R1/R2, break S1.',
                            best: 'Mechanical, objective levels - great for planning entries before the open.' },
                        ] },
                        { name: 'VOLUME', accent: '#60a5fa', rows: [
                          { key: 'rvol', label: 'Volume / RVOL', on: showRvolReadout, set: setShowRvolReadout, swatch: '#10b981',
                            def: 'Raw volume + relative volume (today vs average).',
                            how: 'The confirmation layer for every signal - a breakout without volume is a question; with it, an answer.',
                            best: 'Confirm every entry; no volume, no conviction.' },
                          { key: 'rvolpane', label: 'RVOL pane', on: showRVOL, set: setShowRVOL, swatch: '#f59e0b',
                            def: 'Relative volume per candle, at whatever timeframe you are on, with a 1x line.',
                            how: 'Compares each bar to what this stock normally trades AT THAT TIME OF DAY, so you can watch RVOL trend like price instead of reading one number.',
                            best: 'A breakout on rising RVOL is real; the same breakout on fading RVOL is not.' },
                          { key: 'vwap', label: 'VWAP', on: showVWAP, set: setShowVWAP, swatch: '#f97316',
                            def: 'Volume-weighted average price - the intraday level where size transacts (resets daily).',
                            how: 'The institutional reference. Long: hold/reclaim above. Short: reject/lose below.',
                            best: 'The intraday line of control - self-fulfilling.' },
                          { key: 'avwap', label: 'Anchored VWAP', on: showAVWAP, set: setShowAVWAP, swatch: '#a78bfa',
                            def: 'VWAP anchored to a key event (session open / breakout bar).',
                            how: 'Cost basis from that moment - support/resistance for the move since the anchor.',
                            best: 'Anchor it to the move you actually care about.' },
                          { key: 'volprofile', label: 'Volume Profile', on: showVolumeProfile, set: setShowVolumeProfile, swatch: '#f5c518',
                            def: 'Volume traded at each price (POC = point of control + value area).',
                            how: 'Shows where price WANTS to trade.',
                            best: 'High ceiling, high skill floor - POC as magnet/support; rewards interpretation.' },
                        ] },
                        { name: 'TREND', accent: '#fbbf24', rows: [
                          { key: 'ema', label: 'Moving Averages (EMA 9/50/200)', on: true, set: () => {}, swatch: '#3b82f6',
                            def: 'Exponential averages over 9/50/200 bars.',
                            how: 'Define trend + act as dynamic S/R. Long: stacked up (9>50>200). Short: stacked down.',
                            best: 'Trend backbone, no babysitting - lag is a feature for trend, a bug for reversals.' },
                          { key: 'macd', label: 'MACD (12/26/9)', on: showMACD, set: setShowMACD, swatch: '#3b82f6',
                            def: 'Trend + momentum in one (EMA difference, signal, histogram).',
                            how: 'Confirms trend shifts + momentum.',
                            best: 'Watch-out: lagging by construction - great for confirmation, poor as a sole trigger.' },
                          { key: 'adx', label: 'ADX (14)', on: showADX, set: setShowADX, swatch: '#eab308',
                            def: 'Average Directional Index - trend STRENGTH, not direction (>25 trending, <20 chop).',
                            how: 'Answers "is there even a trend to trade?"',
                            best: 'A filter, not a trigger - don’t force direction in a range.' },
                        ] },
                        { name: 'MOMENTUM', accent: '#e879f9', rows: [
                          { key: 'rsi', label: 'RSI (14)', on: showRSI, set: setShowRSI, swatch: '#e879f9',
                            def: 'Momentum oscillator, 0-100.',
                            how: 'Divergence + failure swings carry real information.',
                            best: 'Watch-out: "70/30 = sell/buy" lights money on fire - in strong trends it pins and stays pinned; lean on >50/<50 + divergence.' },
                        ] },
                        { name: 'VOLATILITY', accent: '#22d3ee', rows: [
                          { key: 'atr', label: 'ATR (14)', on: showAtrReadout, set: setShowAtrReadout, swatch: '#22d3ee',
                            def: 'Average True Range - the stock’s actual per-bar range.',
                            how: 'The backbone of position sizing + stop placement (not a signal).',
                            best: 'Set stops at N×ATR, size to risk - ignoring it is why people blow up.' },
                          { key: 'bb', label: 'Bollinger Bands (20, 2σ)', on: showBollinger, set: setShowBollinger, swatch: '#60a5fa',
                            def: 'A ±2σ volatility envelope around a moving average.',
                            how: 'Read the volatility REGIME - a squeeze (narrow bands) = compression before expansion.',
                            best: 'Watch-out: don’t naively fade band touches.' },
                        ] },
                        { name: 'ACCUMULATION', accent: '#37d399', rows: [
                          { key: 'obv', label: 'OBV', on: showOBV, set: setShowOBV, swatch: '#f5b642',
                            def: 'On-Balance Volume - a running total that adds volume on up-closes and subtracts it on down-closes.',
                            how: 'Track whether volume is flowing in or out. A rising OBV confirms an uptrend; OBV diverging from price warns the move is unfunded.',
                            best: 'It is a confirmation/divergence tool - the direction of the line matters, not its absolute level.' },
                          { key: 'adline', label: 'A/D Line', on: showADLine, set: setShowADLine, swatch: '#37d399',
                            def: 'Accumulation/Distribution line - cumulative volume weighted by where each bar closes in its range.',
                            how: 'Like OBV but range-aware: a close near the high accumulates, near the low distributes. Rising = accumulation under the tape.',
                            best: 'Watch for divergence from price - it often leads a reversal.' },
                          { key: 'cmf', label: 'Chaikin Money Flow', on: showCMF, set: setShowCMF, swatch: '#a78bfa',
                            def: 'CMF(20) - money-flow volume over total volume, oscillating around 0 (above = buying pressure, below = selling).',
                            how: 'A bounded read of accumulation over the last 20 bars. Sustained >+0.10 = accumulation; <-0.10 = distribution.',
                            best: 'Use the zero line + ±0.10 bands as regime, not the exact value as a trigger.' },
                          { key: 'accdays', label: 'Acc/Dist Days', on: showAccDaysReadout, set: setShowAccDaysReadout, swatch: '#37d399',
                            def: 'A legend count of accumulation days (up on rising volume) vs distribution days (down on rising volume) over the last ~25 bars.',
                            how: 'The classic institutional-footprint tally - more accumulation days than distribution days = big money is building a position.',
                            best: 'A quick regime read; pair it with the A/D line for the full picture.' },
                        ] },
                      ];
                      return (
                        <>
                          {/* click-away backdrop */}
                          <div className="fixed inset-0 z-40" onClick={() => { setIndicatorPanelOpen(false); setOpenInfoKey(null); }} />
                          {/* Mobile: a fixed, viewport-centered sheet so it escapes the action
                              strip's overflow-x-auto clip and its width can't exceed the screen.
                              Desktop (md+): the original absolute dropdown anchored under the button. */}
                          <div className="fixed left-1/2 top-[12vh] -translate-x-1/2 z-50 w-[min(360px,calc(100vw-1.5rem))] max-h-[70vh] overflow-y-auto rounded-xl border border-border/70 bg-background/98 backdrop-blur-sm shadow-2xl ring-1 ring-border dark:ring-black/40 md:absolute md:left-0 md:top-full md:mt-2 md:translate-x-0 md:w-[360px]">
                            <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-2.5 border-b border-border/80 bg-background/98 backdrop-blur-sm">
                              <div className="flex items-center gap-2">
                                <Activity className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                                <span className="text-sm font-bold text-foreground tracking-wide">Indicators</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <a
                                  href="/guide"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 rounded-md border border-border/60 bg-card/70 px-2 py-1 text-[11px] font-semibold text-foreground/80 transition-colors hover:border-amber-300 dark:hover:border-amber-500/50 hover:text-amber-600 dark:hover:text-amber-300"
                                  title="Open the full indicator guide in a new tab"
                                >
                                  <span aria-hidden>📖</span> Guide
                                </a>
                                <button onClick={() => { setIndicatorPanelOpen(false); setOpenInfoKey(null); }} className="text-muted-foreground hover:text-foreground/80 text-lg leading-none px-1">×</button>
                              </div>
                            </div>
                            <div className="p-2 space-y-3">
                              {categories.map((cat) => (
                                <div key={cat.name}>
                                  <div className="flex items-center gap-2 px-2 pb-1.5">
                                    <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: cat.accent }} />
                                    <span className="text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: cat.accent }}>{cat.name}</span>
                                    <span className="flex-1 h-px bg-muted/70" />
                                  </div>
                                  <div className="space-y-0.5">
                                    {cat.rows.map((r) => {
                                      const locked = r.key === 'ema'; // EMAs are always-on
                                      const infoOpen = openInfoKey === r.key;
                                      return (
                                        <div key={r.key} className="rounded-lg overflow-hidden">
                                          <div className={['flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors', infoOpen ? 'bg-card/80' : 'hover:bg-accent dark:hover:bg-zinc-900/50'].join(' ')}>
                                            {/* toggle */}
                                            <button
                                              disabled={locked}
                                              onClick={() => !locked && r.set((v) => !v)}
                                              className={[
                                                'relative inline-flex items-center h-4 w-7 rounded-full transition-colors flex-shrink-0',
                                                r.on ? 'bg-amber-500' : 'bg-muted',
                                                locked ? 'opacity-60 cursor-default' : '',
                                              ].join(' ')}
                                              title={locked ? 'Always on' : (r.on ? 'On' : 'Off')}
                                            >
                                              <span className={['inline-block w-3 h-3 rounded-full bg-white shadow transform transition-transform', r.on ? 'translate-x-3.5' : 'translate-x-0.5'].join(' ')} />
                                            </button>
                                            <span className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: r.swatch }} />
                                            <span className="flex-1 text-xs font-medium text-foreground/80 leading-tight">{r.label}</span>
                                            {/* info affordance */}
                                            <button
                                              onClick={() => setOpenInfoKey(infoOpen ? null : r.key)}
                                              className={['flex-shrink-0 w-5 h-5 rounded-full inline-flex items-center justify-center text-[11px] font-bold transition-colors', infoOpen ? 'bg-amber-50 dark:bg-amber-500/20 text-amber-600 dark:text-amber-300' : 'text-muted-foreground hover:text-amber-600 dark:hover:text-amber-300 hover:bg-accent dark:hover:bg-zinc-800/80'].join(' ')}
                                              title="What is this?"
                                            >ⓘ</button>
                                          </div>
                                          {infoOpen && (
                                            <div className="mx-2 mb-1.5 mt-0.5 rounded-lg border border-border/80 bg-background/40 px-3 py-2.5 text-[11px] leading-relaxed space-y-1.5">
                                              <p className="text-foreground/80"><span className="text-muted-foreground font-semibold uppercase tracking-wide text-[9px] mr-1.5">Def</span>{r.def}</p>
                                              <p className="text-foreground/80"><span className="text-blue-700 dark:text-blue-400 font-semibold uppercase tracking-wide text-[9px] mr-1.5">How</span>{r.how}</p>
                                              <p className="text-foreground/80"><span className="text-amber-600 dark:text-amber-400 font-semibold uppercase tracking-wide text-[9px] mr-1.5">Best</span>{r.best}</p>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                );
              })()}

              {/* ── The Watcher REPLAY toggle ──────────────────────────────────
                  Simulates the Watcher's M1 breakout strategy over this symbol's
                  history. Distinct indigo styling so it reads as a what-if, not a
                  real trade layer. */}
              <button
                onClick={() => {
                  // Cycle OFF → LIVE → DEV → OFF (book picks which ai-config toggles apply)
                  if (!showWatcherReplay) { setWatcherReplayBook('live'); setShowWatcherReplay(true); }
                  else if (watcherReplayBook === 'live') { setWatcherReplayBook('dev'); setWatcherReplay(null); }
                  else { setShowWatcherReplay(false); }
                }}
                title="Replay ALL enabled Watcher entry conditions (double bottom / ema50 / ema200 / breakout / chase / continuation) over this symbol's history - honors your ai-config toggles. Click cycles LIVE → DEV → off. (gap_ripper is intraday-timing driven and not simulated.)"
                className={[
                  'rounded-md px-3 py-1.5 text-xs font-semibold transition-all inline-flex items-center gap-1.5 border',
                  showWatcherReplay
                    ? 'bg-indigo-500 text-foreground border-indigo-300 dark:border-indigo-400 shadow-sm'
                    : 'bg-card/70 text-foreground/80 border-border/60 hover:text-foreground hover:border-indigo-300 dark:hover:border-indigo-500/50',
                ].join(' ')}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src="/assets/watcher/watcher-avatar.png" alt="" aria-hidden className="w-4 h-4 rounded-full object-cover -ml-0.5" />
                The Watcher
                {showWatcherReplay && (
                  <span className={`text-[9px] font-bold px-1 py-0.5 rounded ${watcherReplayBook === 'live' ? 'bg-emerald-50 dark:bg-emerald-500/25 text-emerald-700 dark:text-emerald-200' : 'bg-sky-50 dark:bg-sky-500/25 text-sky-700 dark:text-sky-200'}`}>
                    {watcherReplayBook === 'live' ? '💰 LIVE' : '🧪 DEV'}
                  </span>
                )}
                {showWatcherReplay && watcherReplayLoading && (
                  <span className="text-[10px] opacity-80">…</span>
                )}
                {showWatcherReplay && !watcherReplayLoading && watcherReplay && watcherReplay.symbol === symbol && (
                  <span className={[
                    'inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold tabular-nums',
                    'bg-muted text-foreground',
                  ].join(' ')}>{replayInWindow.idx.length}</span>
                )}
              </button>

            </div>

            {/* The Watcher replay summary — scoped to trades in the LOADED chart window. */}
            {showWatcherReplay && !watcherReplayLoading && watcherReplay && watcherReplay.symbol === symbol && (() => {
              const inTrades = replayInWindow.idx.map((k) => watcherReplay.trades[k]).filter(Boolean);
              const closedT = inTrades.filter((t) => !t.open && t.plPct != null);
              const winsN = closedT.filter((t) => (t.plPct ?? 0) > 0).length;
              const totalPl = closedT.reduce((s2, t) => s2 + (t.plPct ?? 0), 0);
              const perCond = new Map<string, number>();
              for (const t of inTrades) { const k = watcherCondKey(t.setup, t.entryReason); perCond.set(k, (perCond.get(k) ?? 0) + 1); }
              return (
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                  <span className={`inline-flex items-center gap-1 rounded-md px-2 py-1 font-bold text-[10px] border ${watcherReplayBook === 'live' ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-600/40' : 'bg-sky-50 dark:bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-300 dark:border-sky-600/40'}`}>
                    {watcherReplayBook === 'live' ? '💰 LIVE' : '🧪 DEV'} conditions
                  </span>
                  {inTrades.length > 0 ? (
                    <span className="inline-flex items-center gap-2 rounded-lg border border-indigo-300 dark:border-indigo-500/40 bg-indigo-50 dark:bg-indigo-500/10 px-2.5 py-1">
                      <span className="text-foreground/80 tabular-nums font-semibold">{inTrades.length} trade{inTrades.length === 1 ? '' : 's'} in view</span>
                      {closedT.length > 0 && (<>
                        <span className="text-zinc-600">·</span>
                        <span className="text-foreground/80 tabular-nums">{((winsN / closedT.length) * 100).toFixed(0)}% win</span>
                        <span className="text-zinc-600">·</span>
                        <span className={['tabular-nums font-semibold', totalPl >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'].join(' ')}>{totalPl >= 0 ? '+' : ''}{totalPl.toFixed(1)}%</span>
                      </>)}
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-lg border border-border/40 bg-muted/40 px-2.5 py-1 text-muted-foreground">No setups in the loaded window</span>
                  )}
                  {[...perCond.entries()].map(([k, n]) => (
                    <span key={k} className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold"
                      style={{ backgroundColor: `${WATCHER_SETUP_COLORS[k] ?? '#a5b4fc'}22`, color: WATCHER_SETUP_COLORS[k] ?? '#a5b4fc', border: `1px solid ${(WATCHER_SETUP_COLORS[k] ?? '#a5b4fc')}55` }}>
                      ▲ {WATCHER_SETUP_TAGS[k] ?? k} ×{n}
                    </span>
                  ))}
                  {replayInWindow.older > 0 && (
                    <span className="text-[10px] text-muted-foreground">+{replayInWindow.older} older beyond the loaded window · zoom out or switch to a higher timeframe to include them</span>
                  )}
                </div>
              );
            })()}

            {/* ── OHLC / VOLUME READOUT (owner 2026-07-28) ──────────────────────────────────────────
                 "show all the price info ... that way i don't have to scroll down." Reports the bar
                 under the crosshair, falling back to the most recent bar when not hovering, so the row
                 is never empty and never jumps to '--' the moment the cursor leaves the chart.
                 Everything here comes from the SAME array the candles are drawn from, so on 5m these
                 describe the 5-minute candle rather than a minute inside it, and $vol and RVOL agree
                 with the panes below by construction rather than by coincidence. */}
            {/* Shared with the /stocks/[symbol] charts (ChartStatsReadout) so the two cannot drift.
                 Reports the bar under the crosshair, falling back to the most recent bar when not
                 hovering, so the row is never empty and does not flash to '--' as the cursor leaves. */}
            {(() => {
              const bar = hoverBar ?? latestBarRef.current;
              // ⚠ NO BAR YET STILL TAKES THE ROW'S SPACE. Returning null here let the row arrive ~2s after
              // mount and push the legend, chart and Volume card 67px down (v 0.0638 measured, register
              // chart-volume-card-shifts-after-mount-1104). `reserve` paints the same frame with an
              // invisible stand-in, with the same RVOL cells the live row will have.
              if (!bar) {
                return (
                  <ChartStatsReadout
                    className="mb-1.5"
                    hovering={false}
                    bar={null}
                    reserve
                    rvol={null}
                    rvol1m={timeframe !== '1D' ? null : undefined}
                    rvol5m={timeframe !== '1D' ? null : undefined}
                    daily={timeframe === '1D'}
                  />
                );
              }
              const rv = rvolByTimeRef.current.get(bar.t) ?? null;
              // Live 1m + 5m relative volume, from the RAW 1-minute `candles` (so it is correct whatever
              // the displayed timeframe): the customer wanted 5-minute RVOL "in addition to" 1-minute,
              // visible together. Same baseline math as everywhere else: summed volume / summed expected
              // volume, with the weakest sampled minute setting the thin flag. Daily view has no intraday
              // minute window, so the pair is omitted there (the single RVOL cell shows the day).
              const liveRvol = (spanMin: number): { v: number; thin: boolean } | null => {
                const n = candles.length;
                if (!n) return null;
                let vol = 0, base = 0, minN = Infinity, any = false;
                for (let i = Math.max(0, n - spanMin); i < n; i++) {
                  const c = candles[i];
                  if (c.baseVol && c.baseVol > 0 && Number.isFinite(c.volume)) {
                    vol += c.volume; base += c.baseVol; minN = Math.min(minN, c.baseN ?? 0); any = true;
                  }
                }
                if (!any || base <= 0) return null;
                return { v: vol / base, thin: (Number.isFinite(minN) ? minN : 0) < RVOL_MIN_SAMPLES };
              };
              const intraday = timeframe !== '1D';
              return (
                <ChartStatsReadout
                  className="mb-1.5"
                  hovering={!!hoverBar}
                  bar={{ t: bar.t * 1000, open: bar.open, high: bar.high, low: bar.low, close: bar.close, volume: bar.volume }}
                  rvol={rv}
                  rvol1m={intraday ? liveRvol(1) : undefined}
                  rvol5m={intraday ? liveRvol(5) : undefined}
                  daily={timeframe === '1D'}
                />
              );
            })()}

            {/* Combined EMA & Alert Legend — wraps on mobile so no value/chip clips off-screen */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border border-border/60 bg-card/40 px-3 py-2">
              {/* EMA live values — tight + color-coded (blue 9 / amber 50 / green 200). One label, one price. */}
              {/* ⚠ min-w-[7ch] holds a price-wide slot while a value is still '--' (2 chars -> '$202.12', 7). Without it
                  the three values arriving widened the row ~100px, wrapped a chip, and grew the legend 46 -> 69px, which
                  pushed the chart and Volume card down (register chart-volume-card-shifts-after-mount-1104). */}
              <div className="flex items-center gap-3 tabular-nums">
                <span className="flex items-center gap-1.5" title="9-period EMA">
                  <span className="h-[3px] w-3.5 rounded-full" style={{ backgroundColor: '#3b82f6' }} />
                  <span className="text-[11px] font-bold text-muted-foreground">9</span>
                  <span className="inline-block min-w-[7ch] text-[11px] font-medium text-foreground/80">{ema9Value ? `$${formatPrice(ema9Value)}` : '--'}</span>
                </span>
                <span className="flex items-center gap-1.5" title="50-period EMA">
                  <span className="h-[3px] w-3.5 rounded-full" style={{ backgroundColor: '#fbbf24' }} />
                  <span className="text-[11px] font-bold text-muted-foreground">50</span>
                  <span className="inline-block min-w-[7ch] text-[11px] font-medium text-foreground/80">{ema50Value ? `$${formatPrice(ema50Value)}` : '--'}</span>
                </span>
                <span className="flex items-center gap-1.5" title="200-period EMA">
                  <span className="h-[3px] w-3.5 rounded-full" style={{ backgroundColor: '#10b981' }} />
                  <span className="text-[11px] font-bold text-muted-foreground">200</span>
                  <span className="inline-block min-w-[7ch] text-[11px] font-medium text-foreground/80">{ema200Value ? `$${formatPrice(ema200Value)}` : '--'}</span>
                </span>
              </div>

              {/* VWAP / AVWAP legend — only shown when their overlay is on */}
              {showVWAP && (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-0.5 rounded" style={{ backgroundColor: '#f97316' }}></div>
                  <span className="text-xs text-muted-foreground font-medium">VWAP</span>
                  <span className="text-xs text-muted-foreground">${vwapValue ? formatPrice(vwapValue) : '--'}</span>
                </div>
              )}
              {showAVWAP && (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-0.5 rounded" style={{ backgroundColor: '#a78bfa', borderTop: '1px dashed #a78bfa' }}></div>
                  <span className="text-xs text-muted-foreground font-medium">AVWAP</span>
                  <span className="text-xs text-muted-foreground">${avwapValue ? formatPrice(avwapValue) : '--'}</span>
                </div>
              )}

              {/* ATR / RVOL numeric readouts (compact badges — sizing/stops + volume conviction) */}
              {showAtrReadout && (
                <div className="flex items-center gap-2 px-2 py-1 rounded-lg border" style={{ backgroundColor: 'rgba(34,211,238,0.10)', borderColor: 'rgba(34,211,238,0.25)' }} title="Average True Range (14) - per-bar range for stops & position sizing">
                  <span className="text-xs font-semibold" style={{ color: '#22d3ee' }}>ATR</span>
                  <span className="text-xs text-foreground/80 tabular-nums">{atrValue != null ? formatPrice(atrValue) : '--'}</span>
                  {atrPct != null && <span className="text-[10px] text-muted-foreground tabular-nums">({atrPct.toFixed(2)}%)</span>}
                </div>
              )}
              {showRvolReadout && (
                <div className="flex items-center gap-2 px-2 py-1 rounded-lg border" style={{ backgroundColor: 'rgba(16,185,129,0.10)', borderColor: 'rgba(16,185,129,0.25)' }} title="Relative volume - last bar vs the prior 20-bar average">
                  <span className="text-xs font-semibold" style={{ color: '#10b981' }}>RVOL</span>
                  <span className={['text-xs font-bold tabular-nums', rvolValue != null && rvolValue >= 2 ? 'text-emerald-700 dark:text-emerald-300' : 'text-foreground/80'].join(' ')}>{rvolValue != null ? `${rvolValue.toFixed(1)}x` : '--'}</span>
                </div>
              )}
              {showAccDaysReadout && (() => {
                // Up-on-rising-volume vs down-on-rising-volume over the last ~25 bars. Whole readout
                // greens when accumulation leads, reds when distribution leads, muted when tied.
                const acc = accDist?.acc ?? null;
                const dist = accDist?.dist ?? null;
                const tone = acc == null || dist == null ? '#a1a1aa' : acc > dist ? '#37d399' : dist > acc ? '#fb7185' : '#a1a1aa';
                return (
                  <div className="flex items-center gap-2 px-2 py-1 rounded-lg border" style={{ backgroundColor: 'rgba(55,211,153,0.10)', borderColor: 'rgba(55,211,153,0.25)' }} title="Accumulation vs distribution days over the last ~25 bars - up-on-rising-volume vs down-on-rising-volume">
                    <span className="text-xs font-semibold" style={{ color: tone }}>Acc/Dist</span>
                    <span className="text-xs font-bold tabular-nums" style={{ color: tone }}>{acc != null && dist != null ? `Acc ${acc} / Dist ${dist}` : '--'}</span>
                  </div>
                );
              })()}
              {showMACD && macdValue && (
                <div className="flex items-center gap-2" title="MACD (12/26/9) - line / signal / histogram">
                  <div className="w-4 h-0.5 rounded" style={{ backgroundColor: '#3b82f6' }}></div>
                  <span className="text-xs text-muted-foreground font-medium">MACD</span>
                  <span className={['text-xs tabular-nums', macdValue.hist >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'].join(' ')}>{macdValue.macd.toFixed(3)}</span>
                </div>
              )}
              {showADX && (
                <div className="flex items-center gap-2" title="ADX (14) - trend strength (>25 trending, <20 chop)">
                  <div className="w-4 h-0.5 rounded" style={{ backgroundColor: '#eab308' }}></div>
                  <span className="text-xs text-muted-foreground font-medium">ADX</span>
                  <span className={['text-xs tabular-nums', adxValue != null && adxValue >= 25 ? 'text-amber-600 dark:text-amber-300' : 'text-muted-foreground'].join(' ')}>{adxValue != null ? adxValue.toFixed(1) : '--'}</span>
                </div>
              )}

              {/* Separator (hidden on mobile where the legend wraps) */}
              <div className="hidden h-4 w-px bg-muted/60 md:block"></div>

              {/* Marker key — each chip is a TOGGLE: click to hide/show that marker type on the chart (owner:
                  declutter). A hidden chip dims + strikes through; the DOT still carries the color. */}
              <div className="flex flex-wrap items-center gap-1">
                {(() => {
                  const pill = (key: string, swatch: React.ReactNode, label: string, title?: string) => {
                    const off = hiddenMarkers.has(key);
                    return (
                      <button
                        type="button"
                        onClick={() => toggleMarker(key)}
                        title={title ?? `Click to ${off ? 'show' : 'hide'} ${label} markers`}
                        className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 transition-all hover:bg-accent dark:hover:bg-zinc-700/60 ${off ? 'bg-card/40 opacity-40' : 'bg-muted/40'}`}
                      >
                        <span className={off ? 'opacity-50 grayscale' : ''}>{swatch}</span>
                        <span className={`text-[10px] font-semibold text-foreground/80 ${off ? 'line-through decoration-zinc-500' : ''}`}>{label}</span>
                      </button>
                    );
                  };
                  return (
                    <>
                      {pill('rvol', <span className="block h-2 w-2 rounded-full bg-emerald-500" />, 'RVOL')}
                      {pill('mover', <span className="block h-2 w-2 bg-yellow-500" style={{ clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }} />, 'Mover')}
                      {pill('ema', <span className="block h-2 w-2 rounded-full bg-purple-500" />, 'EMA')}
                      {/* Its own chip with a MATCHING swatch. These render as orange down-arrows but used
                          to hide under the purple "EMA" pill, so the most numerous marker on the chart
                          looked unlabeled and un-hideable. */}
                      {pill('emaBreak', <span className="block h-0 w-0 border-x-[4px] border-t-[6px] border-x-transparent border-t-orange-500" />, '200 EMA break', 'Price crossed BELOW the 200 EMA. Click to hide these.')}
                      {pill('halt', <span className="block h-2 w-2 rounded-[1px] bg-red-500" />, 'Halt')}
                      {pill('resume', <span className="block h-2 w-2 rounded-[1px] bg-green-500" />, 'Resume')}
                      {pill('news', <span className="text-[10px] leading-none">📰</span>, 'News')}
                      {pill('trade', <span className="flex h-3 w-3 items-center justify-center rounded-full bg-muted ring-1 ring-amber-300 dark:ring-amber-400/70"><User className="h-1.5 w-1.5 text-amber-600 dark:text-amber-200" strokeWidth={3} /></span>, 'Trade')}
                      {redditPosts.length > 0 && pill('reddit', <span className="flex h-3 w-3 items-center justify-center rounded-full" style={{ background: '#FF4500' }}><svg viewBox="0 0 32 32" width="8" height="8" aria-hidden="true"><ellipse cx="16" cy="19" rx="11" ry="8" fill="#fff" /><circle cx="12" cy="18.5" r="1.7" fill="#FF4500" /><circle cx="20" cy="18.5" r="1.7" fill="#FF4500" /></svg></span>, 'Reddit', 'Minute we posted this ticker to Reddit. Click to hide/show; hover a pin for the move since.')}
                    </>
                  );
                })()}
              </div>
            </div>
            
            {/* flex-col + flex-1 chain so the candle canvas FILLS the card's real height (the ResizeObserver
                sizes the canvas to the container's clientHeight). This replaces the old fixed chartHeight +
                a fragile RESERVED-height guess, which left blank space inside the grown card and pushed the
                volume pane off-screen. chartHeight stays only as a min-height floor so it can never collapse. */}
            <div className="bg-background border border-border/60 rounded-2xl overflow-visible shadow-2xl ring-1 ring-border dark:ring-zinc-800/50" style={{ height: chartHeight + 20, minHeight: 300 }}>
              {/* Chart container only mounted when !loading so it's never covered and gets real layout (fixes blank chart) */}
              <div ref={chartWrapRef} className="relative p-2 w-full" style={{ height: chartHeight }}>
                {loading ? (
                  // h-full of the wrapper, which is already chartHeight tall: a fixed min-h-[500px] here overflowed the card
                  // and drew over the Volume card once the loading card is sized like the loaded one (see chartWrapRef).
                  <div className="w-full h-full flex items-center justify-center bg-background/90">
                    <div className="text-center">
                      <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-amber-300 dark:border-amber-400 border-r-transparent mb-4"></div>
                      <p className="text-muted-foreground text-sm font-medium">Loading chart data...</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div
                      ref={chartContainerRef}
                      data-chart-container
                      className="w-full bg-background"
                      style={{ width: '100%', height: chartHeight, minHeight: chartHeight, position: 'relative', display: 'block', visibility: 'visible', flexShrink: 0 }}
                    />
                    {/* ── Session shading bands (premarket blue / after-hours purple) ── */}
                    <div ref={sessionBandsRef} className="pointer-events-none absolute left-0 top-0 w-full overflow-hidden" style={{ height: chartHeight, zIndex: 2 }} />
                    {/* ── Hover tooltip: news 📰 + scanner alerts on the crosshaired bar ── */}
                    {hoverInfo && (
                      <div className="pointer-events-none absolute left-3 top-3 z-30 w-[300px] max-w-[calc(100%-1.5rem)] overflow-hidden rounded-xl border border-border/80 bg-background/95 shadow-2xl dark:shadow-black/70 ring-1 ring-border dark:ring-black/40 backdrop-blur-sm">
                        {hoverInfo.news.length > 0 && (
                          <div className="border-b border-border/80 bg-gradient-to-r from-sky-500/10 to-transparent px-3 py-2">
                            {hoverInfo.news.map((n, i) => (
                              <div key={i} className={i > 0 ? 'mt-2 border-t border-border/70 pt-2' : ''}>
                                <div className="mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider">
                                  <span className="text-sm leading-none">📰</span>
                                  <span className="text-sky-700 dark:text-sky-300">News</span>
                                  {n.publisher && <span className="truncate font-medium normal-case tracking-normal text-muted-foreground">· {n.publisher}</span>}
                                  {n.sentiment && (
                                    <span className={`ml-auto shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold ${n.sentiment === 'positive' ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : n.sentiment === 'negative' ? 'bg-rose-50 dark:bg-rose-500/15 text-rose-600 dark:text-rose-300' : 'bg-muted/40 text-foreground/80'}`}>
                                      {n.sentiment === 'positive' ? 'BULLISH' : n.sentiment === 'negative' ? 'BEARISH' : 'NEUTRAL'}
                                    </span>
                                  )}
                                </div>
                                <div className="text-[13px] font-semibold leading-snug text-foreground">{n.title}</div>
                              </div>
                            ))}
                          </div>
                        )}
                        {hoverInfo.alerts.length > 0 && (
                          <div className="space-y-1 px-3 py-2">
                            {hoverInfo.alerts.map((a, i) => (
                              <div key={i} className="flex items-center gap-2 text-[11px]">
                                <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: a.color }} />
                                <span className="font-semibold text-foreground/80">{a.label}</span>
                                {a.rvol != null && <span className="font-semibold text-amber-600 dark:text-amber-300">{a.rvol.toFixed(1)}× RVOL</span>}
                                {a.pct != null && <span className={a.pct >= 0 ? 'font-semibold text-emerald-700 dark:text-emerald-400' : 'font-semibold text-rose-600 dark:text-rose-400'}>{a.pct >= 0 ? '+' : ''}{a.pct.toFixed(1)}%</span>}
                                {a.price != null && a.price > 0 && <span className="ml-auto text-muted-foreground">${a.price < 1 ? a.price.toFixed(3) : a.price.toFixed(2)}</span>}
                              </div>
                            ))}
                          </div>
                        )}
                        {hoverInfo.alerts.some((a) => a.label === 'EMA trending') && emaTrendMatrix && emaTrendMatrix.symbol === symbol && emaTrendMatrix.tfs.length > 0 && (
                          <div className="border-t border-indigo-300 dark:border-indigo-500/20 bg-gradient-to-r from-indigo-500/10 to-transparent px-3 py-2">
                            <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-indigo-700 dark:text-indigo-300">
                              <span className="h-2 w-2 rounded-full" style={{ background: '#818cf8' }} />
                              Candles trending above EMA
                            </div>
                            <table className="w-full text-[11px] tabular-nums">
                              <thead>
                                <tr className="text-[9px] uppercase tracking-wider text-muted-foreground">
                                  <th className="pb-0.5 text-left font-semibold">TF</th>
                                  <th className="pb-0.5 text-right font-semibold">9 EMA</th>
                                  <th className="pb-0.5 text-right font-semibold">50 EMA</th>
                                  <th className="pb-0.5 text-right font-semibold">200 EMA</th>
                                </tr>
                              </thead>
                              <tbody>
                                {emaTrendMatrix.tfs.map((row) => (
                                  <tr key={row.tf} className="border-t border-border/50">
                                    <td className="py-[3px] text-left font-bold text-foreground/80">{row.tf}</td>
                                    <td className={`py-[3px] text-right font-semibold ${row.count9 > 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-zinc-600'}`}>{row.count9 > 0 ? row.count9 : '-'}</td>
                                    <td className={`py-[3px] text-right font-semibold ${row.count50 > 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-zinc-600'}`}>{row.count50 > 0 ? row.count50 : '-'}</td>
                                    <td className={`py-[3px] text-right font-semibold ${row.count200 > 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-zinc-600'}`}>{row.count200 > 0 ? row.count200 : '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <div className="mt-1 text-[9px] leading-snug text-zinc-600">Consecutive bars closed at or above each EMA. 1m/5m/15m reset each session; 30m/1h span sessions.</div>
                          </div>
                        )}
                        {hoverInfo.news.length > 0 && (
                          <div className="border-t border-border/60 px-3 py-1.5 text-[9px] uppercase tracking-wider text-zinc-600">Open the News panel to read the full story</div>
                        )}
                      </div>
                    )}
                    {/* plCard removed — chart-level Alpaca state tracks a different trade than the one being hovered */}
                    {/* ── Reddit post pins ── little snoo at the minute we posted this ticker to Reddit,
                        with a hover card: price-at-post, peak-since (%), and the current move. ── */}
                    {!hiddenMarkers.has('reddit') && redditPins.map((pin, i) => {
                      const x = redditXPos[i];
                      if (x == null || !Number.isFinite(x)) return null;
                      // The bucket's FIRST post drives the price math: "move since" is measured from
                      // the post we actually published first, not an arbitrary one in the group.
                      const post = pin.posts[0];
                      const psec = post.postedAt / 1000;
                      // SHORT-biased posts profit when price FALLS, so the favorable extreme is the LOWEST LOW
                      // (not the highest high) and the % must invert (entry−price)/entry (owner: SNDK short showed
                      // gains rising as price rose). Long posts keep highest-high + (price−entry)/entry.
                      const isShort = post.bias === 'short';
                      // Price when we posted = close of the last bar at/before the post minute.
                      let priceAt: number | null = null;
                      let peak: number | null = null; // long: best HIGH since; short: best (lowest) LOW since
                      for (const c of candles) {
                        const cs = c.timestamp / 1000;
                        if (cs <= psec) priceAt = c.close;
                        if (cs >= psec) peak = peak == null ? (isShort ? c.low : c.high) : (isShort ? Math.min(peak, c.low) : Math.max(peak, c.high));
                      }
                      if (priceAt == null && candles.length) priceAt = candles[0].close;
                      const now = currentPrice ?? (candles.length ? candles[candles.length - 1].close : null);
                      // Favorable % for the post's direction: long = up is good, short = down is good.
                      const favPct = (px: number | null) => (priceAt && px ? ((isShort ? (priceAt - px) : (px - priceAt)) / priceAt) * 100 : null);
                      const peakPct = favPct(peak);
                      const nowPct = favPct(now);
                      const isHovered = hoveredRedditIdx === i;
                      const biasLabel = post.bias === 'long' ? 'Long' : post.bias === 'short' ? 'Short' : post.bias === 'neutral' ? 'Watch' : (post.bias || 'Watch');
                      const biasClass = post.bias === 'long' ? 'text-emerald-700 dark:text-emerald-300' : post.bias === 'short' ? 'text-rose-600 dark:text-rose-300' : 'text-amber-600 dark:text-amber-300';
                      const timeLabel = new Date(post.postedAt).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
                      const pctClass = (v: number | null) => v == null ? 'text-muted-foreground' : v >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400';
                      const pctText = (v: number | null) => v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
                      return (
                        <div
                          key={`reddit-${i}`}
                          className="absolute pointer-events-auto"
                          style={{ left: x - 11, top: 6, zIndex: isHovered ? 60 : 22 }}
                          onMouseEnter={() => setHoveredRedditIdx(i)}
                          onMouseLeave={() => setHoveredRedditIdx((cur) => (cur === i ? null : cur))}
                        >
                          {/* Snoo badge — Reddit orange, scales on hover */}
                          <a
                            href={post.url || undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`We posted $${symbol} to Reddit at ${timeLabel} ET`}
                            className={`relative flex h-[22px] w-[22px] items-center justify-center rounded-full shadow-md ring-2 transition-transform ${isHovered ? 'scale-110 ring-border dark:ring-white/70' : 'ring-border dark:ring-black/30'}`}
                            style={{ background: '#FF4500' }}
                          >
                            <svg viewBox="0 0 32 32" width="14" height="14" aria-hidden="true">
                              <circle cx="23" cy="7" r="2.6" fill="#fff" />
                              <path d="M15.5 8 L22 7.4" stroke="#fff" strokeWidth="1.4" fill="none" strokeLinecap="round" />
                              <ellipse cx="16" cy="19" rx="11" ry="8" fill="#fff" />
                              <circle cx="12" cy="18.5" r="1.7" fill="#FF4500" />
                              <circle cx="20" cy="18.5" r="1.7" fill="#FF4500" />
                              <path d="M11.5 23 Q16 25.6 20.5 23" stroke="#FF4500" strokeWidth="1.4" fill="none" strokeLinecap="round" />
                            </svg>
                            {/* COUNT BADGE. Only when the bucket holds more than one post, so a single
                                post stays a clean icon and a cluster says how many it stands for. */}
                            {pin.count > 1 && (
                              <span
                                className="absolute -right-1 -top-1 flex h-[13px] min-w-[13px] items-center justify-center rounded-full bg-background px-[3px] text-[9px] font-bold leading-none text-foreground ring-1 ring-border dark:ring-white/50"
                                aria-hidden="true"
                              >
                                {pin.count}
                              </span>
                            )}
                          </a>
                          {/* Hover card — PINNED to the chart's top-left (owner: top-right covered the price
                              axis and entry/exit labels; the left corner stays clear of every label). */}
                          {isHovered && (() => {
                            return (
                            <div
                              className="absolute w-[210px] rounded-xl border border-orange-300 dark:border-orange-500/40 bg-background/95 p-2.5 shadow-2xl backdrop-blur-sm"
                              style={{ zIndex: 61, top: 2, left: 19 - x }}
                            >
                              <div className="mb-1.5 flex items-center gap-1.5 border-b border-border pb-1.5">
                                <span className="flex h-4 w-4 items-center justify-center rounded-full" style={{ background: '#FF4500' }}>
                                  <svg viewBox="0 0 32 32" width="10" height="10" aria-hidden="true">
                                    <ellipse cx="16" cy="19" rx="11" ry="8" fill="#fff" />
                                    <circle cx="12" cy="18.5" r="1.7" fill="#FF4500" />
                                    <circle cx="20" cy="18.5" r="1.7" fill="#FF4500" />
                                  </svg>
                                </span>
                                <span className="text-[11px] font-bold text-foreground">Reddit post</span>
                                <span className={`ml-auto text-[10px] font-bold uppercase tracking-wide ${biasClass}`}>{biasLabel}</span>
                              </div>
                              <div className="mb-1.5 flex items-center justify-between text-[10px] text-muted-foreground">
                                <span>{timeLabel} ET</span>
                                {post.timeframes && <span className="rounded bg-muted/80 px-1.5 py-0.5 font-semibold text-foreground/80">{post.timeframes}</span>}
                              </div>
                              <div className="space-y-1 text-[11px]">
                                <div className="flex items-center justify-between">
                                  <span className="text-muted-foreground">Price when posted</span>
                                  <span className="font-semibold tabular-nums text-foreground/80">{priceAt != null ? `$${formatPrice(priceAt)}` : '—'}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-muted-foreground">{isShort ? 'Low since' : 'Peak since'}</span>
                                  <span className={`font-semibold tabular-nums ${pctClass(peakPct)}`}>{peak != null ? `$${formatPrice(peak)}` : '—'} <span className="opacity-80">({pctText(peakPct)})</span></span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-muted-foreground">Now</span>
                                  <span className={`font-semibold tabular-nums ${pctClass(nowPct)}`}>{now != null ? `$${formatPrice(now)}` : '—'} <span className="opacity-80">({pctText(nowPct)})</span></span>
                                </div>
                              </div>
                              {post.url && (
                                <div className="mt-1.5 border-t border-border pt-1.5 text-[10px] font-semibold text-orange-600 dark:text-orange-400">Open the Reddit thread →</div>
                              )}
                            </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                    {/* ── News pins ── one crisp sky-blue newspaper icon per article-minute, at the bottom of
                        the price pane. Hovering it drives the SAME top-left card as the scanner markers
                        (headline + publisher + sentiment) — reliable per-icon hover, no crosshair-bucket miss. */}
                    {showNewsMarkers && !hiddenMarkers.has('news') && newsPins.map((pin, i) => {
                      const x = newsXPos[i];
                      if (x == null || !Number.isFinite(x)) return null;
                      const isHovered = hoveredNewsIdx === i;
                      return (
                        <div
                          key={`news-${pin.bucket}`}
                          className="absolute pointer-events-auto"
                          style={{ left: x - 9, bottom: 6, zIndex: isHovered ? 60 : 21 }}
                          onMouseEnter={() => { setHoveredNewsIdx(i); setHoverInfo({ news: pin.articles, alerts: [] }); }}
                          onMouseLeave={() => { setHoveredNewsIdx((cur) => (cur === i ? null : cur)); setHoverInfo(null); }}
                        >
                          <span
                            aria-label={pin.count > 1 ? `${pin.count} news articles` : 'News article'}
                            className={`flex h-[18px] w-[18px] items-center justify-center rounded-full bg-sky-500 shadow-md ring-2 transition-transform ${isHovered ? 'scale-110 ring-border dark:ring-white/70' : 'ring-border dark:ring-black/30'}`}
                          >
                            <Newspaper className="h-2.5 w-2.5 text-foreground" strokeWidth={2.5} />
                          </span>
                          {pin.count > 1 && (
                            <span className="absolute -right-1 -top-1 flex h-3 min-w-[12px] items-center justify-center rounded-full bg-card px-0.5 text-[8px] font-bold text-sky-700 dark:text-sky-300 ring-1 ring-sky-300 dark:ring-sky-500/60">{pin.count}</span>
                          )}
                        </div>
                      );
                    })}
                    {/* ── Thoth / Ra trade marker overlay ── (hidden when the Trade legend chip is toggled off) */}
                    {!hiddenMarkers.has('trade') && (() => {
                    const _visibleTradeAlerts = tradeAlertsForRender.filter(isTradeAlert).filter((alert) => {
                      // DEV-book markers hidden unless the DEV pill is on (see showDevBook). Rider is LIVE —
                      // see isLiveWatcherBook (the old !== 'Watcher' test hid every synthesized Rider marker: ADVB).
                      if (!showDevBook && (alert.type === 'watcher' || alert.data?.triggeredBy === 'watcher')) {
                        const dm = parseTradeMeta(alert);
                        if (!isLiveWatcherBook(dm?.instance)) return false;
                      }
                      // A3 — ONE marker per ENTRY, not per fill: a multi-fill / pyramid entry emits
                      // several buy alerts grouped into one pair. Render only the pair's PRIMARY buy
                      // alert; secondary fills fold into it (shown as multi-entry in the hover card).
                      // Sell/exit alerts are never grouped into buyAlerts, so they always render.
                      // EXCEPTION: a synthetic fill-exit SELL (Ra exit with no sell alert) must render
                      // its own exit avatar — it's the only hover target for the exit line / P&L card
                      // (there's no sidebar entry for it). Real sell alerts stay hidden (sidebar covers them).
                      if (isFillExitAlert(alert)) return true;
                      // Phantom Watcher SELL (premature-exit bug, no real Alpaca sell fill):
                      // suppress the exit marker so the chart matches Alpaca (position still held).
                      if (phantomWatcherSellIds.has(alert.id)) return false;
                      // Suppress ONLY secondary pyramid BUY alerts (fold into the primary buy).
                      // The SELL alert is also registered in completedPairByAlertId (so the hover
                      // card can be reached from the exit), but it is the EXIT avatar and MUST
                      // render — never suppress it. Guard on "is a non-primary buyAlert of the pair"
                      // instead of "id !== pair.buyAlert.id" (the old test wrongly hid the sell).
                      const cpf = completedPairByAlertId.get(alert.id);
                      if (cpf && cpf.pair.sellAlert.id !== alert.id
                          && cpf.pair.buyAlerts.some(b => b.id === alert.id)
                          && cpf.pair.buyAlert.id !== alert.id) return false;
                      // UMBRELLA TRADE (owner): scale-out sell legs fold into their round-trip's card — no
                      // separate avatars per bank. The canonical exit avatar carries the whole blended story.
                      if (cpf && cpf.pair.partialSellAlerts?.some((s) => s.id === alert.id)) return false;
                      const opf = openPairByAlertId.get(alert.id);
                      if (opf && opf.buyAlert.id !== alert.id) return false;
                      return true;
                    });
                    // ── Clustered trade markers ──────────────────────────────────
                    // Every trade marker sits on the SAME fixed row (bottom: 44 — clears the ~28px time axis so the
                    // avatars sit IN the plot, not over the axis labels), so a
                    // dense time window collapses avatars into an unreadable pile. Group
                    // by x-proximity ONLY (they can only overlap horizontally) and draw
                    // ONE cluster badge per pile of >= 2; singles render unchanged below.
                    // The badge is a fan of up to 3 avatars (~64px wide), so two clusters whose CENTROIDS are only
                    // ~25px apart still collide (owner: the "×2 ×2 ×3" pile-up at the base). Chain each marker to the
                    // cluster's RIGHTMOST member (not its drifting centroid) at the badge FOOTPRINT width, so a dense
                    // area collapses into ONE clean badge; the badge is then placed at the pile's centroid.
                    const CLUSTER_PX = 40;
                    const _clusterEntries = _visibleTradeAlerts
                      .map((a) => ({ alert: a, x: tradeXPos[a.id] }))
                      .filter((e): e is { alert: Alert; x: number } => e.x != null && Number.isFinite(e.x))
                      .sort((a, b) => a.x - b.x);
                    const _rawClusters: { key: string; x: number; maxX: number; alerts: Alert[] }[] = [];
                    for (const e of _clusterEntries) {
                      const last = _rawClusters[_rawClusters.length - 1];
                      if (last && e.x - last.maxX <= CLUSTER_PX) {
                        last.alerts.push(e.alert);
                        last.maxX = e.x;
                        last.x = last.alerts.reduce((s, al) => s + (tradeXPos[al.id] as number), 0) / last.alerts.length;
                      } else {
                        _rawClusters.push({ key: e.alert.id, x: e.x, maxX: e.x, alerts: [e.alert] });
                      }
                    }
                    const tradeClusters: { key: string; x: number; alerts: Alert[] }[] =
                      _rawClusters.map(({ key, x, alerts }) => ({ key, x, alerts }));
                    const clusteredIds = new Set<string>();
                    for (const c of tradeClusters) if (c.alerts.length >= 2) for (const a of c.alerts) clusteredIds.add(a.id);
                    return (
                    <>
                    {_visibleTradeAlerts.map((alert) => {
                      if (clusteredIds.has(alert.id)) return null;
                      const x = tradeXPos[alert.id];
                      if (x == null || !Number.isFinite(x)) return null;
                      const meta = parseTradeMeta(alert);
                      const strategy = resolveTradeStrategy(alert);
                      const avatarSrc = resolveTradeAvatar(alert, strategy);
                      // A member's own paper-game trade: render THEIR picture + a compact bought/sold
                      // card (price · shares · value; realized P&L on sells) instead of AI entry/exit rows.
                      const isMember = strategy === 'member';
                      const memberPrice = (meta?.side === 'sell' ? (meta?.exitPrice ?? alert.data?.price) : (meta?.entryPrice ?? alert.data?.price));
                      const memberShares = meta?.shares;
                      const memberValue = (typeof memberPrice === 'number' && typeof memberShares === 'number')
                        ? memberPrice * memberShares : undefined;
                      // DEV-book trades are visually distinct (CLRO 2026-07-07: live open + dev
                      // round-trip on the same chart read as a contradiction with no book tags).
                      const isDevBook = strategy === 'watcher' && !isLiveWatcherBook(meta?.instance);
                      const strategyLabel = resolveTradeLabel(alert, strategy) + (isDevBook ? ' \u00b7 DEV book' : '');
                      const rawSide = meta?.side ?? 'buy';
                      // Osiris is a short strategy: SHORT (sell) = entry, COVER (buy) = exit.
                      // isBuy means "is this the entry marker" for display (ring color, tooltip label).
                      const isBuy = strategy === 'osiris' ? rawSide === 'sell' : rawSide === 'buy';
                      const isDirectHover = hoveredTradeId === alert.id;

                      // Pair lookup — used for partner-column highlight + unified tooltip
                      const pairEntry = completedPairByAlertId.get(alert.id);
                      const pair = pairEntry?.pair ?? null;
                      const tradeNum = pairEntry?.tradeNum ?? null;
                      const isPairHover = !!pair && (
                        pair.buyAlerts.some(b => hoveredTradeId === b.id) ||
                        hoveredTradeId === pair.sellAlert.id
                      );
                      // Open position: group all pyramid slices together for cross-highlighting
                      const openPair = isBuy && !pair ? (openPairByAlertId.get(alert.id) ?? null) : null;
                      const openIsMulti = (openPair?.buyAlerts.length ?? 0) > 1;
                      const isOpenPairHover = !!openPair && openPair.buyAlerts.some(b => hoveredTradeId === b.id);
                      const isHovered = isDirectHover || isPairHover || isOpenPairHover;
                      // Closed-position buy: the Ra/Thoth position was exited (pair exists).
                      // Dim these so the ACTIVE open-position marker stands out clearly.
                      const isClosedBuy = isBuy && pair !== null;

                      // Per-trade Alpaca fill resolution: Ra/Watcher meta prices are SIGNAL prices;
                      // the real fill differs slightly. Prefer the matched fill, fall back to meta.
                      // Watcher resolves against watcherFills (its CONFIRMED fills), so the marker
                      // price reflects the actual Alpaca fill, not the breakout signal level.
                      const cardMs = alert.barTimestamp ?? alert.timestamp;
                      const cardFill = (strategy === 'ra' || strategy === 'watcher')
                        ? findFillForStrategy(strategy, rawSide === 'buy' ? 'buy' : 'sell', cardMs)
                        : null;

                      // SIGNAL price from the alert (breakout level for Watcher); used to show
                      // "signal $X → filled $Y" in the hover card alongside the real fill.
                      const signalPrice = isBuy ? (meta?.entryPrice ?? alert.data?.price) : (meta?.exitPrice ?? alert.data?.price);
                      // Trade price for price-line effect on hover — the real fill when we have one.
                      const tradePrice = cardFill?.price ?? signalPrice;

                      // Completed pair data for the rich tooltip
                      const buyMeta  = pair ? parseTradeMeta(pair.buyAlert)  : null;
                      const sellMeta = pair?.sellMeta ?? null;
                      const pl    = sellMeta?.realizedPl;
                      const plPct = sellMeta?.realizedPlPct;
                      // Fill-resolved entry/exit for the whole round trip (weighted across pyramid buys)
                      const pairFillPx = alpacaPricesForPair(pair);
                      // Canonical entry: Alpaca fill avg → paper avgEntryPrice → meta
                      const entryPx = pairFillPx.entry ?? pair?.avgEntryPrice ?? buyMeta?.entryPrice ?? pair?.buyAlert.data?.price;
                      const exitPx  = pairFillPx.exit  ?? sellMeta?.exitPrice  ?? pair?.sellAlert.data?.price;
                      // Total shares: prefer actual Alpaca fill qty, then sell meta, then sum of buys
                      const pairShares = pairFillPx.qty ?? sellMeta?.shares ?? pair?.totalBuyShares ?? buyMeta?.shares;
                      const rawDiff = (typeof entryPx === 'number' && typeof exitPx === 'number') ? exitPx - entryPx : null;
                      const computedPl    = rawDiff !== null && typeof pairShares === 'number' ? rawDiff * pairShares : rawDiff;
                      const computedPlPct = rawDiff !== null && typeof entryPx === 'number' && entryPx > 0 ? (rawDiff / entryPx) * 100 : null;
                      // When we have real fills, the computed P&L IS the Alpaca P&L — prefer it
                      // over the paper-game realizedPl (paper sizing differs from live sizing).
                      const hasFillPl = pairFillPx.entry != null && pairFillPx.exit != null;
                      const effectivePl    = hasFillPl && typeof computedPl === 'number' ? computedPl
                        : typeof pl === 'number' ? pl : (typeof computedPl === 'number' ? computedPl : null);
                      const effectivePlPct = hasFillPl && typeof computedPlPct === 'number' ? computedPlPct
                        : typeof plPct === 'number' ? plPct : (typeof computedPlPct === 'number' ? computedPlPct : null);
                      // Multi-entry info
                      const entryCount = pair?.buyAlerts.length ?? 1;
                      const isMultiEntry = entryCount > 1;
                      const isProfit = typeof effectivePl === 'number' ? effectivePl >= 0 : null;
                      const entryMs = pair ? (pair.buyAlert.barTimestamp  ?? pair.buyAlert.timestamp)  : null;
                      const exitMs  = pair ? (pair.sellAlert.barTimestamp ?? pair.sellAlert.timestamp) : null;
                      const durationMin = entryMs && exitMs ? Math.round((exitMs - entryMs) / 60000) : null;
                      // PEAK during the trade (owner: "did we leave money on the table, and how much"). Max high of
                      // the loaded bars inside the round trip — page-faithful: the same wave the owner sees on the
                      // chart. Null when the trade sits inside a single coarser bar (e.g. a 5-minute trade viewed on
                      // the daily timeframe), where a bar high would overstate the intra-trade peak.
                      let peakPx: number | null = null;
                      if (entryMs != null && exitMs != null && typeof entryPx === 'number' && entryPx > 0) {
                        for (const c of candles) { if (c.timestamp >= entryMs && c.timestamp <= exitMs && (peakPx == null || c.high > peakPx)) peakPx = c.high; }
                      }
                      const peakPct = peakPx != null && typeof entryPx === 'number' && entryPx > 0 ? (peakPx / entryPx - 1) * 100 : null;
                      const leftOnTablePct = peakPct != null && typeof effectivePlPct === 'number' ? Math.max(0, peakPct - effectivePlPct) : null;

                      const reasonLabel: Record<string, string> = {
                        trail_exit: 'Trail stop',
                        condAB_9ema_trail: '9-EMA trail',
                        condJLM_9ema_trail: '9-EMA trail',
                        condKV_9ema_trail: '9-EMA trail',
                        profit_50ema_trail: '50-EMA lock',
                        profit_9ema_tight: '9-EMA lock',
                        max_loss: 'Max loss',
                        cross_below_ema: 'EMA cross',
                        below_ema_entry_stop: 'EMA entry stop',
                        manual: 'Manual',
                        half_profit_take_100pct: '50% profit take',
                        blowoff_take: 'Blow-off take',
                        wick_rejection_take: 'Wick-rejection take',
                        friday_eod_derisk: 'Friday de-risk',
                        condC_pattern_failed: 'Pattern failed',
                        sub5_stop: 'Sub-$5 stop',
                        sub5_eod_exit: 'EOD exit',
                        same_day_disappointment: 'Disappointment cut',
                        overnight_gap_stop: 'Overnight gap stop',
                        alpaca_fill_exit: 'Alpaca exit',
                      };

                      // Extract entry condition label from buy-side reason (e.g. "condJ · EMA20d..." → "condJ")
                      const rawEntryReason = buyMeta?.reason ?? (isBuy ? meta?.reason : null) ?? '';
                      const condLabelMatch = rawEntryReason.match(/^(cond[A-Z][a-zA-Z0-9]*)/);
                      // Prefer the journal's AUTHORITATIVE condition (matched to this marker by nearest
                      // entry price, within 2%) over the alert-reason guess — fixes condA-shown-for-condH.
                      const jCondMatch = (raJournalEntries.length > 0 && typeof entryPx === 'number' && entryPx > 0)
                        ? raJournalEntries
                            .filter(j => j.entryReason && Math.abs(j.entryPrice - entryPx) / entryPx < 0.02)
                            .sort((a, b) => Math.abs(a.entryPrice - entryPx) - Math.abs(b.entryPrice - entryPx))[0]
                        : null;
                      // Watcher trades carry their entry reason in `watcherEntries` (NOT raJournalEntries,
                      // which is Ra-only) — match the nearest BUY by fill/signal price so a Watcher marker
                      // (e.g. FCEL) surfaces its setup instead of a blank. This is the leg the first fix missed.
                      const wCondMatch = (watcherEntries.length > 0 && typeof entryPx === 'number' && entryPx > 0)
                        ? watcherEntries
                            .filter(w => w.side === 'buy' && w.reason && ((w.fillPrice ?? w.signalPrice) > 0) && Math.abs(((w.fillPrice ?? w.signalPrice) as number) - entryPx) / entryPx < 0.02)
                            .sort((a, b) => Math.abs(((a.fillPrice ?? a.signalPrice) as number) - entryPx) - Math.abs(((b.fillPrice ?? b.signalPrice) as number) - entryPx))[0]
                        : null;
                      const condLabel = jCondMatch?.entryReason ?? wCondMatch?.reason ?? (condLabelMatch ? condLabelMatch[1] : null);
                      // RVOL from entry reason (e.g. "RVOL 18.57×" → "18.6×")
                      const rvolMatch = rawEntryReason.match(/RVOL\s+([\d.]+)[×x]/);
                      const rvolDisplay = rvolMatch ? `${parseFloat(rvolMatch[1]).toFixed(1)}×` : null;
                      // % price move from entry to exit
                      const pctMove = typeof entryPx === 'number' && typeof exitPx === 'number' && entryPx > 0
                        ? ((exitPx - entryPx) / entryPx) * 100
                        : null;

                      // ── Hover detail: strategy timeframe · live unrealized % · full entry reason ──
                      // Timeframe lane (scalp/day/swing/…) rides in the Rider's strategyLabel ("The Rider · scalp").
                      // Surface it so the hover says WHICH lane took the trade (owner: "the watcher · scalp").
                      const tfTag = (() => {
                        const sl = buyMeta?.strategyLabel ?? meta?.strategyLabel ?? '';
                        const seg = sl.includes('·') ? sl.split('·').pop()!.trim() : '';
                        return /^(scalp|day|swing|position|investing)$/i.test(seg) ? seg.toLowerCase() : null;
                      })();
                      // Live unrealized % for an OPEN position — current price vs the (avg) entry, so hovering an
                      // open marker shows how the trade is doing RIGHT NOW (owner: "current % chg of open position").
                      const openCostBasis = openPair?.avgEntryPrice ?? (typeof tradePrice === 'number' ? tradePrice : null);
                      const openLivePct = (isBuy && !pair && !isMember && typeof currentPrice === 'number' && currentPrice > 0
                        && typeof openCostBasis === 'number' && openCostBasis > 0)
                        ? ((currentPrice - openCostBasis) / openCostBasis) * 100 : null;
                      // Fuller, human entry reason (owner wanted MORE detail than the one-clause version): keep the
                      // whole reason, drop a leading "condX ·" (shown as its own chip), soften snake_case + tags.
                      const humanizeReason = (r: string | null | undefined): string | null => {
                        if (!r) return null;
                        let s = String(r).split('__THOTH')[0].replace(/^cond[A-Z][a-zA-Z0-9]*\s*[·:-]?\s*/, '').trim();
                        s = s.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
                        return s.length ? s.charAt(0).toUpperCase() + s.slice(1) : null;
                      };
                      // Open-position entry reason (buys have their setup in meta.reason; closed trades use the
                      // pair's buyMeta below). Prefer the journal/watcher-matched reason when present.
                      const openEntryReason = isBuy && !pair
                        ? humanizeReason(wCondMatch?.reason ?? jCondMatch?.entryReason ?? meta?.reason)
                        : null;

                      // Top accent bar color
                      const accentCls = pair
                        ? (isProfit === true ? 'bg-emerald-500/70' : isProfit === false ? 'bg-rose-500/70' : 'bg-muted/40')
                        : (isBuy ? 'bg-emerald-500/50' : (typeof meta?.realizedPl === 'number' ? (meta.realizedPl >= 0 ? 'bg-emerald-500/60' : 'bg-rose-500/60') : 'bg-rose-500/40'));

                      // Tooltip positioning
                      const chartW = chartContainerRef.current?.clientWidth ?? 700;
                      const chartH = chartContainerRef.current?.clientHeight ?? 500;
                      const nearLeft  = x < 160;
                      const nearRight = x > chartW - 160;
                      const tooltipPos = nearLeft
                        ? { left: -8 }
                        : nearRight
                          ? { right: -8 }
                          : { left: '50%' as const, transform: 'translateX(-50%)' };

                      // Border colour driven by pair outcome, or by side for open trades
                      const borderCls = pair
                        ? (isProfit === true ? 'border-emerald-300 dark:border-emerald-500/40' : isProfit === false ? 'border-rose-300 dark:border-rose-500/40' : 'border-border/50')
                        : (isBuy ? 'border-emerald-300 dark:border-emerald-500/50' : 'border-rose-300 dark:border-rose-500/50');
                      const arrowBorderCls = borderCls;

                      // Paper vs Alpaca distinction — only meaningful for Ra buy entries.
                      // Paper = Ra fired but no Alpaca fill found (chase abort, blocked, etc.)
                      // alpacaStatus 'filled' or a matched cardFill both confirm real money.
                      const isPaperRaEntry = isBuy && strategy === 'ra' && !isClosedBuy && (
                        (meta?.alpacaStatus != null && meta.alpacaStatus !== 'filled') ||
                        (meta?.alpacaStatus == null && alpacaFills.length > 0 && cardFill === null)
                      );

                      return (
                        <div
                          key={alert.id}
                          className="absolute pointer-events-auto transition-opacity duration-200"
                          style={{ left: x - 15, bottom: 44, zIndex: 20, opacity: isClosedBuy && !isHovered ? 0.34 : 1 }}
                          onMouseEnter={() => setHoveredTradeId(alert.id)}
                          onMouseLeave={() => setHoveredTradeId(null)}
                        >
                          {/* Candle-column glow on hover (also lights up the partner bar) */}
                          {isHovered && (
                            <div
                              className="absolute pointer-events-none"
                              style={{
                                left: 1, bottom: 0, width: 28,
                                height: chartHeight - 28,
                                background: isBuy
                                  ? 'rgba(52,211,153,0.13)'
                                  : 'rgba(251,113,133,0.13)',
                                borderLeft:  isBuy ? '1px solid rgba(52,211,153,0.5)' : '1px solid rgba(251,113,133,0.5)',
                                borderRight: isBuy ? '1px solid rgba(52,211,153,0.5)' : '1px solid rgba(251,113,133,0.5)',
                              }}
                            />
                          )}

                          {/* Entry-exit connector on hover — drawn even when the partner candle is
                              scrolled OUT OF VIEW: the line clamps to the chart edge with a labeled
                              tag (owner 2026-07-07: hovering an exit gave no hint where the entry
                              was without zooming out). */}
                          {isHovered && pair && (() => {
                            const partnerAlert = isBuy ? pair.sellAlert : pair.buyAlerts[0];
                            if (!partnerAlert) return null;
                            const partnerX = tradeXPos[partnerAlert.id] ?? null;
                            const cw = chartContainerRef.current?.clientWidth ?? 800;
                            const partnerIsLeft = (partnerAlert.barTimestamp ?? partnerAlert.timestamp) <= (alert.barTimestamp ?? alert.timestamp);
                            const clamped = partnerX == null || partnerX < 0 || partnerX > cw;
                            const partnerScreenX = !clamped && partnerX != null ? partnerX : (partnerIsLeft ? 6 : cw - 6);
                            // wrapper origin = screen (x - 15); avatar center sits at rel 15
                            const rel = (sx: number) => sx - (x - 15);
                            const r1 = Math.min(rel(partnerScreenX), 15);
                            const r2 = Math.max(rel(partnerScreenX), 15);
                            const win2 = typeof pair.sellMeta?.realizedPl === 'number' ? pair.sellMeta.realizedPl >= 0 : null;
                            const lineColor = win2 === false ? 'rgba(251,113,133,0.7)' : 'rgba(52,211,153,0.7)';
                            const txtColor = win2 === false ? '#fda4af' : '#6ee7b7';
                            const pm = parseTradeMeta(partnerAlert);
                            const priceOf = (n: number | null | undefined) => (n != null && n > 0 ? (n < 1 ? n.toFixed(4) : n.toFixed(2)) : null);
                            const partnerPrice = partnerIsLeft
                              ? priceOf(pair.avgEntryPrice ?? pm?.entryPrice ?? pm?.exitPrice)
                              : priceOf(pair.sellMeta?.exitPrice ?? pm?.exitPrice);
                            const partnerLabel = `${partnerIsLeft ? 'entry' : 'exit'}${partnerPrice ? ` $${partnerPrice}` : ''}`;
                            return (
                              <>
                                <div className="absolute pointer-events-none" style={{ left: r1, width: Math.max(0, r2 - r1), bottom: 13, borderTop: `2px dashed ${lineColor}`, zIndex: 18 }} />
                                {clamped && (
                                  <div className="pointer-events-none absolute whitespace-nowrap rounded border bg-card/95 px-1.5 py-0.5 text-[9px] font-bold"
                                    style={{ left: partnerIsLeft ? r1 + 2 : undefined, right: partnerIsLeft ? undefined : 15 - r2 + 2, bottom: 19, borderColor: lineColor, color: txtColor, zIndex: 21 }}>
                                    {partnerIsLeft ? `\u2190 ${partnerLabel} (off-screen)` : `${partnerLabel} (off-screen) \u2192`}
                                  </div>
                                )}
                              </>
                            );
                          })()}

                          {/* Avatar circle — click to open AI's game profile in new tab */}
                          {/* The Watcher gets a distinct indigo identity: indigo entry ring + "W"
                              cap so it never reads like a generic Ra/RVOL marker. Exits are
                              colored by realized P&L (emerald win / rose loss) for at-a-glance outcome. */}
                          {(() => {
                            const isWatcher = strategy === 'watcher';
                            // Exit P&L tint (Watcher only) — fall back to side-colored ring otherwise.
                            const exitWin = !isBuy && isWatcher && typeof meta?.realizedPl === 'number' ? meta.realizedPl >= 0 : null;
                            // A 30px photo of a persona told you nothing at this size and piled into
                            // unreadable clusters. A direction + a trade number tells you everything:
                            // what happened, and which trade it belongs to.
                            const tone = isBuy
                              ? isClosedBuy ? 'zinc'
                                : isPaperRaEntry ? 'amber'
                                : isWatcher ? 'indigo'
                                : 'emerald'
                              : exitWin === true ? 'emerald'
                                : exitWin === false ? 'rose'
                                : isWatcher ? 'indigo'
                                : 'rose';
                            const GLYPH: Record<string, string> = {
                              zinc: 'text-muted-foreground', amber: 'text-amber-600 dark:text-amber-400', indigo: 'text-indigo-700 dark:text-indigo-400',
                              emerald: 'text-emerald-700 dark:text-emerald-400', rose: 'text-rose-600 dark:text-rose-400',
                            };
                            const CHIP: Record<string, string> = {
                              zinc: 'bg-muted text-foreground', amber: 'bg-amber-500 text-zinc-950',
                              indigo: 'bg-indigo-500 text-foreground', emerald: 'bg-emerald-500 text-zinc-950',
                              rose: 'bg-rose-500 text-foreground',
                            };
                            const tradeNo = tradeNumberByAlertId.get(alert.id) ?? null;
                            // Ring carries the OUTCOME, matching the glyph/chip palette above.
                            const RING: Record<string, string> = {
                              zinc: 'ring-border dark:ring-zinc-500', amber: 'ring-amber-300 dark:ring-amber-400', indigo: 'ring-indigo-300 dark:ring-indigo-400',
                              emerald: 'ring-emerald-300 dark:ring-emerald-400', rose: 'ring-rose-300 dark:ring-rose-400',
                            };
                            return (
                          <div className="relative">
                            {/* ── MARKER: avatar body + outcome ring + direction caret + trade number ──────────
                                Owner wants the Watcher's face on the chart rather than a bare arrow, but the
                                NUMBER has to survive: it was added 07-25 to answer "I can't tell how many trades
                                happened", and plain avatars were dropped back then precisely because a 30px photo
                                alone conveys nothing and piled into unreadable clusters. So carry all three
                                signals on one glyph instead of choosing between them —
                                  WHO       the avatar disc (identity, the thing that was missing)
                                  OUTCOME   the ring colour (entry indigo / win emerald / loss rose)
                                  DIRECTION a caret badge, bottom-left, so entry vs exit stays unmistakable
                                  WHICH     the number chip, top-right
                                Cluster rendering is untouched and still collapses to a RANGE, which is what kept
                                avatar pile-ups readable. */}
                            <Link
                              href={resolveTradeProfileUrl(alert, strategy)}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={`${tradeNo ? `Trade ${tradeNo} - ` : ''}${isBuy ? 'entry' : 'exit'} - view ${strategyLabel} profile`}
                              className={[
                                'relative h-[30px] w-[30px] flex items-center justify-center leading-none transition-all duration-150 cursor-pointer select-none',
                                isHovered ? 'scale-[1.35] drop-shadow-lg z-10' : 'scale-100 hover:brightness-125',
                              ].join(' ')}
                            >
                              <span className={`block h-[24px] w-[24px] overflow-hidden rounded-full bg-card ring-2 ${RING[tone]} shadow-md dark:shadow-black/50`}>
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                  src={avatarSrc}
                                  alt=""
                                  className="h-full w-full object-cover"
                                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
                                />
                              </span>
                              {/* Direction caret — bottom-left so it never collides with the number chip or the
                                  paper "P" badge that sits bottom-centre. */}
                              <span
                                className={`absolute -bottom-[2px] -left-[1px] rounded-full bg-background px-[2px] text-[9px] leading-[11px] ${GLYPH[tone]} shadow-sm`}
                                aria-hidden
                              >{isBuy ? '▲' : '▼'}</span>
                              {tradeNo != null && (
                                <span className={`absolute -right-[4px] -top-[3px] min-w-[13px] rounded-full px-[3px] text-center text-[8px] font-black leading-[13px] shadow-sm ${CHIP[tone]}`}>{tradeNo}</span>
                              )}
                            </Link>
                            {/* DEV-book cap. The plain "W" is gone: the avatar now carries the Watcher's identity,
                                so a letter on top of its own face is noise. DEV still needs calling out because
                                those markers must never be mistaken for the live book. */}
                            {isWatcher && isDevBook && (
                              <div className="absolute -top-[8px] left-1/2 -translate-x-1/2 rounded-[3px] bg-sky-500 px-[3.5px] text-[8px] font-black leading-[11px] tracking-tight text-foreground shadow-sm dark:shadow-sky-900/50 select-none pointer-events-none">DEV</div>
                            )}
                            {/* Amber "P" badge below avatar — visible without hovering */}
                            {isPaperRaEntry && (
                              <div className="absolute -bottom-[9px] left-1/2 -translate-x-1/2 bg-amber-500 rounded-sm px-[3px] text-[7px] font-bold text-zinc-950 leading-[10px] select-none pointer-events-none">P</div>
                            )}
                          </div>
                            );
                          })()}

                          {/* ── Tooltip pinned to the chart's TOP-LEFT (owner: top-right covered the price axis
                              and the entry/exit labels on the right side — the left corner keeps every label
                              readable and screenshots cleanly) ── */}
                          {isDirectHover && (() => {
                            return (
                            <div
                              className="absolute z-50 pointer-events-none"
                              style={{
                                top: 66 - chartH,
                                left: 27 - x,
                                bottom: 'auto',
                                minWidth: 230,
                                maxWidth: 290,
                              }}
                            >
                              <div className={`rounded-xl border shadow-2xl text-xs backdrop-blur-sm bg-card/96 overflow-hidden ${borderCls}`}>
                                {/* Accent strip — instantly signals outcome */}
                                <div className={`h-0.5 w-full ${accentCls}`} />
                                <div className="p-3">

                                {pair ? (
                                  /* ─── Completed trade card ─────────────────────── */
                                  <>
                                    {/* ── Marketing header: the STOCK is the hero (logo + $SYMBOL + company),
                                        the outcome %/badge reads at a glance, trader is the attribution line ── */}
                                    <div className="flex items-start gap-2 mb-2">
                                      <img
                                        src={`/api/logo?symbol=${encodeURIComponent(symbol)}`}
                                        alt=""
                                        className="h-7 w-7 rounded-md object-contain bg-muted border border-border/50 flex-shrink-0 mt-0.5"
                                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
                                      />
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5">
                                          <span className="font-bold text-foreground text-[15px] leading-none tracking-tight">${symbol}</span>
                                          {tfTag && (
                                            <span className="font-mono text-[8px] bg-indigo-50 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 px-1 py-0.5 rounded border border-indigo-300 dark:border-indigo-500/30 leading-none uppercase tracking-wide">{tfTag}</span>
                                          )}
                                        </div>
                                        {companyName && (
                                          <span className="block text-muted-foreground text-[9px] truncate leading-tight mt-1">{companyName}</span>
                                        )}
                                      </div>
                                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                        <span className={[
                                          'rounded px-1.5 py-0.5 font-bold text-[9px] uppercase tracking-wide',
                                          isProfit === true ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : isProfit === false ? 'bg-rose-50 dark:bg-rose-500/15 text-rose-600 dark:text-rose-300' : 'bg-muted/40 text-muted-foreground',
                                        ].join(' ')}>
                                          {isProfit === true ? '▲ Win' : isProfit === false ? '▼ Loss' : '- Open'}
                                        </span>
                                        {typeof effectivePlPct === 'number' && (
                                          <span className={`font-mono font-bold text-[16px] leading-none ${effectivePl != null && effectivePl >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                            {effectivePlPct >= 0 ? '+' : ''}{effectivePlPct.toFixed(1)}%
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    {/* Attribution line: which AI caught it + the setup tag */}
                                    <div className="flex items-center gap-1.5 mb-2.5">
                                      <img src={avatarSrc} alt="" className="h-4 w-4 rounded-full object-cover flex-shrink-0" />
                                      <span className="text-foreground/80 text-[10px] font-medium">{strategyLabel}</span>
                                      {condLabel && (
                                        <span className="font-mono text-[9px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded border border-border/60 leading-none">{condLabel}</span>
                                      )}
                                      {tradeNum ? <span className="text-zinc-600 text-[9px]">#{tradeNum}</span> : null}
                                      {isMultiEntry ? <span className="text-zinc-600 text-[9px]">· {entryCount}× in</span> : null}
                                    </div>

                                    {/* ── ROUND-TRIP SUMMARY — the whole trade in one glance, FIRST ──────────
                                        Owner 2026-07-27: "it does not show summary of the trade like price chg
                                        and % chg and top p/l — should be best in class". Everything here was
                                        already on the card, but scattered: entry and exit sat in separate rows
                                        of the detail block and the P&L number lived BELOW it, so reading the
                                        actual result meant assembling it yourself. This strip states the round
                                        trip outright — entry → exit, hold time, P&L in dollars, percent, and
                                        the per-share move × size. The detail block underneath keeps the pyramid
                                        legs, umbrella exits and signal-vs-fill lines for when they're wanted. */}
                                    {((typeof entryPx === 'number' && entryPx > 0 && typeof exitPx === 'number' && exitPx > 0) || typeof effectivePl === 'number') && (
                                      <div className={`rounded-lg border px-2.5 py-2 mb-2.5 ${
                                        isProfit === true ? 'border-emerald-300 dark:border-emerald-500/25 bg-emerald-500/[0.07]'
                                        : isProfit === false ? 'border-rose-300 dark:border-rose-500/25 bg-rose-500/[0.07]'
                                        : 'border-border/50 bg-muted/40'}`}>
                                        {typeof entryPx === 'number' && entryPx > 0 && typeof exitPx === 'number' && exitPx > 0 && (
                                          <div className="flex items-baseline gap-1.5 font-mono tabular-nums">
                                            <span className="text-[15px] font-bold text-foreground">${formatPrice(entryPx)}</span>
                                            <span className="text-[12px] text-muted-foreground">→</span>
                                            <span className="text-[15px] font-bold text-foreground">${formatPrice(exitPx)}</span>
                                            {durationMin != null && (
                                              <span className="ml-auto text-[9px] text-muted-foreground">
                                                held {durationMin < 60 ? `${durationMin}m` : `${Math.floor(durationMin / 60)}h ${durationMin % 60}m`}
                                              </span>
                                            )}
                                          </div>
                                        )}
                                        <div className="mt-1.5 flex items-baseline gap-2 font-mono tabular-nums">
                                          {typeof effectivePl === 'number' && (
                                            <span className={`text-[17px] font-bold leading-none ${effectivePl >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                              {effectivePl >= 0 ? '+' : '−'}${Math.abs(effectivePl).toFixed(2)}
                                            </span>
                                          )}
                                          {typeof effectivePlPct === 'number' && (
                                            <span className={`text-[11px] font-semibold ${effectivePlPct >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                                              {effectivePlPct >= 0 ? '+' : ''}{effectivePlPct.toFixed(2)}%
                                            </span>
                                          )}
                                          {typeof entryPx === 'number' && typeof exitPx === 'number' && (
                                            <span className="ml-auto text-[9px] text-muted-foreground">
                                              {(exitPx - entryPx) >= 0 ? '+' : '−'}${Math.abs(exitPx - entryPx).toFixed(3)}/sh
                                              {typeof pairShares === 'number' && pairShares > 0
                                                ? ` × ${pairShares % 1 === 0 ? pairShares : pairShares.toFixed(1)}`
                                                : ''}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    )}

                                    {/* Entry / Exit block */}
                                    <div className="rounded-lg bg-muted/40 px-2.5 py-2 mb-2.5 space-y-1.5">

                                      {/* Entry row (averaged for pyramids) */}
                                      {typeof entryPx === 'number' && entryPx > 0 && (
                                        <div className="flex items-center gap-1.5">
                                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                                          <span className="text-muted-foreground w-7 flex-shrink-0 text-[10px]">
                                            {isMultiEntry ? 'Avg' : (strategy === 'osiris' ? 'Short' : 'Entry')}
                                          </span>
                                          <span className="font-mono font-semibold text-foreground flex-1">${formatPrice(entryPx)}</span>
                                          <span className="text-zinc-600 text-[10px]">
                                            {new Date(pair.buyAlert.barTimestamp ?? pair.buyAlert.timestamp).toLocaleTimeString('en-US', {
                                              timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', hour12: true,
                                            })}
                                          </span>
                                        </div>
                                      )}

                                      {/* signal → filled for a single-entry fill trade (Ra/Watcher) */}
                                      {!isMultiEntry && pairFillPx.entry != null && typeof buyMeta?.entryPrice === 'number'
                                        && Math.abs(pairFillPx.entry - buyMeta.entryPrice) / Math.max(buyMeta.entryPrice, 0.0001) > 0.0005 && (
                                        <div className="text-[9px] text-zinc-600 pl-3 font-mono">
                                          signal ${formatPrice(buyMeta.entryPrice)} → filled ${formatPrice(pairFillPx.entry)}
                                        </div>
                                      )}

                                      {/* Pyramid breakdown */}
                                      {isMultiEntry && pair.buyAlerts.map((ba, bi) => {
                                        const bm = parseTradeMeta(ba);
                                        const baFill = (strategy === 'ra' || strategy === 'watcher') ? findFillForStrategy(strategy, 'buy', ba.barTimestamp ?? ba.timestamp) : null;
                                        const bPx = baFill?.price ?? bm?.entryPrice ?? ba.data?.price;
                                        if (!(typeof bPx === 'number' && bPx > 0)) return null;
                                        const bSh = bm?.shares;
                                        return (
                                          <div key={ba.id} className="flex items-center gap-1.5 pl-2.5 border-l border-border/50">
                                            <span className="w-1 h-1 rounded-full bg-amber-400/40 flex-shrink-0" />
                                            <span className="text-zinc-600 text-[10px] w-7 flex-shrink-0">#{bi + 1}</span>
                                            <span className="font-mono text-muted-foreground flex-1 text-[10px]">${formatPrice(bPx)}</span>
                                            {typeof bSh === 'number' && bSh > 0 && (
                                              <span className="text-zinc-600 text-[9px]">×{bSh % 1 === 0 ? bSh : bSh.toFixed(1)}</span>
                                            )}
                                            <span className="text-zinc-600 text-[10px]">
                                              {new Date(ba.barTimestamp ?? ba.timestamp).toLocaleTimeString('en-US', {
                                                timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', hour12: true,
                                              })}
                                            </span>
                                          </div>
                                        );
                                      })}

                                      {/* Exit row — for scaled trades this is the BLENDED average across all sells (umbrella) */}
                                      <div className="flex items-center gap-1.5">
                                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isProfit === true ? 'bg-emerald-400' : isProfit === false ? 'bg-rose-400' : 'bg-muted'}`} />
                                        <span className="text-muted-foreground w-7 flex-shrink-0 text-[10px]">{strategy === 'osiris' ? 'Cover' : (sellMeta?.bankedQty ? 'Avg out' : 'Exit')}</span>
                                        <span className="font-mono font-semibold text-foreground flex-1">${formatPrice(sellMeta?.bankedQty ? (sellMeta.exitPrice ?? 0) : (typeof exitPx === 'number' ? exitPx : (sellMeta?.exitPrice ?? pair.sellAlert.data?.price ?? 0)))}</span>
                                        {pctMove !== null && (
                                          <span className={`font-mono text-[10px] mr-1 ${pctMove >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                                            {pctMove >= 0 ? '+' : ''}{pctMove.toFixed(1)}%
                                          </span>
                                        )}
                                        <span className="text-zinc-600 text-[10px]">
                                          {new Date(pair.sellAlert.barTimestamp ?? pair.sellAlert.timestamp).toLocaleTimeString('en-US', {
                                            timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', hour12: true,
                                          })}
                                        </span>
                                      </div>

                                      {/* UMBRELLA breakdown — the sell legs behind the blended average */}
                                      {typeof sellMeta?.bankedQty === 'number' && sellMeta.bankedQty > 0 && (
                                        <>
                                          <div className="flex items-center gap-1.5 pl-2.5 border-l border-border/50">
                                            <span className="w-1 h-1 rounded-full bg-emerald-400/50 flex-shrink-0" />
                                            <span className="text-zinc-600 text-[10px] w-12 flex-shrink-0">Banked</span>
                                            <span className="font-mono text-muted-foreground flex-1 text-[10px]">${formatPrice(sellMeta.bankedAvg ?? 0)}</span>
                                            <span className="text-zinc-600 text-[9px]">×{Math.round(sellMeta.bankedQty)}</span>
                                            <span className="text-zinc-600 text-[9px]">into strength</span>
                                          </div>
                                          <div className="flex items-center gap-1.5 pl-2.5 border-l border-border/50">
                                            <span className="w-1 h-1 rounded-full bg-sky-400/50 flex-shrink-0" />
                                            <span className="text-zinc-600 text-[10px] w-12 flex-shrink-0">Runner</span>
                                            <span className="font-mono text-muted-foreground flex-1 text-[10px]">${formatPrice(sellMeta.finalExitPrice ?? 0)}</span>
                                            <span className="text-zinc-600 text-[9px]">×{Math.round(sellMeta.finalExitQty ?? 0)}</span>
                                            <span className="text-zinc-600 text-[9px]">final</span>
                                          </div>
                                        </>
                                      )}
                                      {/* signal → filled for the exit (Ra/Watcher real sell fill differs from signal; skip for blended umbrellas) */}
                                      {!sellMeta?.bankedQty && pairFillPx.exit != null && typeof sellMeta?.exitPrice === 'number'
                                        && Math.abs(pairFillPx.exit - sellMeta.exitPrice) / Math.max(sellMeta.exitPrice, 0.0001) > 0.0005 && (
                                        <div className="text-[9px] text-zinc-600 pl-3 font-mono">
                                          signal ${formatPrice(sellMeta.exitPrice)} → filled ${formatPrice(pairFillPx.exit)}
                                        </div>
                                      )}
                                    </div>

                                    {/* (The P&L number that used to sit here now LEADS the card, in the
                                        round-trip summary strip above the entry/exit detail.) */}

                                    {/* Peak during the trade + money left on the table (owner) */}
                                    {peakPct != null && peakPct > 0.5 && (
                                      <div className="flex items-center justify-between mb-2.5 px-0.5 text-[10px]">
                                        <span className="text-muted-foreground">Peak during trade</span>
                                        <span className="font-mono tabular-nums">
                                          <span className="font-semibold text-sky-700 dark:text-sky-400">+{peakPct.toFixed(1)}%</span>
                                          {peakPx != null && <span className="text-zinc-600"> @ ${formatPrice(peakPx)}</span>}
                                          {leftOnTablePct != null && leftOnTablePct >= 1 && (
                                            <span className="text-amber-600 dark:text-amber-400/90"> · left {leftOnTablePct.toFixed(1)}%</span>
                                          )}
                                        </span>
                                      </div>
                                    )}

                                    {/* Entry + Exit reasons — BOTH legs of the round trip, so hovering
                                        either avatar surfaces WHY it bought AND why it sold. The entry
                                        reason (breakout/setup) is the primary buy's first clause; the exit
                                        reason is the sell's. */}
                                    {(() => {
                                      const entryReasonRaw = buyMeta?.reason ?? null;
                                      // First clause only (e.g. "M1-breakout") — the full string is verbose.
                                      // Fall back to the JOURNAL's matched entry reason (condLabel) when the alert
                                      // meta is missing — e.g. a phantom-close/re-adopt split leaves a marker with no
                                      // alert reason (FCEL). Prevents the tooltip from rendering fully blank.
                                      const fmtReason = (r: string) => reasonLabel[r.split('·')[0].split('(')[0].trim()] ?? r.split('·')[0].trim();
                                      // Fuller entry reason (owner: more detail) — keep the whole setup string,
                                      // not just the first clause; fall back to the condLabel's short name.
                                      const entryReason = humanizeReason(entryReasonRaw)
                                        ?? (condLabel ? fmtReason(condLabel) : null);
                                      const exitReason = sellMeta?.reason
                                        ? (() => { const rk = sellMeta.reason.split('(')[0].trim().split(' ')[0]; return reasonLabel[rk] ?? reasonLabel[sellMeta.reason] ?? rk; })()
                                        : null;
                                      if (!entryReason && !exitReason) return null;
                                      return (
                                        <div className="rounded-md bg-muted/30 px-2 py-1.5 mb-2 space-y-1 text-[10px]">
                                          {entryReason && (
                                            <div className="flex items-center gap-1.5">
                                              <span className="text-emerald-700 dark:text-emerald-400/80 font-semibold w-7 flex-shrink-0">Why</span>
                                              <span className="text-muted-foreground flex-1 leading-tight">{entryReason}</span>
                                            </div>
                                          )}
                                          {exitReason && (
                                            <div className="flex items-center gap-1.5">
                                              <span className="text-rose-600 dark:text-rose-400/80 font-semibold w-7 flex-shrink-0">Sold</span>
                                              <span className="text-muted-foreground flex-1 leading-tight">{exitReason}</span>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}

                                    {/* Footer: hold time · RVOL */}
                                    <div className="border-t border-border/60 pt-2 flex items-center justify-between text-[10px] text-muted-foreground">
                                      <div className="flex items-center gap-2">
                                        {durationMin !== null && (
                                          <span>⏱ {durationMin < 60 ? `${durationMin}m` : `${Math.floor(durationMin / 60)}h ${durationMin % 60}m`}</span>
                                        )}
                                      </div>
                                      {rvolDisplay && <span className="text-zinc-600">RVOL {rvolDisplay}</span>}
                                    </div>
                                    {/* ── Brand bar — makes a screenshot self-branding for social ── */}
                                    <div className="mt-2.5 -mx-3 -mb-3 px-3 py-1.5 bg-emerald-500/[0.07] border-t border-border/80 flex items-center gap-1.5">
                                      <img src="/seedling-logo.png" alt="" className="h-3.5 w-3.5 object-contain flex-shrink-0" />
                                      <span className="text-[9px] font-semibold text-foreground/80 tracking-wide">TheDesperateTrader<span className="text-emerald-700 dark:text-emerald-400/80">.com</span></span>
                                      <span className="text-[8px] text-muted-foreground ml-auto tracking-wide">The Watcher (ai)</span>
                                    </div>
                                  </>
                                ) : (
                                  /* ─── Open / unpaired trade card ──────────────── */
                                  <>
                                    {/* Header */}
                                    <div className="flex items-start gap-2 mb-2">
                                      <img src={avatarSrc} alt="" className="h-5 w-5 rounded-full object-cover flex-shrink-0 mt-0.5" />
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5">
                                          <span className="font-semibold text-foreground text-[11px]">{strategyLabel}</span>
                                          {tfTag && (
                                            <span className="font-mono text-[9px] bg-indigo-50 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 px-1.5 py-0.5 rounded border border-indigo-300 dark:border-indigo-500/30 leading-none uppercase tracking-wide">{tfTag}</span>
                                          )}
                                          {condLabel && (
                                            <span className="font-mono text-[9px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded border border-border/60 leading-none">{condLabel}</span>
                                          )}
                                        </div>
                                        <span className="text-zinc-600 text-[9px] uppercase tracking-wider">
                                          ${symbol} · {isMember ? (isBuy ? 'Bought' : 'Sold') : (isBuy ? (openIsMulti ? `Open · ${openPair!.buyAlerts.length} entries` : 'Open position') : 'Exit')}
                                        </span>
                                      </div>
                                      <span className={[
                                        'rounded px-1.5 py-0.5 font-bold text-[9px] uppercase tracking-wide flex-shrink-0',
                                        isBuy ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'bg-rose-50 dark:bg-rose-500/15 text-rose-600 dark:text-rose-300',
                                      ].join(' ')}>
                                        {isMember ? (isBuy ? '▲ Bought' : '▼ Sold') : (isBuy ? (strategy === 'osiris' ? '▼ Short' : '▲ Entry') : (strategy === 'osiris' ? '▲ Cover' : '▼ Exit'))}
                                      </span>
                                    </div>

                                    {/* Member trade body: price · shares · value (realized P&L on sells uses the
                                        shared P&L block below). Kept separate from the AI entry/exit rows so a
                                        member's card reads as a person's trade, not a strategy's signal levels. */}
                                    {isMember && (
                                      <div className="rounded-lg bg-muted/40 px-2.5 py-2 mb-2 space-y-1.5">
                                        <div className="flex items-center gap-1.5">
                                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isBuy ? 'bg-emerald-400' : 'bg-rose-400'}`} />
                                          <span className="text-muted-foreground w-11 flex-shrink-0 text-[10px]">{isBuy ? 'Bought' : 'Sold'}</span>
                                          {typeof memberPrice === 'number' && memberPrice > 0 && (
                                            <span className="font-mono font-bold text-foreground flex-1 text-[13px]">${formatPrice(memberPrice)}</span>
                                          )}
                                          {typeof memberShares === 'number' && memberShares > 0 && (
                                            <span className="text-muted-foreground text-[10px]">×{memberShares % 1 === 0 ? memberShares : memberShares.toFixed(2)}</span>
                                          )}
                                        </div>
                                        {typeof memberValue === 'number' && memberValue > 0 && (
                                          <div className="flex items-center gap-1.5">
                                            <span className="w-1.5 h-1.5 rounded-full bg-muted flex-shrink-0" />
                                            <span className="text-muted-foreground w-11 flex-shrink-0 text-[10px]">Value</span>
                                            <span className="font-mono text-foreground/80 flex-1 text-[11px]">${memberValue.toFixed(2)}</span>
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {/* Exit cards: show entry+exit rows if we have both prices */}
                                    {!isMember && !isBuy && meta?.entryPrice ? (() => {
                                      // Unpaired Ra/Watcher exit: resolve the real entry fill (nearest
                                      // buy fill before this exit); fall back to the signal price in meta.
                                      const unpairedEntryPx = ((strategy === 'ra' || strategy === 'watcher')
                                        ? findFillForStrategy(strategy, 'buy', cardMs, undefined, cardMs)?.price
                                        : null) ?? meta.entryPrice!;
                                      // True when the exit price shown is the real Alpaca fill (cardFill),
                                      // so we can annotate the signal level alongside it.
                                      const exitIsFill = cardFill != null && typeof signalPrice === 'number' && Math.abs(cardFill.price - signalPrice) / Math.max(signalPrice, 0.0001) > 0.0005;
                                      return (
                                      <div className="rounded-lg bg-muted/40 px-2.5 py-2 mb-2 space-y-1.5">
                                        <div className="flex items-center gap-1.5">
                                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                                          <span className="text-muted-foreground w-7 flex-shrink-0 text-[10px]">Entry</span>
                                          <span className="font-mono font-semibold text-foreground/80 flex-1">${formatPrice(unpairedEntryPx)}</span>
                                        </div>
                                        {typeof tradePrice === 'number' && tradePrice > 0 && (
                                          <div className="flex items-center gap-1.5">
                                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${typeof meta?.realizedPl === 'number' ? (meta.realizedPl >= 0 ? 'bg-emerald-400' : 'bg-rose-400') : 'bg-rose-400'}`} />
                                            <span className="text-muted-foreground w-7 flex-shrink-0 text-[10px]">Exit</span>
                                            <span className="font-mono font-semibold text-foreground flex-1">${formatPrice(tradePrice)}</span>
                                            {unpairedEntryPx > 0 && (
                                              <span className={`font-mono text-[10px] ${tradePrice >= unpairedEntryPx ? 'text-emerald-500' : 'text-rose-500'}`}>
                                                {((tradePrice - unpairedEntryPx) / unpairedEntryPx * 100) >= 0 ? '+' : ''}{((tradePrice - unpairedEntryPx) / unpairedEntryPx * 100).toFixed(1)}%
                                              </span>
                                            )}
                                            <span className="text-zinc-600 text-[9px] font-mono flex-shrink-0">{new Date(cardMs).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', hour12: true })} ET</span>
                                          </div>
                                        )}
                                        {/* signal → filled (only when the displayed exit IS a real fill that differs from signal) */}
                                        {exitIsFill && typeof signalPrice === 'number' && (
                                          <div className="text-[9px] text-zinc-600 pl-3 font-mono">
                                            signal ${formatPrice(signalPrice)} → filled ${formatPrice(cardFill!.price)}
                                          </div>
                                        )}
                                      </div>
                                      );
                                    })() : (!isMember && isBuy) ? (
                                      /* Open position: show pyramid breakdown when multi-slice, single entry otherwise */
                                      <div className="rounded-lg bg-muted/40 px-2.5 py-2 mb-2 space-y-1.5">
                                        {/* Avg row — only when multiple slices */}
                                        {openIsMulti && openPair?.avgEntryPrice != null && (
                                          <div className="flex items-center gap-1.5">
                                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                                            <span className="text-muted-foreground w-7 flex-shrink-0 text-[10px]">Avg</span>
                                            <span className="font-mono font-bold text-foreground flex-1 text-[13px]">${formatPrice(openPair.avgEntryPrice)}</span>
                                            <span className="text-zinc-600 text-[9px]">{openPair.buyAlerts.length}× entries</span>
                                          </div>
                                        )}
                                        {/* Single entry — when only one slice */}
                                        {!openIsMulti && typeof tradePrice === 'number' && tradePrice > 0 && (() => {
                                          const singleBa = openPair?.buyAlerts[0];
                                          const singleFill = (strategy === 'ra' || strategy === 'watcher') && singleBa ? findFillForStrategy(strategy, 'buy', singleBa.barTimestamp ?? singleBa.timestamp) : null;
                                          const singleMeta = singleBa ? parseTradeMeta(singleBa) : null;
                                          const singleAlpacaStatus = singleMeta?.alpacaStatus;
                                          const isSinglePaper = strategy === 'ra' && singleBa != null && (
                                            (singleAlpacaStatus != null && singleAlpacaStatus !== 'filled') ||
                                            (singleAlpacaStatus == null && alpacaFills.length > 0 && singleFill === null)
                                          );
                                          const badge = isSinglePaper ? alpacaPaperBadge(singleAlpacaStatus, singleMeta?.alpacaReason) : null;
                                          // Watcher: annotate the breakout signal alongside the real fill.
                                          const singleSignal = singleMeta?.entryPrice ?? singleBa?.data?.price;
                                          const entryIsFill = singleFill != null && typeof singleSignal === 'number' && Math.abs(singleFill.price - singleSignal) / Math.max(singleSignal, 0.0001) > 0.0005;
                                          return (
                                            <>
                                            <div className="flex items-center gap-1.5">
                                              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                                              <span className="text-muted-foreground w-7 flex-shrink-0 text-[10px]">{strategy === 'osiris' ? 'Short' : 'Entry'}</span>
                                              <span className="font-mono font-bold text-foreground flex-1 text-[14px] leading-tight">${formatPrice(tradePrice)}</span>
                                              {badge && (
                                                <span title={badge.tooltip} className="text-yellow-500/70 text-[9px] font-medium">{badge.label}</span>
                                              )}
                                              {cardMs > 0 && (
                                                <span className="text-zinc-600 text-[9px] font-mono flex-shrink-0">{new Date(cardMs).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', hour12: true })} ET</span>
                                              )}
                                            </div>
                                            {entryIsFill && typeof singleSignal === 'number' && (
                                              <div className="text-[9px] text-zinc-600 pl-3 font-mono">
                                                signal ${formatPrice(singleSignal)} → filled ${formatPrice(singleFill!.price)}
                                              </div>
                                            )}
                                            </>
                                          );
                                        })()}
                                        {/* Pyramid breakdown: Init + Add 1, Add 2, ... */}
                                        {openIsMulti && openPair?.buyAlerts.map((ba, bi) => {
                                          const bm = parseTradeMeta(ba);
                                          const baFill = (strategy === 'ra' || strategy === 'watcher') ? findFillForStrategy(strategy, 'buy', ba.barTimestamp ?? ba.timestamp) : null;
                                          const bPx = baFill?.price ?? bm?.entryPrice ?? ba.data?.price;
                                          if (!(typeof bPx === 'number' && bPx > 0)) return null;
                                          const bSh = bm?.shares;
                                          const baAlpacaStatus = bm?.alpacaStatus;
                                          const isPaper = strategy === 'ra' && (
                                            (baAlpacaStatus != null && baAlpacaStatus !== 'filled') ||
                                            (baAlpacaStatus == null && alpacaFills.length > 0 && baFill === null)
                                          );
                                          const baBadge = isPaper ? alpacaPaperBadge(baAlpacaStatus, bm?.alpacaReason) : null;
                                          return (
                                            <div key={ba.id} className="flex items-center gap-1.5 pl-2.5 border-l border-border/50">
                                              <span className="w-1 h-1 rounded-full bg-amber-400/40 flex-shrink-0" />
                                              <span className="text-zinc-600 text-[10px] w-8 flex-shrink-0">
                                                {bi === 0 ? 'Init' : `Add ${bi}`}
                                              </span>
                                              <span className="font-mono text-muted-foreground flex-1 text-[10px]">${formatPrice(bPx)}</span>
                                              {typeof bSh === 'number' && bSh > 0 && (
                                                <span className="text-zinc-600 text-[9px]">×{bSh % 1 === 0 ? bSh : bSh.toFixed(1)}</span>
                                              )}
                                              {baBadge ? (
                                                <span title={baBadge.tooltip} className="text-yellow-500/70 text-[9px] font-medium">{baBadge.label}</span>
                                              ) : (
                                                <span className="text-zinc-600 text-[10px]">
                                                  {new Date(ba.barTimestamp ?? ba.timestamp).toLocaleTimeString('en-US', {
                                                    timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', hour12: true,
                                                  })}
                                                </span>
                                              )}
                                            </div>
                                          );
                                        })}
                                        {/* Unrealized P&L from raGameEntry (paper avg cost) */}
                                        {openIsMulti && raGameEntry?.avgCost != null && (
                                          <div className="flex items-center gap-1.5 pt-1 border-t border-border/40">
                                            <span className="text-muted-foreground text-[10px] w-14">Avg cost</span>
                                            <span className="font-mono text-foreground/80 text-[10px] flex-1">${formatPrice(raGameEntry.avgCost)}</span>
                                          </div>
                                        )}
                                        {/* LIVE unrealized % — how the open position is doing RIGHT NOW vs entry */}
                                        {openLivePct !== null && typeof currentPrice === 'number' && (
                                          <div className="flex items-center gap-1.5 pt-1 border-t border-border/40">
                                            <span className="w-1.5 h-1.5 rounded-full bg-sky-400 flex-shrink-0" />
                                            <span className="text-muted-foreground w-7 flex-shrink-0 text-[10px]">Now</span>
                                            <span className="font-mono font-semibold text-foreground flex-1 text-[12px]">${formatPrice(currentPrice)}</span>
                                            <span className={`font-mono text-[11px] font-bold ${openLivePct >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                              {openLivePct >= 0 ? '+' : '−'}{Math.abs(openLivePct).toFixed(2)}%
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    ) : null}

                                    {/* Entry reason — WHY the open position was taken (owner: more detail on hover) */}
                                    {openEntryReason && (
                                      <div className="rounded-md bg-muted/30 px-2 py-1.5 mb-2 text-[10px]">
                                        <div className="flex items-start gap-1.5">
                                          <span className="text-emerald-700 dark:text-emerald-400/80 font-semibold w-7 flex-shrink-0 mt-px">Why</span>
                                          <span className="text-muted-foreground flex-1 leading-snug">{openEntryReason}</span>
                                        </div>
                                      </div>
                                    )}

                                    {/* P&L for exit cards */}
                                    {!isBuy && typeof meta?.realizedPl === 'number' && (
                                      <div className="flex items-baseline justify-between mb-2 px-0.5">
                                        <span className={`font-mono font-bold text-[18px] leading-none ${meta.realizedPl >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                          {meta.realizedPl >= 0 ? '+' : '−'}${Math.abs(meta.realizedPl).toFixed(2)}
                                        </span>
                                        {typeof meta.realizedPlPct === 'number' && (
                                          <span className={`font-mono font-semibold text-[11px] ${meta.realizedPl >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                                            {meta.realizedPlPct >= 0 ? '+' : ''}{meta.realizedPlPct.toFixed(2)}%
                                          </span>
                                        )}
                                      </div>
                                    )}

                                    {/* Footer: time · exit reason | size · RVOL */}
                                    <div className="border-t border-border/60 pt-2 flex items-center justify-between text-[10px] text-muted-foreground">
                                      <div className="flex items-center gap-2">
                                        <span className="text-zinc-600">
                                          {new Date(alert.barTimestamp ?? alert.timestamp).toLocaleTimeString('en-US', {
                                            timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', hour12: true,
                                          })} ET
                                        </span>
                                        {!isBuy && meta?.reason && (() => {
                                          const rk = meta.reason.split('(')[0].trim().split(' ')[0];
                                          return <span className="text-zinc-600">{reasonLabel[rk] ?? reasonLabel[meta.reason] ?? rk}</span>;
                                        })()}
                                      </div>
                                      <div className="flex items-center gap-2">
                                        {isBuy && typeof meta?.positionSizePct === 'number' && (
                                          <span className="text-zinc-600">{meta.positionSizePct.toFixed(1)}% acct</span>
                                        )}
                                        {rvolDisplay && <span className="text-zinc-600">RVOL {rvolDisplay}</span>}
                                      </div>
                                    </div>
                                  </>
                                )}

                                </div>
                              </div>
                            </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                    {/* ── Cluster badges: ONE polished badge per overlapping pile ── */}
                    {tradeClusters.filter((c) => c.alerts.length >= 2).map((cluster) => {
                      const isClusterHovered = hoveredClusterKey === cluster.key;
                      // Rows sorted oldest → newest (barTimestamp preferred, insert time fallback).
                      const sortedAlerts = [...cluster.alerts].sort(
                        (a, b) => (a.barTimestamp ?? a.timestamp) - (b.barTimestamp ?? b.timestamp)
                      );
                      const fanAlerts = sortedAlerts.slice(0, 3);
                      const count = cluster.alerts.length;
                      // Header time = the pile's first trade (they land within CLUSTER_PX px).
                      const headMs = sortedAlerts[0].barTimestamp ?? sortedAlerts[0].timestamp;
                      const headTime = new Date(headMs).toLocaleTimeString('en-US', {
                        timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', hour12: true,
                      });
                      // PINNED to the chart's top-left (owner: top-right covered the price axis + entry/exit
                      // labels; the left corner keeps every label readable).
                      const cChartH = chartContainerRef.current?.clientHeight ?? 500;
                      const cardPos = { top: 66 - cChartH, bottom: 'auto' as const, left: 27 - cluster.x };
                      // POSITION grouping (owner: KUST "which are which, it looks erratic"). A pile can hold legs
                      // from SEVERAL positions (KUST: a scalp-burst + a day-burst, 1 buy + 3 sells that don't
                      // reconcile). Group every leg under its round-trip so scale-outs nest under THEIR entry, and
                      // compute per-leg P&L from that position's ENTRY (avg cost) — the old code stamped the
                      // position's TOTAL % on every row (so a $1.68 scale off a $1.44 entry read "+6.2%" instead of
                      // its real +16.7%, and buys showed a meaningless %) and double-counted the net.
                      // `setup` and `entryReason` ride along per leg (owner 2026-08-05: "I want to see
                      // entry times, what entry reason and exit reason, what setup"). They were already
                      // on the parsed meta and simply never surfaced — the card showed only the EXIT
                      // reason, so a losing trade could not be read back to the thing that opened it.
                      type ClLeg = { a: Alert; side: 'buy' | 'sell'; price: number | null; shares: number | undefined; isTrim: boolean; reason: string | null; legPct: number | null; legPl: number | null };
                      type ClGroup = { key: string; entry: number | null; open: boolean; label: string; avatar: string; href: string; legs: ClLeg[]; netPl: number; hasNet: boolean; setup: string | null };
                      const posGroupsMap = new Map<string, ClGroup>();
                      let clBuys = 0, clSells = 0;
                      for (const a of sortedAlerts) {
                        const cp = completedPairByAlertId.get(a.id)?.pair ?? null;
                        const op = cp ? null : (openPairByAlertId.get(a.id) ?? null);
                        const pair = cp ?? op;
                        // ── ONE CARD PER POSITION, NOT PER LEG (owner 2026-07-28: "why two icons for
                        // the watcher, is this separate trades, i cannot tell") ────────────────────────
                        // The fallback key used to be the ALERT id, so an entry and its later ADD became
                        // two separate "positions" from the same AI stacked on top of each other. SEDG:
                        // buy 45 @ $38.85 (scalp entry) then add 6 @ $39.09 — one position, two legs,
                        // rendered as "2 positions".
                        // Unpaired legs now group by STRATEGY, which is what an entry plus its adds
                        // actually is. Paired legs keep the pair's own key and are unaffected.
                        const st = resolveTradeStrategy(a);
                        const key = pair ? pair.buyAlert.id : `unpaired::${st}`;
                        const m = parseTradeMeta(a);
                        const side: 'buy' | 'sell' = (m?.side ?? 'buy') === 'buy' ? 'buy' : 'sell';
                        if (side === 'buy') clBuys++; else clSells++;
                        const entryBasis = pair?.avgEntryPrice ?? cp?.sellMeta?.entryPrice ?? m?.entryPrice ?? null;
                        const price = side === 'buy' ? (m?.entryPrice ?? a.data?.price ?? null) : (m?.exitPrice ?? a.data?.price ?? null);
                        const isTrim = side === 'sell' && !!op;
                        const reasonRaw = m?.reason ?? (side === 'sell' ? cp?.sellMeta?.reason : null) ?? null;
                        const reason = reasonRaw ? String(reasonRaw).replace(/_/g, ' ') : null;
                        // Per-leg P&L from the ENTRY basis (correct per-scale math), never on a buy.
                        let legPct: number | null = null, legPl: number | null = null;
                        if (side === 'sell' && typeof price === 'number' && price > 0 && typeof entryBasis === 'number' && entryBasis > 0) {
                          legPct = (price / entryBasis - 1) * 100;
                          if (typeof m?.shares === 'number' && m.shares > 0) legPl = (price - entryBasis) * m.shares;
                        }
                        let g = posGroupsMap.get(key);
                        if (!g) {
                          g = { key, entry: typeof entryBasis === 'number' ? entryBasis : null, open: !!op,
                                label: resolveTradeLabel(a, st), avatar: resolveTradeAvatar(a, st), href: resolveTradeProfileUrl(a, st),
                                legs: [], netPl: 0, hasNet: false,
                                // The setup that OPENED the position. Now carried on the alert meta itself
                                // (added 2026-08-05) rather than inferred — strategyLabel is only persona +
                                // lane, so without this every Watcher position on a symbol looked identical.
                                // ⚠ Alerts written BEFORE that change have no `setup`; those groups keep
                                // null and simply render no chip, which is honest about not knowing.
                                setup: (m?.setup ? String(m.setup) : null) };
                          posGroupsMap.set(key, g);
                        }
                        if (op) g.open = true;
                        // Prefer the BUY leg's setup: an exit echoes it, but the entry is what defined it.
                        if (side === 'buy' && m?.setup) g.setup = String(m.setup);
                        else if (!g.setup && m?.setup) g.setup = String(m.setup);
                        g.legs.push({ a, side, price, shares: m?.shares, isTrim, reason, legPct, legPl });
                        if (typeof legPl === 'number') { g.netPl += legPl; g.hasNet = true; }
                      }
                      const posGroups = [...posGroupsMap.values()];
                      for (const g of posGroups) g.legs.sort((x, y) => (x.side === y.side ? 0 : x.side === 'buy' ? -1 : 1));  // entry(s) first, then scale-outs/exit
                      // ── "CLOSED" MUST BE PROVEN, NOT ASSUMED (owner 2026-07-28) ────────────────────
                      // `open` came solely from the open-pair lookup, so a MISS rendered as "Closed" —
                      // absence of evidence treated as evidence of closure. Watcher legs are excluded
                      // from the alert-pairing pipeline on purpose (the journal owns them), so any
                      // position the journal has not written yet lands in exactly that gap. SEDG was
                      // still open, the chip in the corner said Open from /api/watcher/position, and
                      // this card said CLOSED off the staler source.
                      // A group with NO sell leg is not a completed round trip and must never claim to
                      // be one. The open-pair lookup still wins when it has an answer.
                      for (const g of posGroups) {
                        if (!g.open) g.open = !g.legs.some((l) => l.side === 'sell');
                      }
                      const clPl = posGroups.reduce((s, g) => s + g.netPl, 0);
                      const clHasPl = posGroups.some((g) => g.hasNet);
                      const posCount = posGroups.length;
                      // Draw every entry (emerald) / exit (rose) price in the pile as a dashed line while hovered,
                      // so the viewer instantly SEES where the algo got in and out. Deduped by side+price so a
                      // scalp+day leg at the same fill don't stack. Cleared on mouse-leave.
                      const drawClusterLines = () => {
                        const series = candleSeriesRef.current;
                        if (!series) return;
                        // Clear any lines left from a prior hover (defensive — a fast enter→enter without leave).
                        clusterHoverLinesRef.current.forEach((l) => { try { series.removePriceLine(l); } catch { /* noop */ } });
                        clusterHoverLinesRef.current = [];
                        // ── TWO LINES, NOT TWENTY (owner 2026-08-05) ───────────────────────────────
                        // This used to draw one dashed line per DISTINCT PRICE PER SIDE. On a name the
                        // bot worked repeatedly that is a thicket: ALT ran ten positions across one
                        // session, so the hover painted up to twenty lines and the chart became
                        // unreadable exactly where the most activity happened — the case you most want
                        // to study.
                        // ⭐ SHARE-WEIGHTED, not a mean of prices. The weighted average IS the cost
                        // basis and the realised average, so these two lines are the numbers the P&L
                        // is actually computed from. A plain mean would weight a 5-share scalp the same
                        // as a 1,300-share adopted leg and quietly disagree with the broker.
                        // ⚠ Falls back to an unweighted mean only when share counts are missing, and
                        // says so in the label — an average whose basis is unknown must not silently
                        // masquerade as the cost basis.
                        const acc = { buy: { n: 0, sh: 0, shPx: 0, px: 0 }, sell: { n: 0, sh: 0, shPx: 0, px: 0 } };
                        for (const a of sortedAlerts) {
                          const m = parseTradeMeta(a);
                          const isBuy = (m?.side ?? 'buy') === 'buy';
                          const px = isBuy ? (m?.entryPrice ?? a.data?.price) : (m?.exitPrice ?? a.data?.price);
                          if (typeof px !== 'number' || !(px > 0)) continue;
                          const sh = typeof m?.shares === 'number' && m.shares > 0 ? m.shares : 0;
                          const t = isBuy ? acc.buy : acc.sell;
                          t.n += 1; t.px += px; t.sh += sh; t.shPx += sh * px;
                        }
                        const lines: ReturnType<typeof series.createPriceLine>[] = [];
                        for (const [side, t] of [['buy', acc.buy], ['sell', acc.sell]] as const) {
                          if (!t.n) continue;
                          const weighted = t.sh > 0;
                          const avg = weighted ? t.shPx / t.sh : t.px / t.n;
                          if (!(avg > 0)) continue;
                          const isBuy = side === 'buy';
                          try {
                            lines.push(series.createPriceLine({
                              price: avg, color: isBuy ? '#34d399' : '#fb7185', lineWidth: 2,
                              lineStyle: LineStyle.Dashed, axisLabelVisible: true,
                              title: `Avg ${isBuy ? 'Buy' : 'Sell'} $${avg.toFixed(2)} · ${t.n} fill${t.n === 1 ? '' : 's'}${weighted ? '' : ' (unweighted)'}`,
                            }));
                          } catch { /* createPriceLine can throw before data loads — harmless */ }
                        }
                        clusterHoverLinesRef.current = lines;
                      };
                      const clearClusterLines = () => {
                        const series = candleSeriesRef.current;
                        clusterHoverLinesRef.current.forEach((l) => { try { series?.removePriceLine(l); } catch { /* noop */ } });
                        clusterHoverLinesRef.current = [];
                      };
                      return (
                        <div
                          key={cluster.key}
                          className="absolute pointer-events-auto"
                          style={{ left: cluster.x - 15, bottom: 44, zIndex: isClusterHovered ? 60 : 22 }}
                          onMouseEnter={() => { setHoveredClusterKey(cluster.key); drawClusterLines(); }}
                          onMouseLeave={() => { setHoveredClusterKey(null); clearClusterLines(); }}
                        >
                          {/* ONE avatar for the pile (owner: "I want a watcher profile picture icon, just 1
                              in that area that I can hover over"). The persona is the identity; the count chip
                              says how many trades are stacked here; the hover card carries the detail. */}
                          <div className={`relative flex items-center transition-transform duration-150 ${isClusterHovered ? 'scale-110' : 'scale-100'}`}>
                            {(() => {
                              const lead = cluster.alerts[0];
                              const st = resolveTradeStrategy(lead);
                              const anyBuy = cluster.alerts.some(a => (parseTradeMeta(a)?.side ?? 'buy') === 'buy');
                              const anySell = cluster.alerts.some(a => parseTradeMeta(a)?.side === 'sell');
                              const ring = anyBuy && !anySell ? 'ring-emerald-300 dark:ring-emerald-400' : anySell && !anyBuy ? 'ring-rose-300 dark:ring-rose-400' : 'ring-indigo-300 dark:ring-indigo-400';
                              return (
                                <img
                                  src={resolveTradeAvatar(lead, st)}
                                  alt=""
                                  className={`h-[28px] w-[28px] rounded-full object-cover ring-2 transition-all duration-150 ${isClusterHovered ? 'ring-indigo-300 brightness-110' : ring}`}
                                />
                              );
                            })()}
                            {/* count chip only when the range does not already imply it (non-contiguous pile) */}
                            <span className={`absolute -top-[7px] -right-[7px] flex h-[14px] min-w-[14px] items-center justify-center rounded-full px-1 text-[8px] font-black leading-none ring-2 ring-border dark:ring-zinc-950 ${isClusterHovered ? 'bg-indigo-500 text-foreground' : 'bg-muted text-foreground'}`}>
                              {count}
                            </span>
                          </div>

                          {/* Hover card — clean SUMMARY: one row per trade in the pile. */}
                          {isClusterHovered && (
                            <div
                              className="absolute pointer-events-auto"
                              // 320 -> 420. At 320 the reason line had ~40px of usable width after the tag,
                              // price, shares, time and percentage took theirs, so it truncated to nothing.
                              style={{ width: 420, ...cardPos, zIndex: 61 }}
                            >
                              <div className="rounded-xl border border-border bg-background/[0.98] shadow-2xl dark:shadow-black/60 overflow-hidden">
                                {/* ── Marketing hero: the STOCK is the headline (logo + $SYMBOL + company) ── */}
                                <div className="flex items-center gap-2 px-3 pt-2.5 pb-2 border-b border-border/80">
                                  <img
                                    src={`/api/logo?symbol=${encodeURIComponent(symbol)}`}
                                    alt=""
                                    className="h-7 w-7 rounded-md object-contain bg-muted border border-border/50 flex-shrink-0"
                                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="font-bold text-foreground text-[15px] leading-none tracking-tight">${symbol}</div>
                                    {companyName && <div className="text-muted-foreground text-[9px] truncate leading-tight mt-1">{companyName}</div>}
                                  </div>
                                  {clHasPl && (
                                    <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide flex-shrink-0 ${clPl >= 0 ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'bg-rose-50 dark:bg-rose-500/15 text-rose-600 dark:text-rose-300'}`}>
                                      {clPl >= 0 ? '▲ Win' : '▼ Loss'}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center justify-between gap-2 border-b border-border/80 px-3 py-2">
                                  <div className="flex items-baseline gap-2">
                                    <span className="text-[11px] font-semibold text-foreground">{posCount} position{posCount !== 1 ? 's' : ''}</span>
                                    <span className="text-[9px] text-muted-foreground">{clBuys} buy · {clSells} sell</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {clHasPl && (
                                      <span className={`text-[10px] font-bold tabular-nums ${clPl >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                        {clPl >= 0 ? '+' : '−'}${Math.abs(clPl).toFixed(0)}
                                      </span>
                                    )}
                                    <span className="text-[9px] uppercase tracking-wider text-muted-foreground">{headTime} ET</span>
                                  </div>
                                </div>
                                {/* One CARD per position: entry, then its scale-outs/exit nested, with a per-position
                                    net. Each leg's % is off the position's OWN entry (a $1.68 scale off $1.44 reads
                                    +16.7%, not the position total). Fixes KUST "which are which / it looks erratic." */}
                                {/* max-h 72 -> 96: ALT ran ten positions in one session and the list showed
                                    barely two before needing a scroll, on a card whose scrollbar is easy to
                                    miss. Taller list + a visible hint when there is more below. */}
                                <div className="max-h-96 overflow-auto overscroll-contain p-2 space-y-2">
                                  {posGroups.map((g) => (
                                    <Link
                                      key={g.key}
                                      href={g.href}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="block rounded-lg border border-border/70 bg-card/40 hover:bg-accent dark:hover:bg-zinc-800/40 transition-colors overflow-hidden"
                                    >
                                      {/* position header — who · entry basis · open/closed · net */}
                                      <div className="flex items-center gap-2 px-2.5 py-1.5 border-b border-border/60">
                                        <img src={g.avatar} alt="" className="h-[18px] w-[18px] rounded-full object-cover flex-shrink-0" />
                                        <span className="text-[11px] font-medium text-foreground/80 truncate min-w-0">{g.label}</span>
                                        {/* THE SETUP that opened this position. It was never shown, so two
                                            positions from the same AI on one symbol were indistinguishable —
                                            you could not tell a coil-under9 from a breakout-orb. */}
                                        {g.setup && (
                                          <span className="rounded bg-indigo-50 dark:bg-indigo-500/15 px-1.5 py-0.5 text-[9px] font-semibold text-indigo-700 dark:text-indigo-300 flex-shrink-0 max-w-[130px] truncate">
                                            {g.setup}
                                          </span>
                                        )}
                                        <span className="flex-1 min-w-0" />
                                        {/* Size + average cost across the BUY legs. With an entry plus an add, "in $38.85"
                                            alone described only the first leg and made two legs of one position look like
                                            two unrelated trades. "51 sh @ $38.88" states plainly that this is one position. */}
                                        {(() => {
                                          let sh = 0, cost = 0;
                                          for (const l of g.legs) {
                                            if (l.side !== 'buy') continue;
                                            const s = typeof l.shares === 'number' ? l.shares : 0;
                                            if (s > 0 && typeof l.price === 'number' && l.price > 0) { sh += s; cost += s * l.price; }
                                          }
                                          const avg = sh > 0 ? cost / sh : g.entry;
                                          if (avg == null) return null;
                                          return (
                                            <span className="text-[9px] text-muted-foreground flex-shrink-0 tabular-nums">
                                              {sh > 0 ? `${sh} sh @ ` : 'in '}${formatPrice(avg)}
                                            </span>
                                          );
                                        })()}
                                        <span className={`rounded px-1 py-0.5 text-[8px] font-bold uppercase tracking-wide flex-shrink-0 ${g.open ? 'bg-sky-50 dark:bg-sky-500/15 text-sky-700 dark:text-sky-300' : 'bg-muted/40 text-muted-foreground'}`}>{g.open ? 'Open' : 'Closed'}</span>
                                        {g.hasNet && (
                                          <span className={`font-mono text-[10px] font-bold flex-shrink-0 ${g.netPl >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                            {g.netPl >= 0 ? '+' : '−'}${Math.abs(g.netPl).toFixed(2)}
                                          </span>
                                        )}
                                      </div>
                                      {/* legs — Buy / Trim / Sell, each with price · shares · reason · per-leg % */}
                                      <div className="divide-y divide-border/40">
                                        {g.legs.map((leg, li) => {
                                          const tag = leg.side === 'buy' ? 'Buy' : leg.isTrim ? 'Trim' : 'Sell';
                                          const tagCls = leg.side === 'buy' ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : leg.isTrim ? 'bg-amber-50 dark:bg-amber-500/15 text-amber-600 dark:text-amber-300' : 'bg-rose-50 dark:bg-rose-500/15 text-rose-600 dark:text-rose-300';
                                          return (
                                            // ── TWO LINES PER LEG (owner 2026-08-05) ──────────────────
                                            // This was ONE row where the reason held `truncate flex-1` while
                                            // competing with a tag, price, share count, timestamp and a
                                            // percentage. In a 320px card the reason rendered as three or four
                                            // characters — the single field that says WHY was the one squeezed
                                            // out. Numbers now own the top line and the reason owns its own
                                            // line at full width, which is the whole readability fix.
                                            <div key={`${leg.a.id}-${li}`} className="px-2.5 py-1.5">
                                              <div className="flex items-center gap-2">
                                                <span className={`rounded px-1 py-0.5 text-[8px] font-bold uppercase tracking-wide flex-shrink-0 ${tagCls}`}>{tag}</span>
                                                {typeof leg.price === 'number' && leg.price > 0 && (
                                                  <span className="font-mono text-[12px] text-foreground flex-shrink-0">${formatPrice(leg.price)}</span>
                                                )}
                                                {typeof leg.shares === 'number' && leg.shares > 0 && (
                                                  <span className="text-[10px] text-muted-foreground flex-shrink-0">&times;{leg.shares % 1 === 0 ? leg.shares : leg.shares.toFixed(1)}</span>
                                                )}
                                                <span className="flex-1" />
                                                {/* Each leg's own time — a Buy shows the ENTRY time, a Sell the EXIT time. */}
                                                <span className="text-[10px] text-muted-foreground font-mono flex-shrink-0 tabular-nums">
                                                  {new Date(leg.a.barTimestamp ?? leg.a.timestamp).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', hour12: true })}
                                                </span>
                                                {typeof leg.legPct === 'number' && (
                                                  <span className={`font-mono text-[11px] font-bold flex-shrink-0 tabular-nums ${leg.legPct >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                                    {leg.legPct >= 0 ? '+' : '−'}{Math.abs(leg.legPct).toFixed(1)}%
                                                  </span>
                                                )}
                                              </div>
                                              {(leg.reason || leg.isTrim) && (
                                                <div className="mt-0.5 text-[10px] leading-snug text-muted-foreground break-words">
                                                  <span className="text-zinc-600">{leg.side === 'buy' ? 'entry: ' : 'exit: '}</span>
                                                  {leg.reason ?? '—'}
                                                  {leg.isTrim && <span className="text-amber-600 dark:text-amber-400/80"> · still holding</span>}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </Link>
                                  ))}
                                </div>
                                {/* ── Brand bar — makes a screenshot self-branding for social ── */}
                                <div className="px-3 py-1.5 bg-emerald-500/[0.07] border-t border-border/80 flex items-center gap-1.5">
                                  <img src="/seedling-logo.png" alt="" className="h-3.5 w-3.5 object-contain flex-shrink-0" />
                                  <span className="text-[9px] font-semibold text-foreground/80 tracking-wide">TheDesperateTrader<span className="text-emerald-700 dark:text-emerald-400/80">.com</span></span>
                                  <span className="text-[8px] text-muted-foreground ml-auto tracking-wide">The Watcher (ai)</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    </>
                    );
                    })()}

                    {/* ── The Watcher REPLAY: DOM avatar overlays + hover-to-inspect ── */}
                    {/* One up-arrow avatar over the ENTRY candle and one down-arrow over the */}
                    {/* EXIT candle. Hovering either highlights the round trip (price lines via */}
                    {/* the replayHoverLinesRef effect) and shows the entry/exit/P&L tooltip. */}
                    {showWatcherReplay && watcherReplay && watcherReplay.symbol === symbol &&
                      watcherReplay.trades.map((tr, i) => {
                        const xp = replayXPos[i];
                        if (!xp) return null;
                        const isHovered = hoveredReplayIdx === i;
                        const win = tr.plPct != null && tr.plPct > 0;
                        const closed = !tr.open && tr.exitPrice != null && tr.plPct != null;
                        const chartW = chartContainerRef.current?.clientWidth ?? 700;
                        const reasonShort = tr.exitReason?.startsWith('chandelier') ? 'Chandelier'
                          : tr.exitReason?.startsWith('hard_stop') ? 'Hard stop'
                          : tr.exitReason?.startsWith('hold_cap') ? 'Hold cap'
                          : tr.open ? 'Open' : (tr.exitReason ?? 'Exit');
                        // Hold duration in calendar days (daily replay).
                        const eT = replayDateToChartTime(tr.entryDate);
                        const xT = tr.exitDate ? replayDateToChartTime(tr.exitDate) : null;
                        const holdDays = eT != null && xT != null ? Math.max(1, Math.round((xT - eT) / 86400)) : null;
                        // Shared tooltip (rendered next to whichever marker is hovered).
                        const renderTooltip = (anchorX: number) => {
                          const nearRight = anchorX > chartW - 150;
                          return (
                            <div
                              className="absolute z-50 pointer-events-none"
                              style={nearRight
                                ? { bottom: 30, right: 0, minWidth: 190, maxWidth: 230 }
                                : { bottom: 30, left: 0, minWidth: 190, maxWidth: 230 }}
                            >
                              <div className={`rounded-xl border shadow-2xl text-xs backdrop-blur-sm bg-card/96 overflow-hidden ${win ? 'border-emerald-300 dark:border-emerald-500/40' : closed ? 'border-rose-300 dark:border-rose-500/40' : 'border-indigo-300 dark:border-indigo-500/40'}`}>
                                <div className={`h-0.5 w-full ${closed ? (win ? 'bg-emerald-500/70' : 'bg-rose-500/70') : 'bg-indigo-500/60'}`} />
                                <div className="p-2.5">
                                  <div className="flex items-center gap-2 mb-2">
                                    <img src="/assets/watcher/watcher-avatar.png" alt="" className="h-5 w-5 rounded-full object-cover flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <div className="font-semibold text-foreground text-[11px]">The Watcher</div>
                                      <div className="text-zinc-600 text-[9px] uppercase tracking-wider">${symbol} · replay</div>
                                    </div>
                                    <span className={[
                                      'rounded px-1.5 py-0.5 font-bold text-[9px] uppercase tracking-wide flex-shrink-0',
                                      closed ? (win ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'bg-rose-50 dark:bg-rose-500/15 text-rose-600 dark:text-rose-300') : 'bg-indigo-50 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300',
                                    ].join(' ')}>
                                      {closed ? (win ? '▲ Win' : '▼ Loss') : '- Open'}
                                    </span>
                                  </div>
                                  <div className="rounded-lg bg-muted/40 px-2.5 py-2 mb-2 space-y-1.5">
                                    <div className="flex items-center gap-1.5">
                                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-300 flex-shrink-0" />
                                      <span className="text-muted-foreground w-8 flex-shrink-0 text-[10px]">Entry</span>
                                      <span className="font-mono font-semibold text-foreground flex-1">${formatPrice(tr.entryPrice)}</span>
                                      <span className="text-zinc-600 text-[9px]">{tr.entryDate}</span>
                                    </div>
                                    {closed && (
                                      <div className="flex items-center gap-1.5">
                                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${win ? 'bg-emerald-400' : 'bg-rose-400'}`} />
                                        <span className="text-muted-foreground w-8 flex-shrink-0 text-[10px]">Exit</span>
                                        <span className="font-mono font-semibold text-foreground flex-1">${formatPrice(tr.exitPrice!)}</span>
                                        <span className="text-zinc-600 text-[9px]">{tr.exitDate}</span>
                                      </div>
                                    )}
                                  </div>
                                  {(tr.setup || tr.entryReason) && (
                                    <div className="rounded-lg bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-300 dark:border-indigo-500/20 px-2.5 py-1.5 mb-2">
                                      <div className="text-[9px] uppercase tracking-wider text-indigo-700 dark:text-indigo-300 font-bold mb-0.5">
                                        Condition · {String(tr.setup ?? 'M1').replace('_', ' ')}
                                      </div>
                                      {tr.entryReason && (
                                        <div className="text-[9px] text-muted-foreground leading-snug break-words">{tr.entryReason}</div>
                                      )}
                                      {tr.stop != null && tr.target != null && (
                                        <div className="text-[9px] text-muted-foreground mt-0.5 font-mono">
                                          stop ${formatPrice(tr.stop)} · target ${formatPrice(tr.target)}{tr.plannedRr != null ? ` · ${tr.plannedRr.toFixed(1)}R` : ''}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {closed && tr.plPct != null && (
                                    <div className="flex items-baseline justify-between mb-2 px-0.5">
                                      <span className={`font-mono font-bold text-[16px] leading-none ${tr.plPct >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                        {tr.plPct >= 0 ? '+' : ''}{tr.plPct.toFixed(1)}%
                                      </span>
                                    </div>
                                  )}
                                  <div className="border-t border-border/60 pt-2 flex items-center justify-between text-[10px] text-muted-foreground">
                                    <span className="text-zinc-600">{reasonShort}</span>
                                    {holdDays !== null && <span>⏱ {holdDays}d</span>}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        };
                        return (
                          <div key={`watcher-replay-${i}`}>
                            {/* Entry avatar — over the entry candle, below-bar style. */}
                            {xp.entry != null && Number.isFinite(xp.entry) && (
                              <div
                                className="absolute pointer-events-auto"
                                style={{ left: xp.entry - 15, bottom: 6, zIndex: 19 }}
                                onMouseEnter={() => setHoveredReplayIdx(i)}
                                onMouseLeave={() => setHoveredReplayIdx(null)}
                              >
                                {isHovered && (
                                  <div className="absolute pointer-events-none" style={{ left: -3, bottom: 0, width: 26, height: chartHeight - 12, background: 'rgba(165,180,252,0.07)', borderLeft: '1px solid rgba(165,180,252,0.3)', borderRight: '1px solid rgba(165,180,252,0.3)' }} />
                                )}
                                <div
                                  className={['h-[22px] min-w-[30px] px-1.5 rounded-md flex items-center justify-center gap-0.5 shadow-lg transition-all duration-150 font-black text-[10px] leading-none whitespace-nowrap', isHovered ? 'scale-125' : 'scale-100'].join(' ')}
                                  style={{
                                    backgroundColor: WATCHER_SETUP_COLORS[watcherCondKey(tr.setup, tr.entryReason)] ?? '#a5b4fc',
                                    color: '#09090b',
                                    boxShadow: `0 0 10px ${(WATCHER_SETUP_COLORS[watcherCondKey(tr.setup, tr.entryReason)] ?? '#a5b4fc')}66`,
                                  }}
                                >
                                  ▲ {WATCHER_SETUP_TAGS[watcherCondKey(tr.setup, tr.entryReason)] ?? 'M1'}
                                </div>
                                {isHovered && renderTooltip(xp.entry)}
                              </div>
                            )}
                            {/* Exit avatar — over the exit candle, ringed green/red by outcome. */}
                            {closed && xp.exit != null && Number.isFinite(xp.exit) && (
                              <div
                                className="absolute pointer-events-auto"
                                style={{ left: xp.exit - 15, bottom: 6, zIndex: 19 }}
                                onMouseEnter={() => setHoveredReplayIdx(i)}
                                onMouseLeave={() => setHoveredReplayIdx(null)}
                              >
                                <div
                                  className={['h-[22px] min-w-[30px] px-1.5 rounded-md flex items-center justify-center gap-0.5 shadow-lg transition-all duration-150 font-black text-[10px] leading-none whitespace-nowrap text-zinc-950', win ? 'bg-emerald-400' : 'bg-rose-400', isHovered ? 'scale-125' : 'scale-100'].join(' ')}
                                  style={{ boxShadow: win ? '0 0 10px rgba(52,211,153,0.45)' : '0 0 10px rgba(251,113,133,0.45)' }}
                                >
                                  ▼ {tr.plPct != null ? `${tr.plPct >= 0 ? '+' : ''}${tr.plPct.toFixed(0)}%` : ''}
                                </div>
                                {isHovered && renderTooltip(xp.exit)}
                              </div>
                            )}
                          </div>
                        );
                      })}

                    {candles.length === 0 && (
                      <div className="pointer-events-none absolute inset-0 flex items-center justify-center z-0">
                        <div className="text-center text-muted-foreground bg-card/60 px-4 py-2 rounded-lg border border-border/70">
                          <p className="text-sm font-medium">No chart data available for {symbol}</p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* ── Positions strip (owner 2026-07-07): chips BELOW the chart, right-aligned —
                 never covering price action. Watcher chips are public; the "You" chip is the
                 signed-in user's own game book (open position, else last closed round-trip). ── */}
            {(visibleWatcherPos.length > 0 || visibleWatcherClosed || userPos.open || userPos.lastClosed) && (
              <div className="-mt-1 flex flex-wrap items-center justify-end gap-2 px-1">
                {(userPos.open || userPos.lastClosed) && (() => {
                  const open = userPos.open;
                  const closedTr = userPos.lastClosed;
                  const livePl = open && currentPrice != null && open.avgCost > 0 ? ((currentPrice - open.avgCost) / open.avgCost) * 100 : null;
                  const closedPl = closedTr?.realizedPlPct != null ? closedTr.realizedPlPct : null;
                  const pl = open ? livePl : closedPl;
                  const heldDays = open && open.since > 0 ? Math.max(0, (Date.now() - open.since) / 86_400_000) : null;
                  return (
                    <div className="group relative">
                      <div className={`flex cursor-default items-center gap-1.5 rounded-full border py-1 pl-1 pr-2 backdrop-blur transition-colors ${
                        open ? 'border-border/70 bg-card/80 group-hover:border-border dark:group-hover:border-zinc-400' : 'border-border bg-card/50 group-hover:border-border dark:group-hover:border-zinc-600'
                      }`}>
                        {session?.user?.image ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img src={session.user.image} alt="" className="h-[18px] w-[18px] rounded-full object-cover ring-1 ring-border dark:ring-zinc-600/60" />
                        ) : (
                          <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-gradient-to-br dark:from-zinc-500 dark:to-zinc-700 text-[9px] font-black text-foreground">
                            {(session?.user?.username ?? 'Y')[0].toUpperCase()}
                          </span>
                        )}
                        <span className="text-[11px] font-bold text-foreground/80">You{!open && ' · closed'}</span>
                        {pl != null && (
                          <span className={`rounded-full px-1.5 py-0.5 font-mono text-[10px] font-bold tabular-nums ${
                            pl >= 0 ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'bg-rose-50 dark:bg-rose-500/15 text-rose-600 dark:text-rose-300'
                          }`}>
                            {pl >= 0 ? '+' : ''}{pl.toFixed(1)}%
                          </span>
                        )}
                      </div>
                      <div className="pointer-events-none absolute bottom-full right-0 z-30 mb-2 hidden w-64 rounded-xl border border-border bg-background/[0.98] p-3 shadow-2xl backdrop-blur group-hover:block">
                        <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-foreground/80">
                          Your {open ? 'open position' : 'last trade'} <span className="text-zinc-600">(only you see this)</span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono text-[11px] text-muted-foreground">
                          {open ? (
                            <>
                              <span>Entry <span className="text-foreground">${open.avgCost < 1 ? open.avgCost.toFixed(4) : open.avgCost.toFixed(2)}</span></span>
                              <span>Shares <span className="text-foreground">{open.shares % 1 === 0 ? open.shares : open.shares.toFixed(4)}</span></span>
                              {currentPrice != null && <span>Now <span className="text-foreground">${currentPrice < 1 ? currentPrice.toFixed(4) : currentPrice.toFixed(2)}</span></span>}
                              {heldDays != null && <span>Held <span className="text-foreground">{heldDays < 1 ? `${Math.round(heldDays * 24)}h` : `${heldDays.toFixed(1)}d`}</span></span>}
                            </>
                          ) : closedTr ? (
                            <>
                              <span>Exit <span className="text-foreground">${closedTr.exitPrice < 1 ? closedTr.exitPrice.toFixed(4) : closedTr.exitPrice.toFixed(2)}</span></span>
                              {closedTr.realizedPl != null && (
                                <span>P&L <span className={closedTr.realizedPl >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-600 dark:text-rose-300'}>${closedTr.realizedPl.toFixed(2)}</span></span>
                              )}
                              {closedTr.at > 0 && (
                                <span className="col-span-2">Closed <span className="text-foreground">{new Date(closedTr.at).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })} ET</span></span>
                              )}
                            </>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })()}
                {(visibleWatcherPos.length > 0 || visibleWatcherClosed) && (
                  <div className="group relative" onMouseEnter={() => setWatcherHover(true)} onMouseLeave={() => setWatcherHover(false)}>
                    <div className="flex items-center gap-1.5">
                      {/* OPEN chip. It carries a live pulse dot and the POSITION COUNT (owner 2026-07-28:
                          "hard to tell there was an open position going" - the chip said only "Watcher",
                          so an open book and a flat one looked identical apart from a number that could
                          just as easily have been the closed trade's). Open reads as live, closed as past. */}
                      {visibleWatcherPos.length > 0 && (() => {
                        const lead = visibleWatcherPos.find((p) => p.aiName === 'Watcher') ?? visibleWatcherPos[0];
                        const pl = currentPrice != null && lead.entryPrice > 0 ? ((currentPrice - lead.entryPrice) / lead.entryPrice) * 100 : null;
                        const n = visibleWatcherPos.length;
                        return (
                          <div
                            title={`The Watcher is HOLDING ${symbol} right now (${n} open position${n === 1 ? '' : 's'}). Hover for details.`}
                            className="flex cursor-default items-center gap-1.5 rounded-full border border-amber-300 dark:border-amber-500/50 bg-card/80 py-1 pl-1 pr-2 backdrop-blur transition-colors group-hover:border-amber-300 dark:group-hover:border-amber-400">
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img src="/assets/watcher/watcher-avatar.png" alt="The Watcher" className="h-[18px] w-[18px] rounded-full object-cover ring-1 ring-amber-300 dark:ring-amber-500/50" />
                            <span className="relative flex h-1.5 w-1.5" aria-hidden>
                              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
                            </span>
                            <span className="text-[11px] font-bold text-amber-600 dark:text-amber-300">
                              {n > 1 ? `${n} open` : 'Open'}
                            </span>
                            {pl != null && (
                              <span className={`rounded-full px-1.5 py-0.5 font-mono text-[10px] font-bold tabular-nums ${pl >= 0 ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'bg-rose-50 dark:bg-rose-500/15 text-rose-600 dark:text-rose-300'}`}>
                                {pl >= 0 ? '+' : ''}{pl.toFixed(1)}%
                              </span>
                            )}
                          </div>
                        );
                      })()}
                      {/* Most recent CLOSED round-trip — its own ALWAYS-VISIBLE chip (owner 2026-07-07:
                          'supposed to show open position and most recently closed' — closed was hover-only). */}
                      {/* ⭐ THE CHIP IS THE DOOR TO THE REVIEW VIEW (owner 2026-08-07): "there is a closed
                          button, if you click on this you get to see a dashboard view of all trades done
                          by the watcher... this is where I'll investigate trades for a symbol."
                          It was a `cursor-default` div — it LOOKED like a control, said "Closed", and did
                          nothing on click. The hover card lists recent closes; the full ledger already
                          existed at /chart/<symbol>/trades with exactly ONE entry point, a small button
                          far away in the toolbar. Same failure as the API-docs nav: the thing was built
                          and was not reachable from where the user was actually looking.
                          ⚠ stopPropagation — this chip lives inside the hover-card group, so the click
                          must not bubble to the wrapper. */}
                      {visibleWatcherClosed && (
                        <Link
                          href={`/chart/${encodeURIComponent(symbol)}/trades`}
                          onClick={(e) => e.stopPropagation()}
                          title={`Open the full ${symbol} trade ledger: every Watcher trade with setup, entry/exit, capture, size guards and exit reason.`}
                          className="flex cursor-pointer items-center gap-1.5 rounded-full border border-border bg-card/50 py-1 pl-1 pr-2 backdrop-blur transition-colors hover:border-amber-300 dark:hover:border-amber-500/60 hover:bg-amber-500/[0.07] group-hover:border-border dark:group-hover:border-zinc-600">
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img src="/assets/watcher/watcher-avatar.png" alt="" className="h-[18px] w-[18px] rounded-full object-cover opacity-80 ring-1 ring-border dark:ring-zinc-600/60" />
                          <span className="text-[11px] font-bold text-foreground/80">Closed</span>
                          {visibleWatcherClosed.aiName !== 'Watcher' && (
                            <span className="rounded bg-sky-50 dark:bg-sky-500/15 px-1 py-px text-[8px] font-bold uppercase tracking-wider text-sky-700 dark:text-sky-300 ring-1 ring-sky-300 dark:ring-sky-500/30">dev</span>
                          )}
                          {visibleWatcherClosed.realizedPlPct != null && (
                            <span className={`rounded-full px-1.5 py-0.5 font-mono text-[10px] font-bold tabular-nums ${visibleWatcherClosed.realizedPlPct >= 0 ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400/90' : 'bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400/90'}`}>
                              {visibleWatcherClosed.realizedPlPct >= 0 ? '+' : ''}{visibleWatcherClosed.realizedPlPct.toFixed(1)}%
                            </span>
                          )}
                          {/* A chevron so the chip READS clickable. The reason it sat unused for a month
                              is that nothing about it said it was a door. */}
                          <span aria-hidden className="text-[11px] font-bold leading-none text-zinc-600 transition-colors group-hover:text-amber-600 dark:group-hover:text-amber-400">›</span>
                        </Link>
                      )}
                    </div>
                    {/* ── TWO SECTIONS, NOT ONE STACK (owner 2026-07-28) ────────────────────────────
                         The card used to render opens and the one closed trade in a single flat
                         `space-y` column, so a live position and a finished one were the same object
                         with a different word on the badge: "hard to tell there was an open position
                         going". Now OPEN and RECENTLY CLOSED are separate, headed, counted sections
                         with a rule between them, and closed is visually recessive (dimmer, no live
                         column) so the eye lands on what is still working first. ── */}
                    <div className="pointer-events-none absolute bottom-full right-0 z-30 mb-2 hidden w-[19rem] max-w-[92vw] overflow-hidden rounded-xl border border-border bg-background/[0.98] shadow-2xl backdrop-blur group-hover:block">
                      <div className="flex items-center gap-2 border-b border-border bg-card/60 px-3 py-2 text-xs font-bold uppercase tracking-wider text-amber-600 dark:text-amber-300">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src="/assets/watcher/watcher-avatar.png" alt="" className="h-4 w-4 rounded-full object-cover ring-1 ring-amber-300 dark:ring-amber-500/50" />
                        The Watcher on {symbol}
                      </div>

                      {/* ── SECTION 1: OPEN ── */}
                      <div className="px-3 pb-3 pt-2.5">
                        <div className="mb-1.5 flex items-center gap-1.5">
                          <span className="relative flex h-1.5 w-1.5" aria-hidden>
                            <span className={`absolute inline-flex h-full w-full rounded-full bg-emerald-400 ${visibleWatcherPos.length > 0 ? 'animate-ping opacity-75' : 'opacity-0'}`} />
                            <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${visibleWatcherPos.length > 0 ? 'bg-emerald-400' : 'bg-muted'}`} />
                          </span>
                          <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">Open now</span>
                          <span className="ml-auto font-mono text-[10px] tabular-nums text-muted-foreground">
                            {visibleWatcherPos.length || 'none'}
                          </span>
                        </div>
                        {visibleWatcherPos.length === 0 ? (
                          <p className="rounded-lg border border-dashed border-border px-2 py-1.5 text-[11px] text-zinc-600">
                            Not holding {symbol} right now.
                          </p>
                        ) : (
                        <div className="space-y-2">
                        {visibleWatcherPos.map((p) => {
                          const pl = currentPrice != null && p.entryPrice > 0 ? ((currentPrice - p.entryPrice) / p.entryPrice) * 100 : null;
                          const plDollar = currentPrice != null && p.entryPrice > 0 ? (currentPrice - p.entryPrice) * p.shares : null;
                          const heldDays = p.entryTime > 0 ? Math.max(0, (Date.now() - p.entryTime) / 86_400_000) : null;
                          const enteredEt = p.entryTime > 0 ? new Date(p.entryTime).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }) : null;
                          return (
                            <div key={p.aiName} className="rounded-lg border border-border bg-card/70 p-2">
                              <div className="mb-1 flex items-center gap-2">
                                <span
                                  title={p.aiName === 'Watcher'
                                    ? "The Watcher's LIVE strategy book - executes on Alpaca when live trading is enabled; tracks the strategy either way"
                                    : 'DEV forward-test book (Alpaca paper) - new conditions prove themselves here first'}
                                  className={`cursor-help rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${p.aiName === 'Watcher' ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'bg-sky-50 dark:bg-sky-500/15 text-sky-700 dark:text-sky-300'}`}>
                                  {p.aiName === 'Watcher' ? 'LIVE' : 'DEV'}
                                </span>
                                {pl != null && (
                                  <span className={`ml-auto flex items-baseline gap-1.5 font-mono font-bold tabular-nums ${pl >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-600 dark:text-rose-300'}`}>
                                    {plDollar != null && <span className="text-[10px] opacity-80">{plDollar >= 0 ? '+' : '-'}${Math.abs(plDollar).toFixed(2)}</span>}
                                    <span className="text-xs">{pl >= 0 ? '+' : ''}{pl.toFixed(2)}%</span>
                                  </span>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono text-[11px] text-muted-foreground">
                                <span>Entry <span className="text-foreground">${p.entryPrice < 1 ? p.entryPrice.toFixed(4) : p.entryPrice.toFixed(2)}</span></span>
                                <span>Shares <span className="text-foreground">{p.shares % 1 === 0 ? p.shares : p.shares.toFixed(4)}</span></span>
                                {currentPrice != null && (
                                  <span>Now <span className="text-foreground">${currentPrice < 1 ? currentPrice.toFixed(4) : currentPrice.toFixed(2)}</span></span>
                                )}
                                {heldDays != null && (
                                  <span>Held <span className="text-foreground">{heldDays < 1 ? `${Math.round(heldDays * 24)}h` : `${heldDays.toFixed(1)}d`}</span></span>
                                )}
                                {enteredEt && (
                                  <span className="col-span-2">Entered <span className="text-foreground">{enteredEt} ET</span></span>
                                )}
                              </div>
                              {p.entryReason && <p className="mt-1 line-clamp-2 text-[10px] leading-snug text-muted-foreground">{p.entryReason}</p>}
                            </div>
                          );
                        })}
                        </div>
                        )}
                      </div>

                      {/* ── SECTION 2: RECENTLY CLOSED ── */}
                      {visibleWatcherClosedList.length > 0 && (
                        <div className="border-t border-border bg-card/30 px-3 pb-3 pt-2.5">
                          <div className="mb-1.5 flex items-center gap-1.5">
                            <span className="h-1.5 w-1.5 rounded-full bg-muted" aria-hidden />
                            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Recently closed</span>
                            <span className="ml-auto font-mono text-[10px] tabular-nums text-muted-foreground">{visibleWatcherClosedList.length}</span>
                          </div>
                          <div className="space-y-1.5">
                            {visibleWatcherClosedList.map((c, i) => {
                              const closedEt = c.exitTime > 0
                                ? new Date(c.exitTime).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
                                : null;
                              return (
                                // Newest closed trade keys off exitTime; index is the tiebreak for the
                                // (possible) two exits stamped in the same second.
                                <div key={`${c.exitTime}-${i}`} className="rounded-lg border border-border/80 bg-card/40 px-2 py-1.5">
                                  <div className="flex items-baseline gap-2">
                                    <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
                                      ${c.entryPrice < 1 ? c.entryPrice.toFixed(4) : c.entryPrice.toFixed(2)}
                                      <span className="mx-1 text-zinc-600">&rarr;</span>
                                      ${c.exitPrice < 1 ? c.exitPrice.toFixed(4) : c.exitPrice.toFixed(2)}
                                    </span>
                                    {c.aiName !== 'Watcher' && (
                                      <span className="rounded bg-sky-50 dark:bg-sky-500/15 px-1 py-px text-[8px] font-bold uppercase tracking-wider text-sky-700 dark:text-sky-300 ring-1 ring-sky-300 dark:ring-sky-500/30">dev</span>
                                    )}
                                    {c.realizedPlPct != null && (
                                      <span className={`ml-auto font-mono text-[11px] font-bold tabular-nums ${c.realizedPlPct >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-600 dark:text-rose-300'}`}>
                                        {c.realizedPlPct >= 0 ? '+' : ''}{c.realizedPlPct.toFixed(2)}%
                                      </span>
                                    )}
                                  </div>
                                  <div className="mt-0.5 flex items-baseline gap-2 text-[10px] leading-snug text-muted-foreground">
                                    {closedEt && <span className="whitespace-nowrap">{closedEt} ET</span>}
                                    {c.exitReason && <span className="ml-auto truncate">{c.exitReason}</span>}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Volume Chart - Integrated Design */}
            <div className="bg-background border border-border/60 rounded-2xl overflow-hidden shadow-2xl ring-1 ring-border dark:ring-zinc-800/50 flex-shrink-0">
              <div className="px-4 py-2 border-b border-border/50 bg-card/40">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500 shadow-lg dark:shadow-blue-500/50"></div>
                  <h3 className="text-sm font-bold text-foreground tracking-wide">Volume</h3>
                </div>
              </div>
              <div ref={volumeContainerRef} className="w-full p-2" style={{ height: volHeight, minHeight: 100 }} />
            </div>

            {/* RSI(14) sub-pane — kept mounted (synced) and collapsed when off, like the volume pane.
                Toggle via the "RSI" layer button. 0-100 scale with 30/70 reference bands. */}
            <div
              className="bg-background border border-border/60 rounded-2xl overflow-hidden shadow-2xl ring-1 ring-border dark:ring-zinc-800/50 flex-shrink-0"
              style={{ display: showRSI ? 'block' : 'none' }}
            >
              <div className="px-4 py-2 border-b border-border/50 bg-card/40">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-fuchsia-500 shadow-lg dark:shadow-fuchsia-500/50"></div>
                  <h3 className="text-sm font-bold text-foreground tracking-wide">RSI (14)</h3>
                  <span className="text-xs text-muted-foreground ml-1">{rsiValue != null ? rsiValue.toFixed(1) : '--'}</span>
                </div>
              </div>
              <div ref={rsiContainerRef} className="w-full p-2" style={{ height: 120, minHeight: 100 }} />
            </div>

            {/* RVOL sub-pane — relative volume per candle at the SELECTED timeframe, so RVOL reads as a
                trend the way price does. Denominator is volume_baseline (average volume for that
                minute-of-day), which is what keeps the open from permanently printing 5x.
                Toggle via the "RVOL" indicator. */}
            <div
              className="bg-background border border-border/60 rounded-2xl overflow-hidden shadow-2xl ring-1 ring-border dark:ring-zinc-800/50 flex-shrink-0"
              style={{ display: showRVOL ? 'block' : 'none' }}
            >
              <div className="px-4 py-2 border-b border-border/50 bg-card/40">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-amber-500 shadow-lg dark:shadow-amber-500/50"></div>
                  <h3 className="text-sm font-bold text-foreground tracking-wide">RVOL</h3>
                  {/* Hovered bar wins; with no crosshair this falls back to the latest bar. The value is
                      colour-coded here rather than on the line, since a line series cannot colour points. */}
                  {(() => {
                    const shown = rvolHover ? rvolHover.v : rvolPaneValue;
                    const thin = rvolHover ? rvolHover.thin : rvolPaneThin;
                    const tone = shown == null ? 'text-muted-foreground'
                      : shown >= 3 ? 'text-rose-600 dark:text-rose-300' : shown >= 1 ? 'text-amber-600 dark:text-amber-300' : 'text-muted-foreground';
                    return (
                      <>
                        <span className={`ml-1 font-mono text-xs font-bold tabular-nums ${tone}`}>
                          {shown != null ? `${shown >= 10 ? Math.round(shown) : shown.toFixed(1)}x` : '--'}
                        </span>
                        <span className="text-[10px] text-zinc-600">
                          {rvolHover ? 'at cursor' : 'latest'} · vs this time of day
                        </span>
                        {thin && shown != null && (
                          <span
                            title="Thin baseline: only a few past sessions recorded for this time of day, so the comparison is rough. It firms up as we collect more."
                            className="cursor-help rounded border border-border bg-muted/60 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground"
                          >
                            thin
                          </span>
                        )}
                      </>
                    );
                  })()}
                  {/* Honesty badge. A brand-new mover can have a baseline built from a handful of
                      sessions, and that is exactly the name a user is most likely to be looking at. */}
                </div>
              </div>
              <div ref={rvolContainerRef} className="w-full p-2" style={{ height: 120, minHeight: 100 }} />
            </div>

            {/* MACD(12/26/9) sub-pane — kept mounted (synced) and collapsed when off, like RSI.
                Toggle via the "MACD" indicator. Histogram + MACD/signal lines with a 0 reference. */}
            <div
              className="bg-background border border-border/60 rounded-2xl overflow-hidden shadow-2xl ring-1 ring-border dark:ring-zinc-800/50 flex-shrink-0"
              style={{ display: showMACD ? 'block' : 'none' }}
            >
              <div className="px-4 py-2 border-b border-border/50 bg-card/40">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500 shadow-lg dark:shadow-blue-500/50"></div>
                  <h3 className="text-sm font-bold text-foreground tracking-wide">MACD (12/26/9)</h3>
                  {macdValue && (
                    <span className="text-xs ml-1 tabular-nums">
                      <span className="text-blue-700 dark:text-blue-400">{macdValue.macd.toFixed(3)}</span>
                      <span className="text-zinc-600 mx-1">/</span>
                      <span className="text-orange-600 dark:text-orange-400">{macdValue.signal.toFixed(3)}</span>
                      <span className={['ml-2', macdValue.hist >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'].join(' ')}>{macdValue.hist >= 0 ? '+' : ''}{macdValue.hist.toFixed(3)}</span>
                    </span>
                  )}
                </div>
              </div>
              <div ref={macdContainerRef} className="w-full p-2" style={{ height: 120, minHeight: 100 }} />
            </div>

            {/* ADX(14) sub-pane — trend strength (>25 trending, <20 chop), 25 reference line. */}
            <div
              className="bg-background border border-border/60 rounded-2xl overflow-hidden shadow-2xl ring-1 ring-border dark:ring-zinc-800/50 flex-shrink-0"
              style={{ display: showADX ? 'block' : 'none' }}
            >
              <div className="px-4 py-2 border-b border-border/50 bg-card/40">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-yellow-500 shadow-lg dark:shadow-yellow-500/50"></div>
                  <h3 className="text-sm font-bold text-foreground tracking-wide">ADX (14)</h3>
                  <span className={['text-xs ml-1 tabular-nums', adxValue != null && adxValue >= 25 ? 'text-amber-600 dark:text-amber-300' : 'text-muted-foreground'].join(' ')}>
                    {adxValue != null ? adxValue.toFixed(1) : '--'}{adxValue != null ? (adxValue >= 25 ? ' · trending' : adxValue < 20 ? ' · chop' : '') : ''}
                  </span>
                </div>
              </div>
              <div ref={adxContainerRef} className="w-full p-2" style={{ height: 120, minHeight: 100 }} />
            </div>

            {/* OBV sub-pane — On-Balance Volume, a single running-total line. Kept mounted (synced) and
                collapsed when off, like the other panes. Toggle via the "OBV" indicator. */}
            <div
              className="bg-background border border-border/60 rounded-2xl overflow-hidden shadow-2xl ring-1 ring-border dark:ring-zinc-800/50 flex-shrink-0"
              style={{ display: showOBV ? 'block' : 'none' }}
            >
              <div className="px-4 py-2 border-b border-border/50 bg-card/40">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full shadow-lg" style={{ backgroundColor: '#f5b642', boxShadow: '0 0 8px rgba(245,182,66,0.5)' }}></div>
                  <h3 className="text-sm font-bold text-foreground tracking-wide">OBV</h3>
                  <span className="text-xs text-muted-foreground ml-1 tabular-nums">{obvValue != null ? fmtCompactNum(obvValue) : '--'}</span>
                </div>
              </div>
              <div ref={obvContainerRef} className="w-full p-2" style={{ height: 120, minHeight: 100 }} />
            </div>

            {/* A/D line sub-pane — Williams Accumulation/Distribution, a single cumulative line.
                Toggle via the "A/D Line" indicator. */}
            <div
              className="bg-background border border-border/60 rounded-2xl overflow-hidden shadow-2xl ring-1 ring-border dark:ring-zinc-800/50 flex-shrink-0"
              style={{ display: showADLine ? 'block' : 'none' }}
            >
              <div className="px-4 py-2 border-b border-border/50 bg-card/40">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full shadow-lg" style={{ backgroundColor: '#37d399', boxShadow: '0 0 8px rgba(55,211,153,0.5)' }}></div>
                  <h3 className="text-sm font-bold text-foreground tracking-wide">A/D Line</h3>
                  <span className="text-xs text-muted-foreground ml-1 tabular-nums">{adlValue != null ? fmtCompactNum(adlValue) : '--'}</span>
                </div>
              </div>
              <div ref={adlContainerRef} className="w-full p-2" style={{ height: 120, minHeight: 100 }} />
            </div>

            {/* CMF(20) sub-pane — Chaikin Money Flow, a bounded oscillator around 0 (>0 accumulation,
                <0 distribution) with ±0.10 reference lines. Toggle via the "Chaikin Money Flow" indicator. */}
            <div
              className="bg-background border border-border/60 rounded-2xl overflow-hidden shadow-2xl ring-1 ring-border dark:ring-zinc-800/50 flex-shrink-0"
              style={{ display: showCMF ? 'block' : 'none' }}
            >
              <div className="px-4 py-2 border-b border-border/50 bg-card/40">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full shadow-lg" style={{ backgroundColor: '#a78bfa', boxShadow: '0 0 8px rgba(167,139,250,0.5)' }}></div>
                  <h3 className="text-sm font-bold text-foreground tracking-wide">CMF (20)</h3>
                  <span className={['text-xs ml-1 tabular-nums', cmfValue == null ? 'text-muted-foreground' : cmfValue > 0.05 ? 'text-emerald-700 dark:text-emerald-300' : cmfValue < -0.05 ? 'text-rose-600 dark:text-rose-300' : 'text-muted-foreground'].join(' ')}>
                    {cmfValue != null ? `${cmfValue >= 0 ? '+' : ''}${cmfValue.toFixed(2)}` : '--'}{cmfValue != null ? (cmfValue > 0.05 ? ' · accum' : cmfValue < -0.05 ? ' · distrib' : '') : ''}
                  </span>
                </div>
              </div>
              <div ref={cmfContainerRef} className="w-full p-2" style={{ height: 120, minHeight: 100 }} />
            </div>

            {/* ── S/R legend (shows what's drawn; line hover isn't available in lightweight-charts) ── */}
            {srLegend && (
              <div className="px-4 py-3 bg-muted/30 rounded-lg border border-border/30 text-xs space-y-2">
                <div className="flex flex-wrap items-start gap-x-6 gap-y-2">
                  {([
                    { group: 'Buy-side', items: [
                      { label: 'Support', color: '#34d399', style: 'dotted', def: 'A price floor where it has repeatedly bounced up.' },
                      { label: 'DB ↑ (double bottom)', color: '#2dd4bf', style: 'dashed', def: 'Double bottom - bullish; a buy trigger if it breaks up through the neckline.' }] },
                    { group: 'Sell-side', items: [
                      { label: 'Resistance', color: '#fb7185', style: 'dotted', def: 'A price ceiling where it has repeatedly stalled.' },
                      { label: 'DT ↓ (double top)', color: '#f87171', style: 'dashed', def: 'Double top - bearish; a sell trigger if it breaks down through the neckline.' }] },
                    { group: 'Structural', items: [
                      { label: 'POC', color: '#f5c518', style: 'solid', def: 'Point of control - the single most-traded price (by volume).' },
                      { label: 'HVN', color: 'rgba(245,197,24,0.5)', style: 'solid', def: 'High-volume node - a price where heavy volume has transacted; acts as a magnet/strong level.' },
                      { label: 'Contested', color: '#b8a888', style: 'dashed', def: 'A double-top trigger and a double-bottom support at the same price - two-sided, no clean direction.' },
                      { label: 'Flipped ↺', color: '#34d399', style: 'dotted', def: 'A level that broke and now acts from the other side - old resistance now support, or vice versa.' }] },
                  ] as const).map((g) => (
                    <div key={g.group} className="flex flex-col gap-1">
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">{g.group}</span>
                      {g.items.map((it) => (
                        <span key={it.label} title={it.def} className="inline-flex items-center gap-2 text-foreground/80 cursor-help">
                          <span className="inline-block w-6" style={{ borderTopWidth: 2, borderTopStyle: it.style as 'solid' | 'dashed' | 'dotted', borderTopColor: it.color }} />
                          {it.label}
                        </span>
                      ))}
                    </div>
                  ))}
                </div>
                <div className="text-[11px] text-muted-foreground border-t border-border/40 pt-1.5">
                  Line <span className="text-muted-foreground">opacity + width</span> = volume strength · <span className="text-muted-foreground">★</span> = sits on a volume node · necklines <span className="text-muted-foreground">dashed</span> (break trigger), S/R <span className="text-muted-foreground">dotted</span>, <span className="text-muted-foreground">↺</span> = flipped
                </div>
                <details className="text-[11px] text-muted-foreground">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground/80 select-none">What do these mean?</summary>
                  <ul className="mt-1.5 space-y-1 pl-1">
                    <li><span className="text-emerald-700 dark:text-emerald-400 font-medium">Support / Resistance</span> - price floors / ceilings the stock keeps reacting to.</li>
                    <li><span className="text-teal-700 dark:text-teal-300 font-medium">DB ↑</span> (double bottom) - bullish; buy trigger on a break up through the neckline.</li>
                    <li><span className="text-red-600 dark:text-red-400 font-medium">DT ↓</span> (double top) - bearish; sell trigger on a break down through the neckline.</li>
                    <li><span style={{ color: '#34d399' }} className="font-medium">Flipped ↺</span> - old resistance now acting as support (or vice versa) after a break and retest.</li>
                    <li><span style={{ color: '#b8a888' }} className="font-medium">Contested</span> - both a DT trigger and DB support at one price; two-sided, no clean direction.</li>
                    <li><span style={{ color: '#f5c518' }} className="font-medium">POC</span> - most-traded price by volume. <span style={{ color: '#f5c518' }} className="font-medium">HVN</span> - a heavy-volume level.</li>
                  </ul>
                </details>
                {(srLegend.levels.length > 0 || srLegend.patterns.length > 0 || srLegend.poc != null) && (
                  <div className="flex flex-wrap gap-1.5 pt-0.5">
                    {srLegend.levels.map((l, i) => (
                      <span key={`l${i}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-card/60 border border-border/40"
                        style={{ color: l.kind === 'support' ? '#34d399' : '#fb7185' }}>
                        {l.kind === 'support' ? 'Sup' : 'Res'} ${formatPrice(l.price)} ·{l.touches}{l.at_hvn ? ' ★' : ''}{l.flipped ? ' ↺' : ''} <span className="text-zinc-600">{l.source_tf}</span>
                      </span>
                    ))}
                    {srLegend.patterns.map((p, i) => {
                      const st = patternStyle(p as { kind: 'double_top' | 'double_bottom' | 'contested'; confirmed: boolean });
                      return (
                        <span key={`p${i}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-card/60 border border-border/40" style={{ color: st.color }}>
                          {st.label} ${formatPrice(p.level)}{p.confirmed ? '' : ' (forming)'}
                        </span>
                      );
                    })}
                    {srLegend.poc != null && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-card/60 border border-border/40" style={{ color: '#f5c518' }}>POC ${formatPrice(srLegend.poc)}</span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Data Freshness Indicator */}
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground px-4 py-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
                  <span>Auto-updating every 3s</span>
              {lastUpdate && (
                <>
                  <span className="text-zinc-600">*</span>
                  <span>Last: {new Date(lastUpdate).toLocaleTimeString()}</span>
                </>
              )}
            </div>
          </div>

          {/* Alerts Sidebar — viewport-bounded on desktop so the daily chart is always pinned in view (no magic pixels). */}
          <div className={`flex flex-col gap-3 h-fit ${chatOpen ? '' : 'xl:col-span-1 xl:h-[calc(100dvh-176px)] xl:min-h-0'}`}>
            {/* ── RAIL SWITCHER ────────────────────────────────────────────────────────────────
                One segmented control, one panel below it. Previously Due Diligence and Recent
                Alerts both rendered, stacked, inside a rail that is height-bounded to the
                viewport — so on an active name the alert list ate the space and pushed the daily
                chart out of view entirely. A toggle is the honest fix: both panels are dense
                reference surfaces that deserve the full rail, and nobody reads them at once. */}
            {!isShot && !isEmbed && (
              <div className="flex-shrink-0 grid grid-cols-2 gap-1 rounded-xl border border-border/60 bg-background p-1 shadow-2xl ring-1 ring-border dark:ring-zinc-800/40">
                {([
                  { key: 'dd' as const, label: 'Due Diligence', Icon: ShieldCheck, count: null as number | null },
                  { key: 'alerts' as const, label: 'Alerts', Icon: Activity, count: alerts.length },
                ]).map(({ key, label, Icon, count }) => {
                  const on = railTab === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setRailTab(key)}
                      aria-pressed={on}
                      className={`flex items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-[12px] font-bold transition ${
                        on ? 'bg-amber-500 text-black shadow-lg dark:shadow-amber-500/20' : 'text-muted-foreground hover:bg-accent dark:hover:bg-zinc-800/70 hover:text-foreground/80'
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                      <span className="truncate">{label}</span>
                      {count != null && count > 0 && (
                        <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${
                          on ? 'bg-background/20 text-black' : 'bg-amber-50 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400'
                        }`}>{count}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Due-diligence cockpit (catalyst, dilution, float, short/SSR, earnings, analyst, insider).
                ⚠ Wrapped in a flex-1 + min-h-0 + overflow-y-auto shell so DD's tall content SCROLLS
                INSIDE the leftover rail height instead of overflowing the viewport-capped rail and
                pushing the pinned daily chart off the bottom (the exact "daily is off the page" bug).
                Mirrors what the Alerts panel below already does. */}
            {!isShot && !isEmbed && railTab === 'dd' && (
              <div className="h-fit xl:flex-1 xl:min-h-0 xl:overflow-y-auto xl:pr-0.5 dt-rail-scroll">
                <DueDiligencePanel symbol={symbol} price={currentPrice} />
              </div>
            )}
            {/* Today's Alerts Container - flexes to fill the rail's leftover space; its list scrolls internally.
                Always rendered for the screenshot/embed views, which have no switcher to reach it with. */}
            <div className={`bg-background border border-border/60 rounded-2xl p-3.5 shadow-2xl ring-1 ring-border dark:ring-zinc-800/40 h-fit xl:h-auto xl:flex-1 xl:min-h-0 xl:flex-col ${
              (isShot || isEmbed || railTab === 'alerts') ? 'xl:flex' : 'hidden'
            }`}>
              <div className="flex items-center justify-between mb-2.5 pb-2 border-b border-border/50">
                <div className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  <h2 className="text-sm font-bold text-foreground uppercase tracking-wide">Recent Alerts</h2>
                </div>
                <div className="px-2.5 py-1 bg-amber-50 dark:bg-amber-500/20 border border-amber-300 dark:border-amber-500/30 rounded-full">
                  <span className="text-xs font-bold text-amber-600 dark:text-amber-400">{alerts.length}</span>
                </div>
              </div>
              
              {alerts.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground">
                  <Activity className="h-16 w-16 mx-auto mb-4 text-zinc-800 opacity-20" />
                  <p className="text-sm font-medium">No recent alerts for this symbol</p>
                </div>
              ) : (
                <div className="space-y-1.5 max-h-[42vh] xl:max-h-none xl:flex-1 xl:min-h-0 overflow-y-auto pr-1 custom-scrollbar">
                  {[...alerts].reverse().map((alert) => {
                    const isRvol = alert.type === 'rvol';
                    const isMover = alert.type === 'mover';
                    const isEma = alert.type === 'ema' || alert.type === 'ema_near';
                    const isEmaCrossAbove = alert.type === 'ema_cross_above';
                    const isEmaCrossBelow = alert.type === 'ema_cross_below';
                    const isHalt = alert.type === 'halt';
                    const isResume = alert.type === 'resume';
                    const isEmaTrending = alert.type === 'ema_trending';
                    const isNews = alert.type === 'news';
                    const newsSentiment = alert.news?.sentiment ?? null;
                    // Parse tf + period from triggeredBy for ema_trending cards
                    const emaTrendMeta = (() => {
                      if (!isEmaTrending) return null;
                      const tb: string = alert.data?.triggeredBy ?? '';
                      const nm = tb.match(/^ema_trend_([^_]+)_(\d+)$/);
                      if (nm) return { tf: nm[1], period: parseInt(nm[2], 10) };
                      const lm = tb.match(/^ema_trend_(\d+)$/);
                      if (lm) return { tf: '1m', period: parseInt(lm[1], 10) };
                      // ⚠ DELIBERATELY CONSTANT. This previously read `alert.data?.timeframe` and
                      // `alert.data?.emaValue`, neither of which the alerts API puts in `data` - so both
                      // were always undefined and this already evaluated to exactly {'1m', 9}. `emaValue`
                      // exists only as a DB column on rvolAlerts and is a STRING (sig.ema.toFixed(4)), so
                      // the `typeof === 'number'` test could never pass either. Widening the type to
                      // silence TS would have hidden dead code rather than fixed anything.
                      // To make this real, plumb timeframe + a NUMERIC ema period through the API's
                      // baseData; until then the honest default is the 1m/9 pair the branch already used.
                      return { tf: '1m', period: 9 };
                    })();
                    const isTrade = isTradeAlert(alert);
                    const tradeStrategy = isTrade ? resolveTradeStrategy(alert) : null;
                    const tradeMeta = isTrade ? parseTradeMeta(alert) : null;
                    const rawTradeSide = tradeMeta?.side ?? 'buy';
                    // Osiris: SHORT (sell) = entry, COVER (buy) = exit — flip so display matches Ra/Thoth
                    const tradeIsBuy = tradeStrategy === 'osiris' ? rawTradeSide === 'sell' : rawTradeSide === 'buy';
                    const tradeAvatar = tradeStrategy ? resolveTradeAvatar(alert, tradeStrategy) : '/assets/thoth/thoth-avatar.png';
                    const tradeLabel = tradeStrategy ? resolveTradeLabel(alert, tradeStrategy) : 'Thoth AI';
                    const tradeFill = (tradeStrategy === 'ra' || tradeStrategy === 'watcher')
                      ? findFillForStrategy(tradeStrategy, rawTradeSide === 'buy' ? 'buy' : 'sell', alert.barTimestamp ?? alert.timestamp)
                      : null;
                    // Show the real Alpaca fill when we have one; else the signal/alert price.
                    const tradeSignalPrice = tradeIsBuy ? (tradeMeta?.entryPrice ?? alert.data?.price) : (tradeMeta?.exitPrice ?? alert.data?.price);
                    const tradePrice = tradeFill?.price ?? tradeSignalPrice;
                    const exitReasonLabel: Record<string, string> = {
                      trail_exit: 'Trail stop',
                      condAB_9ema_trail: '9-EMA trail',
                      condJLM_9ema_trail: '9-EMA trail',
                      condKV_9ema_trail: '9-EMA trail',
                      profit_50ema_trail: '50-EMA lock',
                      profit_9ema_tight: '9-EMA lock',
                      max_loss: 'Max loss',
                      cross_below_ema: 'EMA cross',
                      below_ema_entry_stop: 'EMA entry stop',
                      manual: 'Manual',
                      condC_pattern_failed: 'Pattern failed',
                      sub5_stop: 'Sub-$5 stop',
                      same_day_disappointment: 'Disappointment cut',
                      blowoff_take: 'Blow-off take',
                      wick_rejection_take: 'Wick-rejection take',
                      friday_eod_derisk: 'Friday de-risk',
                    };

                    // Thoth/Ra trade card — distinct rich layout
                    if (isTrade) {
                      return (
                        <div
                          key={alert.id}
                          className={[
                            'border rounded-xl p-3 transition-all duration-200 hover:scale-[1.01] cursor-pointer backdrop-blur-sm',
                            tradeIsBuy
                              ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-600/40 hover:border-emerald-300 dark:hover:border-emerald-500/60 hover:bg-emerald-50 dark:hover:bg-emerald-950/50'
                              : 'bg-rose-50 dark:bg-rose-950/30 border-rose-300 dark:border-rose-600/40 hover:border-rose-300 dark:hover:border-rose-500/60 hover:bg-rose-50 dark:hover:bg-rose-950/50',
                          ].join(' ')}
                          onMouseEnter={() => setHoveredTradeId(alert.id)}
                          onMouseLeave={() => setHoveredTradeId(null)}
                        >
                          <div className="flex items-start gap-2.5">
                            <Link
                            href={getAiGameUrl(tradeStrategy)}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={`View ${tradeLabel} game profile`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <img src={tradeAvatar} alt={tradeLabel} className="h-8 w-8 rounded-full object-cover ring-2 ring-violet-300 dark:ring-violet-500/40 flex-shrink-0 mt-0.5 hover:ring-violet-300 dark:hover:ring-violet-400/80 hover:brightness-110 transition-all" />
                          </Link>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                                <Link
                                  href={getAiGameUrl(tradeStrategy)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className={`text-[10px] font-semibold px-1.5 py-0.5 rounded hover:underline ${tradeStrategy === 'osiris' ? 'text-amber-600 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/20' : tradeStrategy === 'ra' ? 'text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-500/20' : 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/20'}`}
                                >{tradeLabel}</Link>
                                <span className={[
                                  'text-[10px] font-bold uppercase px-1.5 py-0.5 rounded',
                                  tradeIsBuy ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/20' : 'text-rose-600 dark:text-rose-300 bg-rose-50 dark:bg-rose-500/20',
                                ].join(' ')}>
                                  {tradeIsBuy
                                    ? (tradeStrategy === 'osiris' ? 'SHORT' : 'BUY')
                                    : (tradeStrategy === 'osiris' ? 'COVER' : 'SELL')}
                                </span>
                                <span className="text-[10px] text-muted-foreground ml-auto">
                                  {new Date(alert.barTimestamp ?? alert.timestamp).toLocaleString('en-US', {
                                    timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
                                  })} ET
                                </span>
                              </div>
                              {typeof tradePrice === 'number' && tradePrice > 0 && (
                                <div className="text-sm font-semibold text-foreground">${formatPrice(tradePrice)}</div>
                              )}
                              {/* signal → filled when the displayed price is a real fill differing from the signal */}
                              {tradeFill != null && typeof tradeSignalPrice === 'number'
                                && Math.abs(tradeFill.price - tradeSignalPrice) / Math.max(tradeSignalPrice, 0.0001) > 0.0005 && (
                                <div className="text-[10px] text-muted-foreground font-mono">signal ${formatPrice(tradeSignalPrice)} → filled ${formatPrice(tradeFill.price)}</div>
                              )}
                              {!tradeIsBuy && typeof tradeMeta?.realizedPl === 'number' && (
                                <div className={`text-xs font-bold ${tradeMeta.realizedPl >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                  P&L: {tradeMeta.realizedPl >= 0 ? '+' : '-'}${Math.abs(tradeMeta.realizedPl).toFixed(2)}
                                  {typeof tradeMeta.realizedPlPct === 'number' && (
                                    <span className="ml-1 font-normal opacity-80">({tradeMeta.realizedPlPct >= 0 ? '+' : ''}{tradeMeta.realizedPlPct.toFixed(2)}%)</span>
                                  )}
                                </div>
                              )}
                              {tradeIsBuy && (() => {
                                const condMatch = tradeMeta?.reason?.match(/^(cond[A-Z][a-zA-Z0-9]*)/);
                                const condLbl = condMatch ? condMatch[1] : null;
                                return (
                                  <div className="flex items-center gap-1.5 mt-0.5">
                                    {condLbl && (
                                      <span className="font-mono text-[9px] bg-muted text-violet-700 dark:text-violet-300 px-1.5 py-0.5 rounded border border-border/60 leading-none">{condLbl}</span>
                                    )}
                                    {typeof tradeMeta?.positionSizePct === 'number' && (
                                      <span className="text-[10px] text-muted-foreground">{tradeMeta.positionSizePct.toFixed(1)}% account</span>
                                    )}
                                  </div>
                                );
                              })()}
                              {!tradeIsBuy && tradeMeta?.reason && (
                                <div className="text-[10px] text-muted-foreground">
                                  {(() => {
                                    const rk = tradeMeta.reason.split('(')[0].trim().split(' ')[0];
                                    return exitReasonLabel[rk] ?? exitReasonLabel[tradeMeta.reason] ?? rk;
                                  })()}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={alert.id}
                        className={`border rounded-lg px-2.5 py-1.5 transition-colors cursor-pointer ${
                          isRvol ? 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-300 dark:border-emerald-500/40 hover:border-emerald-300 dark:hover:border-emerald-500/60 hover:bg-emerald-50 dark:hover:bg-emerald-500/15' :
                          isMover ? 'bg-yellow-50 dark:bg-yellow-500/10 border-yellow-300 dark:border-yellow-500/40 hover:border-yellow-300 dark:hover:border-yellow-500/60 hover:bg-yellow-50 dark:hover:bg-yellow-500/15' :
                          isEma ? 'bg-purple-50 dark:bg-purple-500/10 border-purple-300 dark:border-purple-500/40 hover:border-purple-300 dark:hover:border-purple-500/60 hover:bg-purple-50 dark:hover:bg-purple-500/15' :
                          isEmaCrossAbove ? 'bg-cyan-50 dark:bg-cyan-500/10 border-cyan-300 dark:border-cyan-500/40 hover:border-cyan-300 dark:hover:border-cyan-500/60 hover:bg-cyan-50 dark:hover:bg-cyan-500/15' :
                          isEmaCrossBelow ? 'bg-orange-50 dark:bg-orange-500/10 border-orange-300 dark:border-orange-500/40 hover:border-orange-300 dark:hover:border-orange-500/60 hover:bg-orange-50 dark:hover:bg-orange-500/15' :
                          isHalt ? 'bg-red-50 dark:bg-red-500/10 border-red-300 dark:border-red-500/40 hover:border-red-300 dark:hover:border-red-500/60 hover:bg-red-50 dark:hover:bg-red-500/15' :
                          isResume ? 'bg-green-50 dark:bg-green-500/10 border-green-300 dark:border-green-500/40 hover:border-green-300 dark:hover:border-green-500/60 hover:bg-green-50 dark:hover:bg-green-500/15' :
                          isEmaTrending ? 'bg-amber-50 dark:bg-amber-500/10 border-amber-300 dark:border-amber-500/40 hover:border-amber-300 dark:hover:border-amber-500/60 hover:bg-amber-50 dark:hover:bg-amber-500/15' :
                          isNews ? (newsSentiment === 'positive' ? 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-300 dark:border-emerald-500/40 hover:border-emerald-300 dark:hover:border-emerald-500/60 hover:bg-emerald-50 dark:hover:bg-emerald-500/15' : newsSentiment === 'negative' ? 'bg-rose-50 dark:bg-rose-500/10 border-rose-300 dark:border-rose-500/40 hover:border-rose-300 dark:hover:border-rose-500/60 hover:bg-rose-50 dark:hover:bg-rose-500/15' : 'bg-sky-50 dark:bg-sky-500/10 border-sky-300 dark:border-sky-500/40 hover:border-sky-300 dark:hover:border-sky-500/60 hover:bg-sky-50 dark:hover:bg-sky-500/15') :
                          'bg-muted/30 border-border/30 hover:border-border dark:hover:border-zinc-600/40'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <div className={`w-1.5 h-1.5 mt-1 flex-shrink-0 ${
                            isRvol ? 'bg-emerald-500 dark:shadow-emerald-500/50 rounded-full' :
                            isMover ? 'bg-yellow-500 dark:shadow-yellow-500/50 rounded-full' :
                            isEma ? 'bg-purple-500 dark:shadow-purple-500/50 rounded-full' :
                            isEmaCrossAbove ? 'bg-cyan-400 dark:shadow-cyan-400/50 rounded-full' :
                            isEmaCrossBelow ? 'bg-orange-500 dark:shadow-orange-500/50 rounded-full' :
                            isHalt ? 'bg-red-500 dark:shadow-red-500/50' :
                            isResume ? 'bg-green-500 dark:shadow-green-500/50' :
                            isEmaTrending ? 'bg-amber-400 dark:shadow-amber-400/50 rounded-full' :
                            isNews ? (newsSentiment === 'positive' ? 'bg-emerald-400 dark:shadow-emerald-400/50 rounded-full' : newsSentiment === 'negative' ? 'bg-rose-400 dark:shadow-rose-400/50 rounded-full' : 'bg-sky-400 dark:shadow-sky-400/50 rounded-full') :
                            'bg-muted rounded-full'
                          }`}></div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                              <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                                isRvol ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/30' :
                                isMover ? 'text-yellow-600 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-500/30' :
                                isEma ? 'text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-500/30' :
                                isEmaCrossAbove ? 'text-cyan-700 dark:text-cyan-300 bg-cyan-50 dark:bg-cyan-500/30' :
                                isEmaCrossBelow ? 'text-orange-600 dark:text-orange-300 bg-orange-50 dark:bg-orange-500/30' :
                                isHalt ? 'text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-500/30' :
                                isResume ? 'text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-500/30' :
                                isEmaTrending ? 'text-amber-600 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/30' :
                                isNews ? (newsSentiment === 'positive' ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/25' : newsSentiment === 'negative' ? 'text-rose-600 dark:text-rose-300 bg-rose-50 dark:bg-rose-500/25' : 'text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-500/25') :
                                'text-muted-foreground bg-muted/20'
                              }`}>
                                {isEma ? 'EMA Near' : isEmaCrossAbove ? 'EMA Cross ↑' : isEmaCrossBelow ? 'EMA Cross ↓' :
                                 isEmaTrending
                                   ? `${emaTrendMeta?.period ?? 9}EMA${emaTrendMeta && emaTrendMeta.tf !== '1m' ? ` · ${emaTrendMeta.tf === '60m' ? '1hr' : emaTrendMeta.tf}` : ''}`
                                   : isNews ? '📰 News'
                                   : alert.type}
                              </span>
                              <span className="text-[10px] text-muted-foreground font-medium">
                                {new Date(alert.timestamp).toLocaleString('en-US', {
                                  timeZone: 'America/New_York',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true
                                })} ET
                              </span>
                            </div>
                            
                            <div className="flex items-center gap-x-2 gap-y-0.5 flex-wrap mb-0.5">
                              {isRvol && alert.data?.rvol && (
                                <div className="text-sm text-emerald-700 dark:text-emerald-400 font-bold">
                                  {alert.data.rvol.toFixed(2)}x RVOL
                                </div>
                              )}
                              
                              {isMover && typeof alert.data?.percentChange === 'number' && (
                                <div className={`text-sm font-bold ${
                                  alert.data.percentChange > 0 ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                                }`}>
                                  {alert.data.percentChange > 0 ? '+' : ''}{alert.data.percentChange.toFixed(2)}%
                                </div>
                              )}
                              
                              {isEma && alert.data?.emaPercentDiff && (
                                <div className="text-sm text-purple-700 dark:text-purple-400 font-bold">
                                  {alert.data.emaPercentDiff > 0 ? '+' : ''}{alert.data.emaPercentDiff.toFixed(2)}% from 200 EMA
                                </div>
                              )}
                              
                              {isHalt && (() => {
                                // Extract halt code from message (e.g., "LULD", "T1", "T2", "T3", etc.)
                                const message = alert.message || '';
                                const haltCode = (() => {
                                  if (message.includes('LULD')) return 'LULD';
                                  if (message.includes('T1')) return 'T1';
                                  if (message.includes('T2')) return 'T2';
                                  if (message.includes('T3')) return 'T3';
                                  if (message.includes('T12')) return 'T12';
                                  if (message.includes('CB')) return 'CB';
                                  if (message.includes('C')) return 'C';
                                  if (message.includes('R')) return 'R';
                                  // Try to extract from end of message after dash
                                  const parts = message.split(' - ');
                                  if (parts.length > 1) {
                                    const lastPart = parts[parts.length - 1].trim();
                                    if (lastPart.length <= 4 && lastPart.length >= 2) {
                                      return lastPart;
                                    }
                                  }
                                  return 'HALT';
                                })();
                                
                                return (
                                  <div className="text-sm text-red-600 dark:text-red-400 font-bold">
                                    Trading Halted ({haltCode})
                                  </div>
                                );
                              })()}
                              
                              {isResume && (
                                <div className="text-sm text-green-700 dark:text-green-400 font-bold">
                                  Trading Resumed
                                </div>
                              )}

                              {isEmaTrending && emaTrendMeta && (() => {
                                const count = typeof alert.data?.delta === 'number' ? Math.round(alert.data.delta) : undefined;
                                const level = typeof alert.data?.alertLevel === 'number' ? alert.data.alertLevel : 0;
                                const unit = emaTrendMeta.tf === 'daily' ? 'days' : 'bars';
                                const levelColors = ['text-amber-600 dark:text-amber-400', 'text-amber-600 dark:text-amber-400', 'text-teal-700 dark:text-teal-400', 'text-green-700 dark:text-green-400', 'text-emerald-700 dark:text-emerald-400', 'text-emerald-700 dark:text-emerald-400'];
                                const levelColor = levelColors[Math.min(level, 5)] ?? 'text-amber-600 dark:text-amber-400';
                                return (
                                  <div className="flex items-center gap-2 flex-wrap">
                                    {count !== undefined && (
                                      <span className={`text-sm font-bold ${levelColor}`}>
                                        {count} {unit}
                                      </span>
                                    )}
                                    {level > 0 && (
                                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${
                                        level >= 4 ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/10 border-emerald-300 dark:border-emerald-500/30' :
                                        level >= 3 ? 'text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-500/10 border-green-300 dark:border-green-500/30' :
                                        level >= 2 ? 'text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-500/10 border-teal-300 dark:border-teal-500/30' :
                                        'text-amber-600 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 border-amber-300 dark:border-amber-500/30'
                                      }`}>L{level}</span>
                                    )}
                                  </div>
                                );
                              })()}

                              {isNews && (
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  {newsSentiment && (
                                    <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                                      newsSentiment === 'positive' ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/15' :
                                      newsSentiment === 'negative' ? 'text-rose-600 dark:text-rose-300 bg-rose-50 dark:bg-rose-500/15' :
                                      'text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-500/15'
                                    }`}>
                                      {newsSentiment === 'positive' ? '▲ Bullish' : newsSentiment === 'negative' ? '▼ Bearish' : '● Neutral'}
                                    </span>
                                  )}
                                  {alert.news?.publisher && (
                                    <span className="text-[10px] text-muted-foreground font-medium truncate">{alert.news.publisher}</span>
                                  )}
                                </div>
                              )}

                              {!isNews && alert.data?.price && alert.data.price > 0 && (
                                <div className="text-xs text-muted-foreground">
                                  Price: <span className="text-foreground/80 font-medium">${formatPrice(alert.data.price)}</span>
                                </div>
                              )}
                            </div>
                            
                            <p className={`leading-snug ${isNews ? 'line-clamp-2 text-xs text-foreground/80 font-medium' : 'line-clamp-1 text-[11px] text-muted-foreground'}`}>
                              {isHalt ? (() => {
                                // Show a cleaner message for halt alerts
                                const message = alert.message || '';
                                const parts = message.split(' - ');
                                if (parts.length > 1) {
                                  // Show the first part (before the halt code)
                                  return parts[0].trim();
                                }
                                return message;
                              })() : alert.message}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Last 12 Months Chart - Enhanced Polish — pinned (never pushed below fold) as the rail's fixed-size tail. */}
            <div className="bg-background border border-border/60 rounded-2xl overflow-hidden shadow-2xl ring-1 ring-border dark:ring-zinc-800/50 flex-shrink-0">
              <div className="flex items-center justify-between px-4 py-2 border-b border-border/50 bg-card/40">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-lg dark:shadow-emerald-500/50"></div>
                  <h3 className="text-sm font-bold text-foreground tracking-wide">Last 12 Months</h3>
                  <span className="text-xs text-muted-foreground font-medium">* Daily candles</span>
                  {/* EMA legend + Avg Daily % — updates on crosshair hover */}
                  {!dailyChartLoading && (
                    <div className="flex items-center gap-3 ml-2">
                      <span className="flex items-center gap-1 text-xs font-mono">
                        <span className="inline-block w-3 h-[2px] bg-blue-500 rounded"></span>
                        <span className="text-blue-700 dark:text-blue-400">9</span>
                        <span className="text-foreground/80">{dailyEma9Value != null ? `$${dailyEma9Value.toFixed(2)}` : '–'}</span>
                      </span>
                      <span className="flex items-center gap-1 text-xs font-mono">
                        <span className="inline-block w-3 h-[2px] bg-amber-400 rounded"></span>
                        <span className="text-amber-600 dark:text-amber-400">50</span>
                        <span className="text-foreground/80">{dailyEma50Value != null ? `$${dailyEma50Value.toFixed(2)}` : '–'}</span>
                      </span>
                      <span className="flex items-center gap-1 text-xs font-mono">
                        <span className="inline-block w-3 h-[2px] bg-emerald-400 rounded"></span>
                        <span className="text-emerald-700 dark:text-emerald-400">200</span>
                        <span className="text-foreground/80">{dailyEma200Value != null ? `$${dailyEma200Value.toFixed(2)}` : '–'}</span>
                      </span>
                      {avgDailyPct != null && (
                        <span className="flex items-center gap-1 text-xs font-mono border-l border-border pl-3">
                          <span className="text-muted-foreground">avg/day</span>
                          <span className={avgDailyPct >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>
                            {avgDailyPct >= 0 ? '+' : ''}{avgDailyPct.toFixed(2)}%
                          </span>
                        </span>
                      )}
                    </div>
                  )}
                </div>
                  {dailyChartLoading && (
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full animate-pulse"></div>
                    <span className="text-xs text-muted-foreground font-medium">Loading...</span>
            </div>
                  )}
                </div>
              <div ref={dailyContainerRef as any} className="w-full h-[210px] p-2 bg-background flex items-center justify-center" style={{ minHeight: 180 }} />
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom spacing to ensure legend is visible */}
      <div className="h-64"></div>
      
      <style dangerouslySetInnerHTML={{ __html: '.custom-scrollbar::-webkit-scrollbar{width:8px}.custom-scrollbar::-webkit-scrollbar-track{background:#18181b;border-radius:4px}.custom-scrollbar::-webkit-scrollbar-thumb{background:#3f3f46;border-radius:4px}.custom-scrollbar::-webkit-scrollbar-thumb:hover{background:#52525b}' }} />
    </div>

    {/* Manual Alpaca Buy modal — admin only */}
    {manualBuyOpen && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 backdrop-blur-sm"
        onClick={(e) => { if (e.target === e.currentTarget) setManualBuyOpen(false); }}
      >
        <div className="bg-card border border-border rounded-xl p-6 w-80 shadow-2xl dark:shadow-black/60">
          <h2 className="text-lg font-bold text-foreground mb-1">Alpaca Buy</h2>
          <p className="text-xs text-muted-foreground mb-4">
            Places a Ra limit order. Ra adopts the position on its next tick and manages exits.
          </p>
          <div className="space-y-3">
            <div>
              <span className="text-xs text-muted-foreground uppercase tracking-widest">Symbol</span>
              <div className="mt-1 px-3 py-2 bg-muted rounded-lg text-sm font-bold text-amber-600 dark:text-amber-400 border border-border/50">
                ${symbol}
              </div>
            </div>
            <div>
              <label htmlFor="manual-buy-price" className="text-xs text-muted-foreground uppercase tracking-widest">
                Limit Price
              </label>
              <input
                id="manual-buy-price"
                type="number"
                step="0.01"
                min="0.0001"
                value={manualBuyPrice}
                onChange={(e) => setManualBuyPrice(e.target.value)}
                className="mt-1 w-full px-3 py-2 bg-muted rounded-lg text-sm text-foreground border border-border focus:outline-none focus:border-emerald-300 dark:focus:border-emerald-500/70 focus:ring-1 focus:ring-emerald-300 dark:focus:ring-emerald-500/30"
                placeholder="0.00"
                autoFocus
                disabled={manualBuyLoading || manualBuySuccess}
              />
            </div>
            <div>
              <label htmlFor="manual-buy-pct" className="text-xs text-muted-foreground uppercase tracking-widest">
                Size - % of account <span className="text-zinc-600 normal-case lowercase">(blank = auto)</span>
              </label>
              <div className="relative mt-1">
                <input
                  id="manual-buy-pct"
                  type="number"
                  step="0.5"
                  min="0.1"
                  max="100"
                  value={manualBuyPct}
                  onChange={(e) => setManualBuyPct(e.target.value)}
                  className="w-full px-3 py-2 pr-7 bg-muted rounded-lg text-sm text-foreground border border-border focus:outline-none focus:border-emerald-300 dark:focus:border-emerald-500/70 focus:ring-1 focus:ring-emerald-300 dark:focus:ring-emerald-500/30"
                  placeholder="auto"
                  disabled={manualBuyLoading || manualBuySuccess}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
              </div>
              {parseFloat(manualBuyPct) > 0 && (
                <p className="mt-1 text-[11px] text-emerald-700 dark:text-emerald-400/80">
                  Invests {parseFloat(manualBuyPct)}% of account value (vs the automatic slice).
                </p>
              )}
            </div>
            {manualBuyError && (
              <p className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 border border-red-300 dark:border-red-500/20 rounded-lg px-3 py-2">{manualBuyError}</p>
            )}
            {manualBuyOneSharePrice && (
              <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/30 rounded-lg px-3 py-2 space-y-2">
                <p className="text-xs text-amber-600 dark:text-amber-300">
                  Extended hours - fractional orders are blocked. Minimum purchase is 1 whole share at ${manualBuyOneSharePrice.toFixed(2)}.
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={async () => {
                      setManualBuyLoading(true);
                      setManualBuyOneSharePrice(null);
                      try {
                        const res = await fetch('/api/ra/manual-buy', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ symbol, price: manualBuyOneSharePrice, confirmOneShare: true }),
                        });
                        const data = await res.json() as { error?: string };
                        if (!res.ok) {
                          setManualBuyError(data.error ?? 'Order failed');
                        } else {
                          setManualBuySuccess(true);
                          setTimeout(() => setManualBuyOpen(false), 1800);
                        }
                      } catch {
                        setManualBuyError('Network error - check connection');
                      } finally {
                        setManualBuyLoading(false);
                      }
                    }}
                    className="flex-1 px-3 py-1.5 text-xs font-semibold bg-amber-600/80 hover:bg-amber-600 text-foreground rounded-lg transition"
                  >
                    Buy 1 share @ ${manualBuyOneSharePrice.toFixed(2)}
                  </button>
                  <button
                    type="button"
                    onClick={() => setManualBuyOneSharePrice(null)}
                    className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground/80 rounded-lg transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            {manualBuySuccess && (
              <p className="text-xs text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/20 rounded-lg px-3 py-2">
                Order submitted - Ra will adopt this position.
              </p>
            )}
          </div>
          <div className="flex gap-3 mt-5">
            <button
              type="button"
              onClick={() => setManualBuyOpen(false)}
              className="flex-1 px-3 py-2 text-sm text-muted-foreground bg-muted hover:bg-accent dark:hover:bg-zinc-700 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={manualBuyLoading || manualBuySuccess || !manualBuyPrice || parseFloat(manualBuyPrice) <= 0}
              onClick={async () => {
                setManualBuyLoading(true);
                setManualBuyError(null);
                setManualBuyOneSharePrice(null);
                try {
                  const pctVal = parseFloat(manualBuyPct);
                  const res = await fetch('/api/ra/manual-buy', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      symbol,
                      price: parseFloat(manualBuyPrice),
                      ...(Number.isFinite(pctVal) && pctVal > 0 ? { investPct: pctVal } : {}),
                    }),
                  });
                  const data = await res.json() as { error?: string; canConfirmOneShare?: boolean; price?: number };
                  if (!res.ok) {
                    if (data.canConfirmOneShare && data.price) {
                      // AH fractional limit — surface a one-share confirmation instead of plain error
                      setManualBuyOneSharePrice(data.price);
                    } else {
                      setManualBuyError(data.error ?? 'Order failed');
                    }
                  } else {
                    setManualBuySuccess(true);
                    setTimeout(() => setManualBuyOpen(false), 1800);
                  }
                } catch {
                  setManualBuyError('Network error - check connection');
                } finally {
                  setManualBuyLoading(false);
                }
              }}
              className="flex-1 px-3 py-2 text-sm font-semibold bg-emerald-600/80 hover:bg-emerald-600 text-foreground rounded-lg transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {manualBuyLoading ? 'Submitting…' : 'Buy'}
            </button>
          </div>
        </div>
      </div>
    )}
    </ErrorBoundary>
  );
}
